import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE_URL, INT_QUESTIONNAIRE_BASE_URL, fetchJson } from './api';
import useDeviceType from './useDeviceType';
import SCurveChart from './SCurveChart';
import { computeSCurveBaseline } from './ProjectCostsSummaryTable';
import { useNotifications } from './notifications';
import { Alert, Box, Button, Chip, CircularProgress, Container, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import PortalShell from './PortalShell';
import { formatReportVersion, getReportVersionNaming } from './reportVersioning';
import { filterInfoRowFields } from './infoRowFieldFilter';
import { AnalyticsPageHero, AnalyticsPanelSection, analyticsPanelSx } from './analyticsSurface';
import SiteVisitDialog from './SiteVisitDialog';
import ProjectRepositoryExplorer from './ProjectRepositoryExplorer';
import ProjectCostDashboard from './ProjectCostDashboard';
import ProjectPortalNavigation, { resolveProjectPortalWorkspace } from './ProjectPortalNavigation';
import ProjectPortalOverview from './ProjectPortalOverview';
import ProjectRecordNavigation, { resolveProjectRecordSection } from './ProjectRecordNavigation';
import ProjectRecordDetail from './ProjectRecordDetail';
import ReportingPeriodWorkspace from './ReportingPeriodWorkspace';
import DataIntroductionDialog from './DataIntroductionDialog';
import ReportDataConflictDialog, { isReportDataReconciliationError, QuestionnairePreflightDialog } from './ReportDataConflictDialog';
import InitialReportInformationGrid from './InitialReportInformationGrid';
import InitialReportSectionTabs from './InitialReportSectionTabs';
import ReportEvidenceEditor from './ReportEvidenceEditor';
import { projectRecordArea, projectRecordServices } from './projectRecordEvidenceProjection';
import ReportEvidenceOutputReview from './ReportEvidenceOutputReview';
import ReportGenerationProgress, { getReportGenerationDetail } from './ReportGenerationProgress';
import ReportDiagnostics, { canViewReportDiagnostics } from './ReportDiagnostics';
import { getPriorInterimNumbers } from './priorInterimNumbers';

const DEV_PORTAL_SETTINGS_KEY = 'monsurv_developer_portal_settings_v1';
const DEV_PORTAL_START_VIEWS = ['overview', 'reports', 'activity', 'request_valuation', 'settings'];

// Initial Report appendices: fixed list (document types + Site Photos).
// Availability is derived from uploaded documents/photos and displayed as "(N/A)" when missing.
const INITIAL_REPORT_APPENDIX_ITEMS = [
    { title: 'Site Photos', kind: 'photos', uploadCategory: 'existing-site-photos' },
    { title: 'Planning Decision Notice', kind: 'document_type' },
    { title: 'Construction Costs', kind: 'document_type' },
    { title: 'Proposed Designs', kind: 'document_type' },
    { title: 'CIL Liability Notice', kind: 'document_type' }
];

// Interim Report appendices (phase 1): fixed to Site Photos only.
const INTERIM_REPORT_APPENDIX_ITEMS = [
    { title: 'Site Photos', kind: 'photos', answerField: 'cp_site_photos' }
];

// Map fixed appendix labels to the actual ini_documents.document_type values used in SQL.
// (Keeps the UX stable while linking to existing SQL taxonomy.)
const INITIAL_REPORT_APPENDIX_SQL_ALIASES = {
    'planning decision notice': ['decision notice'],
    'construction costs': ['project cost documentation'],
    'proposed designs': ['project case studies (photos)'],
    'cil liability notice': ['cil liability notice']
};

const DASHBOARD_TOKEN_KEY = 'monsurv_auth_token';
const PORTAL_TOKEN_KEY = 'monsurv_portal_auth_token';

function getInterimNumberFromReportInstance(reportInstance) {
    const match = String(reportInstance || '').trim().toLowerCase().match(/^interim:(\d+)$/);
    const value = match ? Number(match[1]) : 0;
    return Number.isFinite(value) && value > 0 ? Math.trunc(value) : null;
}

function approvedIntroductionLabel(category) {
    const labels = {
        planning_details: 'Planning details',
        planning_history: 'Planning history',
        costs: 'Construction cost benchmark',
        gia: 'Gross internal area',
        notes: 'Surveyor notes',
        voice_detail: 'Voice report detail',
        voice_transcript: 'Voice transcript',
        email_detail: 'Email project detail',
        soi_mapping: 'Scope of instruction'
    };
    const key = String(category || '').trim().toLowerCase();
    return labels[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase()) || 'Approved evidence';
}

function formatApprovedIntroductionValue(proposal) {
    const category = String(proposal?.category || '').trim().toLowerCase();
    const value = proposal?.proposed_value;
    if (category === 'voice_transcript') return String(value?.transcript || '').trim();
    if (category === 'voice_detail') return String(value?.professional_note || '').trim();
    if (category === 'notes') return String(value?.unclassified_text || '').trim();
    if (category === 'email_detail') return String(value?.inferred_detail || '').trim();
    if (category === 'gia') return Number.isFinite(Number(value)) ? `${Number(value).toLocaleString('en-GB')} sq ft` : String(value || '');
    if (category === 'planning_history' && Array.isArray(value)) {
        return value.map((item) => String(item?.reference || item?.name || item?.entity || '').trim()).filter(Boolean).join('; ');
    }
    if (category === 'planning_details' && value && typeof value === 'object') {
        const authorities = (Array.isArray(value.planning_authorities) ? value.planning_authorities : [])
            .map((item) => String(item?.name || item?.reference || '').trim()).filter(Boolean);
        const constraints = (Array.isArray(value.site_constraints) ? value.site_constraints : [])
            .map((item) => String(item?.name || item?.reference || item?.dataset || '').trim()).filter(Boolean);
        return [
            authorities.length ? `Planning authority: ${authorities.join(', ')}` : '',
            constraints.length ? `Site constraints: ${constraints.join('; ')}` : 'No intersecting constraints were returned by Planning Data.'
        ].filter(Boolean).join(' ');
    }
    if (category === 'costs' && value && typeof value === 'object') {
        const benchmark = Number(value.benchmark_cost_per_sqft);
        const low = Number(value.benchmark_range_low);
        const high = Number(value.benchmark_range_high);
        const implied = Number(value.submitted_implied_cost_per_sqft);
        const comparison = String(value.comparison_to_submitted || '').trim();
        return [
            Number.isFinite(benchmark) ? `Benchmark: £${benchmark.toLocaleString('en-GB')} per sq ft.` : '',
            Number.isFinite(low) && Number.isFinite(high) ? `Indicative range: £${low.toLocaleString('en-GB')}–£${high.toLocaleString('en-GB')} per sq ft.` : '',
            Number.isFinite(implied) ? `Submitted implied rate: £${implied.toLocaleString('en-GB')} per sq ft.` : '',
            comparison && comparison !== '[object Object]' ? comparison : '',
            Array.isArray(value.key_drivers) && value.key_drivers.length ? `Drivers: ${value.key_drivers.join('; ')}.` : '',
            Array.isArray(value.caveats) && value.caveats.length ? `Caveats: ${value.caveats.join('; ')}.` : '',
            String(proposal?.rationale || '').trim() ? `Assessment: ${String(proposal.rationale).trim()}` : ''
        ].filter(Boolean).join(' ');
    }
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return value == null ? '' : JSON.stringify(value);
}

// --- Risk Matrix (shared with Project Summary) ---
// Backend supports /summary_risk_rating; keep requests single-path to avoid 404 spam.
const RISK_API_PATHS = ['/summary_risk_rating'];
const RISK_CRITERIA_LIST = [
    'Site Progress',
    'Planning',
    'Building Control',
    'Building Warranty',
    'Procurement',
    'Programme',
    'Insurances',
    'Costs',
    'Outstanding Information'
];
const RISK_FIELD_MAP = {
    'Site Progress': 'site_progress',
    Planning: 'planning',
    'Building Control': 'building_control',
    'Building Warranty': 'building_warranty',
    Procurement: 'procurement',
    Programme: 'programme',
    Costs: 'costs',
    Insurances: 'insurances',
    'Outstanding Information': 'outstanding_info'
};

function normalizeRiskRating(value) {
    const v = String(value || '').trim().toLowerCase();
    if (!v) return '';
    // Canonical values:
    if (v === 'low' || v === 'l') return 'Low';
    if (v === 'medium' || v === 'med' || v === 'm') return 'Medium';
    if (v === 'high' || v === 'h') return 'High';
    // Back-compat legacy RAG:
    if (v === 'green' || v === 'g') return 'Low';
    if (v === 'amber' || v === 'yellow' || v === 'a' || v === 'y') return 'Medium';
    if (v === 'red' || v === 'r') return 'High';
    return String(value).trim();
}

function normalizeRiskMatrix(apiData) {
    const filled = {};
    RISK_CRITERIA_LIST.forEach((c) => {
        filled[c] = { rating: '', comment: '' };
    });

    if (!apiData) return filled;

    if (apiData.data && typeof apiData.data === 'object') {
        return normalizeRiskMatrix(apiData.data);
    }

    RISK_CRITERIA_LIST.forEach((c) => {
        const v = apiData[c];
        if (v && typeof v === 'object') {
            if (typeof v.rating === 'string') filled[c].rating = normalizeRiskRating(v.rating);
            if (typeof v.comment === 'string') filled[c].comment = v.comment;
        }
    });

    RISK_CRITERIA_LIST.forEach((c) => {
        const prefix = RISK_FIELD_MAP[c];
        if (!prefix) return;
        const rag = apiData[`${prefix}_rag`]
            ?? (c === 'Site Progress' ? apiData.site_rag : c === 'Procurement' ? apiData.contract_rag : undefined);
        const comment = apiData[`${prefix}_comment`]
            ?? (c === 'Site Progress' ? apiData.site_comment : c === 'Procurement' ? apiData.contract_comment : undefined);
        if (typeof rag === 'string') filled[c].rating = normalizeRiskRating(rag);
        if (typeof comment === 'string') filled[c].comment = comment;
    });

    return filled;
}

function normalizeRiskMatrixFromInterimAnswers(answers) {
    const a = answers && typeof answers === 'object' ? answers : null;
    if (!a) return normalizeRiskMatrix(null);

    const getRating = (...keys) => {
        for (const key of keys) {
            const raw = String(a?.[key] || '').trim();
            if (raw) return normalizeRiskRating(raw);
        }
        return '';
    };

    const getComment = (...keys) => {
        for (const key of keys) {
            const raw = String(a?.[key] || '').trim();
            if (raw) return raw;
        }
        return '';
    };

    return {
        'Site Progress': {
            rating: getRating('risk_designation_construction_progress', 'risk_designation_site_progress'),
            comment: getComment('risk_designation_commentary_construction_progress', 'risk_designation_commentary_site_progress')
        },
        Planning: {
            rating: getRating('risk_designation_planning'),
            comment: getComment('risk_designation_commentary_planning')
        },
        'Building Control': {
            rating: getRating('risk_designation_building_control', 'risk_designation_building_control_warranty'),
            comment: getComment('risk_designation_commentary_building_control', 'risk_designation_commentary_building_control_warranty')
        },
        'Building Warranty': {
            rating: getRating('risk_designation_building_warranty', 'risk_designation_building_control_warranty'),
            comment: getComment('risk_designation_commentary_building_warranty', 'risk_designation_commentary_building_control_warranty')
        },
        Procurement: {
            rating: getRating('risk_designation_procurement_contract', 'risk_designation_procurement'),
            comment: getComment('risk_designation_commentary_procurement_contract', 'risk_designation_commentary_procurement')
        },
        Programme: {
            rating: getRating('risk_designation_programme'),
            comment: getComment('risk_designation_commentary_programme')
        },
        Insurances: {
            rating: getRating('risk_designation_insurances'),
            comment: getComment('risk_designation_commentary_insurances')
        },
        Costs: {
            rating: getRating('risk_designation_development_costs', 'risk_designation_drawdown', 'risk_designation_costs'),
            comment: getComment('risk_designation_commentary_development_costs', 'risk_designation_commentary_drawdown', 'risk_designation_commentary_costs')
        },
        'Outstanding Information': {
            rating: getRating('risk_designation_other_items', 'risk_designation_outstanding_information'),
            comment: getComment('risk_designation_commentary_other_items', 'risk_designation_commentary_outstanding_information')
        }
    };
}

function isTransientGatewayError(err) {
    if (err?.isNetworkError || err?.isGatewayError) return true;
    const status = Number(err?.status || 0);
    if (status === 502 || status === 503 || status === 504) return true;
    if (err?.name === 'AbortError') return true;

    const msg = String(err?.message || '').toLowerCase();
    if (msg.includes('failed to fetch')) return true;
    if (msg.includes('network request failed')) return true;
    return false;
}

function waitMs(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
}

const REPORT_DOCX_WAIT_TIMEOUT_MS = 15 * 60 * 1000;
const REPORT_LOG_FETCH_ATTEMPTS = 2;
const MAX_TRANSIENT_POLL_FAILURES = 6;

function getPollingDelayMs({ consecutiveFailures, successDelayMs = 900, maxDelayMs = 7000 }) {
    const failures = Math.max(0, Number(consecutiveFailures) || 0);
    if (!failures) return successDelayMs;
    const growth = Math.min(failures, 6);
    const next = Math.round(successDelayMs * Math.pow(1.8, growth));
    return Math.min(maxDelayMs, Math.max(successDelayMs, next));
}

function getDefaultInitialqBaseUrl() {
    const useLocalhost = ['1', 'true', 'yes', 'y', 'on'].includes(String(process.env.REACT_APP_USE_LOCALHOST || '').trim().toLowerCase());
    const loopbackHost = String(process.env.REACT_APP_LOOPBACK_HOST || '127.0.0.1').trim();
    const loopbackIniPort = String(process.env.REACT_APP_INITIALQ_PORT || '3050').trim();
    if (process.env.NODE_ENV !== 'production') {
        const host = useLocalhost ? 'localhost' : loopbackHost;
        return `http://${host}:${loopbackIniPort}`;
    }
    try {
        const host = String(window.location.hostname || '').toLowerCase();
        if (host.endsWith('.monsurv.co.uk')) return 'https://initialq.monsurv.co.uk';
    } catch {
        // ignore
    }
    return 'https://initialq.monsurv.com';
}

function normalizePortalCostKey(text) {
    return String(text || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

function toPortalMoney(value) {
    const n = Number(String(value ?? '').replace(/[^0-9.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

function getPortalReportValue(report, candidateKeys) {
    const candidates = new Set((Array.isArray(candidateKeys) ? candidateKeys : []).map(normalizePortalCostKey));
    const tables = report?.tables && typeof report.tables === 'object' ? report.tables : {};
    for (const rows of Object.values(tables)) {
        if (!Array.isArray(rows)) continue;
        for (const row of rows) {
            if (!row || typeof row !== 'object') continue;
            for (const [key, value] of Object.entries(row)) {
                if (!candidates.has(normalizePortalCostKey(key))) continue;
                if (value == null || typeof value === 'object') continue;
                const text = String(value).trim();
                if (text) return text;
            }
        }
    }
    return '';
}

function getPortalRowValue(row, candidateKeys) {
    if (!row || typeof row !== 'object') return '';
    for (const key of candidateKeys) {
        const value = row[key];
        if (value == null || typeof value === 'object') continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return '';
}

function countPortalReportFacts(report) {
    const tables = report?.tables && typeof report.tables === 'object' ? report.tables : {};
    return Object.values(tables).reduce((count, rows) => {
        if (!Array.isArray(rows)) return count;
        return count + rows.reduce((rowCount, row) => {
            if (!row || typeof row !== 'object') return rowCount;
            return rowCount + Object.entries(row).filter(([key, value]) => {
                if (['id', 'project_reference', 'created_at', 'updated_at'].includes(String(key).toLowerCase())) return false;
                return value != null && typeof value !== 'object' && String(value).trim() !== '';
            }).length;
        }, 0);
    }, 0);
}

function formatPortalShortDate(value) {
    const date = value ? new Date(value) : null;
    if (!date || !Number.isFinite(date.getTime())) return '';
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function mapProjectSetupToPortalCostBaseline(projectSetupRecord) {
    const costs = projectSetupRecord?.development_costs && typeof projectSetupRecord.development_costs === 'object'
        ? projectSetupRecord.development_costs
        : (projectSetupRecord?.developmentCosts && typeof projectSetupRecord.developmentCosts === 'object'
            ? projectSetupRecord.developmentCosts
            : null);
    if (!costs) return null;

    const constructionRows = Array.isArray(costs.constructionBudgetRows) ? costs.constructionBudgetRows : [];
    const professionalRows = Array.isArray(costs.professionalFeeBudgetRows) ? costs.professionalFeeBudgetRows : [];
    const additionalRows = Array.isArray(costs.additionalCostBudgetRows) ? costs.additionalCostBudgetRows : [];
    const contingencyRows = Array.isArray(costs.contingencyBudgetRows) ? costs.contingencyBudgetRows : [];
    const previousDrawdownRows = Array.isArray(costs.previousDrawdownRows) ? costs.previousDrawdownRows : [];

    const borrowerInputRelevantRaw = costs.borrowerInputRelevant ?? costs.borrower_input_relevant;
    const borrowerInputRelevantText = String(borrowerInputRelevantRaw ?? '').trim().toLowerCase();
    const borrowerInputAmountRaw =
        costs.borrowerInputAmount
        ?? costs.borrower_input_amount
        ?? costs.borrower_input
        ?? costs.borrower_equity
        ?? costs.borrowerEquity
        ?? costs.borrower_equity_provided
        ?? costs.borrowerEquityProvided
        ?? costs.less_borrower_input
        ?? costs.lessBorrowerInput;
    const borrowerInputAmountPresent = borrowerInputAmountRaw != null && String(borrowerInputAmountRaw).trim() !== '';
    const borrowerInputAmount = borrowerInputAmountPresent ? toPortalMoney(borrowerInputAmountRaw) : null;
    const borrowerInputRelevantExplicit = borrowerInputRelevantRaw === true
        || borrowerInputRelevantRaw === false
        || ['yes', 'no', 'true', 'false', '1', '0'].includes(borrowerInputRelevantText);
    const borrowerInputRelevant = borrowerInputRelevantExplicit
        ? (borrowerInputRelevantRaw === true || borrowerInputRelevantText === 'yes' || borrowerInputRelevantText === 'true' || borrowerInputRelevantText === '1')
        : ((borrowerInputAmount ?? 0) > 0);

    const constructionTotal = constructionRows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0);
    const professionalTotal = professionalRows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0);
    const additionalTotal = additionalRows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0);
    const contingencyTotal = toPortalMoney(costs.contingencyBudgetTotal)
        || contingencyRows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0);

    const costToDate = {
        construction: constructionRows.reduce((sum, row) => sum + toPortalMoney(row?.costToDate), 0),
        contingency: contingencyRows.reduce((sum, row) => sum + toPortalMoney(row?.costToDate), 0),
        professional: professionalRows.reduce((sum, row) => sum + toPortalMoney(row?.costToDate), 0),
        additional: additionalRows.reduce((sum, row) => sum + toPortalMoney(row?.costToDate), 0)
    };

    const drawdownHistory = previousDrawdownRows
        .map((row, idx) => ({
            label: String(row?.item || `Drawdown ${idx + 1}`).trim() || `Drawdown ${idx + 1}`,
            amount: toPortalMoney(row?.amount)
        }))
        .filter((row) => row.amount > 0);

    const ini7CostsRow = {
        build_cost: String(constructionTotal || ''),
        professional_fees: String(professionalTotal || ''),
        other_development_costs: String(additionalTotal || ''),
        contingency: String(contingencyTotal || ''),
        construction_cost_breakdown: JSON.stringify(constructionRows.map((row) => ({
            heading: String(row?.item || '').trim(),
            amount: String(row?.budget || '').trim()
        })).filter((row) => row.heading || row.amount)),
        professional_fees_breakdown: JSON.stringify(professionalRows.map((row) => ({
            heading: String(row?.item || '').trim(),
            amount: String(row?.budget || '').trim()
        })).filter((row) => row.heading || row.amount)),
        other_fees_breakdown: JSON.stringify(additionalRows.map((row) => ({
            heading: String(row?.item || '').trim(),
            amount: String(row?.budget || '').trim()
        })).filter((row) => row.heading || row.amount))
    };

    const fallbackCostMap = new Map();
    fallbackCostMap.set('build_cost', ini7CostsRow.build_cost);
    fallbackCostMap.set('professional_fees', ini7CostsRow.professional_fees);
    fallbackCostMap.set('other_development_costs', ini7CostsRow.other_development_costs);
    fallbackCostMap.set('contingency', ini7CostsRow.contingency);

    constructionRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim();
        const key = heading ? `construction_cost_breakdown:${heading}` : `construction_cost_breakdown:idx:${idx + 1}`;
        fallbackCostMap.set(key, String(row?.budget || '').trim());
    });

    professionalRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim();
        const key = heading ? `professional_fees_breakdown:${heading}` : `professional_fees_breakdown:idx:${idx + 1}`;
        fallbackCostMap.set(key, String(row?.budget || '').trim());
    });

    additionalRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim() || `Item ${idx + 1}`;
        const key = `other_cost:${normalizePortalCostKey(heading).replace(/\s+/g, '_')}`;
        fallbackCostMap.set(key, String(row?.budget || '').trim());
    });

    const constructionCostToDateByKey = {};
    constructionRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim();
        const key = makeCostItemKey('construction_cost_breakdown', heading, idx);
        constructionCostToDateByKey[key] = toPortalMoney(row?.costToDate);
    });

    const professionalCostToDateByKey = {};
    professionalRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim();
        const key = makeCostItemKey('professional_fees_breakdown', heading, idx);
        professionalCostToDateByKey[key] = toPortalMoney(row?.costToDate);
    });

    const additionalCostToDateByKey = {};
    additionalRows.forEach((row, idx) => {
        const heading = String(row?.item || '').trim() || `Item ${idx + 1}`;
        const key = `other_cost:${normalizePortalCostKey(heading).replace(/\s+/g, '_')}`;
        additionalCostToDateByKey[key] = toPortalMoney(row?.costToDate);
    });

    return {
        ini7CostsRow,
        fallbackCostMap,
        borrowerInputRelevant,
        borrowerInputAmount,
        costToDate,
        constructionCostToDateByKey,
        professionalCostToDateByKey,
        additionalCostToDateByKey,
        drawdownHistory,
        drawdownTotal: drawdownHistory.reduce((sum, row) => sum + row.amount, 0)
    };
}

function DrawdownHistoryTable({ items, projectSetupRecord, loading, error }) {
    if (loading) return <div style={{ color: '#6b7280', fontSize: 12 }}>Loading drawdown history…</div>;
    if (error) return <div style={{ color: '#b91c1c', fontSize: 12 }}>{error}</div>;

    const periods = Array.isArray(items) ? items : [];
    const costs = projectSetupRecord?.development_costs && typeof projectSetupRecord.development_costs === 'object'
        ? projectSetupRecord.development_costs
        : {};
    const drawdownNumbers = Array.from(new Set(
        periods.map((item) => Number(item?.drawdown_number)).filter((number) => number > 0)
    )).sort((a, b) => a - b);
    const money = (value) => Number(value || 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
    const makeRow = (key, label, budget) => ({ key, label, budget: toPortalMoney(budget), values: {} });
    const sectorConfigs = [
        {
            key: 'construction',
            title: 'Construction Costs',
            baselineRows: Array.isArray(costs.constructionBudgetRows) ? costs.constructionBudgetRows : [],
            drawdownField: 'dd_construction',
            itemKey: (row, index) => makeCostItemKey('construction_cost_breakdown', row?.item, index)
        },
        {
            key: 'contingency',
            title: 'Contingency',
            baselineRows: Array.isArray(costs.contingencyBudgetRows) ? costs.contingencyBudgetRows : [],
            drawdownField: 'contingency_claims',
            itemKey: (row, index) => `contingency:${normalizePortalCostKey(row?.item) || index}`
        },
        {
            key: 'additional',
            title: 'Additional Costs',
            baselineRows: Array.isArray(costs.professionalFeeBudgetRows) ? costs.professionalFeeBudgetRows : [],
            drawdownField: 'dd_professional_fees',
            itemKey: (row, index) => makeCostItemKey('professional_fees_breakdown', row?.item, index)
        },
        {
            key: 'other',
            title: 'Other Costs',
            baselineRows: Array.isArray(costs.additionalCostBudgetRows) ? costs.additionalCostBudgetRows : [],
            drawdownField: 'dd_additional_costs',
            itemKey: (row, index) => `other_cost:${normalizePortalCostKey(row?.item || `Item ${index + 1}`).replace(/\s+/g, '_')}`
        }
    ];

    const sectorTables = sectorConfigs.map((sector) => {
        const rows = new Map();
        sector.baselineRows.forEach((row, index) => {
            const key = sector.itemKey(row, index);
            const contingencyBudget = sector.key === 'contingency' && sector.baselineRows.length === 1
                ? (row?.budget || costs.contingencyBudgetTotal)
                : row?.budget;
            rows.set(key, makeRow(key, String(row?.item || `Item ${index + 1}`).trim(), contingencyBudget));
        });

        if (sector.key === 'contingency' && !rows.size && toPortalMoney(costs.contingencyBudgetTotal) > 0) {
            rows.set('contingency:allowance', makeRow('contingency:allowance', 'Contingency Allowance', costs.contingencyBudgetTotal));
        }

        const findRow = (itemKey, label) => {
            if (rows.has(itemKey)) return rows.get(itemKey);
            const normalizedLabel = normalizePortalCostKey(label);
            return Array.from(rows.values()).find((row) => normalizePortalCostKey(row.label) === normalizedLabel) || null;
        };

        periods.forEach((period) => {
            const drawdownNumber = Number(period?.drawdown_number);
            const periodCosts = period?.drawdown_costs && typeof period.drawdown_costs === 'object' ? period.drawdown_costs : {};
            if (sector.key === 'contingency') {
                (Array.isArray(periodCosts.contingency_claims) ? periodCosts.contingency_claims : []).forEach((claim, index) => {
                    const label = String(claim?.description || `Contingency claim ${index + 1}`).trim();
                    const key = `contingency:${normalizePortalCostKey(label) || index}`;
                    let row = findRow(key, label);
                    if (!row) {
                        row = makeRow(key, label, 0);
                        rows.set(key, row);
                    }
                    row.values[drawdownNumber] = (row.values[drawdownNumber] || 0) + toPortalMoney(claim?.value);
                });
                return;
            }

            const values = periodCosts[sector.drawdownField];
            Object.entries(values && typeof values === 'object' ? values : {}).forEach(([itemKey, value]) => {
                const rawLabel = String(itemKey || '').split(':').slice(1).join(':') || itemKey;
                const label = rawLabel.replace(/^idx:\d+$/i, 'Item').replace(/_/g, ' ').trim() || 'Item';
                let row = findRow(itemKey, label);
                if (!row) {
                    row = makeRow(itemKey, label, 0);
                    rows.set(itemKey, row);
                }
                row.values[drawdownNumber] = toPortalMoney(value);
            });
        });

        return { ...sector, rows: Array.from(rows.values()) };
    });
    const allRows = sectorTables.flatMap((sector) => sector.rows);
    const widestLength = (heading, values) => Math.max(heading.length, ...values.map((value) => String(value).length));
    const columnWidths = {
        lineItem: `${widestLength('Budget Line Item', allRows.map((row) => row.label)) + 2}ch`,
        originalBudget: `${widestLength('Original Budget', allRows.map((row) => money(row.budget))) + 2}ch`,
        drawdowns: Object.fromEntries(drawdownNumbers.map((number) => [
            number,
            `${widestLength(`DD${number}`, allRows.map((row) => money(row.values[number]))) + 2}ch`
        ])),
        percentClaimed: `${widestLength('% Claimed', allRows.map((row) => {
            const claimed = drawdownNumbers.reduce((sum, number) => sum + toPortalMoney(row.values[number]), 0);
            return row.budget > 0 ? `${((claimed / row.budget) * 100).toFixed(1)}%` : '—';
        })) + 2}ch`,
        remainingFunds: `${widestLength('Remaining Funds', allRows.map((row) => {
            const claimed = drawdownNumbers.reduce((sum, number) => sum + toPortalMoney(row.values[number]), 0);
            return money(row.budget - claimed);
        })) + 2}ch`
    };
    const historyCellStyle = { border: '1px solid #e5e7eb', padding: '8px 10px', whiteSpace: 'nowrap' };
    const historyHeaderStyle = { ...historyCellStyle, borderColor: '#d1d5db' };
    const historyTableWidth = `calc(${[
        columnWidths.lineItem,
        columnWidths.originalBudget,
        ...drawdownNumbers.map((number) => columnWidths.drawdowns[number]),
        columnWidths.percentClaimed,
        columnWidths.remainingFunds
    ].join(' + ')})`;

    return (
        <div style={{ marginTop: 22 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Drawdown History</h3>
            {sectorTables.map((sector) => (
                <div key={sector.key} style={{ marginTop: 16 }}>
                    <h4 style={{ margin: '0 0 8px', fontSize: 14 }}>{sector.title}</h4>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: historyTableWidth, tableLayout: 'fixed', borderCollapse: 'collapse', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                            <colgroup>
                                <col style={{ width: columnWidths.lineItem }} />
                                <col style={{ width: columnWidths.originalBudget }} />
                                {drawdownNumbers.map((number) => (
                                    <col key={`${sector.key}-dd-col-${number}`} style={{ width: columnWidths.drawdowns[number] }} />
                                ))}
                                <col style={{ width: columnWidths.percentClaimed }} />
                                <col style={{ width: columnWidths.remainingFunds }} />
                            </colgroup>
                            <thead>
                                <tr style={{ background: '#e5e7eb' }}>
                                    <th style={{ ...historyHeaderStyle, textAlign: 'left' }}>Budget Line Item</th>
                                    <th style={{ ...historyHeaderStyle, textAlign: 'right' }}>Original Budget</th>
                                    {drawdownNumbers.map((number) => (
                                        <th key={`${sector.key}-dd-head-${number}`} style={{ ...historyHeaderStyle, textAlign: 'right' }}>DD{number}</th>
                                    ))}
                                    <th style={{ ...historyHeaderStyle, textAlign: 'right' }}>% Claimed</th>
                                    <th style={{ ...historyHeaderStyle, textAlign: 'right' }}>Remaining Funds</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sector.rows.map((row) => {
                                    const claimed = drawdownNumbers.reduce((sum, number) => sum + toPortalMoney(row.values[number]), 0);
                                    const percentClaimed = row.budget > 0 ? `${((claimed / row.budget) * 100).toFixed(1)}%` : '—';
                                    return (
                                        <tr key={row.key}>
                                            <td style={{ ...historyCellStyle, fontWeight: 700 }}>{row.label}</td>
                                            <td style={{ ...historyCellStyle, textAlign: 'right' }}>{money(row.budget)}</td>
                                            {drawdownNumbers.map((number) => (
                                                <td key={`${row.key}-dd-${number}`} style={{ ...historyCellStyle, textAlign: 'right' }}>{money(row.values[number])}</td>
                                            ))}
                                            <td style={{ ...historyCellStyle, textAlign: 'right' }}>{percentClaimed}</td>
                                            <td style={{ ...historyCellStyle, borderColor: '#d1d5db', textAlign: 'right', background: '#f3f4f6', fontWeight: 800 }}>{money(row.budget - claimed)}</td>
                                        </tr>
                                    );
                                })}
                                {!sector.rows.length ? (
                                    <tr><td colSpan={drawdownNumbers.length + 4} style={{ border: '1px solid #e5e7eb', padding: '10px', color: '#6b7280' }}>No budget line items confirmed.</td></tr>
                                ) : null}
                            </tbody>
                        </table>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default function InformationPortal({ mode }) {
    const { deviceType } = useDeviceType();
    const reportVersionNaming = useMemo(() => getReportVersionNaming(), []);
    const [search, setSearch] = useState(() => window.location.search || '');

    // Keep internal search state in sync with in-app navigation that uses history.pushState.
    useEffect(() => {
        const onPopState = () => setSearch(window.location.search || '');
        window.addEventListener('popstate', onPopState);
        return () => window.removeEventListener('popstate', onPopState);
    }, []);

    const params = useMemo(() => new URLSearchParams(search || ''), [search]);
    const reference = (params.get('reference') || '').trim();
    const code = (params.get('code') || '').trim();
    const view = (params.get('view') || '').trim().toLowerCase();
    const page = (params.get('page') || 'initial').toLowerCase();
    const workspace = resolveProjectPortalWorkspace(params.get('workspace'), page);
    const interimNumberRaw = (params.get('interim') || '').trim();
    const interimNumber = interimNumberRaw !== '' && Number.isFinite(Number(interimNumberRaw)) ? Number(interimNumberRaw) : null;

    const [data, setData] = useState(null);
    const [initialReport, setInitialReport] = useState(null);
    const [interimReport, setInterimReport] = useState(null);
    const [projectSetupRecord, setProjectSetupRecord] = useState(null);
    const [confirmLiveOpen, setConfirmLiveOpen] = useState(false);
    const [confirmLiveDraft, setConfirmLiveDraft] = useState(null);
    const [confirmLiveTarget, setConfirmLiveTarget] = useState({ reportType: 'initial', interimNumber: null });
    const [confirmLiveSaving, setConfirmLiveSaving] = useState(false);
    const [confirmLiveError, setConfirmLiveError] = useState('');
    const [costEditorOpen, setCostEditorOpen] = useState(false);
    const [costEditorDraft, setCostEditorDraft] = useState(null);
    const [costEditorSaving, setCostEditorSaving] = useState(false);
    const [costEditorError, setCostEditorError] = useState('');
    const [interimSubmissions, setInterimSubmissions] = useState(() => []);

    const [readiness, setReadiness] = useState(null);
    const [readinessLoading, setReadinessLoading] = useState(false);
    const [readinessError, setReadinessError] = useState('');
    const [readinessReloadKey, setReadinessReloadKey] = useState(0);
    const [aiAlerts, setAiAlerts] = useState(null);
    const [aiAlertsLoading, setAiAlertsLoading] = useState(false);
    const [aiAlertsError, setAiAlertsError] = useState('');
    const [showAiAlerts, setShowAiAlerts] = useState(false);
    const [dataIntroduction, setDataIntroduction] = useState(null);
    const [approvedIntroductions, setApprovedIntroductions] = useState(() => []);
    const [approvedIntroductionsLoading, setApprovedIntroductionsLoading] = useState(false);
    const [approvedIntroductionsError, setApprovedIntroductionsError] = useState('');
    const [approvedIntroductionsReloadKey, setApprovedIntroductionsReloadKey] = useState(0);

    const [drawdownPrefillActuals, setDrawdownPrefillActuals] = useState(() => []);
    const [drawdownPrefillLoading, setDrawdownPrefillLoading] = useState(false);
    const [drawdownPrefillError, setDrawdownPrefillError] = useState('');
    const [drawdownHistory, setDrawdownHistory] = useState(() => []);
    const [drawdownHistoryLoading, setDrawdownHistoryLoading] = useState(false);
    const [drawdownHistoryError, setDrawdownHistoryError] = useState('');
    const [interimSubmissionsWarning, setInterimSubmissionsWarning] = useState('');
    const [nextInterimNumber, setNextInterimNumber] = useState(1);
    const [notesByKey, setNotesByKey] = useState(() => new Map());
    const [fieldReviewsByKey, setFieldReviewsByKey] = useState(() => new Map());
    const [costAssessmentsByKey, setCostAssessmentsByKey] = useState(() => new Map());
    const [editsByKey, setEditsByKey] = useState(() => new Map());
    const [iniDocMetadataById, setIniDocMetadataById] = useState(() => new Map());
    const [iniDocMetadataLoading, setIniDocMetadataLoading] = useState(false);
    const [iniDocMetadataError, setIniDocMetadataError] = useState('');
    const [error, setError] = useState('');
    const [actionError, setActionError] = useState('');
    const [loading, setLoading] = useState(true);
    const [siteVisitOpen, setSiteVisitOpen] = useState(false);
    const [siteVisitState, setSiteVisitState] = useState(null);
    const [siteVisitLoaded, setSiteVisitLoaded] = useState(false);
    const [siteVisitError, setSiteVisitError] = useState('');
    const [siteVisitBusy, setSiteVisitBusy] = useState(false);

    const [generating, setGenerating] = useState(false);
    const [generateError, setGenerateError] = useState('');

    const [generationStage, setGenerationStage] = useState('');
    const [generationStageKey, setGenerationStageKey] = useState('');
    const [generationDetail, setGenerationDetail] = useState('');

    const [latestInitialDocxEntry, setLatestInitialDocxEntry] = useState(null);
    const [latestInterimDocxEntry, setLatestInterimDocxEntry] = useState(null);
    const [hasInitialIssuedPdf, setHasInitialIssuedPdf] = useState(false);
    const [issuedInterimNumbers, setIssuedInterimNumbers] = useState(() => []);
    const generateSeqRef = useRef(0);
    const [reportDataRequest, setReportDataRequest] = useState(null);
    const [questionnaireReviewRequest, setQuestionnaireReviewRequest] = useState(null);
    const reportDataRequestRef = useRef(null);
    const generationExecutingRef = useRef(false);
    const generationContextRef = useRef('');

    const [, setViewedReportOnce] = useState(false);
    const [showQaUpload, setShowQaUpload] = useState(false);
    const [issueUploadBusy, setIssueUploadBusy] = useState(false);
    const [appendixGenBusy, setAppendixGenBusy] = useState(false);
    const [appendixGenMessage, setAppendixGenMessage] = useState('');
    const [appendixPdfUrl, setAppendixPdfUrl] = useState('');

    const [appendixModalOpen, setAppendixModalOpen] = useState(false);
    const [autoInterimApplied, setAutoInterimApplied] = useState(false);
    const [requestedInterims, setRequestedInterims] = useState(() => []);
    const [requestedInterimPayloads, setRequestedInterimPayloads] = useState(() => ({}));

    const loadCurrentSiteVisit = useCallback(async () => {
        if (mode !== 'surveyor' || !reference) {
            setSiteVisitState(null);
            setSiteVisitLoaded(true);
            return null;
        }
        setSiteVisitLoaded(false);
        setSiteVisitError('');
        try {
            const result = await fetchJson(`${API_BASE_URL}/api/site-visits/current?project_reference=${encodeURIComponent(reference)}`);
            setSiteVisitState(result || null);
            return result || null;
        } catch (requestError) {
            setSiteVisitState(null);
            setSiteVisitError(requestError?.message || 'Site Visit details could not be loaded.');
            return null;
        } finally {
            setSiteVisitLoaded(true);
        }
    }, [mode, reference]);

    useEffect(() => {
        void loadCurrentSiteVisit();
    }, [loadCurrentSiteVisit]);

    async function handleSiteVisitSaved(appointment) {
        const nextState = await loadCurrentSiteVisit();
        setSiteVisitOpen(false);
        const bookedInterimNumber = getInterimNumberFromReportInstance(
            appointment?.report_instance || nextState?.appointment?.report_instance
        );
        if (bookedInterimNumber) {
            setAutoInterimApplied(true);
            setQuery({ page: 'interim', interim: bookedInterimNumber });
        }
    }

    async function toggleInitialSlaHold() {
        const appointment = siteVisitState?.appointment;
        if (!appointment || appointment.report_instance !== 'initial') return;
        const activeHold = siteVisitState?.sla_hold?.active;
        const promptLabel = activeHold
            ? 'Optional note for resuming the Initial Report SLA:'
            : 'Reason for pausing the Initial Report SLA while information is outstanding:';
        const note = window.prompt(promptLabel, '');
        if (note === null || (!activeHold && !String(note).trim())) return;
        setSiteVisitBusy(true);
        setSiteVisitError('');
        try {
            if (activeHold) {
                await fetchJson(`${API_BASE_URL}/api/site-visits/${appointment.id}/sla-holds/${activeHold.id}/resume`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note })
                });
            } else {
                await fetchJson(`${API_BASE_URL}/api/site-visits/${appointment.id}/sla-holds`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ reason: note })
                });
            }
            await loadCurrentSiteVisit();
        } catch (requestError) {
            setSiteVisitError(requestError?.message || 'The Initial Report SLA could not be updated.');
        } finally {
            setSiteVisitBusy(false);
        }
    }

    // Developer portal should land on Overview by default.
    useEffect(() => {
        if (mode !== 'developer') return;
        if (String(view || '').trim()) return;

        try {
            let startView = 'overview';
            try {
                const raw = localStorage.getItem(DEV_PORTAL_SETTINGS_KEY);
                const parsed = raw ? JSON.parse(raw) : null;
                const candidate = String(parsed?.defaultStart || '').trim().toLowerCase();
                if (candidate && DEV_PORTAL_START_VIEWS.includes(candidate)) startView = candidate;
            } catch {
                // ignore
            }
            const nextParams = new URLSearchParams(window.location.search || '');
            nextParams.set('view', startView);
            const nextSearch = `?${nextParams.toString()}`;
            window.history.replaceState(null, '', nextSearch);
            setSearch(nextSearch);
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    const reportProgressRows = useMemo(() => {
        return [
            { key: 'preprocessing_documents', label: 'Preparing uploaded documents' },
            { key: 'gathering_data', label: 'Gathering project data' },
            { key: 'passing_to_ai', label: 'Preparing AI inputs' },
            { key: 'gpt_generation', label: 'Generating report sections' },
            { key: 'formatting', label: 'Formatting the report' },
            { key: 'done', label: 'Ready for surveyor review' }
        ];
    }, []);

    function normalizeReportProgressStageKey(value) {
        const stage = String(value || '').trim().toLowerCase();
        if (!stage) return '';
        if (stage === 'preprocessing_documents' || stage.startsWith('preprocessing')) return 'preprocessing_documents';
        if (stage === 'gathering_data' || stage.startsWith('gathering data')) return 'gathering_data';
        if (stage === 'passing_to_ai' || stage.startsWith('sending data') || stage.startsWith('passing data')) return 'passing_to_ai';
        if (
            stage === 'gpt_generation'
            || stage === 'gemini_style'
            || stage === 'claude_validation'
            || stage.startsWith('ai generation')
            || stage.startsWith('style checking')
            || stage.startsWith('validation')
            || stage.includes('gpt5.2')
            || stage.includes('gemini')
            || stage.includes('claude')
        ) return 'gpt_generation';
        if (stage === 'formatting' || stage.startsWith('formatting')) return 'formatting';
        if (stage === 'done' || stage.startsWith('done')) return 'done';
        if (stage === 'error') return 'error';
        return '';
    }

    function getReportProgressStageLabel(value) {
        const stageKey = normalizeReportProgressStageKey(value);
        if (stageKey === 'preprocessing_documents') return 'Preparing Uploaded Documents';
        if (stageKey === 'gathering_data') return 'Gathering Data';
        if (stageKey === 'passing_to_ai') return 'Sending Data to AI Agent';
        if (stageKey === 'gpt_generation') return 'AI Generation in Progress';
        if (stageKey === 'formatting') return 'Formatting Output';
        if (stageKey === 'done') return 'Done!';
        return '';
    }

    function applyPolledReportGenerationStatus(status, startedAtMs, seenLiveStageRef) {
        const stageKey = normalizeReportProgressStageKey(status?.stage);
        if (!stageKey) return false;

        const updatedAtMs = new Date(status?.updated_at || 0).getTime();
        const isFresh = Number.isFinite(updatedAtMs) && updatedAtMs >= (startedAtMs - 1000);
        const isTerminal = stageKey === 'done' || stageKey === 'error';

        if (!isFresh) return false;
        if (!isTerminal) seenLiveStageRef.current = true;
        if (isTerminal && !seenLiveStageRef.current && stageKey !== 'error') return false;

        setGenerationStageKey(stageKey);
        setGenerationDetail(getReportGenerationDetail(status));
        const label = getReportProgressStageLabel(stageKey);
        if (label) setGenerationStage(label);
        return isTerminal;
    }

    const [appendixCount, setAppendixCount] = useState(1);

    const [devOverviewProjects, setDevOverviewProjects] = useState(() => []);
    const [devOverviewSelectedRef, setDevOverviewSelectedRef] = useState('all');
    const [devOverviewDrawnByRef, setDevOverviewDrawnByRef] = useState(() => ({}));
    const [devOverviewLoading, setDevOverviewLoading] = useState(false);
    const [devOverviewError, setDevOverviewError] = useState('');
    const [devProjectsReady, setDevProjectsReady] = useState(false);

    const devAllowedProjectRefsLower = useMemo(() => {
        return (Array.isArray(devOverviewProjects) ? devOverviewProjects : [])
            .map((p) => String(p?.reference || '').trim().toLowerCase())
            .filter(Boolean)
            .sort();
    }, [devOverviewProjects]);

    // Developer portal safety: if the URL has a reference the account isn't allowed to access,
    // clear it so project-specific pages (e.g. Request Valuation) can't appear tied to the wrong project.
    useEffect(() => {
        if (mode !== 'developer') return;
        if (!devAllowedProjectRefsLower.length) return;

        const refLower = String(reference || '').trim().toLowerCase();
        const allowed = devAllowedProjectRefsLower.includes(refLower);

        // If no reference is set and the developer has exactly one project, auto-select it.
        if (!refLower && devAllowedProjectRefsLower.length === 1) {
            try {
                const nextParams = new URLSearchParams(window.location.search || '');
                nextParams.set('reference', String(devOverviewProjects?.[0]?.reference || '').trim());
                const nextSearch = `?${nextParams.toString()}`;
                window.history.replaceState(null, '', nextSearch);
                setSearch(nextSearch);
            } catch {
                // ignore
            }
            return;
        }

        if (!refLower) return;
        if (allowed) return;

        try {
            const nextParams = new URLSearchParams(window.location.search || '');
            nextParams.delete('reference');
            nextParams.delete('page');
            nextParams.delete('interim');
            const nextSearch = `?${nextParams.toString()}`;
            window.history.replaceState(null, '', nextSearch);
            setSearch(nextSearch);
        } catch {
            // ignore
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, reference, devAllowedProjectRefsLower]);

    const [devPortalSettings, setDevPortalSettings] = useState(() => {
        try {
            const raw = localStorage.getItem(DEV_PORTAL_SETTINGS_KEY);
            const parsed = raw ? JSON.parse(raw) : null;
            if (!parsed || typeof parsed !== 'object') {
                return {
                    accountName: '',
                    contactNumber: '',
                    contactEmail: '',
                    defaultStart: 'overview',
                    autoOpenLatestDrawdown: true,
                    notifyReportIssued: true,
                    emailSummary: 'weekly'
                };
            }
            const normalizedStart = (() => {
                const s = String(parsed.defaultStart || '').trim().toLowerCase();
                if (s && DEV_PORTAL_START_VIEWS.includes(s)) return s;
                return 'overview';
            })();
            return {
                accountName: String(parsed.accountName || ''),
                contactNumber: String(parsed.contactNumber || ''),
                contactEmail: String(parsed.contactEmail || ''),
                defaultStart: normalizedStart,
                autoOpenLatestDrawdown: Boolean(parsed.autoOpenLatestDrawdown),
                notifyReportIssued: Boolean(parsed.notifyReportIssued),
                emailSummary: String(parsed.emailSummary || 'weekly')
            };
        } catch {
            return {
                accountName: '',
                contactNumber: '',
                contactEmail: '',
                defaultStart: 'overview',
                autoOpenLatestDrawdown: true,
                notifyReportIssued: true,
                emailSummary: 'weekly'
            };
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(DEV_PORTAL_SETTINGS_KEY, JSON.stringify(devPortalSettings));
        } catch {
            // ignore
        }
    }, [devPortalSettings]);

    const [devContactSaving, setDevContactSaving] = useState(false);
    const [devContactMessage, setDevContactMessage] = useState('');
    const [devContactError, setDevContactError] = useState('');

    async function saveDeveloperContactDetails() {
        if (devContactSaving) return;
        if (!reference) {
            setDevContactError('Missing project reference.');
            return;
        }

        setDevContactError('');
        setDevContactMessage('');
        setDevContactSaving(true);
        try {
            const body = {
                reference,
                borrower_name: String(devPortalSettings.accountName || '').trim(),
                borrower_email: String(devPortalSettings.contactEmail || '').trim(),
                borrower_phone: String(devPortalSettings.contactNumber || '').trim()
            };

            const json = await fetchJson(`${API_BASE_URL}/portal/developer/contact-details`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body)
            });

            const updated = json?.project || {};
            setData((prev) => (prev && typeof prev === 'object'
                ? {
                    ...prev,
                    borrower_name: updated.borrower_name ?? body.borrower_name,
                    borrower_email: updated.borrower_email ?? body.borrower_email,
                    borrower_phone: updated.borrower_phone ?? body.borrower_phone
                }
                : prev));
            setDevContactMessage('Contact details updated.');
        } catch (e) {
            setDevContactError(e?.message || 'Failed to update contact details');
        } finally {
            setDevContactSaving(false);
        }
    }

    useEffect(() => {
        if (mode !== 'developer' || view !== 'settings') return;
        try {
            const key = 'monsurv_settings_section_account_open';
            if (localStorage.getItem(key) === '1') {
                localStorage.removeItem(key);
                const el = document.getElementById('settings-section-account');
                if (el && typeof el.scrollIntoView === 'function') {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }
        } catch {
            // ignore
        }
    }, [mode, view]);

    const [drawdownSchedule, setDrawdownSchedule] = useState(() => []);
    const [drawdownRequestBusy, setDrawdownRequestBusy] = useState(false);
    const [drawdownRequestError, setDrawdownRequestError] = useState('');
    const [drawdownRequestSuccess, setDrawdownRequestSuccess] = useState('');
    const [drawdownClaims, setDrawdownClaims] = useState(() => ({
        construction: {},
        professional: {},
        other: {},
        contingencyByDesc: {},
        contingencyTotal: 0
    }));

    const drawdownScheduleRef = useRef('');
    const [appendixSelections, setAppendixSelections] = useState(() => []);
    const [appendixTarget, setAppendixTarget] = useState('initial');
    const initialAppendixDocs = useMemo(() => {
        return INITIAL_REPORT_APPENDIX_ITEMS.map((it) => String(it?.title || '').trim()).filter(Boolean);
    }, []);

    const interimAppendixDocs = useMemo(() => {
        return INTERIM_REPORT_APPENDIX_ITEMS.map((it) => String(it?.title || '').trim()).filter(Boolean);
    }, []);

    const interimSitePhotosAvailable = useMemo(() => {
        const answers = (interimReport && typeof interimReport === 'object' && interimReport.answers && typeof interimReport.answers === 'object')
            ? interimReport.answers
            : {};
        const photos = Array.isArray(answers?.cp_site_photos) ? answers.cp_site_photos : [];
        if (photos.length > 0) return true;

        const tables = (interimReport && typeof interimReport === 'object' && interimReport.tables && typeof interimReport.tables === 'object')
            ? interimReport.tables
            : {};
        const tablePhotos = Array.isArray(tables?.int_questionnaire_site_photos) ? tables.int_questionnaire_site_photos : [];
        return tablePhotos.length > 0;
    }, [interimReport]);

    const initialAppendixAvailabilityByTitle = useMemo(() => {
        const tables = (initialReport && typeof initialReport === 'object' && initialReport.tables && typeof initialReport.tables === 'object')
            ? initialReport.tables
            : {};
        const docs = Array.isArray(tables?.ini_documents) ? tables.ini_documents : [];
        const uploads = Array.isArray(tables?.ini_section_3_uploads) ? tables.ini_section_3_uploads : [];

        const norm = (s) => String(s || '')
            .trim()
            .toLowerCase()
            .replace(/[_-]+/g, ' ')
            .replace(/\s+/g, ' ');

        const lower = (s) => String(s || '').trim().toLowerCase();

        const buildVariantsLower = (title) => {
            const t = String(title || '').trim();
            if (!t) return [];
            const compact = norm(t);
            const underscore = compact.replace(/\s+/g, '_');
            const dash = compact.replace(/\s+/g, '-');
            const aliases = INITIAL_REPORT_APPENDIX_SQL_ALIASES[compact] || [];
            const aliasVariants = aliases
                .map((a) => String(a || '').trim())
                .filter(Boolean)
                .flatMap((a) => {
                    const c = norm(a);
                    return [lower(a), c, c.replace(/\s+/g, '_'), c.replace(/\s+/g, '-')];
                });

            return Array.from(new Set([
                lower(t),
                compact,
                underscore,
                dash,
                ...aliasVariants
            ].filter(Boolean)));
        };

        const uploadCats = new Set(uploads.map((u) => lower(u?.upload_category)).filter(Boolean));

        const docMatchesAnyVariant = (docRow, variants) => {
            if (!docRow || typeof docRow !== 'object') return false;
            if (!Array.isArray(variants) || !variants.length) return false;
            const fields = [
                docRow.document_title,
                docRow.document_name,
                docRow.file_name,
                docRow.document_type,
                docRow.file_path
            ].map((v) => lower(v)).filter(Boolean);
            if (!fields.length) return false;
            return fields.some((f) => variants.includes(f));
        };

        const out = new Map();
        for (const title of initialAppendixDocs) {
            if (norm(title) === 'site photos') {
                out.set(lower(title), uploadCats.has('existing-site-photos'));
                continue;
            }
            const variants = buildVariantsLower(title);
            const ok = docs.some((d) => docMatchesAnyVariant(d, variants));
            out.set(lower(title), ok);
        }

        return out;
    }, [initialReport, initialAppendixDocs]);
    const [issueUploadError, setIssueUploadError] = useState('');
    const [issueUploadSuccess, setIssueUploadSuccess] = useState('');
    const fileInputRef = useRef(null);
    const collaboraFrameRef = useRef(null);
    const collaboraFormRef = useRef(null);
    const collaboraFrameNameRef = useRef(`monsurv-collabora-${Math.random().toString(36).slice(2)}`);
    const collaboraRefreshRequestRef = useRef(null);
    const collaboraRefreshTokenRef = useRef('');
    const collaboraSessionEpochRef = useRef(0);
    const collaboraFormSubmittedRef = useRef(false);
    const viewReportBusyRef = useRef(false);
    const [issueSendToFunder, setIssueSendToFunder] = useState(false);
    const [sendReportBusy, setSendReportBusy] = useState(false);
    const [reportSentTo, setReportSentTo] = useState('');
    const [collaboraEditorOpen, setCollaboraEditorOpen] = useState(false);
    const [collaboraSession, setCollaboraSession] = useState(null);
    const [viewReportBusy, setViewReportBusy] = useState(false);
    const [monsurvSelectionText, setMonsurvSelectionText] = useState('');
    const [monsurvFactSelectionText, setMonsurvFactSelectionText] = useState('');
    const [monsurvInstructions, setMonsurvInstructions] = useState('');
    const [monsurvActionBusy, setMonsurvActionBusy] = useState('');
    const [monsurvActionError, setMonsurvActionError] = useState('');
    const [monsurvReplacementText, setMonsurvReplacementText] = useState('');
    const [monsurvFactCheckText, setMonsurvFactCheckText] = useState('');
    const [preIssueChecks, setPreIssueChecks] = useState(() => []);
    const [preIssueSource, setPreIssueSource] = useState(null);
    const [preIssueExpanded, setPreIssueExpanded] = useState(() => ({}));
    const [preIssueActioned, setPreIssueActioned] = useState(() => ({}));
    const [monsurvEditorNotice, setMonsurvEditorNotice] = useState('');
    const [monsurvPanelOpen, setMonsurvPanelOpen] = useState(() => ({
        versionControl: true,
        documentSearch: false,
        aiRegeneration: true,
        factChecker: false,
        preIssueChecks: true,
        pdfIssue: true,
        chatbot: false
    }));
    const monsurvSelectionTargetRef = useRef('ai');
    const collaboraSaveRequestRef = useRef(null);
    const [documentSearchLoading, setDocumentSearchLoading] = useState(false);
    const [documentSearchError, setDocumentSearchError] = useState('');
    const [documentSearchFolders, setDocumentSearchFolders] = useState(() => []);
    const [documentSearchRoot, setDocumentSearchRoot] = useState('');
    const [documentSearchSelectedFolder, setDocumentSearchSelectedFolder] = useState('');
    const [versionControlItems, setVersionControlItems] = useState(() => []);
    const [versionControlPath, setVersionControlPath] = useState('');
    const [pendingRevertVersion, setPendingRevertVersion] = useState(null);
    const [monsurvChatMessages, setMonsurvChatMessages] = useState(() => []);
    const [monsurvChatInput, setMonsurvChatInput] = useState('');
    const [monsurvChatBusy, setMonsurvChatBusy] = useState(false);
    const [monsurvChatError, setMonsurvChatError] = useState('');

    const [planningItems, setPlanningItems] = useState(() => []);
    const [planningLoading, setPlanningLoading] = useState(false);
    const [planningError, setPlanningError] = useState('');

    const [riskMatrix, setRiskMatrix] = useState(() => normalizeRiskMatrix(null));
    const [riskLoading, setRiskLoading] = useState(false);
    const [riskError, setRiskError] = useState('');
    const riskFirstLoadRef = useRef(true);
    const riskAutoGenerateAttemptRef = useRef('');
    const [riskAiGenerating, setRiskAiGenerating] = useState(false);
    const [riskAiError, setRiskAiError] = useState('');
    const [riskAiGeneratedAt, setRiskAiGeneratedAt] = useState('');
    const riskMatrixReadOnly = true;

    const [insuranceBorrowerPolicies, setInsuranceBorrowerPolicies] = useState(() => []);
    const [insuranceContractorPolicies, setInsuranceContractorPolicies] = useState(() => []);
    const [insuranceSubcontractorPolicies, setInsuranceSubcontractorPolicies] = useState(() => []);
    const [insuranceConsultantPi, setInsuranceConsultantPi] = useState(() => []);
    const [insuranceLoading, setInsuranceLoading] = useState(false);
    const [insuranceError, setInsuranceError] = useState('');
    const [insuranceManualOverride, setInsuranceManualOverride] = useState(false);
    const [insuranceSaving, setInsuranceSaving] = useState(false);
    const [insuranceRelevance, setInsuranceRelevance] = useState(() => ({
        borrower: true,
        contractor: true,
        subcontractor: true,
        consultant_pi: true
    }));

    const [programmeTrackerState, setProgrammeTrackerState] = useState(() => ({
        works_start_date: '',
        borrower_duration_months: '',
        surveyor_duration_months: '',
        borrower_completion_date: '',
        surveyor_completion_date: '',
        risks: []
    }));
    const [programmeTrackerLoading, setProgrammeTrackerLoading] = useState(false);
    const [programmeTrackerSaving, setProgrammeTrackerSaving] = useState(false);
    const [programmeTrackerError, setProgrammeTrackerError] = useState('');
    const [programmeTrackerWarning, setProgrammeTrackerWarning] = useState('');
    const [programmeTrackerManualOverride, setProgrammeTrackerManualOverride] = useState(false);
    const [programmeTrackerSource, setProgrammeTrackerSource] = useState('');
    const [programmeTrackerUpdatedAt, setProgrammeTrackerUpdatedAt] = useState('');
    const [programmeTrackerOcrBusy, setProgrammeTrackerOcrBusy] = useState(false);
    const [programmeTrackerBorrowerCompletionCorrected, setProgrammeTrackerBorrowerCompletionCorrected] = useState(false);
    const [designTeamMembers, setDesignTeamMembers] = useState(() => []);
    const [designTeamLoading, setDesignTeamLoading] = useState(false);
    const [designTeamSaving, setDesignTeamSaving] = useState(false);
    const [designTeamError, setDesignTeamError] = useState('');
    const [designTeamManualOverride, setDesignTeamManualOverride] = useState(false);
    const [designTeamSource, setDesignTeamSource] = useState('');
    const [designTeamUpdatedAt, setDesignTeamUpdatedAt] = useState('');

    const { notify } = useNotifications();

    function decodeJwtPayload(token) {
        try {
            const parts = String(token || '').split('.');
            if (parts.length < 2) return null;
            const raw = parts[1].replace(/-/g, '+').replace(/_/g, '/');
            const padded = raw.padEnd(Math.ceil(raw.length / 4) * 4, '=');
            const json = atob(padded);
            return JSON.parse(json);
        } catch {
            return null;
        }
    }

    function getTokenFromStorage(key) {
        try {
            return String(localStorage.getItem(key) || '').trim();
        } catch {
            return '';
        }
    }

    function isTokenExpired(payload) {
        const exp = payload?.exp;
        if (!Number.isFinite(Number(exp))) return false;
        return (Number(exp) * 1000) <= Date.now();
    }

    function normalizePortalMode(m) {
        const s = String(m || '').trim().toLowerCase();
        if (s === 'surveyor' || s === 'developer' || s === 'funder') return s;
        return 'surveyor';
    }

    function getAuthToken() {
        // Surveyor portal requires a dashboard token (payload.portal must be falsy).
        // Developer/Funder portals may use a portal token.
        const portalMode = normalizePortalMode(mode);

        if (portalMode === 'developer' || portalMode === 'funder') {
            const portalToken = getTokenFromStorage(PORTAL_TOKEN_KEY);
            if (portalToken) return portalToken;

            // Back-compat: older deployments stored portal tokens in DASHBOARD_TOKEN_KEY.
            const legacy = getTokenFromStorage(DASHBOARD_TOKEN_KEY);
            if (!legacy) return '';
            const legacyPayload = decodeJwtPayload(legacy);
            if (legacyPayload?.portal && !isTokenExpired(legacyPayload)) return legacy;
            return legacy;
        }

        const dashToken = getTokenFromStorage(DASHBOARD_TOKEN_KEY);
        if (!dashToken) return '';
        const payload = decodeJwtPayload(dashToken);
        if (!payload) return dashToken;
        if (payload?.portal) return '';
        if (isTokenExpired(payload)) return '';
        return dashToken;
    }

    function getPortalAuthMode() {
        try {
            return String(localStorage.getItem('monsurv_portal_auth_mode') || '').trim().toLowerCase();
        } catch {
            return '';
        }
    }

    const [authToken, setAuthToken] = useState(() => getAuthToken());
    const portalAuthMode = useMemo(() => getPortalAuthMode(), []);

    useEffect(() => {
        function syncToken() {
            setAuthToken(getAuthToken());
        }

        syncToken();

        function onStorage(e) {
            const k = String(e?.key || '');
            if (k === DASHBOARD_TOKEN_KEY || k === PORTAL_TOKEN_KEY || k === 'monsurv_portal_auth_mode') {
                syncToken();
            }
        }

        window.addEventListener('focus', syncToken);
        window.addEventListener('storage', onStorage);
        document.addEventListener('visibilitychange', syncToken);

        return () => {
            window.removeEventListener('focus', syncToken);
            window.removeEventListener('storage', onStorage);
            document.removeEventListener('visibilitychange', syncToken);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode]);

    useEffect(() => {
        const prev = document.title;

        function roleFromMode() {
            const m = String(mode || '').toLowerCase();
            if (m === 'surveyor') return 'Surveyor';
            if (m === 'developer') return 'Developer';
            if (m === 'funder') return 'Funder';

            // Best-effort fallback if mode isn't present.
            if (authToken) return 'Surveyor';
            return '';
        }

        const role = roleFromMode();
        document.title = role
            ? `Monsurv ${role} Information Portal`
            : 'Monsurv (Surveyor/Developer/Funder) Information Portal';

        return () => {
            document.title = prev || 'Monsurv';
        };
    }, [mode, authToken]);

    const authHeaders = useCallback((extra) => {
        const h = { ...(extra || {}) };
        // Use state token first, but fall back to a fresh storage read to avoid
        // post-login race conditions where authToken state has not re-synced yet.
        const token = String(authToken || getAuthToken() || '').trim();
        if (token) h.Authorization = `Bearer ${token}`;
        return h;
    }, [authToken, mode]);

    useEffect(() => {
        let cancelled = false;

        async function loadApprovedIntroductions() {
            if (!reference || mode !== 'surveyor') {
                if (!cancelled) setApprovedIntroductions([]);
                return;
            }
            setApprovedIntroductionsLoading(true);
            setApprovedIntroductionsError('');
            try {
                const url = `${API_BASE_URL}/project/${encodeURIComponent(reference)}/data-intakes/accepted?mode=surveyor`;
                const result = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                if (!cancelled) setApprovedIntroductions(Array.isArray(result?.proposals) ? result.proposals : []);
            } catch (requestError) {
                if (!cancelled) {
                    setApprovedIntroductions([]);
                    setApprovedIntroductionsError(requestError?.message || 'Approved project evidence could not be loaded.');
                }
            } finally {
                if (!cancelled) setApprovedIntroductionsLoading(false);
            }
        }

        void loadApprovedIntroductions();
        return () => { cancelled = true; };
    }, [reference, mode, authToken, authHeaders, approvedIntroductionsReloadKey]);

    useEffect(() => {
        if (!collaboraEditorOpen) return undefined;
        let expectedOrigin = '';
        try { expectedOrigin = new URL(collaboraSession?.launch_form?.action || '').origin; } catch { return undefined; }
        const onMessage = (event) => {
            if (event?.source !== collaboraFrameRef.current?.contentWindow) return;
            if (event.origin !== expectedOrigin) return;
            let payload = event?.data;
            if (typeof payload === 'string') {
                try { payload = JSON.parse(payload); } catch { /* ignore non-JSON postMessages */ }
            }
            if (!payload || typeof payload !== 'object') return;
            const id = String(payload.MessageId || payload.messageId || payload.type || '').toLowerCase();
            if (id === 'app_tokenexpired' || id === 'app_tokenexpiring') {
                if (collaboraRefreshRequestRef.current) return;
                const sessionEpoch = collaboraSessionEpochRef.current;
                const refresh = (async () => {
                    const qs = new URLSearchParams();
                    qs.set('mode', mode);
                    const refreshUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(collaboraSession?.report_log_id || ''))}/collabora-token-refresh?${qs.toString()}`;
                    const response = await fetch(refreshUrl, {
                        method: 'POST',
                        headers: authHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({
                            working_document_id: collaboraSession?.working_report?.id,
                            refresh_token: collaboraRefreshTokenRef.current
                        })
                    });
                    const json = await response.json().catch(() => null);
                    if (!response.ok) throw new Error(json?.error || `HTTP ${response.status}`);
                    const ttl = Number(json?.access_token_ttl || 0);
                    const token = String(json?.access_token || '');
                    const nextRefreshToken = String(json?.refresh_token || '').trim();
                    if (!token || !Number.isFinite(ttl) || ttl <= Date.now() || !nextRefreshToken) {
                        throw new Error('The refreshed Collabora session credentials are invalid.');
                    }
                    if (collaboraSessionEpochRef.current !== sessionEpoch) return;
                    collaboraRefreshTokenRef.current = nextRefreshToken;
                    collaboraFrameRef.current?.contentWindow?.postMessage(JSON.stringify({
                        MessageId: 'Reset_Access_Token',
                        Values: { token, ttl: String(ttl) }
                    }), expectedOrigin);
                })();
                collaboraRefreshRequestRef.current = refresh;
                refresh.catch((err) => setMonsurvActionError(err?.message || 'Failed to refresh the Collabora session.')).finally(() => {
                    if (collaboraRefreshRequestRef.current === refresh) collaboraRefreshRequestRef.current = null;
                });
                return;
            }
            if (id === 'action_save_resp') {
                const pending = collaboraSaveRequestRef.current;
                if (!pending) return;
                collaboraSaveRequestRef.current = null;
                clearTimeout(pending.timer);
                const rawSuccess = payload.Values?.success ?? payload.Values?.Success ?? payload.success;
                const succeeded = rawSuccess == null || rawSuccess === true || String(rawSuccess).toLowerCase() === 'true';
                if (succeeded) pending.resolve();
                else pending.reject(new Error(String(payload.Values?.result || payload.Values?.error || 'Collabora could not save the current report.')));
                return;
            }
            if (!id.includes('selection') && !id.includes('clipboard')) return;

            const candidates = [
                payload.SelectedText,
                payload.selectedText,
                payload.selection,
                payload.text,
                payload.Values?.SelectedText,
                payload.Values?.text,
                payload.Values?.selection
            ];
            const text = candidates.map((v) => String(v || '').trim()).find(Boolean);
            if (text) {
                const target = monsurvSelectionTargetRef.current === 'fact' ? 'fact' : 'ai';
                if (target === 'fact') setMonsurvFactSelectionText((prev) => (prev === text ? prev : text));
                else setMonsurvSelectionText((prev) => (prev === text ? prev : text));
            }
        };
        window.addEventListener('message', onMessage);
        return () => {
            window.removeEventListener('message', onMessage);
            const pending = collaboraSaveRequestRef.current;
            if (pending) {
                collaboraSaveRequestRef.current = null;
                clearTimeout(pending.timer);
                pending.reject(new Error('Collabora editor closed before the report was saved.'));
            }
        };
    }, [authHeaders, collaboraEditorOpen, collaboraSession?.launch_form?.action, collaboraSession?.report_log_id, collaboraSession?.working_report?.id, mode, reference]);

    useEffect(() => {
        if (!collaboraEditorOpen || !collaboraSession?.launch_form?.action || collaboraFormSubmittedRef.current) return undefined;
        const frameId = window.requestAnimationFrame(() => {
            if (collaboraFormSubmittedRef.current) return;
            const form = collaboraFormRef.current;
            if (!form || !collaboraFrameRef.current) {
                setIssueUploadError('The Collabora editor could not be initialized. Please close it and try again.');
                return;
            }
            collaboraFormSubmittedRef.current = true;
            form.submit();
        });
        return () => window.cancelAnimationFrame(frameId);
    }, [collaboraEditorOpen, collaboraSession?.launch_form?.action]);

    const portalClaims = useMemo(() => decodeJwtPayload(authToken), [authToken]);
    const portalEmail = useMemo(() => String(portalClaims?.email || '').trim().toLowerCase(), [portalClaims]);
    const portalReference = useMemo(() => String(portalClaims?.project_reference || '').trim(), [portalClaims]);
    const portalProjectReferences = useMemo(
        () => (Array.isArray(portalClaims?.project_references) ? portalClaims.project_references : []),
        [portalClaims]
    );

    function loadAiReportConsistencySettings() {
        try {
            const raw = localStorage.getItem('monsurv_ai_report_consistency');
            const parsed = raw ? JSON.parse(raw) : null;
            return parsed && typeof parsed === 'object' ? parsed : null;
        } catch {
            return null;
        }
    }

    async function refreshPlanningTracker({ silent } = {}) {
        if (!reference) {
            setPlanningItems([]);
            setPlanningError('');
            return [];
        }
        if (mode !== 'surveyor') {
            // Surveyor-only endpoints for now.
            setPlanningItems([]);
            setPlanningError('Available in surveyor mode only.');
            return [];
        }
        setPlanningLoading(true);
        if (!silent) setPlanningError('');
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/planning-condition-tracker`;
            const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
            const items = Array.isArray(json?.items) ? json.items : [];
            setPlanningItems(items);
            setPlanningError('');
            return items;
        } catch (e) {
            if (!silent) console.warn('refreshPlanningTracker failed:', e);
            setPlanningItems([]);
            setPlanningError(e?.message || 'Failed to load planning condition tracker');
            return [];
        } finally {
            setPlanningLoading(false);
        }
    }

    async function syncPlanningTrackerFromQuestionnaire({ silent } = {}) {
        if (mode !== 'surveyor') return null;
        if (!reference) return null;

        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/planning-condition-tracker/sync-from-questionnaire`;
            return await fetchJson(url, { method: 'POST', headers: authHeaders() });
        } catch (e) {
            if (!silent) console.warn('syncPlanningTrackerFromQuestionnaire failed:', e);
            return null;
        }
    }

    async function updatePlanningConditionStatus({ id, status }) {
        if (mode !== 'surveyor') throw new Error('Surveyor mode required');
        if (!reference) throw new Error('Missing project reference');

        const nextId = Number(id);
        if (!Number.isFinite(nextId) || nextId <= 0) throw new Error('Invalid id');
        const nextStatus = String(status || '').trim();
        if (!nextStatus) throw new Error('Invalid status');

        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/planning-condition-tracker/${encodeURIComponent(nextId)}`;
        const json = await fetchJson(url, {
            method: 'PATCH',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ status: nextStatus })
        });

        const updated = json?.item && typeof json.item === 'object' ? json.item : null;
        if (updated) {
            setPlanningItems((prev) => (Array.isArray(prev) ? prev.map((it) => (Number(it?.id) === nextId ? { ...it, ...updated } : it)) : prev));
        } else {
            await refreshPlanningTracker({ silent: true });
        }
        return json;
    }

    function normalizeInsuranceRow(r) {
        const obj = r && typeof r === 'object' ? r : {};
        const idRaw = obj.id != null ? Number(obj.id) : null;
        const id = Number.isFinite(idRaw) ? idRaw : null;
        const cid = id != null ? `id_${id}` : String(obj._cid || '').trim() || `cid_${Date.now()}_${Math.random().toString(16).slice(2)}`;
        const expiryDate = String(obj.expiry_date ?? obj.expiryDate ?? obj.expiry ?? '').trim();

        return {
            ...(id != null ? { id } : null),
            _cid: cid,
            consultant: String(obj.consultant || '').trim(),
            policy: String(obj.policy || '').trim(),
            coverage: String(obj.coverage || '').trim(),
            expiry_date: expiryDate,
            is_expired: isInsuranceTrackerDateExpired(expiryDate),
            source: obj.source,
            source_details: obj.source_details
        };
    }

    function isInsuranceCoverageValueValid(raw) {
        const s = String(raw || '').trim();
        if (!s) return true;
        if (/^tbc$/i.test(s)) return true;
        const cleaned = s.replace(/£/g, '').replace(/,/g, '').trim();
        if (!cleaned) return false;
        return /^\d+(?:\.\d+)?$/.test(cleaned);
    }

    function normalizeInsuranceCoverageValue(raw) {
        const s = String(raw || '').trim();
        if (!s) return '';
        if (/^tbc$/i.test(s)) return 'TBC';
        const cleaned = s.replace(/£/g, '').replace(/,/g, '').trim();
        if (!/^\d+(?:\.\d+)?$/.test(cleaned)) return s;
        const n = Number(cleaned);
        if (!Number.isFinite(n)) return s;
        return `£${Math.round(n).toLocaleString('en-GB')}`;
    }

    function isInsuranceExpiryValueValid(raw) {
        const s = String(raw || '').trim();
        if (!s) return true;
        if (/^tbc$/i.test(s)) return true;
        const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (!m) return false;
        const day = Number(m[1]);
        const month = Number(m[2]);
        const year = Number(m[3]);
        if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) return false;
        if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1900) return false;
        const dt = new Date(Date.UTC(year, month - 1, day));
        return dt.getUTCFullYear() === year && (dt.getUTCMonth() + 1) === month && dt.getUTCDate() === day;
    }

    function normalizeInsuranceExpiryValue(raw) {
        const s = String(raw || '').trim();
        if (!s) return '';
        if (/^tbc$/i.test(s)) return 'TBC';
        if (!isInsuranceExpiryValueValid(s)) return s;
        return s;
    }

    function getInsuranceRowsByKey(key) {
        if (key === 'borrower') return insuranceBorrowerPolicies;
        if (key === 'contractor') return insuranceContractorPolicies;
        if (key === 'subcontractor') return insuranceSubcontractorPolicies;
        if (key === 'consultant_pi') return insuranceConsultantPi;
        return [];
    }

    function setInsuranceRowsByKey(key, updater) {
        if (key === 'borrower') return setInsuranceBorrowerPolicies(updater);
        if (key === 'contractor') return setInsuranceContractorPolicies(updater);
        if (key === 'subcontractor') return setInsuranceSubcontractorPolicies(updater);
        if (key === 'consultant_pi') return setInsuranceConsultantPi(updater);
        return null;
    }

    function addInsuranceRow(key) {
        const blank = normalizeInsuranceRow({
            ...(key === 'consultant_pi' ? { consultant: '' } : { policy: '' }),
            coverage: '',
            expiry_date: ''
        });
        setInsuranceRowsByKey(key, (prev) => (Array.isArray(prev) ? [...prev, blank] : [blank]));
    }

    function removeInsuranceRow(key, idx) {
        setInsuranceRowsByKey(key, (prev) => (Array.isArray(prev) ? prev.filter((_, i) => i !== idx) : prev));
    }

    function updateInsuranceRow(key, idx, patch) {
        setInsuranceRowsByKey(key, (prev) => (
            Array.isArray(prev)
                ? prev.map((it, i) => (i === idx ? { ...it, ...patch } : it))
                : prev
        ));
    }

    function validateInsuranceManualEntries() {
        const checks = [
            { key: 'borrower', label: 'Developer and Project Insurances' },
            { key: 'contractor', label: 'Contractor Insurances' },
            { key: 'subcontractor', label: 'Sub-contractor Insurances' },
            { key: 'consultant_pi', label: 'Consultant PI Insurances' }
        ];

        for (const section of checks) {
            if (!insuranceRelevance?.[section.key]) continue;
            const rows = getInsuranceRowsByKey(section.key);
            for (let i = 0; i < rows.length; i += 1) {
                const row = rows[i] || {};
                const coverage = String(row?.coverage || '').trim();
                const expiry = String(row?.expiry_date || '').trim();
                if (!isInsuranceCoverageValueValid(coverage)) {
                    return `${section.label}: row ${i + 1} coverage must be TBC or a numeric currency amount.`;
                }
                if (!isInsuranceExpiryValueValid(expiry)) {
                    return `${section.label}: row ${i + 1} expiry date must be TBC or DD/MM/YYYY.`;
                }
            }
        }

        return '';
    }

    async function refreshInsuranceTracker({ silent } = {}) {
        if (!reference) {
            setInsuranceBorrowerPolicies([]);
            setInsuranceContractorPolicies([]);
            setInsuranceSubcontractorPolicies([]);
            setInsuranceConsultantPi([]);
            setInsuranceError('');
            return;
        }
        if (mode !== 'surveyor') {
            setInsuranceError('Available in surveyor mode only.');
            return;
        }

        setInsuranceLoading(true);
        if (!silent) setInsuranceError('');
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/insurance-tracker`;
            const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });

            const borrower = Array.isArray(json?.dev_project_policies)
                ? json.dev_project_policies
                : (Array.isArray(json?.borrower_policies) ? json.borrower_policies : []);
            const contractor = Array.isArray(json?.contractor_policies) ? json.contractor_policies : [];
            const subcontractor = Array.isArray(json?.subcontractor_policies) ? json.subcontractor_policies : [];
            const consultant = Array.isArray(json?.consultant_pi) ? json.consultant_pi : [];
            const relevance = {
                borrower: json?.relevance?.borrower !== false,
                contractor: json?.relevance?.contractor !== false,
                subcontractor: json?.relevance?.subcontractor !== false,
                consultant_pi: json?.relevance?.consultant_pi !== false
            };

            setInsuranceBorrowerPolicies(borrower.map(normalizeInsuranceRow));
            setInsuranceContractorPolicies(contractor.map(normalizeInsuranceRow));
            setInsuranceSubcontractorPolicies(subcontractor.map(normalizeInsuranceRow));
            setInsuranceConsultantPi(consultant.map(normalizeInsuranceRow));
            setInsuranceRelevance(relevance);
            setInsuranceError('');
        } catch (e) {
            if (!silent) console.warn('refreshInsuranceTracker failed:', e);
            setInsuranceError(e?.message || 'Failed to load insurance tracker');
        } finally {
            setInsuranceLoading(false);
        }
    }

    async function syncInsuranceTrackerFromQuestionnaire({ silent, overwrite } = {}) {
        if (mode !== 'surveyor') return null;
        if (!reference) return null;
        try {
            const q = overwrite ? '?overwrite=1' : '';
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/insurance-tracker/extract${q}`;
            return await fetchJson(url, { method: 'POST', headers: authHeaders() });
        } catch (e) {
            if (!silent) console.warn('syncInsuranceTrackerFromQuestionnaire failed:', e);
            return null;
        }
    }

    async function saveInsuranceRelevanceOnly(nextRelevance) {
        if (mode !== 'surveyor' || !reference) return null;
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/insurance-tracker/relevance`;
            const json = await fetchJson(url, {
                method: 'PATCH',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ relevance: nextRelevance })
            });

            const norm = {
                borrower: json?.relevance?.borrower !== false,
                contractor: json?.relevance?.contractor !== false,
                subcontractor: json?.relevance?.subcontractor !== false,
                consultant_pi: json?.relevance?.consultant_pi !== false
            };
            setInsuranceRelevance(norm);
            return json;
        } catch (e) {
            setInsuranceError(e?.message || 'Failed to save insurance relevance');
            return null;
        }
    }

    async function saveInsuranceTrackerManualOverride() {
        if (mode !== 'surveyor' || !reference) return null;
        setInsuranceSaving(true);
        setInsuranceError('');
        try {
            const validationError = validateInsuranceManualEntries();
            if (validationError) {
                setInsuranceError(validationError);
                return null;
            }

            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/insurance-tracker`;
            const payload = {
                relevance: insuranceRelevance,
                borrower_policies: insuranceBorrowerPolicies.map((r) => ({
                    policy: String(r?.policy || '').trim(),
                    coverage: normalizeInsuranceCoverageValue(String(r?.coverage || '')),
                    expiry_date: normalizeInsuranceExpiryValue(String(r?.expiry_date || ''))
                })),
                contractor_policies: insuranceContractorPolicies.map((r) => ({
                    policy: String(r?.policy || '').trim(),
                    coverage: normalizeInsuranceCoverageValue(String(r?.coverage || '')),
                    expiry_date: normalizeInsuranceExpiryValue(String(r?.expiry_date || ''))
                })),
                subcontractor_policies: insuranceSubcontractorPolicies.map((r) => ({
                    policy: String(r?.policy || '').trim(),
                    coverage: normalizeInsuranceCoverageValue(String(r?.coverage || '')),
                    expiry_date: normalizeInsuranceExpiryValue(String(r?.expiry_date || ''))
                })),
                consultant_pi: insuranceConsultantPi.map((r) => ({
                    consultant: String(r?.consultant || '').trim(),
                    coverage: normalizeInsuranceCoverageValue(String(r?.coverage || '')),
                    expiry_date: normalizeInsuranceExpiryValue(String(r?.expiry_date || ''))
                }))
            };

            const json = await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload)
            });

            const borrower = Array.isArray(json?.dev_project_policies)
                ? json.dev_project_policies
                : (Array.isArray(json?.borrower_policies) ? json.borrower_policies : []);
            const contractor = Array.isArray(json?.contractor_policies) ? json.contractor_policies : [];
            const subcontractor = Array.isArray(json?.subcontractor_policies) ? json.subcontractor_policies : [];
            const consultant = Array.isArray(json?.consultant_pi) ? json.consultant_pi : [];
            const relevance = {
                borrower: json?.relevance?.borrower !== false,
                contractor: json?.relevance?.contractor !== false,
                subcontractor: json?.relevance?.subcontractor !== false,
                consultant_pi: json?.relevance?.consultant_pi !== false
            };

            setInsuranceBorrowerPolicies(borrower.map(normalizeInsuranceRow));
            setInsuranceContractorPolicies(contractor.map(normalizeInsuranceRow));
            setInsuranceSubcontractorPolicies(subcontractor.map(normalizeInsuranceRow));
            setInsuranceConsultantPi(consultant.map(normalizeInsuranceRow));
            setInsuranceRelevance(relevance);
            setInsuranceManualOverride(false);
            notify?.('Insurance tracker override saved.', { type: 'success' });
            return json;
        } catch (e) {
            setInsuranceError(e?.message || 'Failed to save insurance tracker override');
            return null;
        } finally {
            setInsuranceSaving(false);
        }
    }

    function normalizeProgrammeTrackerDurationInput(raw) {
        const text = String(raw || '').trim();
        if (!text) return '';
        const cleaned = text.replace(/[^0-9.-]/g, '');
        const n = Number(cleaned);
        if (!Number.isFinite(n)) return text;
        return String(Math.round(n * 10) / 10);
    }

    function isProgrammeTrackerManualSourceValue(source) {
        const normalized = String(source || '').trim().toLowerCase();
        return normalized === 'manual' || normalized === 'portal_manual';
    }

    function normalizeProgrammeTrackerRisks(raw) {
        const out = [];
        const seen = new Set();
        const push = (value) => {
            const text = String(value || '').trim();
            if (!text) return;
            const key = text.toLowerCase();
            if (seen.has(key)) return;
            seen.add(key);
            out.push(text);
        };

        if (Array.isArray(raw)) {
            for (const item of raw) push(item);
        } else {
            String(raw || '')
                .split(/\r?\n|;|\|/g)
                .forEach((item) => push(item));
        }

        return out.slice(0, 80);
    }

    function addProgrammeTrackerRiskRow() {
        setProgrammeTrackerState((prev) => ({
            ...prev,
            risks: [...normalizeProgrammeTrackerRisks(prev?.risks), '']
        }));
    }

    function updateProgrammeTrackerRiskRow(index, value) {
        const idx = Number(index);
        if (!Number.isFinite(idx) || idx < 0) return;
        setProgrammeTrackerState((prev) => {
            const rows = Array.isArray(prev?.risks) ? [...prev.risks] : [];
            while (rows.length <= idx) rows.push('');
            rows[idx] = String(value || '');
            return { ...prev, risks: rows };
        });
    }

    function removeProgrammeTrackerRiskRow(index) {
        const idx = Number(index);
        if (!Number.isFinite(idx) || idx < 0) return;
        setProgrammeTrackerState((prev) => {
            const rows = Array.isArray(prev?.risks) ? prev.risks : [];
            return { ...prev, risks: rows.filter((_, i) => i !== idx) };
        });
    }

    function setProgrammeTrackerFromPayload(json) {
        const risks = normalizeProgrammeTrackerRisks(json?.risks);
        const sourceDetails = json?.source_details && typeof json.source_details === 'object'
            ? json.source_details
            : {};
        setProgrammeTrackerState({
            works_start_date: String(json?.works_start_date || '').trim(),
            borrower_duration_months: Number.isFinite(Number(json?.borrower_duration_months))
                ? String(Math.round(Number(json.borrower_duration_months) * 10) / 10)
                : '',
            surveyor_duration_months: Number.isFinite(Number(json?.surveyor_duration_months))
                ? String(Math.round(Number(json.surveyor_duration_months) * 10) / 10)
                : '',
            borrower_completion_date: String(json?.borrower_completion_date || '').trim(),
            surveyor_completion_date: String(json?.surveyor_completion_date || '').trim(),
            risks
        });
        setProgrammeTrackerBorrowerCompletionCorrected(sourceDetails?.borrower_completion_corrected === true);
        setProgrammeTrackerSource(String(json?.source || '').trim());
        setProgrammeTrackerUpdatedAt(String(json?.updated_at || '').trim());
        setProgrammeTrackerWarning(String(json?.warning || '').trim());
    }

    async function refreshProgrammeTracker({ silent } = {}) {
        if (!reference) {
            setProgrammeTrackerState({
                works_start_date: '',
                borrower_duration_months: '',
                surveyor_duration_months: '',
                borrower_completion_date: '',
                surveyor_completion_date: '',
                risks: []
            });
            setProgrammeTrackerError('');
            setProgrammeTrackerWarning('');
            setProgrammeTrackerSource('');
            setProgrammeTrackerUpdatedAt('');
            setProgrammeTrackerBorrowerCompletionCorrected(false);
            return null;
        }
        if (mode !== 'surveyor') {
            setProgrammeTrackerError('Available in surveyor mode only.');
            return null;
        }

        setProgrammeTrackerLoading(true);
        if (!silent) {
            setProgrammeTrackerError('');
            setProgrammeTrackerWarning('');
        }
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/programme-tracker`;
            const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
            setProgrammeTrackerFromPayload(json || {});
            setProgrammeTrackerError('');
            return json || null;
        } catch (e) {
            if (!silent) console.warn('refreshProgrammeTracker failed:', e);
            setProgrammeTrackerError(e?.message || 'Failed to load programme tracker');
            return null;
        } finally {
            setProgrammeTrackerLoading(false);
        }
    }

    async function refreshDesignTeamTracker({ silent } = {}) {
        if (!reference || mode !== 'surveyor') return null;
        setDesignTeamLoading(true);
        if (!silent) setDesignTeamError('');
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/design-team-tracker`;
            const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
            setDesignTeamMembers(Array.isArray(json?.team_members) ? json.team_members : []);
            setDesignTeamSource(String(json?.source || '').trim());
            setDesignTeamUpdatedAt(String(json?.updated_at || '').trim());
            setDesignTeamError('');
            return json;
        } catch (e) {
            setDesignTeamError(e?.message || 'Failed to load design team tracker');
            return null;
        } finally {
            setDesignTeamLoading(false);
        }
    }

    function updateDesignTeamMember(index, field, value) {
        setDesignTeamMembers((previous) => previous.map((member, memberIndex) => (
            memberIndex === index ? { ...member, [field]: value } : member
        )));
    }

    async function saveDesignTeamTracker() {
        if (!reference || mode !== 'surveyor') return;
        setDesignTeamSaving(true);
        setDesignTeamError('');
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/design-team-tracker`;
            const json = await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ team_members: designTeamMembers })
            });
            setDesignTeamMembers(Array.isArray(json?.team_members) ? json.team_members : []);
            setDesignTeamSource(String(json?.source || '').trim());
            setDesignTeamUpdatedAt(String(json?.updated_at || '').trim());
            setDesignTeamManualOverride(false);
            notify?.('Design Team Tracker saved.', { type: 'success' });
        } catch (e) {
            setDesignTeamError(e?.message || 'Failed to save design team tracker');
        } finally {
            setDesignTeamSaving(false);
        }
    }

    async function syncProgrammeTrackerFromQuestionnaire({ silent, overwrite } = {}) {
        if (mode !== 'surveyor') return null;
        if (!reference) return null;
        try {
            const q = overwrite ? '?overwrite=1' : '';
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/programme-tracker/sync-from-questionnaire${q}`;
            const json = await fetchJson(url, { method: 'POST', headers: authHeaders() });
            setProgrammeTrackerFromPayload(json || {});
            return json;
        } catch (e) {
            if (!silent) console.warn('syncProgrammeTrackerFromQuestionnaire failed:', e);
            if (!silent) setProgrammeTrackerError(e?.message || 'Failed to sync programme tracker');
            return null;
        }
    }

    async function runProgrammeTrackerTargetedOcrRead() {
        if (mode !== 'surveyor' || !reference) return null;

        const valuationCandidate = Number(currentInterimNumber || data?.in_progress_interim_number || 1);
        const valuation = Number.isFinite(valuationCandidate) && valuationCandidate > 0
            ? Math.trunc(valuationCandidate)
            : 1;

        setProgrammeTrackerOcrBusy(true);
        setProgrammeTrackerError('');
        setProgrammeTrackerWarning('');

        try {
            const ocrUrl = `${String(INT_QUESTIONNAIRE_BASE_URL || '').replace(/\/+$/, '')}/api/questionnaire/project/${encodeURIComponent(reference)}/programme-risks?valuation_number=${encodeURIComponent(String(valuation))}&source=ocr`;
            const ocrRes = await fetchJson(ocrUrl, { method: 'GET' });

            const ocrRisks = normalizeProgrammeTrackerRisks(ocrRes?.risks || []);
            const mergedRisks = normalizeProgrammeTrackerRisks([
                ...(Array.isArray(programmeTrackerState?.risks) ? programmeTrackerState.risks : []),
                ...ocrRisks
            ]);
            const context = ocrRes?.programme_context && typeof ocrRes.programme_context === 'object'
                ? ocrRes.programme_context
                : {};
            const hasContextKey = (key) => Object.prototype.hasOwnProperty.call(context || {}, key);
            const asDateField = (key, fallback) => {
                if (hasContextKey(key)) return String(context?.[key] || '').trim();
                return String(fallback || '').trim();
            };
            const asDurationField = (key, fallback) => {
                if (hasContextKey(key)) return normalizeProgrammeTrackerDurationInput(context?.[key]);
                return normalizeProgrammeTrackerDurationInput(fallback);
            };
            const borrowerCompletionCorrected = context?.borrower_completion_corrected === true;

            const payload = {
                works_start_date: asDateField('start_date', programmeTrackerState?.works_start_date),
                borrower_duration_months: asDurationField('borrower_duration_months', programmeTrackerState?.borrower_duration_months),
                surveyor_duration_months: asDurationField('surveyor_duration_months', programmeTrackerState?.surveyor_duration_months),
                borrower_completion_date: asDateField('borrower_completion_date', programmeTrackerState?.borrower_completion_date),
                surveyor_completion_date: asDateField('surveyor_completion_date', programmeTrackerState?.surveyor_completion_date),
                risks: mergedRisks,
                source_details: {
                    borrower_completion_corrected: borrowerCompletionCorrected,
                    borrower_completion_raw: String(context?.borrower_completion_raw || '').trim() || null,
                    borrower_completion_calculated: String(context?.borrower_completion_calculated || '').trim() || null
                }
            };

            const saveUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/programme-tracker`;
            const saved = await fetchJson(saveUrl, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload)
            });

            setProgrammeTrackerFromPayload(saved || payload);
            if (String(ocrRes?.warning || '').trim()) {
                setProgrammeTrackerWarning(String(ocrRes.warning || '').trim());
            }
            notify?.('Targeted Programme OCR read completed.', { type: 'success' });
            return saved;
        } catch (e) {
            setProgrammeTrackerError(e?.message || 'Targeted Programme OCR read failed');
            return null;
        } finally {
            setProgrammeTrackerOcrBusy(false);
        }
    }

    async function saveProgrammeTrackerManualOverride() {
        if (mode !== 'surveyor' || !reference) return null;
        setProgrammeTrackerSaving(true);
        setProgrammeTrackerError('');
        setProgrammeTrackerWarning('');
        try {
            const payload = {
                works_start_date: String(programmeTrackerState?.works_start_date || '').trim(),
                borrower_duration_months: normalizeProgrammeTrackerDurationInput(programmeTrackerState?.borrower_duration_months),
                surveyor_duration_months: normalizeProgrammeTrackerDurationInput(programmeTrackerState?.surveyor_duration_months),
                borrower_completion_date: String(programmeTrackerState?.borrower_completion_date || '').trim(),
                surveyor_completion_date: String(programmeTrackerState?.surveyor_completion_date || '').trim(),
                risks: normalizeProgrammeTrackerRisks(programmeTrackerState?.risks),
                source_details: {
                    borrower_completion_corrected: false,
                    borrower_completion_raw: null,
                    borrower_completion_calculated: null
                }
            };

            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/programme-tracker`;
            const json = await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload)
            });

            setProgrammeTrackerFromPayload(json || {});
            setProgrammeTrackerManualOverride(false);
            notify?.('Programme tracker override saved.', { type: 'success' });
            return json;
        } catch (e) {
            setProgrammeTrackerError(e?.message || 'Failed to save programme tracker override');
            return null;
        } finally {
            setProgrammeTrackerSaving(false);
        }
    }

    function normalizeReportLogItems(items) {
        const arr = Array.isArray(items) ? items : [];
        const out = arr.map((it) => ({
            ...it,
            revision: it?.revision != null ? Number(it.revision) : null,
            id: it?.id != null ? Number(it.id) : null,
            file_name: it?.file_name != null ? String(it.file_name) : '',
            download_url: it?.download_url != null ? String(it.download_url) : '',
            generated_at: it?.generated_at != null ? String(it.generated_at) : ''
        }));
        out.sort((a, b) => {
            const ta = new Date(a?.generated_at || 0).getTime();
            const tb = new Date(b?.generated_at || 0).getTime();
            if (ta !== tb) return tb - ta;
            const ra = a?.revision != null ? Number(a.revision) : -1;
            const rb = b?.revision != null ? Number(b.revision) : -1;
            if (ra !== rb) return rb - ra;
            const ia = a?.id != null ? Number(a.id) : -1;
            const ib = b?.id != null ? Number(b.id) : -1;
            return ib - ia;
        });
        return out;
    }

    function isDocxItem(it) {
        const name = String(it?.file_name || '').toLowerCase();
        return name.endsWith('.docx');
    }

    function matchesReport(it, { reportInstance, reportType }) {
        if (!it) return false;
        const inst = String(it?.report_instance || '').trim();
        const typ = String(it?.report_type || '').trim();
        if (reportInstance && inst !== reportInstance) return false;
        if (reportType && typ !== reportType) return false;
        return true;
    }

    function extractIssuedInterimNumbers(items) {
        const nums = new Set();
        (Array.isArray(items) ? items : []).forEach((it) => {
            const inst = String(it?.report_instance || '').trim().toLowerCase();
            const m = inst.match(/interim\s*[:\-_]?\s*(\d+)/i);
            if (!m) return;
            const n = Number(m[1]);
            if (!Number.isFinite(n) || n <= 0) return;
            const issued = Boolean(
                it?.issue_checked
                || it?.issue_date
                || it?.issue_pdf_uploaded_at
                || it?.issue_pdf_download_url
                || it?.issue_pdf_file_path
            );
            if (issued) nums.add(Math.trunc(n));
        });
        return Array.from(nums).sort((a, b) => a - b);
    }

    async function refreshLatestInitialReport({ silent } = {}) {
        if (!reference) {
            setLatestInitialDocxEntry(null);
            setHasInitialIssuedPdf(false);
            return null;
        }
        try {
            const qs = new URLSearchParams();
            // Prefer the current view mode, but if we're in developer mode without a code
            // and we do have a dashboard auth token, fall back to surveyor mode so we can still
            // read the report-log.
            const canUseCode = mode !== 'surveyor' && Boolean(code);
            const canUseAuth = Boolean(authToken) && !portalAuthMode;
            const reportLogMode = (mode !== 'surveyor' && !canUseCode && canUseAuth) ? 'surveyor' : mode;
            qs.set('mode', reportLogMode);
            if (reportLogMode !== 'surveyor' && code) qs.set('code', code);
            let json;
            for (let attempt = 0; attempt < REPORT_LOG_FETCH_ATTEMPTS; attempt += 1) {
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), 12000);
                try {
                    // Keep URL stable across polls so Authorization preflight can be cached by the browser.
                    // If we need a second attempt, add a small retry marker only for that request.
                    if (attempt > 0) qs.set('_retry', String(attempt));
                    else qs.delete('_retry');
                    const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log?${qs.toString()}`;
                    json = await fetchJson(url, {
                        method: 'GET',
                        headers: authHeaders(),
                        signal: controller.signal,
                        cache: 'no-store'
                    });
                    break;
                } catch (e) {
                    const isCache304 = Number(e?.status || 0) === 304;
                    if (attempt >= (REPORT_LOG_FETCH_ATTEMPTS - 1) || (!isTransientGatewayError(e) && !isCache304)) throw e;
                    // Brief jittered pause prevents synchronized retry storms after transient 502s.
                    await waitMs(350 + Math.round(Math.random() * 300));
                } finally {
                    window.clearTimeout(timeoutId);
                }
            }
            const items = normalizeReportLogItems(json?.items);
            const firstDocx = items.find((it) => it?.download_url && isDocxItem(it) && matchesReport(it, { reportInstance: 'initial', reportType: 'Initial Report' })) || null;
            const anyIssued = items.some((it) =>
                isDocxItem(it)
                && matchesReport(it, { reportInstance: 'initial', reportType: 'Initial Report' })
                && Boolean(it?.issue_pdf_download_url || it?.issue_pdf_file_path)
            );
            setIssuedInterimNumbers(extractIssuedInterimNumbers(items));
            setLatestInitialDocxEntry(firstDocx);
            setHasInitialIssuedPdf(anyIssued);
            return firstDocx;
        } catch (e) {
            if (!silent) console.warn('refreshLatestInitialReport failed:', e);
            setLatestInitialDocxEntry(null);
            setHasInitialIssuedPdf(false);
            return null;
        }
    }

    async function refreshLatestInterimReport({ interim, silent } = {}) {
        if (!reference) {
            setLatestInterimDocxEntry(null);
            return null;
        }
        const n = Number(interim);
        if (!Number.isFinite(n) || n <= 0) {
            setLatestInterimDocxEntry(null);
            return null;
        }
        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            // Prefer template-driven generation for a formatted DOCX output.
            qs.set('template', '1');
            if (mode !== 'surveyor' && code) qs.set('code', code);
            let json;
            for (let attempt = 0; attempt < REPORT_LOG_FETCH_ATTEMPTS; attempt += 1) {
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), 12000);
                try {
                    // Keep URL stable across polls so Authorization preflight can be cached by the browser.
                    // If we need a second attempt, add a small retry marker only for that request.
                    if (attempt > 0) qs.set('_retry', String(attempt));
                    else qs.delete('_retry');
                    const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log?${qs.toString()}`;
                    json = await fetchJson(url, {
                        method: 'GET',
                        headers: authHeaders(),
                        signal: controller.signal,
                        cache: 'no-store'
                    });
                    break;
                } catch (e) {
                    const isCache304 = Number(e?.status || 0) === 304;
                    if (attempt >= (REPORT_LOG_FETCH_ATTEMPTS - 1) || (!isTransientGatewayError(e) && !isCache304)) throw e;
                    await waitMs(350 + Math.round(Math.random() * 300));
                } finally {
                    window.clearTimeout(timeoutId);
                }
            }
            const reportInstance = `interim:${n}`;
            const items = normalizeReportLogItems(json?.items);
            const firstDocx = items.find((it) => it?.download_url && isDocxItem(it) && matchesReport(it, { reportInstance, reportType: 'Interim Report' })) || null;
            setIssuedInterimNumbers(extractIssuedInterimNumbers(items));
            setLatestInterimDocxEntry(firstDocx);
            return firstDocx;
        } catch (e) {
            if (!silent) console.warn('refreshLatestInterimReport failed:', e);
            setLatestInterimDocxEntry(null);
            return null;
        }
    }

    const activeDocxEntry = page === 'interim' || (page === 'risk' && interimNumber)
        ? latestInterimDocxEntry
        : latestInitialDocxEntry;
    const activeEntryIsInterim = String(activeDocxEntry?.report_instance || '').trim().toLowerCase().startsWith('interim:')
        || String(activeDocxEntry?.report_type || '').trim().toLowerCase().includes('interim');

    function getReportDocxUrl() {
        if (!activeDocxEntry?.download_url) return '';
        return `${API_BASE_URL}${activeDocxEntry.download_url}`;
    }

    function formatRevisionLabel(entry) {
        if (!entry) return '';
        const rev = entry?.revision != null && Number.isFinite(Number(entry.revision)) ? Number(entry.revision) : null;
        const versionText = formatReportVersion(rev, reportVersionNaming);
        const when = entry?.generated_at ? new Date(entry.generated_at) : null;
        const whenText = when && Number.isFinite(when.getTime()) ? when.toLocaleString() : '';
        if (versionText && whenText) return `Latest report: ${versionText} (generated ${whenText})`;
        if (versionText) return `Latest report: ${versionText}`;
        if (whenText) return `Latest report generated: ${whenText}`;
        return 'Latest report available';
    }

    function formatIssuedLabel(entry) {
        if (!entry) return '';
        const issuedAt = entry?.issue_pdf_uploaded_at ? new Date(entry.issue_pdf_uploaded_at) : null;
        if (issuedAt && Number.isFinite(issuedAt.getTime())) {
            return `Report Issued: ${issuedAt.toLocaleString()}`;
        }
        return '';
    }

    async function refreshEdits({ silent } = {}) {
        if (!reference) {
            setEditsByKey(new Map());
            return;
        }

        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            if (mode !== 'surveyor' && code) qs.set('code', code);

            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-edits?${qs.toString()}`;
            const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
            const items = Array.isArray(json?.items) ? json.items : [];

            const map = new Map();
            for (const it of items) {
                const table = String(it?.table_name || '').trim();
                const rowId = String(it?.row_id || '').trim();
                const field = String(it?.field_name || '').trim();
                if (!table || !rowId || !field) continue;
                const key = `${table}:${rowId}:${field}`;
                const list = map.get(key) || [];
                list.push(it);
                map.set(key, list);
            }

            setEditsByKey(map);
        } catch (e) {
            if (!silent) console.warn('refreshEdits failed:', e);
            setEditsByKey(new Map());
        }
    }

    function getIssuePdfUrl() {
        const u = activeDocxEntry?.issue_pdf_download_url;
        return u ? `${API_BASE_URL}${u}` : '';
    }

    async function downloadToPc(url, filename) {
        if (!url) throw new Error('No document available');
        try {
            const resp = await fetch(url);
            if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
            const blob = await resp.blob();
            const a = document.createElement('a');
            const objUrl = URL.createObjectURL(blob);
            a.href = objUrl;
            a.download = filename || 'report';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objUrl);
        } catch (e) {
            // Cross-origin downloads via fetch->blob can fail if CORS isn't configured for /uploads.
            // Fallback: open the file directly; browsers typically download DOCX.
            try {
                window.open(url, '_blank', 'noopener,noreferrer');
                return;
            } catch {
                // ignore
            }
            throw e;
        }
    }

    async function openCollaboraWorkingReport() {
        if (!reference || !activeDocxEntry?.id) throw new Error('No report log row found for this report yet');

        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (mode !== 'surveyor' && code) qs.set('code', code);

        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(activeDocxEntry.id))}/collabora-launch?${qs.toString()}`;
        const res = await fetch(url, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: '{}'
        });
        const json = await res.json().catch(() => null);
        if (!res.ok) {
            const msg = (json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`;
            throw new Error(msg);
        }
        if (!json?.launch_form?.action || !json?.launch_form?.fields?.access_token || Number(json?.launch_form?.fields?.access_token_ttl || 0) <= Date.now()) {
            throw new Error('A valid Collabora launch form was not returned');
        }
        const refreshToken = String(json?.refresh_token || '').trim();
        if (!refreshToken) throw new Error('A Collabora refresh credential was not returned');
        const { refresh_token: ignoredRefreshToken, ...session } = json;
        collaboraSessionEpochRef.current += 1;
        collaboraRefreshTokenRef.current = refreshToken;
        collaboraFormSubmittedRef.current = false;
        setCollaboraSession({ ...session, report_log_id: activeDocxEntry.id });
        setMonsurvSelectionText('');
        setMonsurvFactSelectionText('');
        setMonsurvInstructions('');
        setMonsurvReplacementText('');
        setMonsurvFactCheckText('');
        setPreIssueChecks([]);
        setPreIssueSource(null);
        setPreIssueExpanded({});
        setPreIssueActioned({});
        setMonsurvActionError('');
        setMonsurvEditorNotice('Highlight text in Collabora, then click Use Current Selection.');
        setCollaboraEditorOpen(true);
        return json;
    }

    function buildMonsurvActionUrl(action) {
        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (mode !== 'surveyor' && code) qs.set('code', code);
        return `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(activeDocxEntry?.id || ''))}/monsurv-actions/${action}?${qs.toString()}`;
    }

    function toggleMonsurvPanelSection(key) {
        setMonsurvPanelOpen((prev) => ({ ...prev, [key]: !prev?.[key] }));
    }

    function formatDocumentSize(bytes) {
        const n = Number(bytes || 0);
        if (!Number.isFinite(n) || n <= 0) return '0 B';
        if (n < 1024) return `${Math.round(n)} B`;
        if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
        return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    }

    async function loadDocumentSearch() {
        if (!reference || !activeDocxEntry?.id) return;
        setDocumentSearchLoading(true);
        setDocumentSearchError('');
        try {
            const json = await fetchJson(buildMonsurvActionUrl('document-search'), {
                method: 'GET',
                headers: authHeaders()
            });
            const folders = Array.isArray(json?.document_search?.folders) ? json.document_search.folders : [];
            setDocumentSearchFolders(folders);
            setDocumentSearchRoot(String(json?.document_search?.root || '').trim());
            setDocumentSearchSelectedFolder((prev) => (prev && folders.some((folder) => folder.path === prev) ? prev : (folders[0]?.path || '')));
            setVersionControlItems(Array.isArray(json?.version_control?.items) ? json.version_control.items : []);
            setVersionControlPath(json?.version_control?.bucket && json?.version_control?.prefix ? `s3://${json.version_control.bucket}/${json.version_control.prefix}` : '');
        } catch (e) {
            setDocumentSearchError(e?.message || 'Failed to load report documents');
            setDocumentSearchFolders([]);
            setDocumentSearchRoot('');
            setDocumentSearchSelectedFolder('');
            setVersionControlItems([]);
            setVersionControlPath('');
        } finally {
            setDocumentSearchLoading(false);
        }
    }

    async function revertWorkingReportVersion(item) {
        const target = item && typeof item === 'object' ? item : pendingRevertVersion;
        const workingDocumentId = Number(target?.id || 0);
        if (!Number.isFinite(workingDocumentId) || workingDocumentId <= 0) {
            setMonsurvActionError('Version cannot be reverted because its working document id is missing.');
            return;
        }

        setMonsurvActionBusy('revert');
        setMonsurvActionError('');
        try {
            await fetchJson(buildMonsurvActionUrl('revert-version'), {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ working_document_id: workingDocumentId })
            });
            setPendingRevertVersion(null);
            setMonsurvEditorNotice(`Reverted to ${target?.name || 'the selected version'}. Collabora has been reloaded from that point.`);
            await loadDocumentSearch();
            await openCollaboraWorkingReport();
        } catch (e) {
            setMonsurvActionError(e?.message || 'Failed to revert report version');
        } finally {
            setMonsurvActionBusy('');
        }
    }

    function getCollaboraOrigin() {
        try {
            return new URL(collaboraSession?.launch_form?.action || '').origin;
        } catch {
            return '';
        }
    }

    function postToCollabora(messages) {
        const target = collaboraFrameRef.current?.contentWindow;
        if (!target) return false;
        const list = Array.isArray(messages) ? messages : [messages];
        const targetOrigin = getCollaboraOrigin();
        if (!targetOrigin) return false;
        list.forEach((message) => {
            try { target.postMessage(JSON.stringify(message), targetOrigin); } catch { /* ignore unsupported host bridge */ }
        });
        return true;
    }

    function signalCollaboraPostMessageReady() {
        if (!collaboraFormSubmittedRef.current) return;
        postToCollabora({ MessageId: 'Host_PostmessageReady' });
    }

    function closeCollaboraEditor() {
        collaboraSessionEpochRef.current += 1;
        collaboraRefreshTokenRef.current = '';
        collaboraFormSubmittedRef.current = false;
        setCollaboraEditorOpen(false);
        setCollaboraSession(null);
    }

    function saveCurrentCollaboraDocument() {
        if (collaboraSaveRequestRef.current) return Promise.reject(new Error('A Collabora save is already in progress.'));
        return new Promise((resolve, reject) => {
            const timer = setTimeout(() => {
                if (!collaboraSaveRequestRef.current) return;
                collaboraSaveRequestRef.current = null;
                reject(new Error('Collabora did not confirm the save. Pre-issue checks were not run to avoid checking an older report version.'));
            }, 15000);
            collaboraSaveRequestRef.current = { resolve, reject, timer };
            const posted = postToCollabora([
                { MessageId: 'Host_PostmessageReady' },
                { MessageId: 'Action_Save', Values: { DontTerminateEdit: true, DontSaveIfUnmodified: false, Notify: true } }
            ]);
            if (!posted) {
                collaboraSaveRequestRef.current = null;
                clearTimeout(timer);
                reject(new Error('The Collabora editor is not ready to save.'));
            }
        });
    }

    async function requestCollaboraSelection(target = 'ai') {
        const selectionTarget = target === 'fact' ? 'fact' : 'ai';
        monsurvSelectionTargetRef.current = selectionTarget;
        setMonsurvActionError('');
        setMonsurvEditorNotice(selectionTarget === 'fact' ? 'Reading the current fact-check selection...' : 'Reading the current regeneration selection...');
        let clipboardBefore = '';
        try {
            clipboardBefore = String(await navigator.clipboard.readText() || '').trim();
        } catch {
            clipboardBefore = '';
        }

        postToCollabora([
            { MessageId: 'Host_PostmessageReady' },
            { MessageId: 'Send_UNO_Command', Values: { Command: '.uno:Copy' } },
            { MessageId: 'Action_Copy' },
            { MessageId: 'Action_GetSelectedText' },
            { MessageId: 'Action_GetTextSelection' },
            { MessageId: 'Action_GetClipboard' },
            { MessageId: 'Get_Selection' }
        ]);

        try {
            await new Promise((resolve) => setTimeout(resolve, 350));
            const clip = await navigator.clipboard.readText();
            const text = String(clip || '').trim();
            if (text && text !== clipboardBefore) {
                if (selectionTarget === 'fact') setMonsurvFactSelectionText((prev) => (prev === text ? prev : text));
                else setMonsurvSelectionText((prev) => (prev === text ? prev : text));
                setMonsurvEditorNotice('Selection loaded. You can regenerate or fact-check it now.');
                return;
            }
        } catch {
            // Browser clipboard permissions vary; the manual field remains available.
        }

        setMonsurvEditorNotice('Collabora did not expose a new selection. Press Ctrl+C in Collabora, then click Use Current Selection again.');
    }

    async function runMonsurvRegeneration() {
        const selectedText = String(monsurvSelectionText || '').trim();
        const instructions = String(monsurvInstructions || '').trim();
        if (!selectedText) {
            setMonsurvActionError('Select or paste the report text to regenerate.');
            return;
        }
        if (!instructions) {
            setMonsurvActionError('Describe the edits required before regenerating.');
            return;
        }

        setMonsurvActionBusy('regenerate');
        setMonsurvActionError('');
        setMonsurvReplacementText('');
        try {
            const json = await fetchJson(buildMonsurvActionUrl('regenerate'), {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ selected_text: selectedText, instructions })
            });
            setMonsurvReplacementText(String(json?.replacement_text || '').trim());
            setMonsurvEditorNotice('Regenerated text is ready. Copy it into the highlighted section, then save in Collabora.');
        } catch (e) {
            setMonsurvActionError(e?.message || 'Regeneration failed');
        } finally {
            setMonsurvActionBusy('');
        }
    }

    async function runMonsurvFactCheck() {
        const selectedText = String(monsurvFactSelectionText || '').trim();
        if (!selectedText) {
            setMonsurvActionError('Select or paste the report text to fact-check.');
            return;
        }

        setMonsurvActionBusy('fact-check');
        setMonsurvActionError('');
        setMonsurvFactCheckText('');
        try {
            const json = await fetchJson(buildMonsurvActionUrl('fact-check'), {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ selected_text: selectedText })
            });
            setMonsurvFactCheckText(String(json?.fact_check || '').trim());
            setMonsurvEditorNotice('Fact check complete. Review the findings before editing the report.');
        } catch (e) {
            setMonsurvActionError(e?.message || 'Fact check failed');
        } finally {
            setMonsurvActionBusy('');
        }
    }

    async function runPreIssueChecks() {
        if (!activeDocxEntry?.id) return;
        setMonsurvActionBusy('pre-issue-checks');
        setMonsurvActionError('');
        setPreIssueChecks([]);
        setPreIssueSource(null);
        setPreIssueActioned({});
        setMonsurvEditorNotice('Saving the current Collabora document before pre-issue checks...');
        try {
            await saveCurrentCollaboraDocument();
            setMonsurvEditorNotice('Save confirmed. Running four pre-issue checks against the saved DOCX...');
            const json = await fetchJson(buildMonsurvActionUrl('pre-issue-checks'), {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: '{}'
            });
            const checks = Array.isArray(json?.checks) ? json.checks : [];
            setPreIssueChecks(checks);
            setPreIssueSource(json?.source_working_report || null);
            setPreIssueExpanded({});
            setMonsurvEditorNotice('Pre-issue checks complete. Review every finding before finalising the report.');
            await loadDocumentSearch();
        } catch (e) {
            setMonsurvActionError(e?.message || 'Pre-issue checks failed');
        } finally {
            setMonsurvActionBusy('');
        }
    }

    function togglePreIssueFindingActioned(checkKey, findingIndex) {
        const findingKey = `${checkKey}:${findingIndex}`;
        setPreIssueActioned((prev) => ({ ...prev, [findingKey]: !prev[findingKey] }));
    }

    async function sendMonsurvChatMessage() {
        const message = String(monsurvChatInput || '').trim();
        if (!message || monsurvChatBusy || !reference) return;

        const nextMessages = [...monsurvChatMessages, { role: 'user', content: message }];
        setMonsurvChatMessages(nextMessages);
        setMonsurvChatInput('');
        setMonsurvChatError('');
        setMonsurvChatBusy(true);
        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            qs.set('report_instance', reportInstance || 'initial');
            if (mode !== 'surveyor' && code) qs.set('code', code);
            const json = await fetchJson(`${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/copilot/chat?${qs.toString()}`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ message, messages: nextMessages.slice(-8) })
            });
            const replyText = String(json?.reply?.text || json?.text || '').trim() || 'No response returned.';
            setMonsurvChatMessages((prev) => [...prev, { role: 'assistant', content: replyText }]);
        } catch (e) {
            setMonsurvChatError(e?.message || 'Chatbot request failed');
            setMonsurvChatMessages((prev) => [...prev, { role: 'assistant', content: 'I could not answer that request. Please try again or rephrase the question.' }]);
        } finally {
            setMonsurvChatBusy(false);
        }
    }

    async function copyMonsurvReplacement() {
        const text = String(monsurvReplacementText || '').trim();
        if (!text) return;
        try {
            await navigator.clipboard.writeText(text);
            setMonsurvEditorNotice('Replacement text copied. Paste it over the highlighted text in Collabora.');
        } catch {
            setMonsurvEditorNotice('Copy failed in this browser. Select the replacement text manually.');
        }
    }

    async function applyMonsurvReplacementToEditor() {
        const text = String(monsurvReplacementText || '').trim();
        if (!text) return;

        try {
            await navigator.clipboard.writeText(text);
        } catch {
            setMonsurvEditorNotice('Browser clipboard access was blocked. Use Copy, then paste over the highlighted text in Collabora.');
            return;
        }

        postToCollabora([
            { MessageId: 'Host_PostmessageReady' },
            { MessageId: 'Set_Clipboard', Values: { Text: text, MimeType: 'text/plain' } },
            { MessageId: 'Send_UNO_Command', Values: { Command: '.uno:Paste' } },
            { MessageId: 'Action_Paste' },
            { MessageId: 'Action_InsertText', Values: { Text: text } },
            { MessageId: 'Insert_Text', Values: { text } }
        ]);
        setMonsurvEditorNotice('Replacement copied and paste command sent to Collabora. Confirm the highlighted text changed, then save the report.');
    }

    async function finalizeWorkingReportPdf() {
        if (!activeDocxEntry?.id) return;
        setMonsurvActionBusy('finalize');
        setMonsurvActionError('');
        setIssueUploadError('');
        setIssueUploadSuccess('');
        try {
            await fetchJson(buildMonsurvActionUrl('finalize-pdf'), {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: '{}'
            });
            setIssueUploadSuccess('Issued PDF created from the working report.');
            setMonsurvEditorNotice('Issued PDF created and logged under Issued-Report.');
            if (page === 'interim') await refreshLatestInterimReport({ interim: interimNumber || 1, silent: true });
            else await refreshLatestInitialReport({ silent: true });
        } catch (e) {
            setMonsurvActionError(e?.message || 'Failed to finalize PDF');
        } finally {
            setMonsurvActionBusy('');
        }
    }

    async function onViewReport() {
        if (viewReportBusyRef.current) return;
        viewReportBusyRef.current = true;
        setViewReportBusy(true);
        setIssueUploadError('');
        try {
            if (mode === 'surveyor' && activeDocxEntry?.id) {
                try {
                    await openCollaboraWorkingReport();
                    setViewedReportOnce(true);
                    return;
                } catch (collaboraErr) {
                    if (activeDocxEntry?.collabora_enabled) throw collaboraErr;
                    console.warn('Collabora launch unavailable; falling back to DOCX download:', collaboraErr);
                }
            }

            const url = getReportDocxUrl();
            const fallbackName = page === 'interim'
                ? (reference ? `Interim_Report_${reference}.docx` : 'Interim_Report.docx')
                : (reference ? `Initial_Report_${reference}.docx` : 'Initial_Report.docx');
            const name = activeDocxEntry?.file_name || fallbackName;
            await downloadToPc(url, name);
            setViewedReportOnce(true);
        } catch (e) {
            setIssueUploadError(e?.message || 'Failed to download DOCX');
        } finally {
            viewReportBusyRef.current = false;
            setViewReportBusy(false);
        }
    }

    async function uploadFinalIssuePdf(file, { sendToFunder = false } = {}) {
        if (!reference || !activeDocxEntry?.id) throw new Error('No report log row found for this report yet');
        const name = String(file?.name || '').toLowerCase();
        const type = String(file?.type || '').toLowerCase();
        const isPdf = type === 'application/pdf' || name.endsWith('.pdf');
        if (!isPdf) throw new Error('Please upload a PDF');

        setIssueUploadBusy(true);
        setIssueUploadError('');
        setIssueUploadSuccess('');
        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            if (mode !== 'surveyor' && code) qs.set('code', code);
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(activeDocxEntry.id))}/upload-issue-pdf?${qs.toString()}`;
            const fd = new FormData();
            fd.append('file', file);
            fd.append('send_to_funder', sendToFunder ? '1' : '0');

            const res = await fetch(url, { method: 'POST', headers: authHeaders(), body: fd });
            const json = await res.json().catch(() => null);
            if (!res.ok) {
                const msg = (json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`;
                throw new Error(msg);
            }

            const delivery = json?.delivery && typeof json.delivery === 'object' ? json.delivery : null;
            if (delivery?.sent && delivery?.sent_to) {
                setIssueUploadSuccess(`Issued PDF uploaded and sent to ${delivery.sent_to}.`);
            } else if (delivery?.requested && delivery?.warning) {
                setIssueUploadSuccess(`Issued PDF uploaded. ${delivery.warning}`);
            } else {
                setIssueUploadSuccess('Issued PDF uploaded.');
            }

            try {
                const dashUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/dashboard?mode=${encodeURIComponent(mode)}`;
                const latestDashboard = await fetchJson(dashUrl, { method: 'GET', headers: authHeaders() });
                setData(latestDashboard);
            } catch {
                // Non-blocking: issue upload should still succeed if dashboard refresh fails.
            }

            setShowQaUpload(false);
            setIssueSendToFunder(false);

            if (page === 'interim') {
                const currentInterim = Number(interimNumber || 0) || 1;
                const advancedInterim = Number(json?.progression?.next_interim_number || 0) || (currentInterim + 1);
                const targetInterim = Number.isFinite(advancedInterim) && advancedInterim > currentInterim
                    ? Math.trunc(advancedInterim)
                    : currentInterim;

                if (targetInterim !== currentInterim) {
                    setQuery({ page: 'interim', interim: targetInterim });
                }
                await refreshLatestInterimReport({ interim: targetInterim, silent: true });
            } else {
                await refreshLatestInitialReport({ silent: true });
                // Do not auto-navigate or generate any Interim report files.
                // The Initial issued PDF simply unlocks the Interim Report 1 dashboard button.
            }
        } finally {
            setIssueUploadBusy(false);
        }
    }

    async function sendReportToClient() {
        if (!reference || !activeDocxEntry?.id) throw new Error('No report log row found');
        setSendReportBusy(true);
        setIssueUploadError('');
        setReportSentTo('');
        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            if (mode !== 'surveyor' && code) qs.set('code', code);
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(activeDocxEntry.id))}/send-report?${qs.toString()}`;
            const res = await fetch(url, { method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' } });
            const json = await res.json().catch(() => null);
            if (!res.ok) {
                const msg = (json && (json.error || json.message)) ? (json.error || json.message) : `HTTP ${res.status}`;
                throw new Error(msg);
            }
            setReportSentTo(json?.sent_to || 'client');

            // Refresh to unlock Interim Report 1 bubble
            if (page === 'interim') {
                await refreshLatestInterimReport({ interim: interimNumber || 1, silent: true });
            } else {
                await refreshLatestInitialReport({ silent: true });
            }
        } catch (e) {
            setIssueUploadError(e?.message || 'Failed to send report');
        } finally {
            setSendReportBusy(false);
        }
    }

    async function generateAppendicesPdf() {
        if (!reference || !activeDocxEntry?.id) throw new Error('No report log row found for this report yet');
        setAppendixGenBusy(true);
        setAppendixGenMessage('');
        try {
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            if (mode !== 'surveyor' && code) qs.set('code', code);
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-log/${encodeURIComponent(String(activeDocxEntry.id))}/generate-appendices?${qs.toString()}`;
            const json = await fetchJson(url, { method: 'POST', headers: authHeaders() });
            const dl = json?.item?.appendix_pdf_download_url || '';
            const full = dl ? (String(dl).startsWith('http') ? dl : `${API_BASE_URL}${dl}`) : '';
            setAppendixPdfUrl(full);
            setAppendixGenMessage('Appendices Generated');
        } catch (e) {
            setAppendixGenMessage(e?.message || 'Failed to generate appendices');
        } finally {
            setAppendixGenBusy(false);
        }
    }

    const setQuery = useCallback((patch) => {
        const nextParams = new URLSearchParams(search || '');
        const entries = Object.entries(patch || {});

        for (const [k, v] of entries) {
            if (v === undefined || v === null || v === '') {
                nextParams.delete(k);
            } else {
                nextParams.set(k, String(v));
            }
        }

        const nextSearch = `?${nextParams.toString()}`;
        window.history.pushState(null, '', nextSearch);
        setSearch(nextSearch);
    }, [search]);

    function goToWorkspace(nextWorkspace) {
        if (nextWorkspace === 'overview') {
            setQuery({ workspace: 'overview', page: 'initial', interim: null });
            return;
        }
        if (nextWorkspace === 'project_record') {
            const recordPages = ['site_scheme', 'conditions', 'programme', 'design_team', 'procurement', 'costs', 'cost_tables', 'drawdown', 'insurance', 'approved_evidence'];
            setQuery({
                workspace: 'project_record',
                page: recordPages.includes(page) ? page : 'site_scheme',
                interim: null
            });
            return;
        }
        if (nextWorkspace === 'documents') {
            setQuery({ workspace: 'documents', page: 'initial', interim: null });
            return;
        }

        setQuery({
            workspace: 'reporting_periods',
            page: page === 'interim' || page === 'risk' ? page : 'initial',
            interim: page === 'interim' || page === 'risk' ? interimNumber : null
        });
    }

    function goToProjectRecordSection(nextPage) {
        if (nextPage === 'conditions') return goToConditionsTracker();
        if (nextPage === 'programme') return goToProgrammeTracker();
        if (nextPage === 'design_team') return goToDesignTeamTracker();
        if (nextPage === 'costs') return goToCostTables();
        if (nextPage === 'insurance') return goToInsuranceTracker();
        setQuery({ workspace: 'project_record', page: nextPage, interim: null });
    }

    function goToInitialReport() {
        setQuery({ page: 'initial', interim: null });
    }

    function goToConditionsTracker() {
        setQuery({ page: 'conditions', interim: null });
    }

    function goToRiskTracker() {
        const activeInterim = page === 'interim' ? currentInterimNumber : interimNumber;
        setQuery({ workspace: 'reporting_periods', page: 'risk', interim: activeInterim || null });
    }

    function goToProgrammeTracker() {
        setQuery({ page: 'programme', interim: null });
    }

    function goToDesignTeamTracker() {
        setQuery({ page: 'design_team', interim: null });
    }

    function goToCostTables() {
        setQuery({ page: 'costs', interim: currentInterimNumber });
    }

    function goToDrawdownTracker() {
        setQuery({ page: 'drawdown', interim: null });
    }

    function goToInsuranceTracker() {
        setQuery({ page: 'insurance', interim: null });
    }

    function parseMoneyLoose(v) {
        const s = String(v ?? '').trim();
        if (!s) return 0;
        const cleaned = s.replace(/[^0-9.-]/g, '');
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
    }

    function parseDrawdownNumberFromMessage(message) {
        const raw = String(message || '');
        const m = raw.match(/drawdown\s+(\d+)/i);
        if (!m) return null;
        const n = Number(m[1]);
        return Number.isFinite(n) && n > 0 ? n : null;
    }

    function computeInterimDrawdownClaim(answers) {
        const a = answers && typeof answers === 'object' ? answers : null;
        if (!a) return null;

        const getResolvedValue = (groupKey, itemKey, borrowerValue) => {
            const overrides = a.drawdown_surveyor_overrides;
            const groups = overrides && typeof overrides === 'object' ? overrides : {};
            const group = groups[groupKey] && typeof groups[groupKey] === 'object' ? groups[groupKey] : null;
            const entry = group ? group[itemKey] : null;
            const surveyorValue = String(entry?.surveyor_value || '').trim();
            return surveyorValue || borrowerValue;
        };

        const drawdownCosts = a.drawdown_costs && typeof a.drawdown_costs === 'object' ? a.drawdown_costs : null;

        // Construction
        const ddConstruction = drawdownCosts?.dd_construction && typeof drawdownCosts.dd_construction === 'object' ? drawdownCosts.dd_construction : null;
        let construction = 0;
        if (ddConstruction && typeof ddConstruction === 'object') {
            for (const [itemKey, value] of Object.entries(ddConstruction)) {
                const resolved = getResolvedValue('dd_construction', itemKey, value);
                construction += parseMoneyLoose(resolved);
            }
        } else if (a.construction_cost_claim != null) {
            construction = parseMoneyLoose(a.construction_cost_claim);
        }

        // Contingency
        let contingency = 0;
        if (a.contingency_claim != null) {
            contingency = parseMoneyLoose(a.contingency_claim);
        } else if (Array.isArray(drawdownCosts?.contingency_claims)) {
            contingency = drawdownCosts.contingency_claims.reduce((acc, claim, idx) => {
                const itemKey = String(claim?.id || `idx:${idx + 1}`);
                const resolved = getResolvedValue('contingency_claims', itemKey, claim?.value);
                return acc + parseMoneyLoose(resolved);
            }, 0);
        }

        const total = construction + contingency;
        return Number.isFinite(total) ? Math.max(0, total) : null;
    }

    function makeCostItemKey(prefix, heading, index) {
        const h = String(heading || '').trim();
        if (h) return `${prefix}:${h}`;
        return `${prefix}:idx:${index + 1}`;
    }

    function encodePrefillPayload(payload) {
        try {
            const json = JSON.stringify(payload || {});
            return btoa(unescape(encodeURIComponent(json)));
        } catch {
            return '';
        }
    }

    function normalizeLabelKey(text) {
        return String(text || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    function buildDrawdownPrefillPayload() {
        const dd = {
            dd_construction: {},
            dd_professional_fees: {},
            dd_additional_costs: {},
            contingency_claims: []
        };

        const groups = Array.isArray(drawdownSchedule) ? drawdownSchedule : [];
        for (const group of groups) {
            const children = Array.isArray(group?.children) ? group.children : [];
            if (group.key === 'contingency') {
                children.forEach((row, idx) => {
                    const value = String(row?.requested ?? '').trim();
                    const description = String(row?.label ?? '').trim();
                    if (!value && !description) return;
                    dd.contingency_claims.push({
                        id: `prefill_${idx + 1}`,
                        description,
                        value
                    });
                });
                continue;
            }

            const prefix = group.key === 'construction'
                ? 'construction_cost_breakdown'
                : group.key === 'professional'
                    ? 'professional_fees_breakdown'
                    : 'other_fees_breakdown';

            const target = group.key === 'construction'
                ? dd.dd_construction
                : group.key === 'professional'
                    ? dd.dd_professional_fees
                    : dd.dd_additional_costs;

            children.forEach((row, idx) => {
                const value = String(row?.requested ?? '').trim();
                if (!value) return;
                const key = makeCostItemKey(prefix, row?.label, idx);
                target[key] = value;
            });
        }

        return dd;
    }

    function formatMoney(v) {
        const n = Math.round(parseMoneyLoose(v));
        return `£${n.toLocaleString('en-GB')}`;
    }

    function parseBreakdownRows(raw) {
        if (!raw) return [];
        if (Array.isArray(raw)) return raw;
        if (typeof raw === 'object') return [];
        const s = String(raw || '').trim();
        if (!s) return [];
        try {
            const parsed = JSON.parse(s);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }

    function computeContingencyAmount(raw, base) {
        const s = String(raw ?? '').trim();
        if (!s) return 0;
        if (s.includes('%')) {
            const pct = parseMoneyLoose(s);
            if (!pct || !base) return 0;
            return (base * pct) / 100;
        }
        return parseMoneyLoose(s);
    }

    function buildDrawdownScheduleFromInitial(report) {
        const tables = report?.tables || {};
        const row = Array.isArray(tables.ini_section_7_costs) && tables.ini_section_7_costs.length
            ? tables.ini_section_7_costs[0]
            : (Array.isArray(tables.ini_section_7) && tables.ini_section_7.length ? tables.ini_section_7[0] : null);

        if (!row) return [];

        const constructionRows = parseBreakdownRows(row?.construction_cost_breakdown);
        const professionalRows = parseBreakdownRows(row?.professional_fees_breakdown);
        const otherRows = parseBreakdownRows(row?.other_fees_breakdown);

        const constructionFallback = parseMoneyLoose(row?.build_cost);
        const professionalFallback = parseMoneyLoose(row?.professional_fees);
        const otherFallback = parseMoneyLoose(row?.other_development_costs || row?.other_fees || row?.other_costs);
        const contingencyFallback = computeContingencyAmount(row?.contingency, constructionRows.length
            ? constructionRows.reduce((acc, r) => acc + parseMoneyLoose(r?.amount), 0)
            : constructionFallback);

        function mapBreakdown(prefix, items, fallback, fallbackLabel) {
            const rows = (Array.isArray(items) ? items : [])
                .map((it, idx) => ({
                    key: `${prefix}_${idx + 1}`,
                    label: String(it?.heading ?? it?.item ?? it?.label ?? '').trim() || `Line ${idx + 1}`,
                    budget: parseMoneyLoose(it?.amount ?? it?.value),
                    requested: ''
                }))
                .filter((it) => it.label || it.budget);

            if (rows.length) return rows;
            return [{ key: `${prefix}_total`, label: fallbackLabel, budget: fallback, requested: '' }];
        }

        return [
            {
                key: 'construction',
                label: 'Construction Costs',
                children: mapBreakdown('construction', constructionRows, constructionFallback, 'Construction Costs')
            },
            {
                key: 'professional',
                label: 'Professional Fees',
                children: mapBreakdown('professional', professionalRows, professionalFallback, 'Professional Fees')
            },
            {
                key: 'other',
                label: 'Other Costs',
                children: mapBreakdown('other', otherRows, otherFallback, 'Other Costs')
            },
            {
                key: 'contingency',
                label: 'Contingency',
                children: [
                    { key: 'contingency_1', label: '', budget: contingencyFallback, requested: '' }
                ]
            }
        ];
    }

    useEffect(() => {
        if (mode !== 'developer' || view !== 'request_valuation') return;

        const ref = String(reference || '').trim();
        const refLower = ref.toLowerCase();

        // When switching projects, clear any prior project state immediately so we never show stale values.
        if (drawdownScheduleRef.current !== refLower) {
            drawdownScheduleRef.current = refLower;
            setDrawdownSchedule([]);
            setDrawdownClaims({
                construction: {},
                professional: {},
                other: {},
                contingencyByDesc: {},
                contingencyTotal: 0
            });
            setDrawdownRequestError('');
            setDrawdownRequestSuccess('');
        }

        if (!ref) return;
        if (!initialReport) return;

        const nextSchedule = buildDrawdownScheduleFromInitial(initialReport);

        // Preserve any user-entered "requested" values when the underlying initial report is re-fetched.
        setDrawdownSchedule((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return nextSchedule;

            const requestedByKey = new Map();
            for (const g of prev) {
                const children = Array.isArray(g?.children) ? g.children : [];
                for (const c of children) {
                    const key = String(c?.key || '').trim();
                    if (!key) continue;
                    requestedByKey.set(key, c?.requested ?? '');
                }
            }

            return (Array.isArray(nextSchedule) ? nextSchedule : []).map((g) => {
                const children = Array.isArray(g?.children) ? g.children : [];
                return {
                    ...g,
                    children: children.map((c) => {
                        const key = String(c?.key || '').trim();
                        if (!key) return c;
                        const requested = requestedByKey.has(key) ? requestedByKey.get(key) : (c?.requested ?? '');
                        return { ...c, requested };
                    })
                };
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, view, reference, initialReport]);

    useEffect(() => {
        if (mode !== 'developer' || view !== 'request_valuation') return;
        if (!reference) return;

        let cancelled = false;

        async function loadClaims() {
            const nextClaims = {
                construction: {},
                professional: {},
                other: {},
                contingencyByDesc: {},
                contingencyTotal: 0
            };

            const nums = (Array.isArray(interimSubmissions) ? interimSubmissions : [])
                .map((it) => Number(it?.interim_number || 0))
                .filter((n) => Number.isFinite(n) && n > 0)
                .sort((a, b) => a - b);

            if (!nums.length) {
                if (!cancelled) setDrawdownClaims(nextClaims);
                return;
            }

            await Promise.all(nums.map(async (n) => {
                try {
                    const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report?mode=developer&interim=${encodeURIComponent(String(n))}`;
                    const ir = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                    const a = ir?.answers || {};
                    const drawdownCosts = (a.drawdown_costs && typeof a.drawdown_costs === 'object') ? a.drawdown_costs : {};

                    const ddConstruction = (drawdownCosts.dd_construction && typeof drawdownCosts.dd_construction === 'object') ? drawdownCosts.dd_construction : {};
                    const ddProfessional = (drawdownCosts.dd_professional_fees && typeof drawdownCosts.dd_professional_fees === 'object') ? drawdownCosts.dd_professional_fees : {};
                    const ddAdditional = (drawdownCosts.dd_additional_costs && typeof drawdownCosts.dd_additional_costs === 'object') ? drawdownCosts.dd_additional_costs : {};
                    const contingencyClaims = Array.isArray(drawdownCosts.contingency_claims) ? drawdownCosts.contingency_claims : [];

                    Object.entries(ddConstruction).forEach(([k, v]) => {
                        const num = parseMoneyLoose(v);
                        if (!num) return;
                        nextClaims.construction[k] = (nextClaims.construction[k] || 0) + num;
                    });
                    Object.entries(ddProfessional).forEach(([k, v]) => {
                        const num = parseMoneyLoose(v);
                        if (!num) return;
                        nextClaims.professional[k] = (nextClaims.professional[k] || 0) + num;
                    });
                    Object.entries(ddAdditional).forEach(([k, v]) => {
                        const num = parseMoneyLoose(v);
                        if (!num) return;
                        nextClaims.other[k] = (nextClaims.other[k] || 0) + num;
                    });

                    contingencyClaims.forEach((c) => {
                        const value = parseMoneyLoose(c?.value);
                        if (!value) return;
                        nextClaims.contingencyTotal += value;
                        const key = normalizeLabelKey(c?.description);
                        if (key) nextClaims.contingencyByDesc[key] = (nextClaims.contingencyByDesc[key] || 0) + value;
                    });
                } catch {
                    // ignore per-interim
                }
            }));

            if (!cancelled) setDrawdownClaims(nextClaims);
        }

        loadClaims();
        return () => { cancelled = true; };
    }, [mode, view, reference, interimSubmissions, authHeaders]);

    async function requestDrawdown() {
        if (drawdownRequestBusy) return;
        setDrawdownRequestError('');
        setDrawdownRequestSuccess('');

        if (!String(reference || '').trim()) {
            setDrawdownRequestError('Please select a project first.');
            return;
        }

        if (overclaimDetected) {
            setDrawdownRequestError(overclaimMessage);
            return;
        }

        const totalRequested = (drawdownSchedule || [])
            .flatMap((row) => Array.isArray(row?.children) ? row.children : [])
            .reduce((acc, row) => acc + parseMoneyLoose(row?.requested), 0);
        if (!totalRequested) {
            setDrawdownRequestError('Please enter at least one drawdown value.');
            return;
        }

        setDrawdownRequestBusy(true);
        try {
            const drawdownNumber = Number(nextInterimNumber || 1) || 1;
            const message = `Project ${String(reference || '').trim()} has requested Drawdown ${drawdownNumber}`;
            const payload = {
                drawdown_number: drawdownNumber,
                total_requested: totalRequested,
                drawdown_costs: buildDrawdownPrefillPayload()
            };

            await fetchJson(`${API_BASE_URL}/api/notifications`, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    message,
                    project_reference: String(reference || '').trim(),
                    type: 'drawdown_request',
                    payload
                })
            });

            const prefill = payload.drawdown_costs;
            setDrawdownRequestSuccess(`Drawdown ${drawdownNumber} requested.`);
            openSurveyorQuestionnaire({ reportType: 'interim', valuationNumber: drawdownNumber, prefillDrawdown: prefill });
            setQuery({ page: 'interim', interim: drawdownNumber });
        } catch (e) {
            setDrawdownRequestError(e?.message || 'Failed to request drawdown');
        } finally {
            setDrawdownRequestBusy(false);
        }
    }

    useEffect(() => {
        setAutoInterimApplied(false);
    }, [reference]);

    useEffect(() => {
        if (mode !== 'surveyor' && mode !== 'developer') return;
        if (!reference) return;

        let cancelled = false;
        let timer;

        async function loadRequestedDrawdowns() {
            try {
                const json = await fetchJson(`${API_BASE_URL}/api/notifications?limit=100`, {
                    method: 'GET',
                    headers: authHeaders()
                });
                const items = Array.isArray(json?.items) ? json.items : [];
                const ref = String(reference || '').trim().toLowerCase();
                const drawdowns = items.filter((it) => {
                    if (!it) return false;
                    const type = String(it.type || '').toLowerCase();
                    if (type !== 'drawdown_request') return false;
                    const pref = String(it.project_reference || '').trim().toLowerCase();
                    return pref === ref;
                });
                const payloads = {};
                const nums = Array.from(new Set(drawdowns
                    .map((it) => {
                        const n = parseDrawdownNumberFromMessage(it?.message);
                        if (Number.isFinite(n) && n > 0) {
                            const p = it?.payload && typeof it.payload === 'object' ? it.payload : null;
                            if (p) payloads[n] = p;
                        }
                        return n;
                    })
                    .filter((n) => Number.isFinite(n) && n > 0)))
                    .sort((a, b) => a - b);
                if (!cancelled) setRequestedInterims(nums);
                if (!cancelled) setRequestedInterimPayloads(payloads);
            } catch {
                // ignore
            }
        }

        loadRequestedDrawdowns();
        timer = window.setInterval(loadRequestedDrawdowns, 30_000);

        return () => {
            cancelled = true;
            if (timer) window.clearInterval(timer);
        };
    }, [mode, reference, authHeaders]);

    useEffect(() => {
        if (autoInterimApplied) return;
        if (mode !== 'surveyor' && mode !== 'developer') return;
        if (!reference) return;
        if (mode === 'surveyor' && !siteVisitLoaded) return;
        const hasExplicitPage = params.has('page');

        const inProgressType = String(data?.in_progress_report_type || '').trim().toLowerCase();
        const inProgressNumber = (() => {
            const n = Number(data?.in_progress_interim_number);
            return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
        })();
        const bookedInterimNumber = getInterimNumberFromReportInstance(siteVisitState?.appointment?.report_instance);
        const inProgressInterimReady = inProgressType === 'interim'
            && Boolean(inProgressNumber)
            && (mode !== 'surveyor' || bookedInterimNumber === inProgressNumber);
        const initialConfirmedLive = Boolean(projectSetupRecord?.setup_completed_at || projectSetupRecord?.setupCompletedAt);

        const latest = Array.isArray(requestedInterims) && requestedInterims.length
            ? requestedInterims[requestedInterims.length - 1]
            : null;
        if (mode === 'surveyor' && inProgressNumber && !inProgressInterimReady) {
            if (!interimNumber || Number(interimNumber) === inProgressNumber) {
                setAutoInterimApplied(true);
                setQuery({ page: 'interim', interim: inProgressNumber });
            }
            return;
        }
        if (interimNumber || (hasExplicitPage && page !== 'initial')) return;
        if (initialConfirmedLive && inProgressInterimReady && inProgressNumber) {
            setAutoInterimApplied(true);
            setQuery({ page: 'interim', interim: inProgressNumber });
            return;
        }
        if (initialConfirmedLive && latest) {
            setAutoInterimApplied(true);
            setQuery({ page: 'interim', interim: latest });
        }
    }, [
        autoInterimApplied,
        mode,
        reference,
        interimNumber,
        page,
        params,
        requestedInterims,
        issuedInterimNumbers,
        setQuery,
        siteVisitLoaded,
        siteVisitState?.appointment?.report_instance,
        data?.in_progress_report_type,
        data?.in_progress_interim_number,
        projectSetupRecord?.setup_completed_at,
        projectSetupRecord?.setupCompletedAt
    ]);

    useEffect(() => {
        if (mode !== 'developer') return;

        let cancelled = false;

        async function loadDeveloperOverview() {
            setDevOverviewLoading(true);
            setDevOverviewError('');
            setDevProjectsReady(false);

            try {
                const resp = await fetchJson(`${API_BASE_URL}/portal/developer/projects`, {
                    method: 'GET',
                    headers: authHeaders()
                });
                const rows = Array.isArray(resp?.projects) ? resp.projects : [];

                const mapped = rows
                    .map((p) => ({
                        reference: String(p?.reference || '').trim(),
                        name: String(p?.project_name || p?.name || '').trim(),
                        facility: p?.project_facility ?? p?.facility_amount ?? p?.facility ?? null
                    }))
                    .filter((p) => p.reference);

                if (cancelled) return;

                setDevOverviewProjects(mapped);

                if (!mapped.length) {
                    setDevOverviewDrawnByRef({});
                    return;
                }

                if (devOverviewSelectedRef !== 'all' && !mapped.some((p) => p.reference === devOverviewSelectedRef)) {
                    setDevOverviewSelectedRef('all');
                }

                const nextDrawn = {};
                await Promise.all(mapped.map(async (p) => {
                    try {
                        const qs = new URLSearchParams();
                        qs.set('mode', 'developer');
                        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(p.reference)}/interim-report?${qs.toString()}`;
                        const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                        const amount = computeInterimDrawdownClaim(json?.answers || null);
                        nextDrawn[p.reference] = amount || 0;
                    } catch {
                        nextDrawn[p.reference] = 0;
                    }
                }));

                if (!cancelled) setDevOverviewDrawnByRef(nextDrawn);
            } catch (e) {
                if (!cancelled) {
                    setDevOverviewError(e?.message || 'Failed to load overview metrics');
                    setDevOverviewProjects([]);
                    setDevOverviewDrawnByRef({});
                }
            } finally {
                if (!cancelled) {
                    setDevOverviewLoading(false);
                    setDevProjectsReady(true);
                }
            }
        }

        loadDeveloperOverview();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, portalEmail, portalReference, portalProjectReferences]);

    const devReferenceAllowed = useMemo(() => {
        if (mode !== 'developer') return true;
        const refLower = String(reference || '').trim().toLowerCase();
        if (!refLower) return true;
        if (!devProjectsReady) return false;
        return devAllowedProjectRefsLower.includes(refLower);
    }, [mode, reference, devProjectsReady, devAllowedProjectRefsLower]);

    // Developer compliance guard:
    // Never show or load project-scoped data until the Projects list has validated the reference.
    useEffect(() => {
        if (mode !== 'developer') return;
        if (!reference) return;

        if (!devProjectsReady || !devReferenceAllowed) {
            setData(null);
            setInitialReport(null);
            setInterimReport(null);
            setInterimSubmissions([]);
            setInterimSubmissionsWarning('');
            setNextInterimNumber(1);
            setLatestInitialDocxEntry(null);
            setLatestInterimDocxEntry(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, reference, devProjectsReady, devReferenceAllowed]);

    function goToInterimReport(n) {
        const nextN = Number(n);
        if (!Number.isFinite(nextN) || nextN <= 0) return;
        setQuery({ page: 'interim', interim: nextN });
    }

    // Reset the view/QA flow whenever the linked report changes (e.g., after a new generation).
    useEffect(() => {
        setViewedReportOnce(false);
        setShowQaUpload(false);
        setIssueUploadError('');
        setIssueUploadSuccess('');
        setIssueSendToFunder(false);
        setAppendixGenMessage('');
        setAppendixPdfUrl('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, latestInitialDocxEntry?.id, latestInitialDocxEntry?.download_url, latestInterimDocxEntry?.id, latestInterimDocxEntry?.download_url]);

    useEffect(() => {
        if (page !== 'conditions') return;
        let cancelled = false;
        (async () => {
            const items = await refreshPlanningTracker({ silent: true });
            if (cancelled) return;

            // Auto-populate from questionnaire Decision Notice if tracker is empty.
            if (mode === 'surveyor' && reference && (!items || items.length === 0)) {
                try {
                    await fetchJson(
                        `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/planning-condition-tracker/extract-from-questionnaire-decision-notice`,
                        { method: 'POST', headers: authHeaders() }
                    );
                } catch {
                    // Best-effort; ignore if no decision notice uploaded yet.
                }
            }

            if (cancelled) return;
            await syncPlanningTrackerFromQuestionnaire({ silent: true });
            if (cancelled) return;
            await refreshPlanningTracker({ silent: true });
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode]);

    useEffect(() => {
        if (page !== 'insurance') return;
        let cancelled = false;
        (async () => {
            await refreshInsuranceTracker({ silent: true });
            if (cancelled) return;
            if (!insuranceManualOverride) {
                await syncInsuranceTrackerFromQuestionnaire({ silent: true, overwrite: true });
            }
            if (cancelled) return;
            await refreshInsuranceTracker({ silent: true });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode, insuranceManualOverride]);

    useEffect(() => {
        if (page !== 'programme') return;
        let cancelled = false;
        (async () => {
            const current = await refreshProgrammeTracker({ silent: true });
            if (cancelled) return;

            const hasPersistedManualData = isProgrammeTrackerManualSourceValue(current?.source);
            if (!programmeTrackerManualOverride && !hasPersistedManualData) {
                await syncProgrammeTrackerFromQuestionnaire({ silent: true, overwrite: true });
            }
            if (cancelled) return;
            await refreshProgrammeTracker({ silent: true });
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode, programmeTrackerManualOverride]);

    useEffect(() => {
        if (page !== 'design_team') return;
        refreshDesignTeamTracker({ silent: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode]);

    useEffect(() => {
        if (workspace !== 'overview' || mode !== 'surveyor' || !reference) return;
        void Promise.all([
            refreshPlanningTracker({ silent: true }),
            refreshProgrammeTracker({ silent: true }),
            refreshDesignTeamTracker({ silent: true })
        ]);
        // Overview hydration is read-only; questionnaire sync and extraction remain tracker-only actions.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workspace, reference, mode]);

    async function tryRiskPaths(paths, makeUrl, options) {
        let lastErr;
        for (const path of paths) {
            try {
                return await fetchJson(makeUrl(path), options);
            } catch (e) {
                if (e && e.status && e.status !== 404) throw e;
                lastErr = e;
            }
        }
        throw lastErr || new Error('No API path succeeded');
    }

    async function fetchRiskMatrixForProject(projectReference) {
        return tryRiskPaths(
            RISK_API_PATHS,
            (path) => `${API_BASE_URL}${path}/${encodeURIComponent(projectReference)}?allowMissing=1`,
            { method: 'GET', headers: authHeaders() }
        );
    }

    async function saveRiskMatrixForProject(payload) {
        return tryRiskPaths(
            RISK_API_PATHS,
            (path) => `${API_BASE_URL}${path}`,
            {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(payload)
            }
        );
    }

    async function generateRiskProfileViaAI() {
        return generateRiskProfileViaAIInternal({ confirmIfExisting: true });
    }

    async function generateRiskProfileViaAIInternal({ confirmIfExisting = true } = {}) {
        if (mode !== 'surveyor') throw new Error('Surveyor mode required');
        if (!reference) throw new Error('Missing project reference');

        if (confirmIfExisting && riskAiGeneratedAt) {
            const ok = window.confirm('Generate a new Risk Profile? This will only fill blank fields, but it may change how the profile reads.');
            if (!ok) return null;
        }

        setRiskAiGenerating(true);
        setRiskAiError('');
        try {
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/generate-risk-profile?mode=${encodeURIComponent(mode)}`;
            const json = await fetchJson(url, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    report_instance: reportInstance,
                    current_risk_matrix: riskMatrix
                })
            });

            const aiMatrix = json?.matrix && typeof json.matrix === 'object' ? json.matrix : {};
            setRiskMatrix((prev) => {
                const next = { ...(prev || {}) };
                RISK_CRITERIA_LIST.forEach((c) => {
                    const ai = aiMatrix?.[c] || {};
                    const prevVal = next[c] || { rating: '', comment: '' };
                    const prevRating = String(prevVal.rating || '').trim();
                    const prevComment = String(prevVal.comment || '').trim();
                    const aiRating = String(ai.rating || '').trim();
                    const aiComment = String(ai.comment || '').trim();

                    next[c] = {
                        rating: prevRating || aiRating,
                        comment: prevComment || aiComment
                    };
                });
                return next;
            });

            setRiskAiGeneratedAt(new Date().toISOString());
            return json;
        } catch (e) {
            setRiskAiError(e?.message || 'Failed to generate risk profile');
            return null;
        } finally {
            setRiskAiGenerating(false);
        }
    }

    useEffect(() => {
        if (page !== 'risk') return;
        if (mode !== 'surveyor') {
            setRiskMatrix(normalizeRiskMatrix(null));
            setRiskError('Available in surveyor mode only.');
            setRiskLoading(false);
            return;
        }
        if (!reference) {
            setRiskMatrix(normalizeRiskMatrix(null));
            setRiskError('');
            setRiskLoading(false);
            return;
        }

        let cancelled = false;
        setRiskLoading(true);
        setRiskError('');

        (async () => {
            try {
                const activeInterim = Number(interimNumber || 0);
                if (Number.isFinite(activeInterim) && activeInterim >= 1) {
                    const interimUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report?mode=${encodeURIComponent(mode)}&interim=${encodeURIComponent(String(Math.trunc(activeInterim)))}`;
                    const interimJson = await fetchJson(interimUrl, { method: 'GET', headers: authHeaders() });
                    if (cancelled) return;
                    setRiskMatrix(normalizeRiskMatrixFromInterimAnswers(interimJson?.answers));
                    riskFirstLoadRef.current = true;
                    return;
                }

                const json = await fetchRiskMatrixForProject(reference);
                if (cancelled) return;
                setRiskMatrix(normalizeRiskMatrix(json));
                riskFirstLoadRef.current = true;
            } catch (e) {
                if (cancelled) return;
                setRiskMatrix(normalizeRiskMatrix(null));
                setRiskError(e?.message || 'Failed to load risk matrix');
                riskFirstLoadRef.current = true;
            } finally {
                if (cancelled) return;
                setRiskLoading(false);
            }
        })();

        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode, interimNumber, data?.in_progress_interim_number, authHeaders]);

    useEffect(() => {
        if (page !== 'risk') return;
        if (mode !== 'surveyor') return;
        if (!reference) return;
        if (riskMatrixReadOnly) return;

        if (riskFirstLoadRef.current) {
            riskFirstLoadRef.current = false;
            return;
        }

        const allRagPresent = RISK_CRITERIA_LIST.every((c) => (riskMatrix[c]?.rating || '').trim() !== '');
        if (!allRagPresent) return;

        const payload = { project_reference: reference };
        RISK_CRITERIA_LIST.forEach((criteria) => {
            const prefix = RISK_FIELD_MAP[criteria];
            if (!prefix) return;
            const val = riskMatrix[criteria] || { rating: '', comment: '' };
            payload[`${prefix}_rag`] = val.rating;
            payload[`${prefix}_comment`] = val.comment;
        });

        saveRiskMatrixForProject(payload).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskMatrix, page, reference, mode, riskMatrixReadOnly]);

    useEffect(() => {
        if (page !== 'risk') return;
        if (mode !== 'surveyor') return;
        if (!reference) return;
        if (riskMatrixReadOnly) return;
        if (!initialReport) return;
        if (riskLoading || riskAiGenerating) return;

        const allRagPresent = RISK_CRITERIA_LIST.every((c) => (riskMatrix[c]?.rating || '').trim() !== '');
        if (allRagPresent) return;

        const autoReportInstance = page === 'interim' || (page === 'risk' && interimNumber)
            ? `interim:${interimNumber || 1}`
            : 'initial';
        const autoKey = `${reference}|${autoReportInstance}`;
        if (riskAutoGenerateAttemptRef.current === autoKey) return;
        riskAutoGenerateAttemptRef.current = autoKey;

        generateRiskProfileViaAIInternal({ confirmIfExisting: false }).catch(() => { });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, mode, reference, interimNumber, initialReport, riskLoading, riskAiGenerating, riskMatrix, riskMatrixReadOnly]);

    // Load the dashboard shell + Initial Report baseline tables.
    // Note: Interim views render costs/drawdown against the Initial Report baseline,
    // so we keep the Initial Report loaded even when page === 'interim'.
    useEffect(() => {
        let cancelled = false;

        async function run() {
            // Non project-scoped pages (e.g. developer overview/settings) should not error.
            if (!reference) {
                setError('');
                setLoading(false);
                setData(null);
                setInitialReport(null);
                return;
            }

            if (mode === 'developer') {
                // Always validate against the Projects list endpoint first.
                if (!devProjectsReady) return;
                if (!devReferenceAllowed) {
                    setError('Please select a project from the Projects page.');
                    setLoading(false);
                    setData(null);
                    setInitialReport(null);
                    return;
                }
            }

            setLoading(true);
            setError('');
            setData(null);

            try {
                if (mode !== 'surveyor' && !code && !authToken) throw new Error('Missing access code');

                const codeQs = (mode !== 'surveyor' && code) ? `&code=${encodeURIComponent(code)}` : '';
                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/dashboard?mode=${encodeURIComponent(mode)}${codeQs}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                if (!cancelled) setData(json);

                const irUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/initial-report?mode=${encodeURIComponent(mode)}${codeQs}`;
                const ir = await fetchJson(irUrl, { method: 'GET', headers: authHeaders() });
                if (!cancelled) setInitialReport(ir);
            } catch (e) {
                if (!cancelled) setError(e?.message || 'Failed to load dashboard');
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [mode, reference, code, authToken, authHeaders, devProjectsReady, devReferenceAllowed]);

    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (!reference || mode !== 'surveyor') {
                    if (!cancelled) setProjectSetupRecord(null);
                    return;
                }

                const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/setup`, {
                    method: 'GET',
                    headers: authHeaders()
                });
                if (!cancelled) setProjectSetupRecord(json?.item || null);
            } catch {
                if (!cancelled) setProjectSetupRecord(null);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [reference, mode, authHeaders]);

    function openConfirmLive({ reportType = 'initial', interimNumber = null } = {}) {
        const schedule = buildDrawdownScheduleFromInitial(initialReport);
        const section = (key) => {
            const group = schedule.find((item) => item.key === key);
            return (Array.isArray(group?.children) ? group.children : []).map((row, index) => ({
                item: String(row?.label || `Item ${index + 1}`).trim(),
                budget: String(toPortalMoney(row?.budget)),
                costToDate: '0'
            }));
        };
        const contingencyRows = section('contingency');
        setConfirmLiveDraft({
            programme: {
                worksStartMonth: String(programmeTrackerState?.works_start_date || data?.works_start_month || '').slice(0, 7),
                borrowerDurationMonths: String(programmeTrackerState?.borrower_duration_months || data?.project_duration || ''),
                surveyorDurationMonths: String(programmeTrackerState?.surveyor_duration_months || data?.project_duration || '')
            },
            developmentCosts: {
                constructionBudgetRows: section('construction'),
                contingencyBudgetRows: contingencyRows.map((row) => ({ ...row, budget: '' })),
                contingencyBudgetTotal: String(contingencyRows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0)),
                professionalFeeBudgetRows: section('professional'),
                additionalCostBudgetRows: section('other'),
                borrowerInputRelevant: false,
                borrowerInputAmount: '',
                previousDrawdownRows: [{ item: 'Previous Drawdowns', amount: '0' }]
            }
        });
        setConfirmLiveTarget({ reportType, interimNumber });
        setConfirmLiveError('');
        setConfirmLiveOpen(true);
    }

    function openInProgressSetup() {
        const nextInterimNumber = Number(data?.in_progress_interim_number || 0);
        const uploadedInterimNumber = Number.isFinite(nextInterimNumber) && nextInterimNumber > 1
            ? Math.trunc(nextInterimNumber) - 1
            : null;
        openConfirmLive({
            reportType: uploadedInterimNumber ? 'interim' : 'initial',
            interimNumber: uploadedInterimNumber
        });
    }

    function updateConfirmLiveProgramme(field, value) {
        setConfirmLiveDraft((previous) => ({
            ...(previous || {}),
            programme: { ...(previous?.programme || {}), [field]: value }
        }));
    }

    function updateConfirmLiveCostRow(sectionKey, index, field, value) {
        setConfirmLiveDraft((previous) => {
            const developmentCosts = { ...(previous?.developmentCosts || {}) };
            const rows = Array.isArray(developmentCosts[sectionKey]) ? [...developmentCosts[sectionKey]] : [];
            rows[index] = { ...(rows[index] || {}), [field]: value };
            developmentCosts[sectionKey] = rows;
            return { ...(previous || {}), developmentCosts };
        });
    }

    async function saveConfirmLive() {
        if (!reference || !confirmLiveDraft || confirmLiveSaving) return;
        const programme = confirmLiveDraft.programme || {};
        if (!programme.worksStartMonth || !programme.borrowerDurationMonths || !programme.surveyorDurationMonths) {
            setConfirmLiveError('Complete all programme fields before confirming the project live.');
            return;
        }
        setConfirmLiveSaving(true);
        setConfirmLiveError('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/setup`, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    programme,
                    developmentCosts: confirmLiveDraft.developmentCosts,
                    report_type: confirmLiveTarget.reportType,
                    interim_report_number: confirmLiveTarget.interimNumber,
                    advance_workflow: true
                })
            });
            setProjectSetupRecord(json?.item || null);
            setConfirmLiveOpen(false);
            const nextInterimNumber = confirmLiveTarget.reportType === 'interim'
                ? Number(confirmLiveTarget.interimNumber) + 1
                : 1;
            setData((previous) => ({
                ...(previous || {}),
                in_progress_report_type: 'interim',
                in_progress_interim_number: nextInterimNumber,
                first_action_required: 'Information Portal'
            }));
            notify({ message: `${reference} is set up. Interim Report ${nextInterimNumber} is now available.`, type: 'success' });
        } catch (err) {
            setConfirmLiveError(err?.message || 'Failed to confirm the project live');
        } finally {
            setConfirmLiveSaving(false);
        }
    }

    function openCostEditor() {
        const costs = projectSetupRecord?.development_costs;
        if (!costs || typeof costs !== 'object') {
            setCostEditorError('Confirm the project live before editing cost tables.');
            return;
        }
        setCostEditorDraft(JSON.parse(JSON.stringify(costs)));
        setCostEditorError('');
        setCostEditorOpen(true);
    }

    function updateCostEditorRow(sectionKey, index, field, value) {
        setCostEditorDraft((previous) => {
            const next = { ...(previous || {}) };
            const rows = Array.isArray(next[sectionKey]) ? [...next[sectionKey]] : [];
            rows[index] = { ...(rows[index] || {}), [field]: value };
            next[sectionKey] = rows;
            return next;
        });
    }

    async function saveCostEditor() {
        if (!reference || !costEditorDraft || costEditorSaving) return;
        setCostEditorSaving(true);
        setCostEditorError('');
        try {
            const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/setup`, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    programme: projectSetupRecord?.programme || {},
                    developmentCosts: costEditorDraft,
                    report_type: projectSetupRecord?.report_type || 'initial',
                    interim_report_number: projectSetupRecord?.interim_report_number || null,
                    advance_workflow: false
                })
            });
            setProjectSetupRecord(json?.item || projectSetupRecord);
            setCostEditorOpen(false);
        } catch (err) {
            setCostEditorError(err?.message || 'Failed to save cost table corrections');
        } finally {
            setCostEditorSaving(false);
        }
    }

    useEffect(() => {
        let cancelled = false;

        async function loadDrawdownHistory() {
            if (!reference || mode !== 'surveyor' || page !== 'drawdown') return;
            setDrawdownHistoryLoading(true);
            setDrawdownHistoryError('');
            try {
                const json = await fetchJson(`${API_BASE_URL}/project/${encodeURIComponent(reference)}/drawdown-history`, {
                    method: 'GET',
                    headers: authHeaders()
                });
                if (!cancelled) setDrawdownHistory(Array.isArray(json?.items) ? json.items : []);
            } catch (err) {
                if (!cancelled) {
                    setDrawdownHistory([]);
                    setDrawdownHistoryError(err?.message || 'Failed to load drawdown history');
                }
            } finally {
                if (!cancelled) setDrawdownHistoryLoading(false);
            }
        }

        void loadDrawdownHistory();
        return () => { cancelled = true; };
    }, [reference, mode, page, authHeaders]);

    // Load AI-extracted document metadata (+ surveyor overrides) for the Information Summary.
    // Also kick off a background preprocess run so newly-uploaded surveys get parsed quickly.
    useEffect(() => {
        let cancelled = false;

        async function loadMeta() {
            try {
                if (!reference) {
                    if (!cancelled) {
                        setIniDocMetadataById(new Map());
                        setIniDocMetadataError('');
                        setIniDocMetadataLoading(false);
                    }
                    return;
                }

                if (!initialReport) return;

                if (mode === 'developer') {
                    if (!devProjectsReady) return;
                    if (!devReferenceAllowed) {
                        if (!cancelled) {
                            setIniDocMetadataById(new Map());
                            setIniDocMetadataError('');
                            setIniDocMetadataLoading(false);
                        }
                        return;
                    }
                }

                if (mode !== 'surveyor' && !code && !authToken) {
                    if (!cancelled) {
                        setIniDocMetadataById(new Map());
                        setIniDocMetadataError('');
                        setIniDocMetadataLoading(false);
                    }
                    return;
                }

                if (!cancelled) {
                    setIniDocMetadataLoading(true);
                    setIniDocMetadataError('');
                }

                const qs = new URLSearchParams();
                qs.set('mode', mode);
                if (mode !== 'surveyor' && code) qs.set('code', code);
                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-documents/metadata?${qs.toString()}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });

                const map = new Map();
                for (const it of (json?.items || [])) {
                    const id = it?.document_id;
                    if (id === null || id === undefined) continue;
                    map.set(String(id), it);
                }

                if (!cancelled) setIniDocMetadataById(map);

                // Fire-and-forget preprocess to populate cache quickly.
                if (mode === 'surveyor') {
                    const pUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-documents/preprocess?mode=surveyor`;
                    fetchJson(pUrl, {
                        method: 'POST',
                        headers: authHeaders({ 'Content-Type': 'application/json' }),
                        body: JSON.stringify({ wait: false, max_docs: 8 })
                    }).catch(() => null);
                }
            } catch (e) {
                if (!cancelled) {
                    setIniDocMetadataById(new Map());
                    setIniDocMetadataError(e?.message || 'Failed to load document metadata');
                }
            } finally {
                if (!cancelled) setIniDocMetadataLoading(false);
            }
        }

        loadMeta();
        return () => { cancelled = true; };
    }, [reference, initialReport, mode, code, authToken, authHeaders, devProjectsReady, devReferenceAllowed]);

    async function saveIniDocumentMetadataOverride(documentId, patch) {
        if (!reference) return;
        if (mode !== 'surveyor') return;
        if (!documentId) return;
        if (!patch || typeof patch !== 'object') return;

        setActionError('');

        const qs = new URLSearchParams();
        qs.set('mode', mode);
        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-documents/metadata/${encodeURIComponent(String(documentId))}?${qs.toString()}`;
        await fetchJson(url, {
            method: 'PUT',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({ patch })
        });

        // Refresh metadata so UI stays in sync with backend merge logic.
        try {
            const refreshUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-documents/metadata?${qs.toString()}`;
            const json = await fetchJson(refreshUrl, { method: 'GET', headers: authHeaders() });
            const map = new Map();
            for (const it of (json?.items || [])) {
                const id = it?.document_id;
                if (id === null || id === undefined) continue;
                map.set(String(id), it);
            }
            setIniDocMetadataById(map);
        } catch {
            // Ignore refresh failures; next navigation/refresh will re-fetch.
        }
    }

    // Load Interim Report answers for the selected interim number.
    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (!reference) {
                    setInterimReport(null);
                    return;
                }

                if (mode === 'developer') {
                    if (!devProjectsReady) return;
                    if (!devReferenceAllowed) {
                        setInterimReport(null);
                        return;
                    }
                }

                if (mode !== 'surveyor' && !code && !authToken) {
                    setInterimReport(null);
                    return;
                }

                if (page !== 'interim' && page !== 'cost_tables') {
                    setInterimReport(null);
                    return;
                }

                const n = interimNumber || 1;
                const codeQs = (mode !== 'surveyor' && code) ? `&code=${encodeURIComponent(code)}` : '';
                const intUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report?mode=${encodeURIComponent(mode)}&interim=${encodeURIComponent(String(n))}${codeQs}`;
                setInterimReport(null);
                const ir = await fetchJson(intUrl, { method: 'GET', headers: authHeaders() });
                if (!cancelled) setInterimReport(ir);
            } catch (e) {
                if (!cancelled) {
                    setInterimReport(null);
                    setError(e?.message || 'Failed to load interim report');
                }
            }
        }

        run();
        return () => { cancelled = true; };
    }, [mode, reference, code, page, interimNumber, authToken, authHeaders, devProjectsReady, devReferenceAllowed]);

    // Load interim submission list to drive the green badge + Interim Report buttons.
    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (!reference) {
                    setInterimSubmissions([]);
                    setInterimSubmissionsWarning('');
                    setNextInterimNumber(1);
                    return;
                }

                if (mode === 'developer') {
                    if (!devProjectsReady) return;
                    if (!devReferenceAllowed) {
                        setInterimSubmissions([]);
                        setInterimSubmissionsWarning('');
                        setNextInterimNumber(1);
                        return;
                    }
                }

                if (mode !== 'surveyor' && !code && !authToken) {
                    setInterimSubmissions([]);
                    setInterimSubmissionsWarning('');
                    setNextInterimNumber(1);
                    return;
                }

                const codeQs = (mode === 'developer' && code) ? `&code=${encodeURIComponent(code)}` : '';
                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report/submissions?mode=${encodeURIComponent(mode)}${codeQs}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                if (cancelled) return;
                setInterimSubmissions(Array.isArray(json?.items) ? json.items : []);
                setInterimSubmissionsWarning(String(json?.warning || '').trim());
                setNextInterimNumber(Number(json?.next_interim_number || 1) || 1);
            } catch (e) {
                if (cancelled) return;
                setInterimSubmissions([]);
                setInterimSubmissionsWarning(e?.message || '');
                setNextInterimNumber(1);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [reference, mode, code, authToken, authHeaders, devProjectsReady, devReferenceAllowed]);

    // Load latest available Initial Report DOCX on entry so the UI has a baseline revision.
    useEffect(() => {
        (async () => {
            try {
                if (!reference) return;
                if (mode === 'developer') {
                    if (!devProjectsReady) return;
                    if (!devReferenceAllowed) return;
                }
                // Developer mode can also unlock Interim Report 1, but only if we have a code or auth.
                if (mode === 'developer' && !code && !authToken) return;
                await refreshLatestInitialReport({ silent: true });
            } finally {
                // no-op
            }
        })();
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reference, mode, code, authToken, authHeaders, devProjectsReady, devReferenceAllowed]);

    // Load latest Interim Report DOCX for the selected interim number.
    useEffect(() => {
        (async () => {
            try {
                if (!reference) return;
                if (mode !== 'surveyor') return;
                if (page !== 'interim') return;
                await refreshLatestInterimReport({ interim: interimNumber || 1, silent: true });
            } finally {
                // no-op
            }
        })();
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reference, mode, page, interimNumber]);

    useEffect(() => {
        (async () => {
            try {
                if (!reference) return;
                if (mode === 'developer' && !code && !authToken) return;
                await refreshEdits({ silent: true });
            } finally {
                // no-op
            }
        })();
        return () => { };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reference, mode, code, authToken, authHeaders]);

    const reportInstance = page === 'interim' || (page === 'risk' && interimNumber)
        ? `interim:${interimNumber || 1}`
        : 'initial';

    const editsMaxEditedAtMs = useMemo(() => {
        try {
            let max = 0;
            const iter = editsByKey && typeof editsByKey.values === 'function' ? editsByKey.values() : [];
            for (const list of iter) {
                const arr = Array.isArray(list) ? list : [];
                for (const it of arr) {
                    const t = it?.edited_at ? new Date(it.edited_at).getTime() : 0;
                    if (Number.isFinite(t) && t > max) max = t;
                }
            }
            return max;
        } catch {
            return 0;
        }
    }, [editsByKey]);

    const reportTrackingSig = useMemo(() => {
        const id = activeDocxEntry?.id != null ? Number(activeDocxEntry.id) : 0;
        const gen = activeDocxEntry?.generated_at ? new Date(activeDocxEntry.generated_at).getTime() : 0;
        const issuePdf = activeDocxEntry?.issue_pdf_uploaded_at ? new Date(activeDocxEntry.issue_pdf_uploaded_at).getTime() : 0;
        const qa = activeDocxEntry?.qa_checked ? 1 : 0;
        const issued = activeDocxEntry?.issue_checked ? 1 : 0;
        return `${id}|${Number.isFinite(gen) ? gen : 0}|${Number.isFinite(issuePdf) ? issuePdf : 0}|${qa}|${issued}`;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDocxEntry?.id, activeDocxEntry?.generated_at, activeDocxEntry?.issue_pdf_uploaded_at, activeDocxEntry?.qa_checked, activeDocxEntry?.issue_checked]);

    function readinessBadgeColor(status) {
        const s = String(status || '').toLowerCase();
        if (s === 'green') return { bg: '#16a34a', fg: '#fff' };
        if (s === 'yellow' || s === 'amber') return { bg: '#f59e0b', fg: '#111827' };
        if (s === 'red') return { bg: '#dc2626', fg: '#fff' };
        return { bg: '#e5e7eb', fg: '#111827' };
    }

    function formatReadinessTooltip(r) {
        if (!r) return '';
        const bits = [];
        if (r.status) bits.push(`Status: ${String(r.status).toUpperCase()}`);
        if (Number.isFinite(Number(r.score))) bits.push(`Score: ${Number(r.score)}`);
        if (Array.isArray(r.key_missing) && r.key_missing.length) bits.push(`Key missing: ${r.key_missing.join('; ')}`);
        if (Array.isArray(r.outstanding_documents) && r.outstanding_documents.length) bits.push(`Outstanding docs: ${r.outstanding_documents.join('; ')}`);
        if (Array.isArray(r.reasons) && r.reasons.length) {
            const rs = r.reasons
                .map((x) => (x && typeof x === 'object') ? `${x.severity || 'info'}: ${x.text || ''}` : String(x))
                .filter(Boolean);
            if (rs.length) bits.push(...rs);
        }
        return bits.join('\n');
    }

    function alertsBadgeColor(alerts) {
        const list = Array.isArray(alerts?.alerts) ? alerts.alerts : [];
        const hasCritical = list.some((a) => String(a?.severity || '').toLowerCase() === 'critical');
        const hasWarning = list.some((a) => String(a?.severity || '').toLowerCase() === 'warning');
        if (hasCritical) return { bg: '#fee2e2', fg: '#7f1d1d', border: '#fecaca' };
        if (hasWarning) return { bg: '#fffbeb', fg: '#92400e', border: '#fde68a' };
        return { bg: '#ecfdf5', fg: '#065f46', border: '#bbf7d0' };
    }

    function getAiAlertsCacheKey() {
        const ref = String(reference || '').trim();
        const ri = String(reportInstance || 'initial').trim();
        const m = String(mode || '').trim();

        // Include signals that change when report tracking changes (new revision / QA / issue PDF)
        // or when developer edits change (ini_field_edits.edited_at). This prevents stale alerts.
        const sig = `${reportTrackingSig}|${editsMaxEditedAtMs || 0}`;
        return `monsurv_ai_alerts_${ref}_${m}_${ri}_${sig}`;
    }

    async function loadAiAlerts({ force } = {}) {
        setAiAlertsError('');
        try {
            if (!reference) return;
            if (!(page === 'initial' || page === 'interim')) return;

            const cacheKey = getAiAlertsCacheKey();
            const now = Date.now();
            if (!force) {
                try {
                    const cachedRaw = localStorage.getItem(cacheKey);
                    const cached = cachedRaw ? JSON.parse(cachedRaw) : null;
                    const ts = Number(cached?.ts || 0);
                    const ttlMs = 10 * 60 * 1000;
                    if (cached && cached.data && Number.isFinite(ts) && (now - ts) < ttlMs) {
                        setAiAlerts(cached.data);
                        return;
                    }
                } catch {
                    // ignore cache
                }
            }

            setAiAlertsLoading(true);
            const qs = new URLSearchParams();
            qs.set('mode', mode);
            qs.set('report_instance', String(reportInstance || 'initial'));
            if (mode !== 'surveyor' && code) qs.set('code', code);
            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ai-alerts?${qs.toString()}`;

            const json = await fetchJson(url, {
                method: 'POST',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({ max_alerts: 6 })
            });
            setAiAlerts(json);
            try {
                localStorage.setItem(cacheKey, JSON.stringify({ ts: now, data: json }));
            } catch {
                // ignore
            }
        } catch (e) {
            setAiAlerts(null);
            setAiAlertsError(e?.message || 'Failed to load alerts');
        } finally {
            setAiAlertsLoading(false);
        }
    }

    useEffect(() => {
        setAiAlerts(null);
        setAiAlertsError('');
        setAiAlertsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reference, mode, code, authToken, reportInstance, page, reportTrackingSig, editsMaxEditedAtMs]);

    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (!reference) {
                    setReadiness(null);
                    setReadinessError('');
                    setReadinessLoading(false);
                    return;
                }
                if (mode !== 'surveyor' && !code && !authToken) {
                    setReadiness(null);
                    setReadinessError('');
                    setReadinessLoading(false);
                    return;
                }

                setReadinessLoading(true);
                setReadinessError('');

                const codeQs = (mode !== 'surveyor' && code) ? `&code=${encodeURIComponent(code)}` : '';
                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/readiness?mode=${encodeURIComponent(mode)}&report_instance=${encodeURIComponent(reportInstance)}${codeQs}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                if (cancelled) return;
                setReadiness(json);
            } catch (e) {
                if (cancelled) return;
                setReadiness(null);
                const baseMsg = e?.message || 'Failed to load readiness status';
                const status = e?.status ? ` (HTTP ${e.status})` : '';
                const raw = `${baseMsg}${status}`;
                const isInterimNoData = reportInstance && reportInstance !== 'initial' && /no submissions found/i.test(raw);
                if (isInterimNoData) {
                    const n = String(reportInstance).replace(/^interim:/, '');
                    setReadinessError(`Interim ${n} Information Not Received`);
                } else {
                    setReadinessError(raw);
                }
            } finally {
                if (cancelled) return;
                setReadinessLoading(false);
            }
        }

        run();
        return () => { cancelled = true; };
    }, [reference, mode, code, authToken, reportInstance, authHeaders, readinessReloadKey]);

    // Prefill Actual Drawdown (DDn) from Interim Report n: Construction claim + Contingency claim.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let cancelled = false;

        async function run() {
            try {
                if (page !== 'drawdown') {
                    setDrawdownPrefillActuals([]);
                    setDrawdownPrefillError('');
                    setDrawdownPrefillLoading(false);
                    return;
                }
                if (!reference) {
                    setDrawdownPrefillActuals([]);
                    setDrawdownPrefillError('');
                    setDrawdownPrefillLoading(false);
                    return;
                }
                if (mode !== 'surveyor') {
                    setDrawdownPrefillActuals([]);
                    setDrawdownPrefillError('');
                    setDrawdownPrefillLoading(false);
                    return;
                }
                if (mode === 'developer' && !code && !authToken) {
                    setDrawdownPrefillActuals([]);
                    setDrawdownPrefillError('');
                    setDrawdownPrefillLoading(false);
                    return;
                }

                setDrawdownPrefillLoading(true);
                setDrawdownPrefillError('');

                const nums = (Array.isArray(interimSubmissions) ? interimSubmissions : [])
                    .map((it) => Number(it?.interim_number || 0))
                    .filter((n) => Number.isFinite(n) && n > 0)
                    .sort((a, b) => a - b);

                const codeQs = (mode === 'developer' && code) ? `&code=${encodeURIComponent(code)}` : '';

                const auto = [];
                const setupBaseline = mapProjectSetupToPortalCostBaseline(projectSetupRecord);
                const setupHistory = Array.isArray(setupBaseline?.drawdownHistory) ? setupBaseline.drawdownHistory : [];
                setupHistory.forEach((row, idx) => {
                    const label = String(row?.label || '').trim();
                    const labelMatch = label.match(/(\d+)/);
                    const drawdownNo = Number(labelMatch?.[1] || (idx + 1));
                    const amount = parseMoneyLoose(row?.amount);
                    if (!Number.isFinite(drawdownNo) || drawdownNo <= 0) return;
                    if (!Number.isFinite(amount) || amount <= 0) return;
                    auto[drawdownNo - 1] = amount;
                });

                if (!nums.length) {
                    if (!cancelled) setDrawdownPrefillActuals(auto);
                    return;
                }

                await Promise.all(nums.map(async (n) => {
                    try {
                        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report?mode=${encodeURIComponent(mode)}&interim=${encodeURIComponent(String(n))}${codeQs}`;
                        const ir = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                        const total = computeInterimDrawdownClaim(ir?.answers);
                        if (total === null || total === undefined) return;
                        if (!Number.isFinite(Number(total))) return;
                        auto[n - 1] = total;
                    } catch {
                        // ignore individual missing/failed interim
                    }
                }));

                if (!cancelled) setDrawdownPrefillActuals(auto);
            } catch (e) {
                if (!cancelled) {
                    setDrawdownPrefillActuals([]);
                    setDrawdownPrefillError(e?.message || 'Failed to link interim drawdown values');
                }
            } finally {
                if (!cancelled) setDrawdownPrefillLoading(false);
            }
        }

        run();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, reference, mode, code, authToken, interimSubmissions, projectSetupRecord]);

    useEffect(() => {
        let cancelled = false;

        async function loadNotes() {
            try {
                if (!reference) return;
                // Developer view must not show surveyor comments.
                if (mode !== 'surveyor') {
                    if (!cancelled) setNotesByKey(new Map());
                    return;
                }
                if (page !== 'initial' && page !== 'interim') return;
                if (mode === 'developer' && !code && !authToken) return;

                const qs = new URLSearchParams();
                qs.set('mode', mode);
                if (mode === 'developer' && code) qs.set('code', code);

                // Costs displayed in Interim views are sourced from the Initial Report baseline.
                // (Interim drawdown values come from the Interim Questionnaire submissions.)
                const costsInstance = page === 'interim' ? 'initial' : reportInstance;
                qs.set('report_instance', costsInstance);

                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/notes?${qs.toString()}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                const map = new Map();
                for (const n of (json?.notes || [])) {
                    const k = `${n.table_name}:${n.row_id}`;
                    map.set(k, n.note_text || '');
                }
                if (!cancelled) setNotesByKey(map);
            } catch {
                if (!cancelled) setNotesByKey(new Map());
            }
        }

        loadNotes();
        return () => { cancelled = true; };
    }, [reference, mode, code, reportInstance, page, interimNumber, authToken, authHeaders]);

    useEffect(() => {
        let cancelled = false;

        async function loadFieldReviews() {
            try {
                if (!reference) return;
                // Developer view must not show surveyor reviews.
                if (mode !== 'surveyor') {
                    if (!cancelled) setFieldReviewsByKey(new Map());
                    return;
                }
                if (page !== 'initial' && page !== 'interim') return;

                const qs = new URLSearchParams();
                qs.set('mode', mode);

                // If these reviews are ever shown alongside Interim pages, baseline to initial.
                const reviewsInstance = page === 'interim' ? 'initial' : reportInstance;
                qs.set('report_instance', reviewsInstance);

                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/field-reviews?${qs.toString()}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                const map = new Map();
                for (const it of (json?.items || [])) {
                    const k = `${it.table_name}:${it.row_id}:${it.field_name}`;
                    map.set(k, {
                        opinion: String(it.opinion ?? ''),
                        comments: String(it.comments ?? ''),
                        rag: normalizeRiskRating(it.rag)
                    });
                }
                if (!cancelled) setFieldReviewsByKey(map);
            } catch {
                if (!cancelled) setFieldReviewsByKey(new Map());
            }
        }

        loadFieldReviews();
        return () => { cancelled = true; };
    }, [reference, mode, reportInstance, page, interimNumber, authToken, authHeaders]);

    useEffect(() => {
        let cancelled = false;

        async function loadCostAssessments() {
            try {
                if (!reference) return;
                // Developer view must not show surveyor cost assessments.
                if (mode !== 'surveyor') {
                    if (!cancelled) setCostAssessmentsByKey(new Map());
                    return;
                }
                if (page !== 'initial' && page !== 'interim' && page !== 'drawdown' && page !== 'cost_tables') return;
                if (mode === 'developer' && !code && !authToken) return;

                const qs = new URLSearchParams();
                qs.set('mode', mode);
                if (mode === 'developer' && code) qs.set('code', code);

                // Costs displayed in Interim views are sourced from the Initial Report baseline.
                // The Drawdown Tracker also uses the Initial Report baseline (construction + contingency).
                const costsInstance = (page === 'interim' || page === 'drawdown' || page === 'cost_tables') ? 'initial' : reportInstance;
                qs.set('report_instance', costsInstance);

                const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/cost-assessments?${qs.toString()}`;
                const json = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                const map = new Map();
                for (const it of (json?.items || [])) {
                    map.set(String(it.item_key), String(it.surveyor_value ?? ''));
                }
                if (!cancelled) setCostAssessmentsByKey(map);
            } catch {
                if (!cancelled) setCostAssessmentsByKey(new Map());
            }
        }

        loadCostAssessments();
        return () => { cancelled = true; };
    }, [reference, mode, code, reportInstance, page, interimNumber, authToken, authHeaders]);

    async function saveIniField({ tableName, rowId, patch }) {
        if (!reference) return;
        if (mode !== 'developer') return;
        if (!code && !authToken) return;

        setActionError('');

        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (code) qs.set('code', code);
        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/ini-table/${encodeURIComponent(String(tableName || ''))}?${qs.toString()}`;
        const body = {
            row_id: rowId === undefined ? null : rowId,
            patch: patch || {}
        };

        try {
            await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body)
            });

            // Refresh edits so surveyor view can see version history.
            await refreshEdits({ silent: true });

            // Refresh the initial report payload so the UI reflects the saved value.
            try {
                if (page === 'initial') {
                    const codeQs = (mode === 'developer' && code) ? `&code=${encodeURIComponent(code)}` : '';
                    const irUrl = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/initial-report?mode=${encodeURIComponent(mode)}${codeQs}`;
                    const ir = await fetchJson(irUrl, { method: 'GET', headers: authHeaders() });
                    setInitialReport(ir);
                }
            } catch {
                // If refresh fails, keep UI stable; next navigation/refresh will re-fetch.
            }
        } catch (e) {
            setActionError(e?.message || 'Failed to save field');
            console.error('saveIniField failed:', e);
        }
    }

    async function saveCostAssessment({ itemKey, surveyorValue }) {
        if (!reference) return;

        setActionError('');

        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (mode === 'developer') qs.set('code', code);

        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/cost-assessments?${qs.toString()}`;
        const body = {
            report_instance: reportInstance,
            item_key: String(itemKey),
            surveyor_value: String(surveyorValue ?? '')
        };

        const key = String(itemKey);
        const nextValue = String(surveyorValue ?? '');
        const prevValue = costAssessmentsByKey.get(key);

        // optimistic update
        setCostAssessmentsByKey((prev) => {
            const next = new Map(prev);
            next.set(key, nextValue);
            return next;
        });

        try {
            await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body)
            });
        } catch (e) {
            // rollback optimistic update
            setCostAssessmentsByKey((prev) => {
                const next = new Map(prev);
                if (prevValue === undefined) next.delete(key);
                else next.set(key, prevValue);
                return next;
            });

            setActionError(e?.message || 'Failed to save cost value (backend not reachable)');
            console.error('saveCostAssessment failed:', e);
        }
    }

    async function saveFieldReview({ tableName, rowId, fieldName, patch }) {
        if (!reference) return;
        if (mode !== 'surveyor') return;

        setActionError('');

        const qs = new URLSearchParams();
        qs.set('mode', mode);

        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/field-reviews?${qs.toString()}`;
        const key = `${String(tableName)}:${String(rowId)}:${String(fieldName)}`;
        const prevValue = fieldReviewsByKey.get(key);
        const nextValue = {
            opinion: String(patch?.opinion ?? prevValue?.opinion ?? ''),
            comments: String(patch?.comments ?? prevValue?.comments ?? ''),
            rag: normalizeRiskRating(patch?.rag ?? prevValue?.rag ?? '')
        };

        // optimistic update
        setFieldReviewsByKey((prev) => {
            const next = new Map(prev);
            next.set(key, nextValue);
            return next;
        });

        const body = {
            report_instance: reportInstance,
            table_name: String(tableName || ''),
            row_id: String(rowId || ''),
            field_name: String(fieldName || ''),
            opinion: String(nextValue.opinion || ''),
            comments: String(nextValue.comments || ''),
            rag: String(nextValue.rag || '')
        };

        try {
            await fetchJson(url, {
                method: 'PUT',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify(body)
            });
        } catch (e) {
            // rollback optimistic update
            setFieldReviewsByKey((prev) => {
                const next = new Map(prev);
                if (prevValue === undefined) next.delete(key);
                else next.set(key, prevValue);
                return next;
            });

            setActionError(e?.message || 'Failed to save review value (backend not reachable)');
            console.error('saveFieldReview failed:', e);
        }
    }

    async function saveRowNote({ tableName, rowId, noteText }) {
        if (!reference) return;
        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (mode === 'developer') qs.set('code', code);

        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/notes?${qs.toString()}`;
        const body = {
            report_instance: reportInstance,
            table_name: tableName,
            row_id: String(rowId),
            note_text: String(noteText || '')
        };

        // optimistic update
        setNotesByKey((prev) => {
            const next = new Map(prev);
            next.set(`${tableName}:${String(rowId)}`, String(noteText || ''));
            return next;
        });

        await fetchJson(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body)
        });
    }

    function exitPortal() {
        // Best-effort: record exit time.
        try {
            const token = getAuthToken();
            if (String(token).trim()) {
                fetch(`${API_BASE_URL}/auth/logout`, {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${token}` }
                }).catch(() => null);
            }
        } catch {
            // ignore
        }

        // Developer log off should always clear auth and go back to the login/dashboard page.
        if (mode === 'developer') {
            try { localStorage.removeItem(PORTAL_TOKEN_KEY); } catch { }
            // Back-compat cleanup (older deployments).
            try { localStorage.removeItem(DASHBOARD_TOKEN_KEY); } catch { }
            window.location.assign('/');
            return;
        }

        // Always return to the Projects list ("Select a Project"), not back through
        // in-portal navigation history.
        const returnTo = (() => {
            try {
                const url = new URL(window.location.href);
                url.search = '';
                url.hash = '';
                url.searchParams.set('view', 'reports');
                return `${url.pathname}${url.search}`;
            } catch {
                return '/?view=reports';
            }
        })();

        // If this portal was opened from another tab/window (e.g. via window.open from Reports),
        // best UX is to focus the opener and close this tab.
        try {
            if (window.opener && !window.opener.closed) {
                try {
                    window.opener.location.assign(returnTo);
                } catch {
                    // ignore
                }
                window.opener.focus();
                window.close();
                // Some browsers block closing tabs; if so, navigate instead.
                window.setTimeout(() => {
                    try { window.location.href = returnTo; } catch { window.location.href = '/'; }
                }, 150);
                return;
            }
        } catch { }

        window.location.assign(returnTo);
    }

    function requestReportDataCheck(target, initialState = null, previousIntent = null) {
        if (reportDataRequestRef.current || (generationExecutingRef.current && !previousIntent)) return;
        if (!reference || mode !== 'surveyor' || !getAuthToken()) {
            setGenerateError('Report generation requires a project and an active surveyor session.');
            return;
        }
        const intent = previousIntent || {
            target, reference, reportInstance: target === 'interim' ? `interim:${currentInterimNumber}` : 'initial',
            context: generationContext, appendices: appendixSelections.map((item) => ({ ...item }))
        };
        if (intent.context !== generationContextRef.current) return;
        const request = { ...intent, initialState };
        reportDataRequestRef.current = request;
        setReportDataRequest(request);
        setGenerateError('');
    }

    function cancelReportDataCheck() {
        reportDataRequestRef.current = null;
        setReportDataRequest(null);
        setQuestionnaireReviewRequest(null);
    }

    async function continueReportDataGeneration(request, questionnaireReviewed = false) {
        if (reportDataRequestRef.current !== request || request.context !== generationContextRef.current || generationExecutingRef.current) return;
        if (!questionnaireReviewed) { setQuestionnaireReviewRequest(request); return; }
        cancelReportDataCheck();
        generationExecutingRef.current = true;
        try {
            if (request.target === 'interim') await executeInterimReport(request);
            else await executeInitialReport(request);
        } finally {
            generationExecutingRef.current = false;
        }
    }

    function generateInitialReport() {
        requestReportDataCheck('initial');
    }

    function generateInterimReport() {
        requestReportDataCheck('interim');
    }

    async function executeInitialReport(intent) {
        let stopStatusPoll = false;
        let generationFailure = null;
        setGenerateError('');
        setGenerationStage('');
        setGenerationStageKey('');
        setGenerationDetail('');

        try {
            if (!reference) throw new Error('Missing project reference');
            if (mode !== 'surveyor') throw new Error('Report generation is only available in surveyor mode');

            // Surveyor endpoints require a valid dashboard token.
            if (!getAuthToken()) {
                throw new Error('Your session has expired (or you are not signed in). Please return to the dashboard and sign in again, then retry report generation.');
            }

            setGenerating(true);
            setGenerationStage('Gathering Data');
            setGenerationStageKey('gathering_data');

            const mySeq = ++generateSeqRef.current;

            // Ensure we have a baseline revision before starting generation; otherwise we can
            // mistakenly treat an existing report as the first-ever generated output.
            const baselineEntry = latestInitialDocxEntry?.download_url
                ? latestInitialDocxEntry
                : await refreshLatestInitialReport({ silent: true });
            if (intent.context !== generationContextRef.current) return;

            const startedAtMs = Date.now();
            const baselineRevision = baselineEntry?.revision != null ? Number(baselineEntry.revision) : null;
            const seenLiveStageRef = { current: false };
            let statusPollFailures = 0;

            const pollStatus = async () => {
                try {
                    while (!stopStatusPoll && generateSeqRef.current === mySeq) {
                        if (!reference || !getAuthToken()) break;
                        try {
                            const qs = new URLSearchParams();
                            qs.set('mode', mode);
                            qs.set('report_instance', 'initial');
                            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-generation-status?${qs.toString()}`;
                            const json = await fetchJson(url, { method: 'GET', headers: authHeaders(), cache: 'no-store' });
                            if (stopStatusPoll || generateSeqRef.current !== mySeq) break;
                            statusPollFailures = 0;
                            const shouldStopPolling = applyPolledReportGenerationStatus(json?.status, startedAtMs, seenLiveStageRef);
                            if (shouldStopPolling && json?.status?.stage === 'error') {
                                generationFailure = new Error(json.status.message || 'Report generation failed');
                            }
                            if (shouldStopPolling) break;
                        } catch (e) {
                            if (!isTransientGatewayError(e)) throw e;
                            statusPollFailures += 1;
                            if (statusPollFailures >= 3) break;
                        }

                        // eslint-disable-next-line no-await-in-loop
                        await waitMs(getPollingDelayMs({ consecutiveFailures: statusPollFailures, successDelayMs: 2200, maxDelayMs: 12000 }));
                    }
                } catch {
                    // ignore status polling errors
                }
            };
            void pollStatus();

            const qs = new URLSearchParams();
            qs.set('mode', mode);

            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/generate-initial-report?${qs.toString()}`;

            const aiConsistency = loadAiReportConsistencySettings();
            const body = (aiConsistency && typeof aiConsistency === 'object')
                ? { ai_consistency: aiConsistency }
                : {};

            if (intent.appendices.length) {
                body.appendices = intent.appendices
                    .map((a) => ({
                        label: String(a?.label || '').trim(),
                        document: String(a?.document || '').trim(),
                        ...(a?.available === false ? { available: false } : {})
                    }))
                    .filter((a) => a.label && a.document);
            }

            // This endpoint can take a long time (AI/docx generation) and may keep the connection open.
            // To avoid the UI getting stuck forever, we start generation and then poll report-log for the output.
            let json = null;
            try {
                const controller = new AbortController();
                // Backend can take a couple of minutes (AI + DOCX); keep this long enough
                // that we usually get a definitive success/failure response, while still
                // having a fallback polling mode.
                const timeoutId = window.setTimeout(() => controller.abort(), 210000);
                try {
                    json = await fetchJson(url, {
                        method: 'POST',
                        headers: Object.keys(body).length ? authHeaders({ 'Content-Type': 'application/json' }) : authHeaders(),
                        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
                        signal: controller.signal
                    });
                } finally {
                    window.clearTimeout(timeoutId);
                }
            } catch (e) {
                if (isReportDataReconciliationError(e)) throw e;
                // If the request hangs or a proxy times out (502/503/504), fall back to polling report-log.
                const status = Number(e?.status || 0);
                const isAlreadyRunning = status === 409 || e?.body?.already_in_progress === true;

                // 409 = the backend single-flight guard reports a generation is already in flight for
                // this project. That is not an error: we attach to the existing run and poll for output
                // instead of starting (and paying for) a second AI pass.
                if (!(isTransientGatewayError(e) || isAlreadyRunning)) throw e;
            }

            if (mySeq !== generateSeqRef.current) return;

            if (json) {
                if (json?.queued) {
                    setGenerationStageKey('preprocessing_documents');
                    setGenerationStage('Preparing Uploaded Documents');
                }
                const entry = json?.report_log_entry;
                if (entry?.download_url) {
                    setLatestInitialDocxEntry(entry);
                    setViewedReportOnce(false);
                    setShowQaUpload(false);

                    setGenerationStage('Done!');
                    setGenerationStageKey('done');
                    window.setTimeout(() => {
                        if (generateSeqRef.current === mySeq) {
                            setGenerationStage('');
                            setGenerationStageKey('');
                        }
                    }, 5000);

                    const rev = entry?.revision != null ? Number(entry.revision) : null;
                    notify({
                        message: `${reference} Initial Report ${formatReportVersion(rev, reportVersionNaming) || 'generated'} has been generated`,
                        projectReference: reference,
                        type: 'success'
                    });
                    return;
                }
            }

            let reportLogPollFailures = 0;
            const deadlineMs = Date.now() + REPORT_DOCX_WAIT_TIMEOUT_MS;
            while (Date.now() < deadlineMs) {
                if (mySeq !== generateSeqRef.current) return;
                let latest = null;
                try {
                    latest = await refreshLatestInitialReport({ silent: true });
                    reportLogPollFailures = 0;
                } catch (e) {
                    if (!isTransientGatewayError(e)) throw e;
                    reportLogPollFailures += 1;
                    if (reportLogPollFailures >= MAX_TRANSIENT_POLL_FAILURES) {
                        throw new Error('The API became temporarily unavailable while waiting for the DOCX. Please wait 1-2 minutes and retry. If this keeps happening, support should check API target health/restarts.');
                    }
                }
                if (generationFailure) throw generationFailure;
                if (latest?.download_url) {
                    const latestRevision = latest?.revision != null ? Number(latest.revision) : null;
                    const latestGeneratedAt = new Date(latest?.generated_at || 0).getTime();
                    const isNewerByRevision = baselineRevision != null && latestRevision != null && latestRevision > baselineRevision;
                    const isNewerByTime = Number.isFinite(latestGeneratedAt) && latestGeneratedAt >= (startedAtMs - 1000);

                    // If there was no baseline (first ever), accept the latest doc.
                    if (baselineRevision == null || isNewerByRevision || isNewerByTime) {
                        setLatestInitialDocxEntry(latest);
                        setViewedReportOnce(false);
                        setShowQaUpload(false);

                        setGenerationStage('Done!');
                        setGenerationStageKey('done');
                        window.setTimeout(() => {
                            if (generateSeqRef.current === mySeq) {
                                setGenerationStage('');
                                setGenerationStageKey('');
                            }
                        }, 5000);

                        const rev = latest?.revision != null ? Number(latest.revision) : null;
                        notify({
                            message: `${reference} Initial Report ${formatReportVersion(rev, reportVersionNaming) || 'generated'} has been generated`,
                            projectReference: reference,
                            type: 'success'
                        });
                        return;
                    }
                }
                await waitMs(getPollingDelayMs({ consecutiveFailures: reportLogPollFailures, successDelayMs: 3000, maxDelayMs: 12000 }));
            }

            throw new Error('Report generation started but timed out waiting for the DOCX. Please try again or check the Reports page.');
        } catch (e) {
            if (intent.context !== generationContextRef.current) return;
            if (isReportDataReconciliationError(e)) requestReportDataCheck('initial', e.body?.reconciliation, intent);
            else setGenerateError(e?.message || 'Failed to generate report');
            setGenerationStage('');
            setGenerationStageKey('');
        } finally {
            stopStatusPoll = true;
            setGenerating(false);
        }
    }

    function normalizeAppendixSelections(count, prev) {
        const safeCount = Math.max(0, Math.min(5, Number(count) || 0));
        const out = [];
        for (let i = 0; i < safeCount; i += 1) {
            const letter = String.fromCharCode(65 + i);
            const existing = Array.isArray(prev) ? prev[i] : null;
            out.push({
                label: `Appendix ${letter}`,
                document: existing?.document || '',
                ...(existing?.available === false ? { available: false } : {})
            });
        }
        return out;
    }

    function openAppendixModal(target) {
        const isInterim = target === 'interim';
        setAppendixTarget(isInterim ? 'interim' : 'initial');

        if (!isInterim) {
            const nextCount = Math.max(1, Math.min(5, Number(appendixCount) || 1));
            setAppendixCount(nextCount);
            setAppendixSelections((prev) => {
                const normalized = normalizeAppendixSelections(nextCount, prev);
                return normalized.map((it) => {
                    const doc = initialAppendixDocs.includes(it.document) ? it.document : '';
                    const key = String(doc || '').trim().toLowerCase();
                    const avail = doc ? initialAppendixAvailabilityByTitle.get(key) : undefined;
                    return {
                        ...it,
                        document: doc,
                        ...(avail === false ? { available: false } : (doc ? { available: true } : {}))
                    };
                });
            });
            setAppendixModalOpen(true);
            return;
        }

        const nextCount = Math.max(1, Math.min(5, Number(appendixCount) || 1));
        setAppendixCount(nextCount);
        setAppendixSelections((prev) => {
            const normalized = normalizeAppendixSelections(nextCount, prev);
            return normalized.map((it) => {
                const doc = interimAppendixDocs.includes(it.document) ? it.document : '';
                const isSitePhotos = String(doc || '').trim().toLowerCase() === 'site photos';
                const avail = isSitePhotos ? interimSitePhotosAvailable : undefined;
                return {
                    ...it,
                    document: doc,
                    ...(avail === false ? { available: false } : (doc ? { available: true } : {}))
                };
            });
        });
        setAppendixModalOpen(true);
    }

    function handleAppendixCountChange(next) {
        const min = appendixTarget === 'initial' ? 0 : 1;
        const count = Math.max(min, Math.min(5, Number(next) || min));
        setAppendixCount(count);
        setAppendixSelections((prev) => normalizeAppendixSelections(count, prev));
    }

    function handleAppendixDocChange(idx, value) {
        setAppendixSelections((prev) => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const item = next[idx] || { label: `Appendix ${String.fromCharCode(65 + idx)}`, document: '' };
            const doc = String(value || '');
            if (appendixTarget === 'initial') {
                const key = String(doc || '').trim().toLowerCase();
                const avail = doc ? initialAppendixAvailabilityByTitle.get(key) : undefined;
                next[idx] = {
                    ...item,
                    document: doc,
                    ...(avail === false ? { available: false } : (doc ? { available: true } : {}))
                };
            } else {
                const isSitePhotos = String(doc || '').trim().toLowerCase() === 'site photos';
                const avail = isSitePhotos ? interimSitePhotosAvailable : undefined;
                next[idx] = {
                    ...item,
                    document: doc,
                    ...(avail === false ? { available: false } : (doc ? { available: true } : {}))
                };
            }
            return next;
        });
    }

    async function confirmAppendicesAndGenerate() {
        setAppendixModalOpen(false);
        if (appendixTarget === 'interim') {
            await generateInterimReport();
        } else {
            await generateInitialReport();
        }
    }

    async function executeInterimReport(intent) {
        let stopStatusPoll = false;
        let generationFailure = null;
        setGenerateError('');
        setGenerationStage('');
        setGenerationStageKey('');
        setGenerationDetail('');

        try {
            if (!reference) throw new Error('Missing project reference');
            if (mode !== 'surveyor') throw new Error('Report generation is only available in surveyor mode');

            // Surveyor endpoints require a valid dashboard token.
            // (Tokens expire after ~12 hours on the backend; force re-login if missing/expired.)
            if (!getAuthToken()) {
                throw new Error('Your session has expired (or you are not signed in). Please return to the dashboard and sign in again, then retry report generation.');
            }

            const n = getInterimNumberFromReportInstance(intent.reportInstance);
            if (!Number.isFinite(Number(n)) || Number(n) <= 0) throw new Error('Missing interim number');

            setGenerating(true);
            setGenerationStage('Gathering Data');
            setGenerationStageKey('gathering_data');

            const baselineEntry = latestInterimDocxEntry?.download_url
                ? latestInterimDocxEntry
                : await refreshLatestInterimReport({ interim: n, silent: true });
            if (intent.context !== generationContextRef.current) return;

            const startedAtMs = Date.now();
            const baselineRevision = baselineEntry?.revision != null ? Number(baselineEntry.revision) : null;
            const mySeq = ++generateSeqRef.current;
            const seenLiveStageRef = { current: false };
            let statusPollFailures = 0;

            const pollStatus = async () => {
                try {
                    while (!stopStatusPoll && generateSeqRef.current === mySeq) {
                        if (!reference || !getAuthToken()) break;
                        try {
                            const qs = new URLSearchParams();
                            qs.set('mode', mode);
                            qs.set('report_instance', `interim:${n}`);
                            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/report-generation-status?${qs.toString()}`;
                            const json = await fetchJson(url, { method: 'GET', headers: authHeaders(), cache: 'no-store' });
                            if (stopStatusPoll || generateSeqRef.current !== mySeq) break;
                            statusPollFailures = 0;
                            const shouldStopPolling = applyPolledReportGenerationStatus(json?.status, startedAtMs, seenLiveStageRef);
                            if (shouldStopPolling && json?.status?.stage === 'error') {
                                generationFailure = new Error(json.status.message || 'Report generation failed');
                            }
                            if (shouldStopPolling) break;
                        } catch (e) {
                            if (!isTransientGatewayError(e)) throw e;
                            statusPollFailures += 1;
                            if (statusPollFailures >= 3) break;
                        }

                        // eslint-disable-next-line no-await-in-loop
                        await waitMs(getPollingDelayMs({ consecutiveFailures: statusPollFailures, successDelayMs: 2200, maxDelayMs: 12000 }));
                    }
                } catch {
                    // ignore status polling errors
                }
            };
            void pollStatus();

            const qs = new URLSearchParams();
            qs.set('mode', mode);
            qs.set('interim', String(n));
            // Explicitly request the Interim AI generation path (backend will fall back safely if not configured).
            qs.set('ai', '1');

            const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/generate-interim-report?${qs.toString()}`;

            const aiConsistency = loadAiReportConsistencySettings();
            const body = (aiConsistency && typeof aiConsistency === 'object')
                ? { ai_consistency: aiConsistency }
                : {};

            if (intent.appendices.length) {
                body.appendices = intent.appendices
                    .map((a) => ({
                        label: String(a?.label || '').trim(),
                        document: String(a?.document || '').trim(),
                        ...(a?.available === false ? { available: false } : {})
                    }))
                    .filter((a) => a.label && a.document);
            }

            let json = null;
            try {
                const controller = new AbortController();
                const timeoutId = window.setTimeout(() => controller.abort(), 210000);
                try {
                    json = await fetchJson(url, {
                        method: 'POST',
                        headers: Object.keys(body).length ? authHeaders({ 'Content-Type': 'application/json' }) : authHeaders(),
                        body: Object.keys(body).length ? JSON.stringify(body) : undefined,
                        signal: controller.signal
                    });
                } finally {
                    window.clearTimeout(timeoutId);
                }
            } catch (e) {
                if (isReportDataReconciliationError(e)) throw e;
                const status = Number(e?.status || 0);
                const isAlreadyRunning = status === 409 || e?.body?.already_in_progress === true;

                // 409 = backend single-flight guard: a generation is already running for this project.
                // Attach to the existing run and poll instead of triggering a second AI pass.
                if (!(isTransientGatewayError(e) || isAlreadyRunning)) throw e;
            }

            if (mySeq !== generateSeqRef.current) return;

            if (json) {
                const entry = json?.report_log_entry;
                if (entry?.download_url) {
                    setLatestInterimDocxEntry(entry);
                    setViewedReportOnce(false);
                    setShowQaUpload(false);

                    try {
                        const skipped = String(json?.ai?.skipped_reason || '').trim();
                        const aiUsed = Boolean(json?.ai?.used);
                        if (!aiUsed && skipped) {
                            notify({
                                message: `${reference} Interim Report ${n}: AI unavailable/failed (${skipped}); generated using deterministic fallback`,
                                projectReference: reference,
                                type: 'warning'
                            });
                        }
                    } catch {
                        // ignore
                    }

                    setGenerationStage('Done!');
                    setGenerationStageKey('done');
                    window.setTimeout(() => {
                        if (generateSeqRef.current === mySeq) {
                            setGenerationStage('');
                            setGenerationStageKey('');
                        }
                    }, 5000);

                    const rev = entry?.revision != null ? Number(entry.revision) : null;
                    notify({
                        message: `${reference} Interim Report ${n} ${formatReportVersion(rev, reportVersionNaming) || 'generated'} has been generated`,
                        projectReference: reference,
                        type: 'success'
                    });
                    return;
                }
            }

            let reportLogPollFailures = 0;
            const deadlineMs = Date.now() + REPORT_DOCX_WAIT_TIMEOUT_MS;
            while (Date.now() < deadlineMs) {
                if (mySeq !== generateSeqRef.current) return;
                if (generationFailure) throw generationFailure;
                let latest = null;
                try {
                    latest = await refreshLatestInterimReport({ interim: n, silent: true });
                    reportLogPollFailures = 0;
                } catch (e) {
                    if (!isTransientGatewayError(e)) throw e;
                    reportLogPollFailures += 1;
                    if (reportLogPollFailures >= MAX_TRANSIENT_POLL_FAILURES) {
                        throw new Error('The API became temporarily unavailable while waiting for the DOCX. Please wait 1-2 minutes and retry. If this keeps happening, support should check API target health/restarts.');
                    }
                }
                if (generationFailure) throw generationFailure;
                if (latest?.download_url) {
                    const latestRevision = latest?.revision != null ? Number(latest.revision) : null;
                    const latestGeneratedAt = new Date(latest?.generated_at || 0).getTime();
                    const isNewerByRevision = baselineRevision != null && latestRevision != null && latestRevision > baselineRevision;
                    const isNewerByTime = Number.isFinite(latestGeneratedAt) && latestGeneratedAt >= (startedAtMs - 1000);

                    if (baselineRevision == null || isNewerByRevision || isNewerByTime) {
                        setLatestInterimDocxEntry(latest);
                        setViewedReportOnce(false);
                        setShowQaUpload(false);

                        setGenerationStage('Done!');
                        setGenerationStageKey('done');
                        window.setTimeout(() => {
                            if (generateSeqRef.current === mySeq) {
                                setGenerationStage('');
                                setGenerationStageKey('');
                            }
                        }, 5000);

                        const rev = latest?.revision != null ? Number(latest.revision) : null;
                        notify({
                            message: `${reference} Interim Report ${n} ${formatReportVersion(rev, reportVersionNaming) || 'generated'} has been generated`,
                            projectReference: reference,
                            type: 'success'
                        });
                        return;
                    }
                }
                await waitMs(getPollingDelayMs({ consecutiveFailures: reportLogPollFailures, successDelayMs: 3000, maxDelayMs: 12000 }));
            }

            throw new Error('Report generation started but timed out waiting for the DOCX. Please try again or check the Reports page.');
        } catch (e) {
            if (intent.context !== generationContextRef.current) return;
            if (isReportDataReconciliationError(e)) requestReportDataCheck('interim', e.body?.reconciliation, intent);
            else setGenerateError(e?.message || 'Failed to generate report');
            setGenerationStage('');
            setGenerationStageKey('');
        } finally {
            stopStatusPoll = true;
            setGenerating(false);
        }
    }

    const isInitialView = workspace === 'reporting_periods' && page === 'initial';
    const isInterimView = workspace === 'reporting_periods' && page === 'interim';
    const showKeyInfoSurface = workspace === 'overview';
    const activeProjectRecordSection = resolveProjectRecordSection(page);
    const inProgressReportType = String(data?.in_progress_report_type || '').trim().toLowerCase();
    const inProgressInterimNumber = (() => {
        const n = Number(data?.in_progress_interim_number);
        return Number.isFinite(n) && n > 0 ? Math.trunc(n) : null;
    })();
    const isInProgressInterimPortal = mode === 'surveyor' && inProgressReportType === 'interim' && Boolean(inProgressInterimNumber);
    const setupCostBaseline = useMemo(() => {
        if (!isInProgressInterimPortal) return null;
        return mapProjectSetupToPortalCostBaseline(projectSetupRecord);
    }, [isInProgressInterimPortal, projectSetupRecord]);
    const initialReportForPortal = useMemo(() => {
        if (!isInProgressInterimPortal || !setupCostBaseline?.ini7CostsRow) return initialReport;
        const base = initialReport && typeof initialReport === 'object' ? initialReport : {};
        const baseTables = base.tables && typeof base.tables === 'object' ? base.tables : {};
        const mergedRow = {
            ...(Array.isArray(baseTables.ini_section_7_costs) && baseTables.ini_section_7_costs.length
                ? (baseTables.ini_section_7_costs[0] || {})
                : {}),
            ...setupCostBaseline.ini7CostsRow
        };
        return {
            ...base,
            tables: {
                ...baseTables,
                ini_section_7_costs: [mergedRow]
            }
        };
    }, [isInProgressInterimPortal, initialReport, setupCostBaseline]);
    const costAssessmentsForPortal = useMemo(() => {
        if (!isInProgressInterimPortal || !setupCostBaseline?.fallbackCostMap) return costAssessmentsByKey;
        const merged = new Map();
        if (costAssessmentsByKey && typeof costAssessmentsByKey.forEach === 'function') {
            costAssessmentsByKey.forEach((value, key) => {
                merged.set(String(key), String(value ?? ''));
            });
        }
        setupCostBaseline.fallbackCostMap.forEach((value, key) => {
            const existing = String(merged.get(String(key)) || '').trim();
            if (!existing) merged.set(String(key), String(value ?? ''));
        });
        return merged;
    }, [isInProgressInterimPortal, costAssessmentsByKey, setupCostBaseline]);
    const bookedInterimNumber = getInterimNumberFromReportInstance(siteVisitState?.appointment?.report_instance);
    const isInProgressInterimUnlocked = mode !== 'surveyor'
        || (Boolean(inProgressInterimNumber) && bookedInterimNumber === inProgressInterimNumber);
    const currentInterimNumber = interimNumber || (isInProgressInterimPortal && isInProgressInterimUnlocked ? inProgressInterimNumber : 1);
    const generationContext = `${mode}|${search}|${currentInterimNumber}`;
    generationContextRef.current = generationContext;
    useEffect(() => {
        reportDataRequestRef.current = null;
        setReportDataRequest(null);
        setQuestionnaireReviewRequest(null);
        if (generationExecutingRef.current) generateSeqRef.current += 1;
    }, [generationContext]);
    const initialConfirmedLive = Boolean(projectSetupRecord?.setup_completed_at || projectSetupRecord?.setupCompletedAt);
    const canShowCostTables = mode === 'surveyor' && (hasInitialIssuedPdf || isInProgressInterimPortal);
    const currentReportingCycleInterimNumber = useMemo(() => {
        if (isInProgressInterimPortal && inProgressInterimNumber) return inProgressInterimNumber;

        const issuedNums = (Array.isArray(issuedInterimNumbers) ? issuedInterimNumbers : [])
            .map((n) => Number(n))
            .filter((n) => Number.isFinite(n) && n > 0)
            .map((n) => Math.trunc(n));

        const submittedNums = (Array.isArray(interimSubmissions) ? interimSubmissions : [])
            .map((it) => Number(it?.interim_number || 0))
            .filter((n) => Number.isFinite(n) && n > 0)
            .map((n) => Math.trunc(n));

        // If no interim issue PDFs exist yet, keep the cycle anchored to the
        // latest submitted interim rather than jumping to the next number.
        if (!issuedNums.length && submittedNums.length) {
            return submittedNums.reduce((m, n) => (n > m ? n : m), 0);
        }

        const next = Number(nextInterimNumber || 0);
        return Number.isFinite(next) && next > 0 ? Math.trunc(next) : null;
    }, [isInProgressInterimPortal, inProgressInterimNumber, issuedInterimNumbers, interimSubmissions, nextInterimNumber]);

    const availableInterimNumbers = useMemo(() => {
        if (!initialConfirmedLive) return [];
        const nums = new Set();
        // Show bubbles only when at least one required condition is true:
        // - current reporting cycle
        // - cycle has issued report
        // - previous cycle has issued report
        if (Number.isFinite(Number(currentReportingCycleInterimNumber)) && Number(currentReportingCycleInterimNumber) > 0) {
            nums.add(Math.trunc(Number(currentReportingCycleInterimNumber)));
        }
        (Array.isArray(issuedInterimNumbers) ? issuedInterimNumbers : []).forEach((n) => {
            const v = Number(n);
            if (!Number.isFinite(v) || v <= 0) return;
            nums.add(Math.trunc(v));
        });
        return Array.from(nums).sort((a, b) => a - b);
    }, [initialConfirmedLive, currentReportingCycleInterimNumber, issuedInterimNumbers]);
    const canShowInterimNav = availableInterimNumbers.length > 0;

    async function requestSurveyorQuestionnaireAccessToken({ reportType, interimNumber }) {
        const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/questionnaire/access`;
        return await fetchJson(url, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                report_type: reportType,
                interim_number: interimNumber != null ? Number(interimNumber) || null : null
            })
        });
    }

    async function openSurveyorQuestionnaire({ reportType, valuationNumber, prefillDrawdown }) {
        const report = String(reportType || '').trim().toLowerCase();
        // Open a blank tab synchronously within the user gesture to avoid popup blocking,
        // then navigate it after async token minting completes.
        let popupRef = null;
        try {
            popupRef = window.open('about:blank', '_blank');
            if (popupRef && popupRef.document) {
                popupRef.document.title = 'Opening questionnaire...';
            }
        } catch {
            popupRef = null;
        }
        setActionError('');

        try {
            const access = await requestSurveyorQuestionnaireAccessToken({
                reportType: report,
                interimNumber: report === 'interim' ? valuationNumber : null
            });
            const token = String(access?.token || '').trim();
            if (!token) throw new Error('Surveyor access token was not returned');

            const returnTo = window.location.href;
            if (report === 'initial') {
                const baseUrl = String(getDefaultInitialqBaseUrl() || '').trim();
                if (!baseUrl) throw new Error('Initial Questionnaire URL is not configured.');

                const qs = new URLSearchParams();
                qs.set('token', token);
                qs.set('return_to', returnTo);
                const url = `${baseUrl.replace(/\/+$/, '')}/surveyor-access?${qs.toString()}`;
                if (popupRef && !popupRef.closed) {
                    try {
                        popupRef.location.assign(url);
                    } catch {
                        window.location.href = url;
                    }
                } else {
                    window.location.href = url;
                }
                return;
            }

            const baseUrl = String(INT_QUESTIONNAIRE_BASE_URL || '').trim();
            if (!baseUrl) throw new Error('Interim Questionnaire URL is not configured (missing REACT_APP_INTQ_BASE_URL).');

            const n = Number(valuationNumber || 0);
            const qs = new URLSearchParams();
            qs.set('surveyor_access_token', token);
            qs.set('project_reference', String(reference || '').trim());
            if (n) {
                qs.set('valuation_number', String(n));
                qs.set('interim_number', String(n));
                qs.set('lock_valuation_number', '1');
            }
            qs.set('report_type', 'interim');
            if (prefillDrawdown) {
                const encoded = encodePrefillPayload(prefillDrawdown);
                if (encoded) qs.set('prefill_drawdown', encoded);
            }
            qs.set('return_to', returnTo);
            const leadSurv = String(data?.lead_surveyor || '').trim();
            if (leadSurv) qs.set('lead_surveyor', leadSurv);

            const url = `${baseUrl.replace(/\/+$/, '')}/surveyor/access?${qs.toString()}`;
            if (popupRef && !popupRef.closed) {
                try {
                    popupRef.location.assign(url);
                } catch {
                    window.location.href = url;
                }
            } else {
                window.location.href = url;
            }
        } catch (e) {
            try {
                if (popupRef && !popupRef.closed) popupRef.close();
            } catch {
                // ignore
            }
            const message = e?.message || 'Failed to open questionnaire';
            setActionError(message);
            notify({ severity: 'error', message });
        }
    }

    const currentInterimHasSubmission = Array.isArray(interimSubmissions)
        && interimSubmissions.some((it) => Number(it?.interim_number || 0) === Number(currentInterimNumber));
    const currentInterimRequestedOnly = isInterimView
        && !currentInterimHasSubmission
        && Array.isArray(requestedInterims)
        && requestedInterims.includes(Number(currentInterimNumber));
    const requestedPayload = currentInterimRequestedOnly
        ? (requestedInterimPayloads?.[Number(currentInterimNumber)] || null)
        : null;
    const interimReportForDisplay = interimReport
        ? (interimReport.answers && typeof interimReport.answers === 'object'
            ? interimReport
            : (requestedPayload
                ? {
                    ...interimReport,
                    answers: {
                        valuation_number: Number(currentInterimNumber) || 1,
                        drawdown_costs: requestedPayload.drawdown_costs && typeof requestedPayload.drawdown_costs === 'object'
                            ? requestedPayload.drawdown_costs
                            : requestedPayload
                    }
                }
                : interimReport))
        : (isInProgressInterimPortal
            ? {
                answers: {
                    valuation_number: Number(currentInterimNumber) || 1,
                    drawdown_costs: {}
                },
                submission: null
            }
            : interimReport);

    const viewLabel = (() => {
        if (mode === 'developer') {
            const borrowerCompany = String(data?.borrower_company || data?.borrower_name || '').trim();
            return borrowerCompany ? `Developer - ${borrowerCompany}` : 'Developer';
        }
        return 'Surveyor';
    })();

    const keyInfoSnapshot = [
        { label: 'Reference', value: data?.reference },
        { label: 'Project', value: data?.project_name },
        { label: 'Borrower', value: data?.borrower_name },
        { label: 'Funder', value: data?.funder },
        { label: 'Lead Surveyor', value: data?.lead_surveyor },
        { label: 'Duration', value: data?.project_duration ? `${data.project_duration} months` : '' }
    ];

    const devOverviewProjectsFiltered = useMemo(() => {
        if (devOverviewSelectedRef && devOverviewSelectedRef !== 'all') {
            return devOverviewProjects.filter((p) => p.reference === devOverviewSelectedRef);
        }
        return devOverviewProjects;
    }, [devOverviewProjects, devOverviewSelectedRef]);

    const devOverviewTotals = useMemo(() => {
        const totalFacility = devOverviewProjectsFiltered.reduce((acc, p) => acc + parseMoneyLoose(p?.facility), 0);
        const totalDrawn = devOverviewProjectsFiltered.reduce((acc, p) => acc + (devOverviewDrawnByRef?.[p.reference] || 0), 0);
        const remaining = Math.max(0, totalFacility - totalDrawn);
        return {
            facility: totalFacility,
            drawn: totalDrawn,
            remaining
        };
    }, [devOverviewProjectsFiltered, devOverviewDrawnByRef]);

    const overclaimMessage = 'Please ensure you do not overclaim against your budgets.\nIf the budget is no longer sufficient, please contact your Funder and MS at your earliest convienience to discuss';
    const getClaimedToDate = useCallback((groupKey, itemLabel, index) => {
        if (groupKey === 'contingency') {
            const k = normalizeLabelKey(itemLabel);
            return k ? (drawdownClaims.contingencyByDesc?.[k] || 0) : 0;
        }
        const key = makeCostItemKey(
            groupKey === 'construction'
                ? 'construction_cost_breakdown'
                : groupKey === 'professional'
                    ? 'professional_fees_breakdown'
                    : 'other_fees_breakdown',
            itemLabel,
            index
        );
        const map = groupKey === 'construction'
            ? drawdownClaims.construction
            : groupKey === 'professional'
                ? drawdownClaims.professional
                : drawdownClaims.other;
        return map?.[key] || 0;
    }, [drawdownClaims]);

    const overclaimDetected = useMemo(() => {
        return (drawdownSchedule || [])
            .some((group) => {
                const children = Array.isArray(group?.children) ? group.children : [];
                return children.some((row, idx) => {
                    const budget = parseMoneyLoose(row?.budget);
                    const claimed = getClaimedToDate(group.key, row?.label, idx);
                    const requested = parseMoneyLoose(row?.requested);
                    return (claimed + requested) > budget;
                });
            });
    }, [drawdownSchedule, getClaimedToDate]);

    function handleDeveloperProjectChange(nextRef) {
        const ref = String(nextRef || '').trim();
        if (!ref || ref === reference) return;
        setQuery({ reference: ref, page: 'initial', interim: null });
    }

    const activeKey = mode === 'developer'
        ? ((view === 'project' || !view) ? (view === 'project' ? 'reports' : 'overview') : view)
        : view;

    const showSessionExpiredBanner = mode === 'surveyor' && !String(authToken || '').trim();

    const portalReportStageLabel = isInterimView || (page === 'risk' && interimNumber)
        ? `Interim Report ${currentInterimNumber}`
        : 'Initial Report';
    const portalReportStatusLabel = useMemo(() => {
        if (activeDocxEntry?.issue_checked || activeDocxEntry?.issue_pdf_uploaded_at) return 'Issued PDF ready';
        if (activeDocxEntry?.qa_checked) return 'QA approved';
        if (activeDocxEntry?.download_url) return 'Draft generated';
        if (generating) return 'Generating';
        return 'Not generated';
    }, [activeDocxEntry?.issue_checked, activeDocxEntry?.issue_pdf_uploaded_at, activeDocxEntry?.qa_checked, activeDocxEntry?.download_url, generating]);
    const overviewCostBaseline = mapProjectSetupToPortalCostBaseline(projectSetupRecord);
    const overviewCostRow = overviewCostBaseline?.ini7CostsRow
        || initialReport?.tables?.ini_section_7_costs?.[0]
        || {};
    const projectRecordLocationRow = initialReport?.tables?.ini_section_3_location?.[0] || {};
    const projectRecordDevelopmentRow = initialReport?.tables?.ini_section_6_development?.[0] || {};
    const projectRecordSurveyRow = initialReport?.tables?.ini_surveys?.[0] || {};
    const projectRecordProcurementRow = initialReport?.tables?.ini_section_9_procurement?.[0] || {};
    const projectRecordDocuments = Array.isArray(initialReport?.tables?.ini_documents)
        ? initialReport.tables.ini_documents
        : [];
    const overviewTotalCost = ['build_cost', 'professional_fees', 'other_development_costs', 'contingency']
        .reduce((sum, key) => sum + toPortalMoney(overviewCostRow?.[key]), 0);
    const overviewScope = getPortalReportValue(initialReport, [
        'proposed_development',
        'development_description',
        'project_description',
        'scheme_description',
        'description_of_development',
        'scope_of_works'
    ]) || String(data?.site_address || '').trim();
    const overviewPlanningReference = String(planningItems?.[0]?.notice_planning_reference || '').trim();
    const overviewOutstandingConditions = planningItems.filter((item) => !['discharged', 'not_applicable'].includes(String(item?.status || '').toLowerCase())).length;
    const overviewProgrammeStart = String(programmeTrackerState?.works_start_date || data?.works_start_month || '').trim();
    const overviewProgrammeDuration = String(programmeTrackerState?.borrower_duration_months || data?.project_duration || '').trim();
    const overviewOutstandingAppointments = designTeamMembers.filter((member) => {
        const appointment = String(member?.appointment || '').trim().toLowerCase();
        return !appointment || ['no', 'none', 'unknown', 'tbc', 'not appointed'].includes(appointment);
    }).length;
    const overviewDocumentCount = Object.entries(initialReport?.tables || {})
        .filter(([tableName]) => normalizePortalCostKey(tableName).includes('document'))
        .reduce((count, [, rows]) => count + (Array.isArray(rows) ? rows.length : 0), 0);
    const overviewAiAlerts = Array.isArray(aiAlerts?.alerts) ? aiAlerts.alerts : [];
    const overviewGapItems = Array.isArray(readiness?.key_missing) ? readiness.key_missing : [];
    const overviewOutstandingDocuments = Array.isArray(readiness?.outstanding_documents) ? readiness.outstanding_documents : [];
    const overviewCoverage = Number.isFinite(Number(readiness?.score))
        ? Math.max(0, Math.min(100, Math.round(Number(readiness.score))))
        : Math.max(0, Math.min(100, Math.round(Number(readiness?.completeness_ratio || 0) * 100)));
    const overviewVersion = formatReportVersion(activeDocxEntry?.revision, reportVersionNaming) || '';
    const overviewProjectRows = [
        {
            key: 'site',
            label: 'Site & scheme',
            value: overviewScope || 'Not recorded',
            source: 'Questionnaire',
            onClick: () => goToWorkspace('project_record')
        },
        {
            key: 'planning',
            label: 'Planning & conditions',
            value: planningLoading
                ? 'Loading planning position...'
                : `${overviewPlanningReference ? `Permission ${overviewPlanningReference} · ` : ''}${planningItems.length} condition${planningItems.length === 1 ? '' : 's'} tracked${overviewOutstandingConditions ? ` · ${overviewOutstandingConditions} open` : ''}`,
            source: planningItems.length ? `Tracker${formatPortalShortDate(planningItems?.[0]?.updated_at) ? ` · ${formatPortalShortDate(planningItems[0].updated_at)}` : ''}` : 'Tracker',
            onClick: () => setQuery({ workspace: 'project_record', page: 'conditions', interim: null })
        },
        {
            key: 'costs',
            label: 'Costs & funding',
            value: overviewTotalCost ? `${formatMoney(overviewTotalCost)} total development cost` : 'Cost baseline not recorded',
            source: projectSetupRecord ? 'Cost plan' : 'Questionnaire',
            onClick: mode === 'surveyor' ? () => setQuery({ workspace: 'project_record', page: 'costs', interim: currentInterimNumber }) : null
        },
        {
            key: 'programme',
            label: 'Programme',
            value: programmeTrackerLoading
                ? 'Loading programme...'
                : `${overviewProgrammeStart ? `Start ${formatPortalShortDate(overviewProgrammeStart) || overviewProgrammeStart}` : 'Start not recorded'}${overviewProgrammeDuration ? ` · ${overviewProgrammeDuration}-month borrower programme` : ''}`,
            source: `Programme${formatPortalShortDate(programmeTrackerUpdatedAt) ? ` · ${formatPortalShortDate(programmeTrackerUpdatedAt)}` : ''}`,
            onClick: mode === 'surveyor' ? () => setQuery({ workspace: 'project_record', page: 'programme', interim: null }) : null
        },
        {
            key: 'design-team',
            label: 'Design team',
            value: designTeamLoading
                ? 'Loading design team...'
                : `${designTeamMembers.length} team appointment${designTeamMembers.length === 1 ? '' : 's'} recorded${overviewOutstandingAppointments ? ` · ${overviewOutstandingAppointments} to confirm` : ''}`,
            source: `Questionnaire${formatPortalShortDate(designTeamUpdatedAt) ? ` · ${formatPortalShortDate(designTeamUpdatedAt)}` : ''}`,
            onClick: mode === 'surveyor' ? () => setQuery({ workspace: 'project_record', page: 'design_team', interim: null }) : null
        }
    ];
    const overviewPeriods = [
        { key: 'initial', label: 'Initial Report', active: page !== 'interim', page: 'initial', interim: null },
        ...availableInterimNumbers.map((number) => ({
            key: `interim-${number}`,
            label: `Interim ${String(number).padStart(2, '0')}`,
            active: page === 'interim' && currentInterimNumber === number,
            page: 'interim',
            interim: number
        }))
    ];
    const overviewReporting = {
        coverage: overviewCoverage,
        gapCount: overviewGapItems.length,
        canGenerate: mode === 'surveyor' && (page !== 'interim' || currentInterimHasSubmission),
        canOpen: Boolean(activeDocxEntry?.download_url),
        generateLabel: page === 'interim' ? `Generate Interim ${currentInterimNumber}` : 'Generate Initial Report',
        openLabel: overviewVersion ? `Open ${overviewVersion}` : 'Open Latest Report',
        rows: [
            { label: 'Questionnaire', value: initialReport ? `${Math.round(Number(readiness?.completeness_ratio || 0) * 100)}% complete` : 'Not loaded' },
            { label: 'Data pack', value: overviewDocumentCount ? `${overviewDocumentCount} records linked` : 'No linked records' },
            { label: 'Existing findings', value: overviewAiAlerts.length ? `${overviewAiAlerts.length} to verify` : 'None loaded' },
            { label: 'Latest generated copy', value: overviewVersion || portalReportStatusLabel }
        ]
    };
    const overviewAttentionItems = [
        ...overviewGapItems.map((item) => ({
            kind: 'gap',
            title: String(item),
            detail: 'Required information is missing from the current reporting record.',
            actionLabel: 'Resolve',
            onClick: () => goToWorkspace('reporting_periods')
        })),
        ...overviewOutstandingDocuments.map((item) => ({
            kind: 'document',
            title: String(item),
            detail: 'Expected supporting evidence has not been linked to the project record.',
            actionLabel: 'Open documents',
            onClick: () => goToWorkspace('documents')
        })),
        ...overviewAiAlerts.map((item) => ({
            kind: 'finding',
            title: String(item?.title || 'Existing finding'),
            detail: String(item?.rationale || item?.reason || item?.suggested_action || 'Review the existing finding before report generation.'),
            actionLabel: 'Review finding',
            onClick: () => setShowAiAlerts(true)
        }))
    ].slice(0, 8);
    const reportingSelectedIsInterim = isInterimView;
    const reportingSelectedNumber = reportingSelectedIsInterim ? Number(currentInterimNumber) : null;
    const reportingSelectedInstance = reportingSelectedIsInterim ? `interim:${reportingSelectedNumber}` : 'initial';
    const reportingPeriodNumbers = new Set(availableInterimNumbers);
    if (hasInitialIssuedPdf && !initialConfirmedLive) reportingPeriodNumbers.add(1);
    if (reportingSelectedIsInterim) reportingPeriodNumbers.add(reportingSelectedNumber);
    const reportingPeriods = [
        { key: 'initial', label: 'Initial Report', active: !reportingSelectedIsInterim, page: 'initial', interim: null },
        ...Array.from(reportingPeriodNumbers).sort((a, b) => a - b).map((number) => ({
            key: `interim-${number}`,
            label: `Interim ${String(number).padStart(2, '0')}`,
            active: reportingSelectedIsInterim && reportingSelectedNumber === number,
            page: 'interim',
            interim: number
        }))
    ];
    const reportingSelectedSubmission = reportingSelectedIsInterim
        ? interimSubmissions.find((item) => Number(item?.interim_number || 0) === reportingSelectedNumber)
        : null;
    const reportingSelectedReport = reportingSelectedIsInterim ? interimReportForDisplay : initialReport;
    const reportingLinkedFactCount = countPortalReportFacts(reportingSelectedReport);
    const reportingApprovedIntroductions = approvedIntroductions.filter((proposal) => (
        String(proposal?.report_instance || 'initial').trim().toLowerCase() === reportingSelectedInstance
    ));
    const reportingHybridCaptureCount = reportingApprovedIntroductions.length;
    const reportingCaptureComplete = reportingSelectedIsInterim
        ? Boolean(reportingSelectedSubmission) || reportingHybridCaptureCount > 0
        : reportingLinkedFactCount > 0 || reportingHybridCaptureCount > 0;
    const reportingHistoricalComplete = reportingSelectedIsInterim
        ? Boolean(
            reportingSelectedSubmission
            || issuedInterimNumbers.some((number) => Number(number) === reportingSelectedNumber)
            || activeDocxEntry?.download_url
        )
        : Boolean(hasInitialIssuedPdf || activeDocxEntry?.download_url);
    const reportingVisitMatches = siteVisitState?.appointment?.report_instance === reportingSelectedInstance;
    const reportingVisitComplete = reportingVisitMatches || reportingHistoricalComplete;
    const reportingSetupComplete = !isInProgressInterimPortal || initialConfirmedLive;
    const reportingCanManageVisit = siteVisitState?.can_manage === true && reportingSetupComplete && (reportingVisitMatches || (
        !siteVisitState?.appointment
        && Boolean(siteVisitState?.eligible)
        && siteVisitState?.report_instance === reportingSelectedInstance
    ));
    const reportingVisitDate = reportingVisitMatches
        ? formatPortalShortDate(siteVisitState?.appointment?.starts_at)
        : '';
    const reportingVisitStatus = !siteVisitLoaded
        ? 'Loading visit status...'
        : reportingVisitMatches
            ? `Booked${reportingVisitDate ? ` for ${reportingVisitDate}` : ''}`
            : reportingHistoricalComplete
                ? 'Completed for this reporting period'
                : siteVisitError
                    ? 'Visit status unavailable'
                    : siteVisitState?.appointment
                        ? 'Another reporting period has the current visit booking'
                        : siteVisitState?.eligible
                            ? 'Required before data capture'
                            : 'Booking is not currently available';
    const reportingPeriodGates = [
        ...(isInProgressInterimPortal && !initialConfirmedLive ? [{
            key: 'in-progress-setup',
            label: 'In-Progress Setup',
            complete: false,
            available: true,
            status: 'Set the uploaded report as the project baseline before booking a valuation visit',
            onClick: openInProgressSetup
        }] : reportingSelectedIsInterim && reportingSelectedNumber === 1 ? [{
            key: 'confirm-live',
            label: 'Confirm Live',
            complete: initialConfirmedLive,
            available: !initialConfirmedLive && hasInitialIssuedPdf,
            status: initialConfirmedLive ? 'Initial Report baseline confirmed' : 'Confirm the Initial Report baseline first',
            onClick: openConfirmLive
        }] : []),
        {
            key: 'valuation-visit',
            label: 'Book Valuation Visit',
            complete: reportingVisitComplete,
            available: reportingCanManageVisit,
            status: reportingVisitStatus,
            onClick: () => setSiteVisitOpen(true)
        }
    ];
    const reportingGatesComplete = reportingPeriodGates.every((gate) => gate.complete);
    const reportingCaptureMethods = reportingSelectedIsInterim
        ? [
            {
                key: 'questionnaire',
                label: 'Questionnaire',
                detail: reportingSelectedSubmission ? 'Submitted for this reporting period' : 'Open structured Interim Report capture',
                lockedLabel: 'Complete the required confirmations first',
                onClick: () => openSurveyorQuestionnaire({ reportType: 'interim', valuationNumber: reportingSelectedNumber, prefillDrawdown: requestedPayload })
            },
            {
                key: 'notes-upload',
                label: 'Notes Upload',
                detail: 'Upload or paste site and valuation notes for review',
                lockedLabel: 'Complete the required confirmations first',
                onClick: () => setDataIntroduction({ intakeType: 'notes', reportInstance: reportingSelectedInstance })
            },
            {
                key: 'voice-transcription',
                label: 'Voice Transcription',
                detail: 'Record or upload a consented voice note for transcription',
                lockedLabel: 'Complete the required confirmations first',
                onClick: () => setDataIntroduction({ intakeType: 'voice', reportInstance: reportingSelectedInstance })
            },
            {
                key: 'email-search',
                label: 'Email Search',
                detail: 'Search your linked Gmail inbox for project updates and review each email detail',
                lockedLabel: 'Complete the required confirmations first',
                onClick: () => setDataIntroduction({ intakeType: 'email', reportInstance: reportingSelectedInstance })
            }
        ]
        : [
            {
                key: 'questionnaire',
                label: 'Questionnaire',
                detail: reportingLinkedFactCount ? `${reportingLinkedFactCount} structured facts linked` : 'Open structured Initial Report capture',
                lockedLabel: 'Book the valuation visit first',
                onClick: () => openSurveyorQuestionnaire({ reportType: 'initial' })
            },
            {
                key: 'ai-review',
                label: 'AI Review',
                detail: 'Review project data, address-specific planning and a reasoned construction cost benchmark',
                lockedLabel: 'Book the valuation visit first',
                onClick: () => setDataIntroduction({ intakeType: 'ai_review', reportInstance: reportingSelectedInstance })
            },
            {
                key: 'soi-upload',
                label: 'SOI Upload',
                detail: 'Upload an Excel scope of instruction for controlled review',
                lockedLabel: 'Book the valuation visit first',
                onClick: () => setDataIntroduction({ intakeType: 'soi', reportInstance: reportingSelectedInstance })
            },
            {
                key: 'data-pack-upload',
                label: 'Data Pack Upload',
                detail: 'Upload multiple source files and review extracted proposals',
                lockedLabel: 'Book the valuation visit first',
                onClick: () => setDataIntroduction({ intakeType: 'data_pack', reportInstance: reportingSelectedInstance })
            },
            {
                key: 'email-search',
                label: 'Email Search',
                detail: 'Search your linked Gmail inbox for project updates and review each email detail',
                lockedLabel: 'Book the valuation visit first',
                onClick: () => setDataIntroduction({ intakeType: 'email', reportInstance: reportingSelectedInstance })
            }
        ];
    const reportingReadiness = {
        badge: readinessLoading ? 'Loading' : readinessError ? 'Review unavailable' : reportingGatesComplete && reportingCaptureComplete ? 'Ready' : 'Action needed',
        coverage: overviewCoverage,
        blocked: !reportingGatesComplete || !reportingCaptureComplete || Boolean(readinessError),
        rows: [
            { label: 'Project record linked', value: `${reportingLinkedFactCount} structured facts`, tone: reportingLinkedFactCount ? 'success' : 'warning' },
            { label: 'Period capture', value: reportingCaptureComplete ? (reportingSelectedSubmission ? 'Questionnaire completed' : `${reportingHybridCaptureCount} approved intake source${reportingHybridCaptureCount === 1 ? '' : 's'}`) : 'Capture required', tone: reportingCaptureComplete ? 'success' : 'warning' },
            { label: 'Missing required information', value: readinessLoading ? 'Checking...' : readinessError ? 'Unavailable' : `${overviewGapItems.length} item${overviewGapItems.length === 1 ? '' : 's'}`, tone: overviewGapItems.length || readinessError ? 'warning' : 'success' },
            { label: 'Existing AI findings', value: aiAlerts ? `${overviewAiAlerts.length} finding${overviewAiAlerts.length === 1 ? '' : 's'}` : 'Not reviewed', tone: overviewAiAlerts.length ? 'warning' : 'neutral' },
            { label: 'Latest generated copy', value: overviewVersion || portalReportStatusLabel, tone: activeDocxEntry?.download_url ? 'success' : 'neutral' }
        ]
    };
    const reportingGeneration = {
        canGenerate: mode === 'surveyor' && reportingGatesComplete && reportingCaptureComplete,
        canOpen: Boolean(activeDocxEntry?.download_url),
        canIssue: Boolean(activeDocxEntry?.download_url),
        generating,
        opening: viewReportBusy,
        generateLabel: reportingSelectedIsInterim ? `Generate Interim Report ${reportingSelectedNumber}` : 'Generate Initial Report',
        openLabel: overviewVersion ? `Open ${overviewVersion} in Collabora` : 'Open latest in Collabora',
        issueLabel: activeDocxEntry?.issue_pdf_uploaded_at ? 'Replace Issued Report' : 'Issue Report',
        onGenerate: () => openAppendixModal(reportingSelectedIsInterim ? 'interim' : 'initial'),
        onOpen: onViewReport,
        onIssue: () => {
            setIssueUploadError('');
            setIssueUploadSuccess('');
            setIssueSendToFunder(false);
            setShowQaUpload(true);
        }
    };
    const projectRecordSiteRows = [
        {
            key: 'site-address',
            label: 'Site address',
            value: String(data?.site_address || '').trim() || getPortalRowValue(projectRecordLocationRow, ['site_address', 'project_address']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'existing-site',
            label: 'Existing site',
            value: getPortalRowValue(projectRecordLocationRow, ['existing_site_description', 'existing_site', 'existing_site_text']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'proposed-works',
            label: 'Proposed works',
            value: getPortalRowValue(projectRecordLocationRow, ['proposed_development_description', 'proposed_development'])
                || getPortalRowValue(projectRecordDevelopmentRow, ['development_summary', 'proposed_development', 'development_description', 'scope_of_works']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'development-type',
            label: 'Development type',
            value: getPortalRowValue(projectRecordLocationRow, ['development_type', 'development_type_other'])
                || getPortalRowValue(projectRecordDevelopmentRow, ['development_type', 'property_type', 'scheme_type']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'proposed-gia',
            label: 'Proposed GIA',
            value: projectRecordArea(projectRecordLocationRow, projectRecordDevelopmentRow),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'services',
            label: 'Services overview',
            value: projectRecordServices(projectRecordSurveyRow, projectRecordLocationRow),
            source: 'Questionnaire · confirmed'
        }
    ].map((row) => ({ ...row, source: 'Stored source claim - verification separate' }));
    const projectRecordProcurementRows = [
        {
            key: 'procurement-route',
            label: 'Procurement route',
            value: getPortalRowValue(projectRecordProcurementRow, ['procurement_route', 'procurement_route_other']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contractor',
            label: 'Building contractor',
            value: getPortalRowValue(projectRecordProcurementRow, ['chosen_contractor', 'contractor_name', 'constructed_by_other', 'constructed_by']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contract-basis',
            label: 'Contract basis',
            value: getPortalRowValue(projectRecordProcurementRow, ['contract_type', 'jct_form', 'jct_other_description']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contract-status',
            label: 'Contract status',
            value: getPortalRowValue(projectRecordProcurementRow, ['contract_process_status', 'contract_status']),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contract-sum',
            label: 'Contract sum',
            value: toPortalMoney(projectRecordProcurementRow?.contract_sum)
                ? formatMoney(toPortalMoney(projectRecordProcurementRow.contract_sum))
                : '',
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contract-programme',
            label: 'Contract programme',
            value: [
                getPortalRowValue(projectRecordProcurementRow, ['contract_start_on_site']),
                getPortalRowValue(projectRecordProcurementRow, ['contract_practical_completion']),
                getPortalRowValue(projectRecordProcurementRow, ['contract_duration_months'])
                    ? `${getPortalRowValue(projectRecordProcurementRow, ['contract_duration_months'])} months`
                    : ''
            ].filter(Boolean).join(' · '),
            source: 'Questionnaire · confirmed'
        },
        {
            key: 'contract-security',
            label: 'Contract security',
            value: [
                getPortalRowValue(projectRecordProcurementRow, ['performance_bond_status']),
                getPortalRowValue(projectRecordProcurementRow, ['pcg_status'])
            ].filter(Boolean).join(' · '),
            source: 'Questionnaire · confirmed'
        }
    ];
    const projectRecordPlanningRows = [
        ['ini_planning_applications', 'Planning application'],
        ['ini_section_4_planning_conditions', 'Planning conditions'],
        ['ini_section_4_statutory', 'Statutory']
    ].flatMap(([tableName, recordLabel]) => {
        const rows = Array.isArray(initialReport?.tables?.[tableName]) ? initialReport.tables[tableName] : [];
        return rows.flatMap((row, rowIndex) => filterInfoRowFields({
            tableName,
            row,
            fields: Object.entries(row || {}).filter(([key]) => !['id', 'project_reference', 'created_at', 'updated_at'].includes(String(key).toLowerCase()))
        }).filter(([, value]) => value !== null && value !== undefined && typeof value !== 'object' && String(value).trim() !== '')
            .map(([field, value]) => ({
                key: `${tableName}-${rowIndex}-${field}`,
                label: `${recordLabel}: ${prettyFieldName(field)}`,
                value: typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value),
                source: 'Questionnaire · confirmed'
            })));
    });
    const projectRecordEvidenceRows = projectRecordDocuments.filter((document) => {
        const section = String(document?.related_section || '').trim().toLowerCase();
        const type = String(document?.document_type || '').trim().toLowerCase();
        if (activeProjectRecordSection === 'site_scheme') return section === 'section_3';
        if (activeProjectRecordSection === 'conditions') return section === 'section_4';
        if (activeProjectRecordSection === 'costs') return section === 'section_7' || /cost|appraisal|budget/.test(type);
        if (activeProjectRecordSection === 'programme') return section === 'section_8' || /programme|schedule/.test(type);
        if (activeProjectRecordSection === 'design_team') return section === 'section_9' && /appointment|consultant|warranty/.test(type) && !/insurance|indemnity/.test(type);
        if (activeProjectRecordSection === 'procurement') return section === 'section_9' && /contract|procurement/.test(type);
        if (activeProjectRecordSection === 'insurance') return /insurance|policy|indemnity/.test(type);
        return false;
    }).map((document, index) => ({
        key: `project-record-document-${document?.id || index}`,
        label: String(document?.document_type || 'Supporting document').trim(),
        value: String(document?.document_name || document?.file_name || 'Document').trim(),
        source: 'Data Upload',
        actionLabel: 'Open',
        onAction: () => setQuery({ workspace: 'documents', page: 'initial', interim: null })
    }));
    const approvedIntroductionRows = approvedIntroductions.map((proposal, index) => {
        const reportLabel = String(proposal?.report_instance || 'initial').replace(/^interim:/i, 'Interim ').replace(/^initial$/i, 'Initial Report');
        const approvedDate = formatPortalShortDate(proposal?.reviewed_at);
        return {
            key: `approved-introduction-${proposal?.id || index}`,
            label: proposal.category === 'email_detail' ? proposal.proposed_value?.title || 'Email project detail' : approvedIntroductionLabel(proposal?.category),
            value: formatApprovedIntroductionValue(proposal),
            source: `${reportLabel}${approvedDate ? ` · Approved ${approvedDate}` : ' · Approved'}`
        };
    });
    const acceptedCostBenchmark = approvedIntroductions.find((proposal) => (
        String(proposal?.category || '').trim().toLowerCase() === 'costs'
        && String(proposal?.target_field || '').trim().toLowerCase() === 'ai_construction_cost_benchmark'
    )) || null;
    const portalWorkspaceTitle = workspace === 'project_record'
        ? 'Project Record'
        : workspace === 'reporting_periods'
            ? portalReportStageLabel
            : workspace === 'documents'
                ? 'Documents'
                : 'Overview';
    return (
        <PortalShell
            activeKey={activeKey}
            portalMode={mode}
            portalReference={reference}
            portalCode={code}
            portalPage={page}
            portalInterim={interimNumber || ''}
        >
            <Container
                maxWidth={false}
                disableGutters
                sx={{
                    px: { xs: 2, sm: 2.5, lg: 3 },
                    width: '100%',
                    maxWidth: { xs: 'calc(100vw - 32px)', sm: 'calc(100vw - 40px)', md: 'none' }
                }}
            >
                {showSessionExpiredBanner ? (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                        action={(
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => {
                                    try {
                                        // Return to dashboard login (same app, without portal mode).
                                        window.location.assign('/');
                                    } catch {
                                        window.location.href = '/';
                                    }
                                }}
                            >
                                Go to dashboard
                            </Button>
                        )}
                    >
                        Your surveyor session has expired (or you are not signed in). Please sign in again to continue.
                    </Alert>
                ) : null}
                <div className={`info-portal device-${deviceType}`} style={{ width: '100%', minWidth: 0, maxWidth: '100%', overflowX: 'hidden' }}>
                    {mode === 'developer' && view === 'overview' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                            Overview
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                            Totals across your projects.
                                        </Typography>
                                    </Box>
                                    <FormControl size="small" sx={{ minWidth: 260 }}>
                                        <InputLabel id="dev-overview-project-label">Project</InputLabel>
                                        <Select
                                            labelId="dev-overview-project-label"
                                            value={devOverviewSelectedRef}
                                            label="Project"
                                            onChange={(e) => setDevOverviewSelectedRef(String(e.target.value))}
                                        >
                                            <MenuItem value="all">All projects</MenuItem>
                                            {devOverviewProjects.map((p) => (
                                                <MenuItem key={p.reference} value={p.reference}>
                                                    {p.name ? `${p.name} (${p.reference})` : p.reference}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Paper>

                            {devOverviewError ? (
                                <Paper sx={{ p: 2, border: '1px solid #fecaca', background: '#fff5f5' }}>
                                    <Typography variant="body2" sx={{ color: '#b91c1c' }}>
                                        {devOverviewError}
                                    </Typography>
                                </Paper>
                            ) : null}

                            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                                <Paper sx={{ p: 2, flex: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Total Facility Agreed
                                    </Typography>
                                    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900 }}>
                                        {formatMoney(devOverviewTotals.facility)}
                                    </Typography>
                                </Paper>
                                <Paper sx={{ p: 2, flex: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Total Funds Drawn to Date
                                    </Typography>
                                    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900 }}>
                                        {formatMoney(devOverviewTotals.drawn)}
                                    </Typography>
                                </Paper>
                                <Paper sx={{ p: 2, flex: 1 }}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Total Funds Remaining
                                    </Typography>
                                    <Typography variant="h4" sx={{ mt: 0.5, fontWeight: 900 }}>
                                        {formatMoney(devOverviewTotals.remaining)}
                                    </Typography>
                                </Paper>
                            </Stack>

                            {devOverviewLoading ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Loading metrics…
                                </Typography>
                            ) : null}

                            {!devOverviewLoading && !devOverviewProjects.length ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No projects found for this developer account yet.
                                </Typography>
                            ) : null}
                        </Box>
                    ) : mode === 'developer' && view === 'reports' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center" justifyContent="space-between">
                                    <Box>
                                        <Typography variant="h5" sx={{ fontWeight: 900 }}>
                                            Projects
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                            Select a project to view reports, trackers and activity.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Paper>

                            {devOverviewError ? (
                                <Paper sx={{ p: 2, border: '1px solid #fecaca', background: '#fff5f5' }}>
                                    <Typography variant="body2" sx={{ color: '#b91c1c' }}>
                                        {devOverviewError}
                                    </Typography>
                                </Paper>
                            ) : null}

                            {devOverviewLoading ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Loading projects…
                                </Typography>
                            ) : null}

                            {!devOverviewLoading && !devOverviewProjects.length ? (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    No projects found for this developer account yet.
                                </Typography>
                            ) : null}

                            {!devOverviewLoading && devOverviewProjects.length ? (
                                <Grid container spacing={2}>
                                    {devOverviewProjects.map((p) => {
                                        const drawn = devOverviewDrawnByRef?.[p.reference] || 0;
                                        const facility = parseMoneyLoose(p?.facility);
                                        const remaining = Math.max(0, facility - drawn);
                                        return (
                                            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={`devproj-${p.reference}`}>
                                                <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontWeight: 900, lineHeight: 1.2 }} noWrap title={p.name || p.reference}>
                                                                {p.name || p.reference}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                {p.reference}
                                                            </Typography>
                                                        </Box>
                                                        <Button
                                                            variant="contained"
                                                            size="small"
                                                            onClick={() => setQuery({ reference: p.reference, view: 'project', workspace: 'overview', page: 'initial', interim: null })}
                                                        >
                                                            Open
                                                        </Button>
                                                    </Box>

                                                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                Facility
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 800 }}>
                                                                {formatMoney(facility)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                Drawn
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 800 }}>
                                                                {formatMoney(drawn)}
                                                            </Typography>
                                                        </Box>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                                Remaining
                                                            </Typography>
                                                            <Typography sx={{ fontWeight: 800 }}>
                                                                {formatMoney(remaining)}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </Paper>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : null}
                        </Box>
                    ) : mode === 'developer' && view === 'activity' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                                    Activity
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Recent updates for the selected project.
                                </Typography>
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => setQuery({ view: 'project', workspace: 'project_record', page: 'activity' })}
                                        disabled={!reference}
                                    >
                                        {reference ? 'Open project activity' : 'Select a project first'}
                                    </Button>
                                </Box>
                            </Paper>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Use the Projects page to choose a project, then open its activity feed.
                                </Typography>
                            </Paper>
                        </Box>
                    ) : mode === 'developer' && view === 'request_valuation' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                                    Request Valuation
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Populate the drawdown schedule based on your Initial Report budgets.
                                </Typography>
                            </Paper>

                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 900, mb: 1 }}>
                                    Select project
                                </Typography>
                                {devOverviewLoading ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Loading your projects…
                                    </Typography>
                                ) : devOverviewError ? (
                                    <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 700 }}>
                                        {devOverviewError}
                                    </Typography>
                                ) : devOverviewProjects.length ? (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {devOverviewProjects.map((p) => {
                                            const ref = String(p?.reference || '').trim();
                                            const selected = String(reference || '').trim().toLowerCase() === ref.toLowerCase();
                                            return (
                                                <Button
                                                    key={ref}
                                                    size="small"
                                                    variant={selected ? 'contained' : 'outlined'}
                                                    onClick={() => setQuery({ reference: ref, interim: null })}
                                                    sx={{
                                                        minWidth: 86,
                                                        px: 1.5,
                                                        py: 0.9,
                                                        borderRadius: 2,
                                                        fontWeight: 900,
                                                        textTransform: 'none'
                                                    }}
                                                    title={String(p?.name || '').trim()}
                                                >
                                                    {ref}
                                                </Button>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        No projects are linked to this developer account yet.
                                    </Typography>
                                )}
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                                    Only projects linked to your account will appear here.
                                </Typography>
                            </Paper>

                            <Paper sx={{ p: 2 }}>
                                {mode === 'developer' && !devProjectsReady ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Loading your Projects list…
                                    </Typography>
                                ) : mode === 'developer' && reference && !devReferenceAllowed ? (
                                    <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 700 }}>
                                        This project is not linked to your developer account. Please select a project from the list above.
                                    </Typography>
                                ) : !reference ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Select a project above to load budgets.
                                    </Typography>
                                ) : !initialReport ? (
                                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                        Loading Initial Report budgets…
                                    </Typography>
                                ) : (
                                    <Box sx={{ display: 'grid', gap: 2 }}>
                                        {drawdownSchedule.map((group) => {
                                            const children = Array.isArray(group?.children) ? group.children : [];
                                            const headlineBudget = children.reduce((acc, r) => acc + parseMoneyLoose(r?.budget), 0);
                                            const headlineRequested = children.reduce((acc, r) => acc + parseMoneyLoose(r?.requested), 0);
                                            const headlineClaimed = group.key === 'contingency'
                                                ? drawdownClaims.contingencyTotal
                                                : children.reduce((acc, r, idx) => acc + getClaimedToDate(group.key, r?.label, idx), 0);
                                            const headlinePct = headlineBudget ? ((headlineClaimed + headlineRequested) / headlineBudget) * 100 : 0;

                                            return (
                                                <Box key={group.key} sx={{ display: 'grid', gap: 1.5 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 0.6fr' },
                                                            gap: 2,
                                                            alignItems: 'center',
                                                            pb: 1,
                                                            borderBottom: '1px solid',
                                                            borderColor: 'divider'
                                                        }}
                                                    >
                                                        <Typography sx={{ fontWeight: 800 }}>{group.label}</Typography>
                                                        <Typography sx={{ color: 'text.secondary', fontWeight: 700 }}>{formatMoney(headlineBudget)}</Typography>
                                                        <Typography sx={{ fontWeight: 700 }}>{formatMoney(headlineClaimed)}</Typography>
                                                        <Typography sx={{ fontWeight: 700 }}>{formatMoney(headlineRequested)}</Typography>
                                                        <Typography sx={{ fontWeight: 700 }}>{headlineBudget ? `${headlinePct.toFixed(1)}%` : '—'}</Typography>
                                                    </Box>

                                                    <Box sx={{ display: 'grid', gap: 1, pl: { xs: 0, md: 3 } }}>
                                                        {children.map((item, idx) => (
                                                            <Box key={item.key} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr 1fr 1fr 0.6fr' }, gap: 2, alignItems: 'center' }}>
                                                                {group.key === 'contingency' ? (
                                                                    <TextField
                                                                        label="Contingency item"
                                                                        value={item.label}
                                                                        onChange={(e) => {
                                                                            const v = e.target.value;
                                                                            setDrawdownSchedule((prev) => (Array.isArray(prev)
                                                                                ? prev.map((g) => {
                                                                                    if (g.key !== group.key) return g;
                                                                                    return {
                                                                                        ...g,
                                                                                        children: (Array.isArray(g.children) ? g.children : []).map((c) =>
                                                                                            c.key === item.key ? { ...c, label: v } : c
                                                                                        )
                                                                                    };
                                                                                })
                                                                                : prev));
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <Typography sx={{ fontStyle: 'italic', color: 'text.secondary' }}>{item.label}</Typography>
                                                                )}
                                                                <Typography sx={{ color: 'text.secondary' }}>{formatMoney(item.budget)}</Typography>
                                                                <Typography sx={{ fontWeight: 600 }}>
                                                                    {(() => {
                                                                        const val = getClaimedToDate(group.key, item.label, idx);
                                                                        return val ? formatMoney(val) : '—';
                                                                    })()}
                                                                </Typography>
                                                                <TextField
                                                                    label="Drawdown request"
                                                                    value={item.requested}
                                                                    onChange={(e) => {
                                                                        const v = e.target.value;
                                                                        setDrawdownSchedule((prev) => (Array.isArray(prev)
                                                                            ? prev.map((g) => {
                                                                                if (g.key !== group.key) return g;
                                                                                return {
                                                                                    ...g,
                                                                                    children: (Array.isArray(g.children) ? g.children : []).map((c) =>
                                                                                        c.key === item.key ? { ...c, requested: v } : c
                                                                                    )
                                                                                };
                                                                            })
                                                                            : prev));
                                                                    }}
                                                                />
                                                                <Typography sx={{ fontWeight: 600 }}>
                                                                    {(() => {
                                                                        const budget = parseMoneyLoose(item.budget);
                                                                        if (!budget) return '—';
                                                                        const requested = parseMoneyLoose(item.requested);
                                                                        const claimed = getClaimedToDate(group.key, item.label, idx);
                                                                        const pct = ((claimed + requested) / budget) * 100;
                                                                        return Number.isFinite(pct) ? `${pct.toFixed(1)}%` : '—';
                                                                    })()}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                    {group.key === 'contingency' ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', pl: { xs: 0, md: 3 } }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={() => {
                                                                    setDrawdownSchedule((prev) => (Array.isArray(prev)
                                                                        ? prev.map((g) => {
                                                                            if (g.key !== 'contingency') return g;
                                                                            const nextIdx = (Array.isArray(g.children) ? g.children.length : 0) + 1;
                                                                            const next = { key: `contingency_${nextIdx}`, label: '', budget: 0, requested: '' };
                                                                            return { ...g, children: [...(Array.isArray(g.children) ? g.children : []), next] };
                                                                        })
                                                                        : prev));
                                                                }}
                                                            >
                                                                Add contingency row
                                                            </Button>
                                                        </Box>
                                                    ) : null}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Paper>

                            <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                                    Total Requested
                                </Typography>
                                <Typography variant="h4" sx={{ fontWeight: 900 }}>
                                    {formatMoney(drawdownSchedule
                                        .flatMap((row) => Array.isArray(row?.children) ? row.children : [])
                                        .reduce((acc, row) => acc + parseMoneyLoose(row?.requested), 0))}
                                </Typography>
                                {overclaimDetected ? (
                                    <Typography variant="body2" sx={{ color: '#b91c1c', whiteSpace: 'pre-line' }}>
                                        {overclaimMessage}
                                    </Typography>
                                ) : null}
                                {drawdownRequestError ? (
                                    <Typography variant="body2" sx={{ color: '#b91c1c', whiteSpace: 'pre-line' }}>
                                        {drawdownRequestError}
                                    </Typography>
                                ) : null}
                                {drawdownRequestSuccess ? (
                                    <Typography variant="body2" sx={{ color: '#047857' }}>
                                        {drawdownRequestSuccess}
                                    </Typography>
                                ) : null}
                                <Button
                                    variant="contained"
                                    onClick={requestDrawdown}
                                    disabled={drawdownRequestBusy || !initialReport || overclaimDetected}
                                >
                                    {drawdownRequestBusy
                                        ? 'Requesting…'
                                        : `Request Drawdown ${Number(nextInterimNumber || 1) || 1}`}
                                </Button>
                            </Paper>
                        </Box>
                    ) : mode === 'developer' && view === 'settings' ? (
                        <Box sx={{ display: 'grid', gap: 2 }}>
                            <Paper sx={{ p: 2 }}>
                                <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                                    Developer Settings
                                </Typography>
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Configure how your developer portal behaves. These settings are local to your account.
                                </Typography>
                            </Paper>

                            <Paper id="settings-section-account" sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    Account details
                                </Typography>
                                <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
                                    <TextField
                                        label="Name"
                                        value={devPortalSettings.accountName}
                                        onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, accountName: e.target.value }))}
                                        size="small"
                                    />
                                    <TextField
                                        label="Contact number"
                                        value={devPortalSettings.contactNumber}
                                        onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, contactNumber: e.target.value }))}
                                        size="small"
                                    />
                                    <TextField
                                        label="Email address"
                                        type="email"
                                        value={devPortalSettings.contactEmail}
                                        onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, contactEmail: e.target.value }))}
                                        size="small"
                                    />
                                    <Stack direction="row" spacing={1} alignItems="center">
                                        <Button variant="contained" onClick={saveDeveloperContactDetails} disabled={devContactSaving}>
                                            {devContactSaving ? 'Saving…' : 'Save contact details'}
                                        </Button>
                                        {devContactMessage ? (
                                            <Typography variant="caption" sx={{ color: '#047857' }}>{devContactMessage}</Typography>
                                        ) : null}
                                        {devContactError ? (
                                            <Typography variant="caption" sx={{ color: '#b91c1c' }}>{devContactError}</Typography>
                                        ) : null}
                                    </Stack>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Updates are shared with the surveyor portal for this project.
                                    </Typography>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    Notifications
                                </Typography>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        checked={devPortalSettings.notifyReportIssued}
                                        onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, notifyReportIssued: e.target.checked }))}
                                    />
                                    <span>Notify when a report has been issued</span>
                                </label>
                            </Paper>

                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    Start Page
                                </Typography>
                                <Stack spacing={1.5} sx={{ maxWidth: 360 }}>
                                    <FormControl size="small">
                                        <InputLabel id="dev-start-page-label">Default landing view</InputLabel>
                                        <Select
                                            labelId="dev-start-page-label"
                                            label="Default landing view"
                                            value={devPortalSettings.defaultStart}
                                            onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, defaultStart: String(e.target.value) }))}
                                        >
                                            <MenuItem value="overview">Overview</MenuItem>
                                            <MenuItem value="reports">Projects</MenuItem>
                                            <MenuItem value="activity">Activity</MenuItem>
                                            <MenuItem value="request_valuation">Request Valuation</MenuItem>
                                            <MenuItem value="settings">Settings</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <input
                                            type="checkbox"
                                            checked={devPortalSettings.autoOpenLatestDrawdown}
                                            onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, autoOpenLatestDrawdown: e.target.checked }))}
                                        />
                                        <span>Auto-open the latest requested drawdown</span>
                                    </label>
                                </Stack>
                            </Paper>

                            <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                                    Email Summaries
                                </Typography>
                                <Stack spacing={1.5} sx={{ maxWidth: 360 }}>
                                    <FormControl size="small">
                                        <InputLabel id="dev-summary-label">Summary frequency</InputLabel>
                                        <Select
                                            labelId="dev-summary-label"
                                            label="Summary frequency"
                                            value={devPortalSettings.emailSummary}
                                            onChange={(e) => setDevPortalSettings((prev) => ({ ...prev, emailSummary: String(e.target.value) }))}
                                        >
                                            <MenuItem value="off">Off</MenuItem>
                                            <MenuItem value="weekly">Weekly</MenuItem>
                                            <MenuItem value="monthly">Monthly</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        Summaries include drawdown activity, outstanding questionnaires, and key report milestones.
                                    </Typography>
                                </Stack>
                            </Paper>
                        </Box>
                    ) : (
                        <>
                            <Stack spacing={2.25} sx={{ mb: 2.5 }}>
                                <AnalyticsPageHero
                                    title={(() => {
                                        const refNo = String(reference || data?.reference || '').trim();
                                        const projectName = String(data?.project_name || '').trim();
                                        if (refNo && projectName) return `${refNo} - ${projectName}`;
                                        return refNo || projectName || 'Project';
                                    })()}
                                    description={null}
                                    chips={[]}
                                    sx={(theme) => theme.palette.mode === 'dark' ? {
                                        background: `linear-gradient(135deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#172033', 0.96)} 100%)`,
                                        borderColor: alpha('#94a3b8', 0.18)
                                    } : {}}
                                    actions={(
                                        <Button variant="outlined" onClick={exitPortal}>
                                            Exit Portal
                                        </Button>
                                    )}
                                />

                                <ProjectPortalNavigation activeWorkspace={workspace} onChange={goToWorkspace} />

                                {generating || generationStage ? (
                                    <Paper sx={{ ...analyticsPanelSx, p: 0 }}>
                                        <ReportGenerationProgress generating={generating} stage={generationStageKey}
                                            stageLabel={generationStage} detail={generationDetail} steps={reportProgressRows} />
                                    </Paper>
                                ) : null}

                            </Stack>

                            <AnalyticsPanelSection
                                title={portalWorkspaceTitle}
                                bodySx={{
                                    display: 'grid',
                                    gap: 1.75,
                                    ...(workspace === 'project_record' ? {
                                        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: '170px minmax(0, 1fr)' },
                                        alignItems: 'start',
                                        '& > [data-project-record-nav="true"]': {
                                            gridColumn: 1,
                                            gridRow: { md: '1 / span 30' }
                                        },
                                        '& > :not([data-project-record-nav="true"])': {
                                            gridColumn: { xs: 1, md: 2 }
                                        }
                                    } : {})
                                }}
                                sx={(theme) => theme.palette.mode === 'dark' ? {
                                    background: `linear-gradient(180deg, ${alpha('#111827', 0.98)} 0%, ${alpha('#0f172a', 0.96)} 100%)`,
                                    borderColor: alpha('#94a3b8', 0.16)
                                } : {}}
                                headerSx={(theme) => theme.palette.mode === 'dark' ? {
                                    background: alpha('#111827', 0.78),
                                    borderColor: alpha('#94a3b8', 0.14)
                                } : {}}
                            >
                                {loading && <Alert severity="info">Loading portal data…</Alert>}
                                {!loading && error && (
                                    <Alert severity="error">Unable to load portal data: {error}</Alert>
                                )}

                                {!loading && !error && data && (
                                    <>
                                        {showKeyInfoSurface && (
                                            <Box
                                                sx={{
                                                    p: { xs: 1.1, sm: 1.35 },
                                                    borderRadius: 3,
                                                    background: (theme) => theme.palette.mode === 'dark'
                                                        ? `linear-gradient(180deg, ${alpha('#1e293b', 0.9)} 0%, ${alpha('#111827', 0.86)} 100%)`
                                                        : `linear-gradient(180deg, ${alpha('#ffffff', 0.88)} 0%, ${alpha('#f8fbff', 0.82)} 100%)`,
                                                    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.16) : alpha('#0f172a', 0.06)}`
                                                }}
                                            >
                                                <Box sx={{ display: 'grid', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                            Key Information
                                                        </Typography>
                                                    </Box>

                                                    <Box
                                                        sx={{
                                                            display: 'grid',
                                                            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, minmax(0, 1fr))' },
                                                            gap: 0.8
                                                        }}
                                                    >
                                                        {keyInfoSnapshot.map((item) => (
                                                            <Box
                                                                key={`snapshot-${item.label}`}
                                                                sx={{
                                                                    p: 0.85,
                                                                    borderRadius: 2,
                                                                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? alpha('#0f172a', 0.72) : alpha('#ffffff', 0.76),
                                                                    border: (theme) => `1px solid ${theme.palette.mode === 'dark' ? alpha('#94a3b8', 0.14) : alpha('#0f172a', 0.06)}`,
                                                                    minWidth: 0
                                                                }}
                                                            >
                                                                <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                                                    {item.label}
                                                                </Typography>
                                                                <Typography
                                                                    title={item.value || '—'}
                                                                    sx={{
                                                                        mt: 0.35,
                                                                        fontSize: '0.82rem',
                                                                        fontWeight: 700,
                                                                        color: 'text.primary',
                                                                        overflow: 'hidden',
                                                                        textOverflow: 'ellipsis',
                                                                        whiteSpace: 'nowrap'
                                                                    }}
                                                                >
                                                                    {item.value || '—'}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>

                                                </Box>
                                            </Box>
                                        )}

                                        {workspace === 'overview' ? (
                                            <ProjectPortalOverview
                                                projectRows={overviewProjectRows}
                                                reporting={overviewReporting}
                                                periods={overviewPeriods}
                                                attentionItems={overviewAttentionItems}
                                                onSelectPeriod={(period) => setQuery({ workspace: 'reporting_periods', page: period.page, interim: period.interim })}
                                                onGenerate={() => openAppendixModal(page === 'interim' ? 'interim' : 'initial')}
                                                onOpenReport={onViewReport}
                                                generating={generating}
                                                openingReport={viewReportBusy}
                                            />
                                        ) : null}

                                        {workspace === 'project_record' ? (
                                            <ProjectRecordNavigation activePage={page} onChange={goToProjectRecordSection} />
                                        ) : null}

                                        {workspace === 'project_record' ? (
                                            <ReportEvidenceEditor reference={reference} mode={mode} code={code} reportInstance="project"
                                                location={activeProjectRecordSection} authHeaders={authHeaders} />
                                        ) : null}

                                        {workspace === 'reporting_periods' && (isInitialView || isInterimView) ? (
                                            <ReportEvidenceEditor reference={reference} mode={mode} code={code}
                                                reportInstance={reportingSelectedInstance} authHeaders={authHeaders} />
                                        ) : null}

                                        {workspace === 'project_record' && activeProjectRecordSection === 'site_scheme' ? (
                                            <ProjectRecordDetail
                                                title="Site & scheme"
                                                subtitle="Stored site and development information. Reviewed evidence records are retained separately."
                                                rows={projectRecordSiteRows}
                                                onEdit={() => openSurveyorQuestionnaire({ reportType: 'initial' })}
                                            />
                                        ) : null}

                                        {workspace === 'project_record' && activeProjectRecordSection === 'conditions' ? (
                                            <ProjectRecordDetail
                                                title="Planning & statutory"
                                                subtitle="Planning applications and statutory records. Submission, approval and factual review remain distinct."
                                                rows={projectRecordPlanningRows}
                                                onEdit={() => openSurveyorQuestionnaire({ reportType: 'initial' })}
                                            />
                                        ) : null}

                                        {workspace === 'project_record' && activeProjectRecordSection === 'procurement' ? (
                                            <ProjectRecordDetail
                                                title="Procurement & contract"
                                                subtitle="Recorded procurement route, contractor and contract particulars."
                                                rows={projectRecordProcurementRows}
                                                onEdit={() => openSurveyorQuestionnaire({ reportType: 'initial' })}
                                            />
                                        ) : null}

                                        {workspace === 'project_record' && activeProjectRecordSection === 'approved_evidence' ? (
                                            approvedIntroductionsLoading ? (
                                                <Alert severity="info">Loading approved project evidence...</Alert>
                                            ) : approvedIntroductionsError ? (
                                                <Alert severity="error">{approvedIntroductionsError}</Alert>
                                            ) : approvedIntroductionRows.length ? (
                                                <ProjectRecordDetail
                                                    title="Approved evidence"
                                                    subtitle="Accepted extraction evidence retained across reporting periods; factual decisions remain separate."
                                                    rows={approvedIntroductionRows}
                                                />
                                            ) : (
                                                <Alert severity="info">No extracted project details have been approved yet.</Alert>
                                            )
                                        ) : null}

                                        {workspace === 'project_record' && projectRecordEvidenceRows.length ? (
                                            <ProjectRecordDetail
                                                title="Linked evidence"
                                                subtitle="Supporting uploads remain available here and in the central Documents workspace."
                                                rows={projectRecordEvidenceRows}
                                            />
                                        ) : null}

                                        {mode === 'surveyor' && (isInitialView || isInterimView) ? (
                                            <ReportingPeriodWorkspace
                                                periods={reportingPeriods}
                                                gates={reportingPeriodGates}
                                                captureMethods={reportingCaptureMethods}
                                                readiness={reportingReadiness}
                                                generation={reportingGeneration}
                                                onSelectPeriod={(period) => setQuery({ workspace: 'reporting_periods', page: period.page, interim: period.interim })}
                                                onOpenRisk={goToRiskTracker}
                                            />
                                        ) : null}

                                        <DataIntroductionDialog
                                            open={Boolean(dataIntroduction)}
                                            onClose={() => setDataIntroduction(null)}
                                            onProposalReviewed={(proposal) => {
                                                if (proposal?.review_status === 'accepted') setApprovedIntroductionsReloadKey((value) => value + 1);
                                            }}
                                            reference={reference}
                                            reportInstance={dataIntroduction?.reportInstance || reportingSelectedInstance}
                                            intakeType={dataIntroduction?.intakeType || 'ai_review'}
                                        />

                                        <Box
                                            sx={{
                                                display: workspace === 'reporting_periods' && mode !== 'surveyor' ? 'grid' : 'none',
                                                gap: 1.4,
                                                mb: 0.5,
                                                p: { xs: 1.35, sm: 1.6 },
                                                borderRadius: 3,
                                                backgroundColor: alpha('#ffffff', 0.62),
                                                border: `1px solid ${alpha('#0f172a', 0.06)}`
                                            }}
                                        >
                                            {/* Reports row */}
                                            <div style={{ display: workspace === 'reporting_periods' ? 'flex' : 'none', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                                                {mode === 'surveyor' && siteVisitState?.can_manage === true && siteVisitState?.appointment ? (
                                                    <Button size="small" variant="outlined" onClick={() => setSiteVisitOpen(true)}>
                                                        Site Visit · {new Date(siteVisitState.appointment.starts_at).toLocaleDateString('en-GB')}
                                                    </Button>
                                                ) : null}

                                                {mode === 'surveyor'
                                                    && siteVisitState?.can_manage === true
                                                    && siteVisitState?.appointment?.report_instance === 'initial'
                                                    && new Date(siteVisitState.appointment.starts_at).getTime() <= Date.now() ? (
                                                    <Button size="small" variant="outlined" onClick={() => void toggleInitialSlaHold()} disabled={siteVisitBusy}>
                                                        {siteVisitState?.sla_hold?.active ? 'Resume Initial SLA' : 'Pause Initial SLA'}
                                                    </Button>
                                                ) : null}

                                                {!initialConfirmedLive || !isInProgressInterimPortal ? (
                                                    <Button
                                                        size="small"
                                                        variant={page === 'initial' ? 'contained' : 'outlined'}
                                                        onClick={goToInitialReport}
                                                    >
                                                        Initial Report
                                                    </Button>
                                                ) : null}

                                                {mode === 'surveyor' ? (
                                                    <Button
                                                        size="small"
                                                        color="warning"
                                                        variant={page === 'risk' ? 'contained' : 'outlined'}
                                                        onClick={goToRiskTracker}
                                                    >
                                                        Risk Tracker
                                                    </Button>
                                                ) : null}

                                                {mode === 'surveyor' && hasInitialIssuedPdf && !initialConfirmedLive ? (
                                                    <Button size="small" variant="contained" color="success" onClick={openConfirmLive}>
                                                        Confirm Live
                                                    </Button>
                                                ) : null}

                                                {mode === 'surveyor' && isInProgressInterimPortal && !initialConfirmedLive ? (
                                                    <Button size="small" variant="contained" color="success" onClick={openInProgressSetup}>
                                                        In-Progress Setup
                                                    </Button>
                                                ) : null}

                                                {canShowInterimNav && (
                                                    <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', alignItems: 'center', flex: { xs: '1 1 100%', sm: '0 1 auto' } }}>
                                                        {availableInterimNumbers.map((n) => {
                                                            const active = page === 'interim' && n && currentInterimNumber === n;
                                                            const hasSubmission = Array.isArray(interimSubmissions)
                                                                && interimSubmissions.some((it) => Number(it?.interim_number || 0) === n);
                                                            const isRequestedOnly = !hasSubmission && requestedInterims.includes(n);
                                                            const interimStatus = hasSubmission
                                                                ? 'Submitted'
                                                                : (isRequestedOnly ? 'Requested' : 'Pending Questionnaire');
                                                            return (
                                                                <Button
                                                                    key={`interim-${n}`}
                                                                    size="small"
                                                                    color="success"
                                                                    variant={active ? 'contained' : 'outlined'}
                                                                    onClick={() => goToInterimReport(n)}
                                                                    title={`Interim ${n}: ${interimStatus}`}
                                                                >
                                                                    Interim {n} · {interimStatus}
                                                                </Button>
                                                            );
                                                        })}
                                                    </Box>
                                                )}

                                                {mode === 'surveyor' && siteVisitState?.can_manage === true && initialConfirmedLive && !siteVisitState?.appointment && siteVisitState?.eligible ? (
                                                    <Button
                                                        size="small"
                                                        variant="contained"
                                                        onClick={() => setSiteVisitOpen(true)}
                                                    >
                                                        {siteVisitState.report_instance === 'initial' ? 'Book Initial Visit' : 'Book Next Valuation Visit'}
                                                    </Button>
                                                ) : null}
                                            </div>

                                            {siteVisitError ? <Alert severity="warning">{siteVisitError}</Alert> : null}

                                            <SiteVisitDialog
                                                open={siteVisitOpen}
                                                onClose={() => setSiteVisitOpen(false)}
                                                onSaved={(appointment) => void handleSiteVisitSaved(appointment)}
                                                projects={[{
                                                    reference,
                                                    project_name: data?.project_name,
                                                    site_address: data?.site_address,
                                                    lead_surveyor: data?.lead_surveyor
                                                }]}
                                                initialProjectReference={reference}
                                                initialAppointment={siteVisitState?.appointment || null}
                                                source="information_portal"
                                            />

                                            {/* Divider under report buttons */}
                                            <Box sx={{ display: workspace === 'reporting_periods' ? 'block' : 'none', height: 1, width: '100%', background: alpha('#0f172a', 0.08) }} />

                                        </Box>

                                        <Dialog open={confirmLiveOpen} onClose={() => !confirmLiveSaving && setConfirmLiveOpen(false)} fullWidth maxWidth="md">
                                            <DialogTitle>{confirmLiveTarget.reportType === 'interim' ? 'In-Progress Project Setup' : 'Confirm Live'}</DialogTitle>
                                            <DialogContent>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                                                    {confirmLiveTarget.reportType === 'interim'
                                                        ? `Confirm the programme and development-cost baseline from Interim Report ${confirmLiveTarget.interimNumber}. Interim Report ${Number(confirmLiveTarget.interimNumber) + 1} will become available after this is saved.`
                                                        : 'Confirm the Initial Report programme and development-cost baseline. Interim Report 1 will become available only after this is saved.'}
                                                </Typography>
                                                {confirmLiveError ? <Alert severity="error" sx={{ mb: 2 }}>{confirmLiveError}</Alert> : null}
                                                <Typography sx={{ mb: 1, fontWeight: 900 }}>Programme</Typography>
                                                <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" type="month" label="Works start month" InputLabelProps={{ shrink: true }} value={confirmLiveDraft?.programme?.worksStartMonth || ''} onChange={(event) => updateConfirmLiveProgramme('worksStartMonth', event.target.value)} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" type="number" label="Borrower duration (months)" value={confirmLiveDraft?.programme?.borrowerDurationMonths || ''} onChange={(event) => updateConfirmLiveProgramme('borrowerDurationMonths', event.target.value)} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={4}>
                                                        <TextField fullWidth size="small" type="number" label="Surveyor duration (months)" value={confirmLiveDraft?.programme?.surveyorDurationMonths || ''} onChange={(event) => updateConfirmLiveProgramme('surveyorDurationMonths', event.target.value)} />
                                                    </Grid>
                                                </Grid>
                                                <Typography sx={{ mb: 1, fontWeight: 900 }}>Development Costs</Typography>
                                                {(() => {
                                                    const sections = [
                                                        ['constructionBudgetRows', 'Construction Costs'],
                                                        ['contingencyBudgetRows', 'Contingency'],
                                                        ['professionalFeeBudgetRows', 'Professional Fees'],
                                                        ['additionalCostBudgetRows', 'Additional Costs']
                                                    ].map(([sectionKey, title]) => {
                                                        const rows = Array.isArray(confirmLiveDraft?.developmentCosts?.[sectionKey]) ? confirmLiveDraft.developmentCosts[sectionKey] : [];
                                                        const isContingency = sectionKey === 'contingencyBudgetRows';
                                                        return {
                                                            sectionKey,
                                                            title,
                                                            rows,
                                                            isContingency,
                                                            budgetTotal: isContingency
                                                                ? toPortalMoney(confirmLiveDraft?.developmentCosts?.contingencyBudgetTotal)
                                                                : rows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0),
                                                            costToDateTotal: rows.reduce((sum, row) => sum + toPortalMoney(row?.costToDate), 0)
                                                        };
                                                    });
                                                    const formatConfirmLiveMoney = (value) => toPortalMoney(value).toLocaleString('en-GB', {
                                                        style: 'currency',
                                                        currency: 'GBP',
                                                        maximumFractionDigits: 0
                                                    });
                                                    const confirmLiveInputSx = {
                                                        width: '100%',
                                                        '& .MuiInputBase-root': { fontSize: 14, fontWeight: 700 },
                                                        '& .MuiInputBase-input': { py: 0.75, textAlign: 'right' },
                                                        '& .MuiInputAdornment-root': { mr: 0.25 },
                                                        '& .MuiInput-root:before': { borderBottomColor: '#cbd5e1' }
                                                    };
                                                    const renderConfirmLiveInput = ({ label, value, onChange }) => (
                                                        <TextField
                                                            type="number"
                                                            variant="standard"
                                                            value={value ?? ''}
                                                            onChange={onChange}
                                                            inputProps={{ 'aria-label': label }}
                                                            InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                            sx={confirmLiveInputSx}
                                                        />
                                                    );
                                                    const overallBudget = sections.reduce((sum, section) => sum + section.budgetTotal, 0);
                                                    const overallCostToDate = sections.reduce((sum, section) => sum + section.costToDateTotal, 0);

                                                    return (
                                                        <TableContainer sx={{ border: '1px solid #d1d5db', overflowX: 'auto' }}>
                                                            <Table size="small" sx={{ minWidth: 700, tableLayout: 'fixed' }}>
                                                                <TableHead>
                                                                    <TableRow sx={{ bgcolor: '#e5e7eb' }}>
                                                                        <TableCell sx={{ width: '46%', py: 1.25, fontWeight: 900, fontSize: 14 }}>Description</TableCell>
                                                                        <TableCell align="right" sx={{ width: '27%', py: 1.25, fontWeight: 900, fontSize: 14, whiteSpace: 'nowrap' }}>Original Development Budget</TableCell>
                                                                        <TableCell align="right" sx={{ width: '27%', py: 1.25, fontWeight: 900, fontSize: 14, whiteSpace: 'nowrap' }}>Cost to Date</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {sections.map((section) => (
                                                                        <React.Fragment key={section.sectionKey}>
                                                                            <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                                                <TableCell sx={{ py: 1.2, fontWeight: 900, fontSize: 14 }}>{section.title}</TableCell>
                                                                                <TableCell align="right" sx={{ py: section.isContingency ? 0.4 : 1.2, fontWeight: 900, fontSize: 14 }}>
                                                                                    {section.isContingency
                                                                                        ? renderConfirmLiveInput({
                                                                                            label: 'Contingency original development budget',
                                                                                            value: confirmLiveDraft?.developmentCosts?.contingencyBudgetTotal,
                                                                                            onChange: (event) => setConfirmLiveDraft((previous) => ({ ...(previous || {}), developmentCosts: { ...(previous?.developmentCosts || {}), contingencyBudgetTotal: event.target.value } }))
                                                                                        })
                                                                                        : formatConfirmLiveMoney(section.budgetTotal)}
                                                                                </TableCell>
                                                                                <TableCell align="right" sx={{ py: 1.2, fontWeight: 900, fontSize: 14 }}>{formatConfirmLiveMoney(section.costToDateTotal)}</TableCell>
                                                                            </TableRow>
                                                                            {section.rows.map((row, index) => (
                                                                                <TableRow key={`${section.sectionKey}-${index}`}>
                                                                                    <TableCell sx={{ pl: 3, py: 0.8, fontWeight: 700 }}>{row?.item || `Item ${index + 1}`}</TableCell>
                                                                                    <TableCell align="right" sx={{ py: 0.4 }}>
                                                                                        {section.isContingency ? '—' : renderConfirmLiveInput({
                                                                                            label: `${row?.item || `Item ${index + 1}`} original development budget`,
                                                                                            value: row?.budget,
                                                                                            onChange: (event) => updateConfirmLiveCostRow(section.sectionKey, index, 'budget', event.target.value)
                                                                                        })}
                                                                                    </TableCell>
                                                                                    <TableCell align="right" sx={{ py: 0.4 }}>
                                                                                        {renderConfirmLiveInput({
                                                                                            label: `${row?.item || `Item ${index + 1}`} cost to date`,
                                                                                            value: row?.costToDate,
                                                                                            onChange: (event) => updateConfirmLiveCostRow(section.sectionKey, index, 'costToDate', event.target.value)
                                                                                        })}
                                                                                    </TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </React.Fragment>
                                                                    ))}
                                                                    <TableRow sx={{ bgcolor: '#e5e7eb' }}>
                                                                        <TableCell sx={{ py: 1.25, fontWeight: 900, fontSize: 14 }}>Total Development Cost (excl VAT)</TableCell>
                                                                        <TableCell align="right" sx={{ py: 1.25, fontWeight: 900, fontSize: 14 }}>{formatConfirmLiveMoney(overallBudget)}</TableCell>
                                                                        <TableCell align="right" sx={{ py: 1.25, fontWeight: 900, fontSize: 14 }}>{formatConfirmLiveMoney(overallCostToDate)}</TableCell>
                                                                    </TableRow>
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    );
                                                })()}
                                                <Grid container spacing={1.5} sx={{ mt: 1 }}>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" type="number" label="Previous drawdowns" value={confirmLiveDraft?.developmentCosts?.previousDrawdownRows?.[0]?.amount || ''} onChange={(event) => setConfirmLiveDraft((previous) => ({ ...(previous || {}), developmentCosts: { ...(previous?.developmentCosts || {}), previousDrawdownRows: [{ item: 'Previous Drawdowns', amount: event.target.value }] } }))} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField fullWidth size="small" type="number" label="Borrower input" value={confirmLiveDraft?.developmentCosts?.borrowerInputAmount || ''} onChange={(event) => setConfirmLiveDraft((previous) => ({ ...(previous || {}), developmentCosts: { ...(previous?.developmentCosts || {}), borrowerInputRelevant: toPortalMoney(event.target.value) > 0, borrowerInputAmount: event.target.value } }))} InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }} />
                                                    </Grid>
                                                </Grid>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button onClick={() => setConfirmLiveOpen(false)} disabled={confirmLiveSaving}>Cancel</Button>
                                                <Button variant="contained" color="success" onClick={() => void saveConfirmLive()} disabled={confirmLiveSaving}>{confirmLiveSaving ? 'Confirming…' : 'Confirm Live'}</Button>
                                            </DialogActions>
                                        </Dialog>

                                        {(isInitialView || isInterimView) && mode !== 'surveyor' && (
                                            <Box
                                                sx={{
                                                    display: 'grid',
                                                    gap: 1,
                                                    gridTemplateColumns: { xs: '1fr', lg: mode === 'surveyor' ? 'minmax(0, 0.95fr) minmax(0, 1.05fr)' : '1fr' },
                                                    alignItems: 'start'
                                                }}
                                            >
                                                <Box sx={{ display: 'grid', gap: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                            Report Status
                                                        </Typography>
                                                    </Box>

                                                    <TableContainer
                                                        component={Paper}
                                                        variant="outlined"
                                                        sx={{ borderRadius: 2.5, borderColor: alpha('#0f172a', 0.08), backgroundColor: alpha('#ffffff', 0.86) }}
                                                    >
                                                        <Table size="small" aria-label="Readiness and alerts actions">
                                                            <TableBody>
                                                                <TableRow>
                                                                    <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Readiness</TableCell>
                                                                    <TableCell
                                                                        title={readinessError || ''}
                                                                        sx={{
                                                                            color: readinessError ? '#b91c1c' : '#475569',
                                                                            fontWeight: 700,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        {readinessLoading ? (
                                                                            'Loading...'
                                                                        ) : readinessError ? (
                                                                            readinessError
                                                                        ) : (
                                                                            (() => {
                                                                                const r = readiness;
                                                                                const colors = readinessBadgeColor(r?.status);
                                                                                const label = String(r?.status || '').toUpperCase() || '-';
                                                                                return (
                                                                                    <Box
                                                                                        title={formatReadinessTooltip(r)}
                                                                                        sx={{
                                                                                            display: 'inline-flex',
                                                                                            alignItems: 'center',
                                                                                            justifyContent: 'center',
                                                                                            px: 1.25,
                                                                                            py: 0.35,
                                                                                            borderRadius: 999,
                                                                                            backgroundColor: colors.bg,
                                                                                            color: colors.fg,
                                                                                            fontWeight: 900,
                                                                                            fontSize: 12,
                                                                                            letterSpacing: 0.7
                                                                                        }}
                                                                                    >
                                                                                        {label}
                                                                                    </Box>
                                                                                );
                                                                            })()
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={() => setReadinessReloadKey((prev) => prev + 1)}
                                                                            sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                        >
                                                                            Refresh
                                                                        </Button>
                                                                    </TableCell>
                                                                </TableRow>

                                                                <TableRow>
                                                                    <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Alerts</TableCell>
                                                                    <TableCell
                                                                        title={aiAlertsError || ''}
                                                                        sx={{
                                                                            color: aiAlertsError ? '#b91c1c' : '#475569',
                                                                            fontWeight: 700,
                                                                            whiteSpace: 'nowrap',
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis'
                                                                        }}
                                                                    >
                                                                        {aiAlertsLoading ? (
                                                                            'Scanning...'
                                                                        ) : aiAlertsError ? (
                                                                            aiAlertsError
                                                                        ) : (
                                                                            (() => {
                                                                                const list = Array.isArray(aiAlerts?.alerts) ? aiAlerts.alerts : [];
                                                                                return list.length ? `${list.length} alert${list.length === 1 ? '' : 's'}` : 'No alerts';
                                                                            })()
                                                                        )}
                                                                    </TableCell>
                                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                        <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'wrap' }}>
                                                                            <Button
                                                                                type="button"
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => {
                                                                                    setShowAiAlerts(true);
                                                                                    if (!aiAlerts) loadAiAlerts({ force: false });
                                                                                }}
                                                                                disabled={aiAlertsLoading || !!aiAlertsError}
                                                                                sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                            >
                                                                                View Alerts
                                                                            </Button>
                                                                            <Button
                                                                                type="button"
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => loadAiAlerts({ force: true })}
                                                                                disabled={aiAlertsLoading}
                                                                                sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                            >
                                                                                Refresh
                                                                            </Button>
                                                                        </Stack>
                                                                    </TableCell>
                                                                </TableRow>

                                                                {mode === 'surveyor' && (
                                                                    <TableRow>
                                                                        <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Generate Report</TableCell>
                                                                        <TableCell
                                                                            title={isInterimView && !currentInterimHasSubmission
                                                                                ? `Complete Interim Report ${currentInterimNumber} questionnaire first`
                                                                                : (isInterimView ? `Ready for Interim Report ${currentInterimNumber}` : 'Ready for Initial Report')}
                                                                            sx={{
                                                                                color: '#475569',
                                                                                fontWeight: 700,
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                        >
                                                                            {isInterimView && !currentInterimHasSubmission
                                                                                ? 'Questionnaire required before generating'
                                                                                : (isInterimView ? `Ready for Interim Report ${currentInterimNumber}` : 'Ready for Initial Report')}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={() => openAppendixModal(isInterimView ? 'interim' : 'initial')}
                                                                                disabled={(() => {
                                                                                    if (generating) return true;
                                                                                    if (isInterimView) return !currentInterimHasSubmission;
                                                                                    return false;
                                                                                })()}
                                                                                title={isInterimView && !currentInterimHasSubmission ? `Complete Interim Report ${currentInterimNumber} questionnaire first` : ''}
                                                                                startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
                                                                                size="small"
                                                                                sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                            >
                                                                                {generating
                                                                                    ? 'Generating...'
                                                                                    : (isInterimView ? `Generate Interim Report ${currentInterimNumber}` : 'Generate Initial Report')}
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                )}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </Box>

                                                {mode === 'surveyor' && (
                                                    <Box sx={{ display: 'grid', gap: 1 }}>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                            <Typography sx={{ fontSize: '0.74rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                                                                Report Actions
                                                            </Typography>
                                                            {canViewReportDiagnostics(mode, decodeJwtPayload(authToken)) && (
                                                                <ReportDiagnostics
                                                                    key={`${reference}|${isInterimView ? `interim:${currentInterimNumber}` : 'initial'}`}
                                                                    isAdmin
                                                                    reference={reference}
                                                                    reportInstance={isInterimView ? `interim:${currentInterimNumber}` : 'initial'}
                                                                    authHeaders={authHeaders}
                                                                />
                                                            )}
                                                        </Box>

                                                        <TableContainer
                                                            component={Paper}
                                                            variant="outlined"
                                                            sx={{ borderRadius: 2.5, borderColor: alpha('#0f172a', 0.08), backgroundColor: alpha('#ffffff', 0.86) }}
                                                        >
                                                            <Table size="small" aria-label="Report actions" sx={{ tableLayout: 'fixed', width: '100%' }}>
                                                                <TableBody>
                                                                    <TableRow>
                                                                        <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Report File</TableCell>
                                                                        <TableCell
                                                                            title={activeDocxEntry?.download_url ? formatRevisionLabel(activeDocxEntry) : 'No report generated yet'}
                                                                            sx={{
                                                                                color: '#475569',
                                                                                fontWeight: 700,
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                        >
                                                                            {activeDocxEntry?.download_url ? formatRevisionLabel(activeDocxEntry) : 'No report generated yet'}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 250 }}>
                                                                            <Button size="small" variant="outlined" onClick={onViewReport} disabled={!activeDocxEntry?.download_url || viewReportBusy} title={!activeDocxEntry?.download_url ? 'No report generated yet' : ''} sx={{ textTransform: 'none', fontWeight: 800 }}>
                                                                                {viewReportBusy ? 'Opening...' : 'View Report'}
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>

                                                                    <TableRow>
                                                                        <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Issue Workflow</TableCell>
                                                                        <TableCell
                                                                            title={activeDocxEntry?.issue_pdf_uploaded_at ? formatIssuedLabel(activeDocxEntry) : 'Issued PDF not uploaded yet'}
                                                                            sx={{
                                                                                color: activeDocxEntry?.issue_pdf_uploaded_at ? '#065f46' : '#475569',
                                                                                fontWeight: 700,
                                                                                whiteSpace: 'nowrap',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis'
                                                                            }}
                                                                        >
                                                                            {activeDocxEntry?.issue_pdf_uploaded_at ? formatIssuedLabel(activeDocxEntry) : 'Issued PDF not uploaded yet'}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 250 }}>
                                                                            <Button
                                                                                size="small"
                                                                                variant={activeDocxEntry?.issue_pdf_uploaded_at ? 'outlined' : 'contained'}
                                                                                onClick={() => {
                                                                                    setIssueUploadError('');
                                                                                    setIssueUploadSuccess('');
                                                                                    setIssueSendToFunder(false);
                                                                                    setShowQaUpload(true);
                                                                                }}
                                                                                disabled={!activeDocxEntry?.download_url}
                                                                                title={!activeDocxEntry?.download_url ? 'Generate a report first' : ''}
                                                                                sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                            >
                                                                                Issue Report
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>

                                                                    <TableRow>
                                                                        <TableCell sx={{ width: 180, fontWeight: 800, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Delivery</TableCell>
                                                                        <TableCell sx={{ color: reportSentTo ? '#065f46' : '#475569', fontWeight: 700 }}>
                                                                            {reportSentTo ? `Sent to ${reportSentTo}` : (activeDocxEntry?.issue_pdf_download_url ? 'Ready to send' : 'N/A')}
                                                                        </TableCell>
                                                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: 250 }}>
                                                                            {activeDocxEntry?.issue_pdf_download_url ? (
                                                                                <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ flexWrap: 'nowrap' }}>
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant={!reportSentTo ? 'contained' : 'outlined'}
                                                                                        disabled={sendReportBusy || !!reportSentTo}
                                                                                        onClick={() => sendReportToClient()}
                                                                                        sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                                    >
                                                                                        {sendReportBusy ? 'Sending…' : reportSentTo ? 'Sent' : 'Send Report'}
                                                                                    </Button>
                                                                                    <Button
                                                                                        size="small"
                                                                                        variant="outlined"
                                                                                        onClick={() => {
                                                                                            const url = getIssuePdfUrl();
                                                                                            if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                                                                        }}
                                                                                        sx={{ textTransform: 'none', fontWeight: 800 }}
                                                                                    >
                                                                                        Download PDF
                                                                                    </Button>
                                                                                </Stack>
                                                                            ) : (
                                                                                <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 700 }}>
                                                                                    N/A
                                                                                </Typography>
                                                                            )}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}

                                        {mode === 'surveyor' && workspace === 'documents' && (
                                            <Box
                                                component="section"
                                                aria-label="Project documents"
                                                sx={{ display: 'grid', gap: 1, pt: 0.5, maxWidth: { xs: '100vw', sm: 'none' } }}
                                            >
                                                <ProjectRepositoryExplorer reference={reference} embedded />
                                            </Box>
                                        )}

                                        {interimSubmissionsWarning && (
                                            <Alert severity="warning" sx={{ mb: 0.5 }}>
                                                <strong>Interim reports:</strong> {interimSubmissionsWarning}
                                            </Alert>
                                        )}

                                        {mode === 'surveyor' && issueUploadError && (
                                            <Alert severity="error" sx={{ mt: 1.25 }}>
                                                Report action failed: {issueUploadError}
                                            </Alert>
                                        )}

                                        {mode === 'surveyor' && issueUploadSuccess && (
                                            <Alert severity="success" sx={{ mt: 1.25 }}>
                                                Report updated: {issueUploadSuccess}
                                            </Alert>
                                        )}

                                        {mode === 'surveyor' && collaboraSession?.launch_form?.action && (
                                            <Dialog
                                                key={collaboraSession.launch_form.fields.access_token}
                                                open={collaboraEditorOpen}
                                                onClose={closeCollaboraEditor}
                                                fullScreen
                                                PaperProps={{ sx: { background: '#f3f3f3' } }}
                                            >
                                                <Box sx={{ height: '100vh', display: 'grid', gridTemplateRows: 'auto 1fr', minHeight: 0 }}>
                                                    <Box sx={{ px: 1.5, py: 0.75, background: '#f7f7f7', color: '#1f2933', borderBottom: '1px solid #c8c8c8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                                                        <Box sx={{ minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25 }} noWrap>
                                                                Monsurv Report Editor — {reference}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ color: '#5f6368' }} noWrap>
                                                                {activeDocxEntry?.file_name || collaboraSession?.working_report?.file_name || 'Working report'}
                                                            </Typography>
                                                        </Box>
                                                        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                onClick={closeCollaboraEditor}
                                                                sx={{ minHeight: 28, color: '#1f2933', borderColor: '#b8b8b8', background: '#fff', textTransform: 'none', fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                                                            >
                                                                Close
                                                            </Button>
                                                        </Stack>
                                                    </Box>

                                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '348px minmax(0, 1fr)' }, gap: 0, minHeight: 0 }}>
                                                        <Paper
                                                            variant="outlined"
                                                            sx={{
                                                                height: '100%',
                                                                minHeight: 0,
                                                                overflow: 'auto',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                borderRadius: 0,
                                                                borderTop: 0,
                                                                borderBottom: 0,
                                                                borderLeft: 0,
                                                                borderColor: '#c8c8c8',
                                                                background: '#f7f7f7'
                                                            }}
                                                        >
                                                            <Stack spacing={0} sx={{ minHeight: '100%', width: '100%' }}>
                                                                <Box sx={{ px: 1.25, height: 35, borderBottom: '1px solid #005f90', background: '#006AA0', display: 'flex', alignItems: 'center' }}>
                                                                    <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#ffffff' }}>
                                                                        Monsurv Actions
                                                                    </Typography>
                                                                </Box>

                                                                <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                    {monsurvEditorNotice ? (
                                                                        <Alert severity="info" variant="outlined" sx={{ minHeight: 63, py: 0.75, px: 1, fontSize: 12, background: '#fff', alignItems: 'center', '& .MuiAlert-icon': { fontSize: 17, mr: 0.75, alignItems: 'center' } }}>
                                                                            {monsurvEditorNotice}
                                                                        </Alert>
                                                                    ) : null}

                                                                    {monsurvActionError ? (
                                                                        <Alert severity="error" variant="outlined" sx={{ py: 0.25, px: 1, fontSize: 12, background: '#fff', '& .MuiAlert-icon': { fontSize: 17, mr: 0.75 } }}>
                                                                            {monsurvActionError}
                                                                        </Alert>
                                                                    ) : null}

                                                                </Box>

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => {
                                                                        const willOpen = !monsurvPanelOpen.versionControl;
                                                                        toggleMonsurvPanelSection('versionControl');
                                                                        if (willOpen && !documentSearchFolders.length && !documentSearchLoading) loadDocumentSearch();
                                                                    }}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Version Control</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.versionControl ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.versionControl ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Typography sx={{ fontSize: 11.5, color: '#5f6368', lineHeight: 1.35 }}>
                                                                            Working report versions stored in S3.
                                                                        </Typography>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            onClick={loadDocumentSearch}
                                                                            disabled={documentSearchLoading}
                                                                            sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, justifySelf: 'start', '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                                                                        >
                                                                            {documentSearchLoading ? 'Loading...' : 'Refresh Documents'}
                                                                        </Button>
                                                                        {documentSearchError ? (
                                                                            <Alert severity="error" variant="outlined" sx={{ py: 0.25, px: 1, fontSize: 12, background: '#fff', '& .MuiAlert-icon': { fontSize: 17, mr: 0.75 } }}>
                                                                                {documentSearchError}
                                                                            </Alert>
                                                                        ) : null}
                                                                        {versionControlPath ? (
                                                                            <Typography sx={{ fontSize: 10.5, color: '#5f6368', wordBreak: 'break-all' }}>{versionControlPath}</Typography>
                                                                        ) : null}
                                                                        {versionControlItems.length ? (
                                                                            <Box sx={{ border: '1px solid #d6d6d6', background: '#fff', borderRadius: 1, overflow: 'hidden' }}>
                                                                                {versionControlItems.map((item) => (
                                                                                    <Box key={item.key || item.name} sx={{ px: 1, py: 0.75, borderTop: '1px solid #eeeeee', display: 'grid', gap: 0.25 }}>
                                                                                        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
                                                                                            <Typography title={item.key} sx={{ fontSize: 12.25, color: '#202124', fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                                {item.name}
                                                                                            </Typography>
                                                                                            <Button size="small" variant="text" onClick={() => setPendingRevertVersion(item)} disabled={!!monsurvActionBusy || !item.id} sx={{ minWidth: 0, px: 0.75, color: '#b42318', textTransform: 'none', fontSize: 11.5, fontWeight: 800 }}>
                                                                                                Revert
                                                                                            </Button>
                                                                                        </Stack>
                                                                                        <Typography sx={{ fontSize: 10.75, color: '#5f6368' }}>
                                                                                            {formatDocumentSize(item.size)}{item.last_modified ? ` • ${new Date(item.last_modified).toLocaleString()}` : ''}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                ))}
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography sx={{ fontSize: 12, color: '#5f6368' }}>No working report versions found.</Typography>
                                                                        )}
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => {
                                                                        const willOpen = !monsurvPanelOpen.documentSearch;
                                                                        toggleMonsurvPanelSection('documentSearch');
                                                                        if (willOpen && !documentSearchFolders.length && !documentSearchLoading) loadDocumentSearch();
                                                                    }}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Document Search</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.documentSearch ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.documentSearch ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Typography sx={{ fontSize: 11.5, color: '#5f6368', lineHeight: 1.35 }}>
                                                                            Questionnaire upload folders for this report.
                                                                        </Typography>
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button size="small" variant="outlined" onClick={loadDocumentSearch} disabled={documentSearchLoading} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                {documentSearchLoading ? 'Loading...' : 'Refresh Documents'}
                                                                            </Button>
                                                                        </Stack>
                                                                        {documentSearchRoot ? (
                                                                            <Typography sx={{ fontSize: 10.5, color: '#5f6368', wordBreak: 'break-all' }}>{documentSearchRoot}</Typography>
                                                                        ) : null}
                                                                        {documentSearchError ? (
                                                                            <Alert severity="error" variant="outlined" sx={{ py: 0.25, px: 1, fontSize: 12, background: '#fff', '& .MuiAlert-icon': { fontSize: 17, mr: 0.75 } }}>
                                                                                {documentSearchError}
                                                                            </Alert>
                                                                        ) : null}
                                                                        {documentSearchFolders.length ? (
                                                                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', border: '1px solid #d6d6d6', borderRadius: 1, overflow: 'hidden', background: '#fff' }}>
                                                                                <Box sx={{ background: '#f1f3f4', borderBottom: '1px solid #d6d6d6' }}>
                                                                                    {documentSearchFolders.map((folder) => {
                                                                                        const selected = folder.path === documentSearchSelectedFolder;
                                                                                        return (
                                                                                            <Button
                                                                                                key={folder.path}
                                                                                                fullWidth
                                                                                                onClick={() => setDocumentSearchSelectedFolder(folder.path)}
                                                                                                sx={{ justifyContent: 'flex-start', px: 1, py: 0.65, borderRadius: 0, background: selected ? '#dbeafe' : 'transparent', color: '#202124', textTransform: 'none', '&:hover': { background: selected ? '#bfdbfe' : '#e8eaed' } }}
                                                                                            >
                                                                                                <Typography sx={{ fontSize: 12.25, fontWeight: selected ? 800 : 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                                    {folder.path}
                                                                                                </Typography>
                                                                                            </Button>
                                                                                        );
                                                                                    })}
                                                                                </Box>
                                                                                {(() => {
                                                                                    const selectedFolder = documentSearchFolders.find((folder) => folder.path === documentSearchSelectedFolder) || documentSearchFolders[0];
                                                                                    const items = Array.isArray(selectedFolder?.items) ? selectedFolder.items : [];
                                                                                    return items.length ? (
                                                                                        <Box sx={{ display: 'grid' }}>
                                                                                            {items.map((item) => (
                                                                                                <Box key={item.key || item.id || item.name} sx={{ px: 1, py: 0.75, borderTop: '1px solid #eeeeee', display: 'grid', gap: 0.25 }}>
                                                                                                    <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="space-between" sx={{ minWidth: 0 }}>
                                                                                                        <Box sx={{ minWidth: 0 }}>
                                                                                                            <Typography title={item.key} sx={{ fontSize: 12.25, color: '#202124', fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                                                {item.name || item.key}
                                                                                                            </Typography>
                                                                                                            {item.document_type || item.document_title ? (
                                                                                                                <Typography sx={{ fontSize: 10.75, color: '#5f6368', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                                                    {item.document_title || item.document_type}
                                                                                                                </Typography>
                                                                                                            ) : null}
                                                                                                        </Box>
                                                                                                        <Button size="small" variant="text" onClick={() => item.download_url && window.open(item.download_url, '_blank', 'noopener,noreferrer')} disabled={!item.download_url} sx={{ minWidth: 0, px: 0.75, color: '#0b57d0', textTransform: 'none', fontSize: 11.5, fontWeight: 800 }}>
                                                                                                            Open
                                                                                                        </Button>
                                                                                                    </Stack>
                                                                                                    <Typography sx={{ fontSize: 10.75, color: '#5f6368' }}>
                                                                                                        {formatDocumentSize(item.size)}{item.last_modified ? ` • ${new Date(item.last_modified).toLocaleString()}` : ''}
                                                                                                    </Typography>
                                                                                                </Box>
                                                                                            ))}
                                                                                        </Box>
                                                                                    ) : (
                                                                                        <Typography sx={{ p: 1, fontSize: 12, color: '#5f6368' }}>No files found in this folder.</Typography>
                                                                                    );
                                                                                })()}
                                                                            </Box>
                                                                        ) : (
                                                                            <Typography sx={{ fontSize: 12, color: '#5f6368' }}>No questionnaire upload folders found.</Typography>
                                                                        )}
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => toggleMonsurvPanelSection('aiRegeneration')}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>AI Regeneration</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.aiRegeneration ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.aiRegeneration ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button size="small" variant="outlined" onMouseDown={(e) => e.preventDefault()} onClick={requestCollaboraSelection} disabled={!!monsurvActionBusy} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                Use Current Selection
                                                                            </Button>
                                                                            <Button size="small" variant="outlined" onClick={() => { setMonsurvSelectionText(''); setMonsurvEditorNotice('Selection field cleared.'); }} disabled={!!monsurvActionBusy || !monsurvSelectionText} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                Clear Section
                                                                            </Button>
                                                                        </Stack>
                                                                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                                            <Typography component="label" sx={{ fontSize: 11.5, color: '#5f6368', fontWeight: 700 }}>
                                                                                Selected report text
                                                                            </Typography>
                                                                            <Box
                                                                                component="textarea"
                                                                                value={monsurvSelectionText}
                                                                                onChange={(e) => setMonsurvSelectionText(e.target.value)}
                                                                                placeholder="If needed, press Ctrl+C in Collabora, then click Use Current Selection again."
                                                                                sx={{
                                                                                    width: '100%',
                                                                                    height: 116,
                                                                                    boxSizing: 'border-box',
                                                                                    resize: 'vertical',
                                                                                    border: '1px solid #c8c8c8',
                                                                                    borderRadius: 1,
                                                                                    background: '#fff',
                                                                                    color: '#202124',
                                                                                    font: 'inherit',
                                                                                    fontSize: 12.5,
                                                                                    lineHeight: 1.45,
                                                                                    p: 1,
                                                                                    '&:focus': { outline: '2px solid #8ab4f8', outlineOffset: 0 }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                                            <Typography component="label" sx={{ fontSize: 11.5, color: '#5f6368', fontWeight: 700 }}>
                                                                                Please describe edits required
                                                                            </Typography>
                                                                            <Box
                                                                                component="textarea"
                                                                                value={monsurvInstructions}
                                                                                onChange={(e) => setMonsurvInstructions(e.target.value)}
                                                                                sx={{
                                                                                    width: '100%',
                                                                                    height: 88,
                                                                                    boxSizing: 'border-box',
                                                                                    resize: 'vertical',
                                                                                    border: '1px solid #c8c8c8',
                                                                                    borderRadius: 1,
                                                                                    background: '#fff',
                                                                                    color: '#202124',
                                                                                    font: 'inherit',
                                                                                    fontSize: 12.5,
                                                                                    lineHeight: 1.45,
                                                                                    p: 1,
                                                                                    '&:focus': { outline: '2px solid #8ab4f8', outlineOffset: 0 }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            onClick={runMonsurvRegeneration}
                                                                            disabled={!!monsurvActionBusy}
                                                                            sx={{ minHeight: 30, background: '#0b57d0', textTransform: 'none', fontSize: 12.5, fontWeight: 700, justifySelf: 'start', '&:hover': { background: '#0842a0' } }}
                                                                            startIcon={monsurvActionBusy === 'regenerate' ? <CircularProgress size={14} color="inherit" /> : null}
                                                                        >
                                                                            {monsurvActionBusy === 'regenerate' ? 'Regenerating...' : 'Regenerate'}
                                                                        </Button>

                                                                        {monsurvReplacementText ? (
                                                                            <Box sx={{ display: 'grid', gap: 0.75 }}>
                                                                                <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                                                    <Typography component="label" sx={{ fontSize: 11.5, color: '#5f6368', fontWeight: 700 }}>
                                                                                        Replacement text
                                                                                    </Typography>
                                                                                    <Box
                                                                                        component="textarea"
                                                                                        value={monsurvReplacementText}
                                                                                        onChange={(e) => setMonsurvReplacementText(e.target.value)}
                                                                                        sx={{
                                                                                            width: '100%',
                                                                                            height: 116,
                                                                                            boxSizing: 'border-box',
                                                                                            resize: 'vertical',
                                                                                            border: '1px solid #c8c8c8',
                                                                                            borderRadius: 1,
                                                                                            background: '#fff',
                                                                                            color: '#202124',
                                                                                            font: 'inherit',
                                                                                            fontSize: 12.5,
                                                                                            lineHeight: 1.45,
                                                                                            p: 1,
                                                                                            '&:focus': { outline: '2px solid #8ab4f8', outlineOffset: 0 }
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                                    <Button size="small" variant="outlined" onClick={copyMonsurvReplacement} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                        Copy
                                                                                    </Button>
                                                                                    <Button size="small" variant="contained" onMouseDown={(e) => e.preventDefault()} onClick={applyMonsurvReplacementToEditor} sx={{ minHeight: 30, background: '#0b57d0', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#0842a0' } }}>
                                                                                        Apply
                                                                                    </Button>
                                                                                </Stack>
                                                                            </Box>
                                                                        ) : null}
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => toggleMonsurvPanelSection('factChecker')}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Fact Checker</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.factChecker ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.factChecker ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button size="small" variant="outlined" onMouseDown={(e) => e.preventDefault()} onClick={() => requestCollaboraSelection('fact')} disabled={!!monsurvActionBusy} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                Use Current Selection
                                                                            </Button>
                                                                            <Button size="small" variant="outlined" onClick={() => { setMonsurvFactSelectionText(''); setMonsurvEditorNotice('Fact checker selection cleared.'); }} disabled={!!monsurvActionBusy || !monsurvFactSelectionText} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                Clear Section
                                                                            </Button>
                                                                        </Stack>
                                                                        <Box sx={{ display: 'grid', gap: 0.5 }}>
                                                                            <Typography component="label" sx={{ fontSize: 11.5, color: '#5f6368', fontWeight: 700 }}>
                                                                                Fact-check selection
                                                                            </Typography>
                                                                            <Box
                                                                                component="textarea"
                                                                                value={monsurvFactSelectionText}
                                                                                onChange={(e) => setMonsurvFactSelectionText(e.target.value)}
                                                                                placeholder="Paste or capture the specific text to fact-check."
                                                                                sx={{
                                                                                    width: '100%',
                                                                                    height: 116,
                                                                                    boxSizing: 'border-box',
                                                                                    resize: 'vertical',
                                                                                    border: '1px solid #c8c8c8',
                                                                                    borderRadius: 1,
                                                                                    background: '#fff',
                                                                                    color: '#202124',
                                                                                    font: 'inherit',
                                                                                    fontSize: 12.5,
                                                                                    lineHeight: 1.45,
                                                                                    p: 1,
                                                                                    '&:focus': { outline: '2px solid #8ab4f8', outlineOffset: 0 }
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={runMonsurvFactCheck}
                                                                                disabled={!!monsurvActionBusy}
                                                                                sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                                                                                startIcon={monsurvActionBusy === 'fact-check' ? <CircularProgress size={14} /> : null}
                                                                            >
                                                                                {monsurvActionBusy === 'fact-check' ? 'Checking...' : 'Fact Check Selection'}
                                                                            </Button>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => {
                                                                                    setMonsurvFactCheckText('');
                                                                                    setMonsurvEditorNotice('Fact checker cleared.');
                                                                                }}
                                                                                disabled={!!monsurvActionBusy || !monsurvFactCheckText}
                                                                                sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                                                                            >
                                                                                Clear Fact Checker
                                                                            </Button>
                                                                        </Stack>
                                                                        {monsurvFactCheckText ? (
                                                                            <Paper variant="outlined" sx={{ p: 1, background: '#fff', borderColor: '#d6d6d6', whiteSpace: 'pre-wrap', fontSize: 12.5, lineHeight: 1.45, color: '#202124' }}>
                                                                                {monsurvFactCheckText}
                                                                            </Paper>
                                                                        ) : null}
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => toggleMonsurvPanelSection('preIssueChecks')}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Pre-Issue Checks</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.preIssueChecks ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.preIssueChecks ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Button
                                                                            size="small"
                                                                            variant="contained"
                                                                            onClick={runPreIssueChecks}
                                                                            disabled={!!monsurvActionBusy}
                                                                            sx={{ minHeight: 30, background: '#0b57d0', textTransform: 'none', fontSize: 12.5, fontWeight: 700, justifySelf: 'start', '&:hover': { background: '#0842a0' } }}
                                                                            startIcon={monsurvActionBusy === 'pre-issue-checks' ? <CircularProgress size={14} color="inherit" /> : null}
                                                                        >
                                                                            {monsurvActionBusy === 'pre-issue-checks' ? 'Conducting Checks...' : 'Conduct Pre-Issue Check'}
                                                                        </Button>
                                                                        <ReportEvidenceOutputReview reference={reference} entry={activeDocxEntry}
                                                                            authHeaders={authHeaders} saveDocument={saveCurrentCollaboraDocument} />
                                                                        {preIssueSource ? (
                                                                            <Typography sx={{ fontSize: 10.75, color: '#5f6368', lineHeight: 1.35 }}>
                                                                                Checked saved version {preIssueSource.working_version || 'unknown'}{preIssueSource.updated_at ? ` from ${new Date(preIssueSource.updated_at).toLocaleString()}` : ''}
                                                                                {preIssueSource.sha256 ? ` • SHA-256 ${String(preIssueSource.sha256).slice(0, 12)}...` : ''}
                                                                            </Typography>
                                                                        ) : null}
                                                                        {preIssueChecks.length ? (
                                                                            <TableContainer component={Paper} variant="outlined" sx={{ background: '#fff', borderColor: '#d6d6d6' }}>
                                                                                <Table size="small" aria-label="Pre-issue check results">
                                                                                    <TableBody>
                                                                                        {preIssueChecks.map((check) => {
                                                                                            const findings = Array.isArray(check.findings) ? check.findings : [];
                                                                                            const expanded = !!preIssueExpanded[check.key];
                                                                                            return (
                                                                                                <React.Fragment key={check.key}>
                                                                                                    <TableRow>
                                                                                                        <TableCell sx={{ px: 1, py: 0.75, borderBottom: expanded ? 0 : undefined }}>
                                                                                                            <Button
                                                                                                                fullWidth
                                                                                                                onClick={() => setPreIssueExpanded((prev) => ({ ...prev, [check.key]: !prev[check.key] }))}
                                                                                                                sx={{ p: 0, minHeight: 0, justifyContent: 'space-between', color: '#202124', textTransform: 'none' }}
                                                                                                            >
                                                                                                                <Typography sx={{ fontSize: 12, fontWeight: 800, textAlign: 'left' }}>{check.label}</Typography>
                                                                                                                <Typography sx={{ fontSize: 11, color: findings.length ? '#b42318' : '#137333', flexShrink: 0, ml: 1 }}>
                                                                                                                    {findings.length ? `${findings.length} issue${findings.length === 1 ? '' : 's'}` : 'Clear'} • {expanded ? 'Hide' : 'Show'}
                                                                                                                </Typography>
                                                                                                            </Button>
                                                                                                        </TableCell>
                                                                                                    </TableRow>
                                                                                                    {expanded ? (
                                                                                                        <TableRow>
                                                                                                            <TableCell sx={{ px: 1, pt: 0.25, pb: 1 }}>
                                                                                                                <Typography sx={{ fontSize: 11.5, color: '#5f6368', mb: findings.length ? 0.75 : 0 }}>{check.summary}</Typography>
                                                                                                                {findings.map((finding, index) => {
                                                                                                                    const findingKey = `${check.key}:${index}`;
                                                                                                                    const actioned = !!preIssueActioned[findingKey];
                                                                                                                    return (
                                                                                                                        <Box
                                                                                                                            key={`${check.key}-${index}`}
                                                                                                                            component="button"
                                                                                                                            type="button"
                                                                                                                            aria-pressed={actioned}
                                                                                                                            aria-label={`${actioned ? 'Mark as not actioned' : 'Mark as actioned'}: ${finding.location || finding.issue}`}
                                                                                                                            onClick={() => togglePreIssueFindingActioned(check.key, index)}
                                                                                                                            sx={{
                                                                                                                                width: '100%',
                                                                                                                                border: '1px solid',
                                                                                                                                borderColor: actioned ? '#c7c9cc' : '#eeeeee',
                                                                                                                                borderRadius: 1,
                                                                                                                                p: 0.75,
                                                                                                                                mt: 0.75,
                                                                                                                                display: 'grid',
                                                                                                                                gap: 0.35,
                                                                                                                                background: actioned ? '#eeeeee' : '#fff',
                                                                                                                                color: actioned ? '#5f6368' : '#202124',
                                                                                                                                textAlign: 'left',
                                                                                                                                font: 'inherit',
                                                                                                                                cursor: 'pointer',
                                                                                                                                '&:hover': { background: actioned ? '#e3e3e3' : '#f8f9fa' },
                                                                                                                                '&:focus-visible': { outline: '2px solid #0b57d0', outlineOffset: 1 }
                                                                                                                            }}
                                                                                                                        >
                                                                                                                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                                                                                                                                <Chip size="small" label={actioned ? 'Actioned' : String(finding.severity || 'medium').toUpperCase()} color={actioned ? 'success' : finding.severity === 'high' ? 'error' : finding.severity === 'low' ? 'default' : 'warning'} sx={{ height: 20, fontSize: 10, fontWeight: 800 }} />
                                                                                                                                {finding.location ? <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{finding.location}</Typography> : null}
                                                                                                                            </Stack>
                                                                                                                            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'grid', gap: 0.4 }}>
                                                                                                                                <Typography component="li" sx={{ fontSize: 11.5, lineHeight: 1.4 }}><strong>Issue:</strong> {finding.issue}</Typography>
                                                                                                                                {finding.recommendation ? <Typography component="li" sx={{ fontSize: 11.25, lineHeight: 1.4, color: actioned ? '#6f7378' : '#5f6368' }}><strong>Action:</strong> {finding.recommendation}</Typography> : null}
                                                                                                                            </Box>
                                                                                                                        </Box>
                                                                                                                    );
                                                                                                                })}
                                                                                                            </TableCell>
                                                                                                        </TableRow>
                                                                                                    ) : null}
                                                                                                </React.Fragment>
                                                                                            );
                                                                                        })}
                                                                                    </TableBody>
                                                                                </Table>
                                                                            </TableContainer>
                                                                        ) : null}
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => toggleMonsurvPanelSection('pdfIssue')}
                                                                    sx={{ justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>PDF and Issue</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.pdfIssue ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.pdfIssue ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 0.75 }}>
                                                                        <Typography sx={{ fontSize: 11.5, color: '#5f6368', lineHeight: 1.35 }}>
                                                                            Save the working report in Collabora before finalising.
                                                                        </Typography>
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button
                                                                                size="small"
                                                                                variant="contained"
                                                                                color="success"
                                                                                onClick={finalizeWorkingReportPdf}
                                                                                disabled={!!monsurvActionBusy}
                                                                                sx={{ minHeight: 30, textTransform: 'none', fontSize: 12.5, fontWeight: 700 }}
                                                                                startIcon={monsurvActionBusy === 'finalize' ? <CircularProgress size={14} color="inherit" /> : null}
                                                                            >
                                                                                {monsurvActionBusy === 'finalize' ? 'Finalising...' : 'Finalise Report'}
                                                                            </Button>
                                                                            <Button
                                                                                size="small"
                                                                                variant="outlined"
                                                                                onClick={() => {
                                                                                    const url = getIssuePdfUrl();
                                                                                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                                                                                }}
                                                                                disabled={!!monsurvActionBusy || !getIssuePdfUrl()}
                                                                                sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}
                                                                            >
                                                                                Download PDF Report
                                                                            </Button>
                                                                        </Stack>
                                                                    </Box>
                                                                ) : null}

                                                                <Button
                                                                    fullWidth
                                                                    onClick={() => toggleMonsurvPanelSection('chatbot')}
                                                                    sx={{ mt: 'auto', justifyContent: 'space-between', px: 1.25, py: 1, borderRadius: 0, borderTop: '1px solid #d6d6d6', background: '#eeeeee', color: '#202124', textTransform: 'none', '&:hover': { background: '#e3e3e3' } }}
                                                                >
                                                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>Monsurv Chatbot</Typography>
                                                                    <Typography sx={{ fontSize: 12, color: '#5f6368' }}>{monsurvPanelOpen.chatbot ? 'Hide' : 'Show'}</Typography>
                                                                </Button>
                                                                {monsurvPanelOpen.chatbot ? (
                                                                    <Box sx={{ p: 1.25, display: 'grid', gap: 1 }}>
                                                                        <Typography sx={{ fontSize: 11.5, color: '#5f6368', lineHeight: 1.35 }}>
                                                                            Ask questions about this report, regulations, local authorities, or project context.
                                                                        </Typography>
                                                                        {monsurvChatError ? (
                                                                            <Alert severity="error" variant="outlined" sx={{ py: 0.25, px: 1, fontSize: 12, background: '#fff', '& .MuiAlert-icon': { fontSize: 17, mr: 0.75 } }}>
                                                                                {monsurvChatError}
                                                                            </Alert>
                                                                        ) : null}
                                                                        <Box sx={{ border: '1px solid #d6d6d6', background: '#fff', borderRadius: 1, p: 1, minHeight: 140, maxHeight: 260, overflow: 'auto', display: 'grid', alignContent: 'start', gap: 0.75 }}>
                                                                            {monsurvChatMessages.length ? monsurvChatMessages.map((msg, idx) => {
                                                                                const assistant = msg.role === 'assistant';
                                                                                return (
                                                                                    <Box key={`${msg.role}-${idx}`} sx={{ justifySelf: assistant ? 'start' : 'end', maxWidth: '92%', px: 1, py: 0.75, borderRadius: 1, background: assistant ? '#f1f3f4' : '#dbeafe', color: '#202124', fontSize: 12.25, whiteSpace: 'pre-wrap', lineHeight: 1.45 }}>
                                                                                        {msg.content}
                                                                                    </Box>
                                                                                );
                                                                            }) : (
                                                                                <Typography sx={{ fontSize: 12, color: '#5f6368' }}>No messages yet.</Typography>
                                                                            )}
                                                                            {monsurvChatBusy ? <Typography sx={{ fontSize: 12, color: '#5f6368' }}>Thinking...</Typography> : null}
                                                                        </Box>
                                                                        <Box
                                                                            component="textarea"
                                                                            value={monsurvChatInput}
                                                                            onChange={(e) => setMonsurvChatInput(e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                                                    e.preventDefault();
                                                                                    sendMonsurvChatMessage();
                                                                                }
                                                                            }}
                                                                            placeholder="Ask a question about the report..."
                                                                            sx={{ width: '100%', height: 74, boxSizing: 'border-box', resize: 'vertical', border: '1px solid #c8c8c8', borderRadius: 1, background: '#fff', color: '#202124', font: 'inherit', fontSize: 12.5, lineHeight: 1.45, p: 1, '&:focus': { outline: '2px solid #8ab4f8', outlineOffset: 0 } }}
                                                                        />
                                                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                                                                            <Button size="small" variant="contained" onClick={sendMonsurvChatMessage} disabled={monsurvChatBusy || !String(monsurvChatInput || '').trim()} sx={{ minHeight: 30, background: '#0b57d0', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#0842a0' } }}>
                                                                                {monsurvChatBusy ? 'Sending...' : 'Send'}
                                                                            </Button>
                                                                            <Button size="small" variant="outlined" onClick={() => { setMonsurvChatMessages([]); setMonsurvChatError(''); }} disabled={monsurvChatBusy || !monsurvChatMessages.length} sx={{ minHeight: 30, background: '#fff', borderColor: '#b8b8b8', color: '#202124', textTransform: 'none', fontSize: 12.5, fontWeight: 700, '&:hover': { background: '#ececec', borderColor: '#9e9e9e' } }}>
                                                                                Clear Chat
                                                                            </Button>
                                                                        </Stack>
                                                                    </Box>
                                                                ) : null}
                                                            </Stack>
                                                        </Paper>

                                                        <Box sx={{ minHeight: { xs: '52vh', lg: 0 }, border: 0, overflow: 'hidden', background: '#fff' }}>
                                                            <form
                                                                ref={collaboraFormRef}
                                                                action={collaboraSession.launch_form.action}
                                                                method="post"
                                                                target={collaboraFrameNameRef.current}
                                                                style={{ display: 'none' }}
                                                            >
                                                                <input type="hidden" name="access_token" value={collaboraSession.launch_form.fields.access_token} readOnly />
                                                                <input type="hidden" name="access_token_ttl" value={collaboraSession.launch_form.fields.access_token_ttl} readOnly />
                                                                <input type="hidden" name="postmessage_origin" value={window.location.origin} readOnly />
                                                                <input type="hidden" name="permission" value="edit" readOnly />
                                                            </form>
                                                            <iframe
                                                                ref={collaboraFrameRef}
                                                                name={collaboraFrameNameRef.current}
                                                                title="Collabora working report editor"
                                                                onLoad={signalCollaboraPostMessageReady}
                                                                style={{ display: 'block', width: '100%', height: '100%', minHeight: '52vh', border: 0 }}
                                                                allow="clipboard-read; clipboard-write; fullscreen"
                                                            />
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Dialog>
                                        )}

                                        <Dialog
                                            open={Boolean(pendingRevertVersion)}
                                            onClose={() => {
                                                if (monsurvActionBusy === 'revert') return;
                                                setPendingRevertVersion(null);
                                            }}
                                            maxWidth="xs"
                                            fullWidth
                                        >
                                            <DialogTitle>Revert Report Version</DialogTitle>
                                            <DialogContent>
                                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                    {`Are you sure you want to revert to Version ${pendingRevertVersion?.version || ''}? All edits since this version will be lost.`}
                                                </Typography>
                                            </DialogContent>
                                            <DialogActions>
                                                <Button
                                                    onClick={() => setPendingRevertVersion(null)}
                                                    disabled={monsurvActionBusy === 'revert'}
                                                    sx={{ textTransform: 'none', fontWeight: 800 }}
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    variant="contained"
                                                    color="error"
                                                    onClick={() => revertWorkingReportVersion(pendingRevertVersion)}
                                                    disabled={monsurvActionBusy === 'revert'}
                                                    sx={{ textTransform: 'none', fontWeight: 900 }}
                                                    startIcon={monsurvActionBusy === 'revert' ? <CircularProgress size={14} color="inherit" /> : null}
                                                >
                                                    {monsurvActionBusy === 'revert' ? 'Reverting...' : 'Revert'}
                                                </Button>
                                            </DialogActions>
                                        </Dialog>

                                        {showAiAlerts && (
                                            <div
                                                role="dialog"
                                                aria-modal="true"
                                                style={{
                                                    position: 'fixed',
                                                    inset: 0,
                                                    background: 'rgba(0,0,0,0.55)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: 24,
                                                    zIndex: 9999
                                                }}
                                                onClick={() => setShowAiAlerts(false)}
                                            >
                                                <div
                                                    style={{
                                                        width: 'min(860px, 95vw)',
                                                        maxHeight: 'min(80vh, 820px)',
                                                        background: '#fff',
                                                        borderRadius: 12,
                                                        overflow: 'hidden',
                                                        boxShadow: '0 10px 30px rgba(0,0,0,0.25)'
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div style={{ padding: '10px 12px', background: '#111827', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                                                        <div style={{ fontWeight: 900, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            Alerts — {reference} ({String(reportInstance || 'initial')})
                                                        </div>
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => loadAiAlerts({ force: true })}
                                                                disabled={aiAlertsLoading}
                                                                style={{ padding: '6px 10px', fontWeight: 900, cursor: aiAlertsLoading ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                {aiAlertsLoading ? 'Refreshing…' : 'Refresh'}
                                                            </button>
                                                            <button type="button" onClick={() => setShowAiAlerts(false)} style={{ padding: '6px 10px', fontWeight: 900, cursor: 'pointer' }}>
                                                                Close
                                                            </button>
                                                        </div>
                                                    </div>

                                                    <div style={{ padding: 14, background: '#f9fafb', overflow: 'auto', maxHeight: 'calc(80vh - 44px)' }}>
                                                        {aiAlertsError ? (
                                                            <div style={{ padding: 10, borderRadius: 8, background: '#fee2e2', color: '#7f1d1d', fontWeight: 700 }}>
                                                                {aiAlertsError}
                                                            </div>
                                                        ) : null}

                                                        {!aiAlertsLoading && (!aiAlerts || !Array.isArray(aiAlerts.alerts)) ? (
                                                            <div style={{ color: '#6b7280' }}>No alerts loaded yet.</div>
                                                        ) : null}

                                                        {Array.isArray(aiAlerts?.alerts) && (
                                                            (aiAlerts.alerts.length === 0) ? (
                                                                <div style={{ padding: 12, borderRadius: 10, background: '#ecfdf5', border: '1px solid #bbf7d0', color: '#065f46', fontWeight: 800 }}>
                                                                    No alerts.
                                                                </div>
                                                            ) : (
                                                                <div style={{ display: 'grid', gap: 10 }}>
                                                                    {aiAlerts.alerts.map((a, idx) => {
                                                                        const sev = String(a?.severity || '').toLowerCase();
                                                                        const colors = sev === 'critical'
                                                                            ? { bg: '#fee2e2', fg: '#7f1d1d', border: '#fecaca' }
                                                                            : (sev === 'warning'
                                                                                ? { bg: '#fffbeb', fg: '#92400e', border: '#fde68a' }
                                                                                : { bg: '#eff6ff', fg: '#1e3a8a', border: '#bfdbfe' });
                                                                        return (
                                                                            <div key={idx} style={{ background: '#fff', border: `1px solid ${colors.border}`, borderRadius: 10, padding: 12 }}>
                                                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                                                                                    <div style={{ minWidth: 0 }}>
                                                                                        <div style={{ fontWeight: 900, color: '#111827' }}>{a?.title || 'Alert'}</div>
                                                                                        <div style={{ marginTop: 6, color: '#374151', fontSize: 13 }}>
                                                                                            {a?.rationale || a?.reason || ''}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div style={{ display: 'inline-flex', padding: '3px 10px', borderRadius: 999, background: colors.bg, color: colors.fg, fontWeight: 900, fontSize: 12, letterSpacing: 0.4 }}>
                                                                                        {sev ? sev.toUpperCase() : 'INFO'}
                                                                                    </div>
                                                                                </div>
                                                                                {a?.suggested_action ? (
                                                                                    <div style={{ marginTop: 10, fontSize: 13, color: '#111827' }}>
                                                                                        <span style={{ fontWeight: 900 }}>Suggested action: </span>
                                                                                        <span>{a.suggested_action}</span>
                                                                                    </div>
                                                                                ) : null}
                                                                                {Array.isArray(a?.evidence) && a.evidence.length ? (
                                                                                    <div style={{ marginTop: 10, fontSize: 12, color: '#6b7280' }}>
                                                                                        <span style={{ fontWeight: 900 }}>Evidence: </span>
                                                                                        {a.evidence.slice(0, 3).map((t, i) => (
                                                                                            <span key={i}>{i ? ' • ' : ''}{String(t)}</span>
                                                                                        ))}
                                                                                    </div>
                                                                                ) : null}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )
                                                        )}

                                                        <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280' }}>
                                                            AI-generated alerts are advisory only. They do not add facts; validate against source documents.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {mode === 'surveyor' && activeDocxEntry?.download_url && showQaUpload && (
                                            <div
                                                style={{
                                                    position: 'fixed',
                                                    inset: 0,
                                                    background: 'rgba(17, 24, 39, 0.55)',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    padding: 24,
                                                    zIndex: 10000
                                                }}
                                                onClick={() => {
                                                    if (issueUploadBusy || appendixGenBusy) return;
                                                    setShowQaUpload(false);
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        width: 'min(680px, 96vw)',
                                                        background: '#fff',
                                                        borderRadius: 16,
                                                        boxShadow: '0 24px 60px rgba(0,0,0,0.22)',
                                                        overflow: 'hidden'
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <div style={{ padding: '18px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                                                        <div>
                                                            <div style={{ fontWeight: 900, color: '#111827', fontSize: 18 }}>Issue Report</div>
                                                            <div style={{ marginTop: 4, fontSize: 13, color: '#6b7280' }}>
                                                                Upload the issued report in PDF format to complete the issue step.
                                                            </div>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                if (issueUploadBusy || appendixGenBusy) return;
                                                                setShowQaUpload(false);
                                                            }}
                                                            style={{
                                                                padding: '8px 12px',
                                                                borderRadius: 10,
                                                                border: '1px solid #d1d5db',
                                                                background: '#fff',
                                                                color: '#111827',
                                                                fontWeight: 800,
                                                                cursor: (issueUploadBusy || appendixGenBusy) ? 'not-allowed' : 'pointer',
                                                                opacity: (issueUploadBusy || appendixGenBusy) ? 0.7 : 1
                                                            }}
                                                        >
                                                            Close
                                                        </button>
                                                    </div>

                                                    <div style={{ padding: 20 }}>
                                                        <div
                                                            style={{
                                                                padding: 18,
                                                                border: '2px dashed #a78bfa',
                                                                borderRadius: 14,
                                                                background: '#faf5ff'
                                                            }}
                                                            onDragOver={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                            }}
                                                            onDrop={(e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
                                                                if (!f) return;
                                                                uploadFinalIssuePdf(f, { sendToFunder: issueSendToFunder }).catch((err) => setIssueUploadError(err?.message || 'Upload failed'));
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: 900, color: '#5b21b6' }}>
                                                                {activeEntryIsInterim ? 'Upload Issued Interim Report PDF' : 'Upload Issued Initial Report PDF'}
                                                            </div>
                                                            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 6 }}>
                                                                Drag and drop the final PDF here, or browse to select a file. PDF format only.
                                                            </div>

                                                            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 16, padding: 12, borderRadius: 12, background: '#fff', border: '1px solid #e5e7eb' }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={issueSendToFunder}
                                                                    onChange={(e) => setIssueSendToFunder(Boolean(e.target.checked))}
                                                                    disabled={issueUploadBusy}
                                                                    style={{ marginTop: 3 }}
                                                                />
                                                                <span style={{ fontSize: 13, color: '#111827' }}>
                                                                    <span style={{ display: 'block', fontWeight: 800 }}>Send issued report to the Funder Point of Contact</span>
                                                                    <span style={{ display: 'block', marginTop: 4, color: '#6b7280' }}>
                                                                        {data?.funder_poc
                                                                            ? `The system will try to send the issued PDF to the configured contact for ${data?.funder || 'the selected funder'}.`
                                                                            : 'No funder point of contact is shown on this project, so email delivery may not be available.'}
                                                                    </span>
                                                                </span>
                                                            </label>

                                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
                                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                                    {activeEntryIsInterim && (
                                                                        <button
                                                                            type="button"
                                                                            disabled={appendixGenBusy}
                                                                            onClick={() => generateAppendicesPdf()}
                                                                            style={{
                                                                                padding: '10px 14px',
                                                                                borderRadius: 10,
                                                                                border: '1px solid #d1d5db',
                                                                                background: '#fff',
                                                                                color: '#111827',
                                                                                fontWeight: 800,
                                                                                cursor: appendixGenBusy ? 'not-allowed' : 'pointer',
                                                                                opacity: appendixGenBusy ? 0.7 : 1
                                                                            }}
                                                                        >
                                                                            {appendixGenBusy ? 'Generating…' : 'Generate Appendices'}
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                                                    <button
                                                                        type="button"
                                                                        disabled={issueUploadBusy}
                                                                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                                                        style={{
                                                                            padding: '10px 14px',
                                                                            borderRadius: 10,
                                                                            border: 'none',
                                                                            background: '#111827',
                                                                            color: '#fff',
                                                                            fontWeight: 800,
                                                                            cursor: issueUploadBusy ? 'not-allowed' : 'pointer',
                                                                            opacity: issueUploadBusy ? 0.7 : 1
                                                                        }}
                                                                    >
                                                                        {issueUploadBusy ? 'Uploading…' : 'Browse PDF'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>

                                                    </div>

                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept="application/pdf,.pdf"
                                                        style={{ display: 'none' }}
                                                        onChange={(e) => {
                                                            const f = e.target.files && e.target.files[0];
                                                            e.target.value = '';
                                                            if (!f) return;
                                                            uploadFinalIssuePdf(f, { sendToFunder: issueSendToFunder }).catch((err) => setIssueUploadError(err?.message || 'Upload failed'));
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {isInitialView && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb', minWidth: 0, width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Initial Report Information</h2>
                                                {!initialReport && <div style={{ color: '#666' }}>Loading initial report data…</div>}
                                                {initialReport && (
                                                    <InitialReportSections
                                                        initialReport={initialReport}
                                                        reference={reference}
                                                        mode={mode}
                                                        code={code}
                                                        notesByKey={notesByKey}
                                                        onSaveRowNote={mode === 'surveyor' ? saveRowNote : null}
                                                        fieldReviewsByKey={fieldReviewsByKey}
                                                        onSaveFieldReview={mode === 'surveyor' ? saveFieldReview : null}
                                                        costAssessmentsByKey={costAssessmentsByKey}
                                                        onSaveCostAssessment={mode === 'surveyor' ? saveCostAssessment : null}
                                                        onSaveIniField={mode === 'developer' ? saveIniField : null}
                                                        editsByKey={editsByKey}
                                                        projectFacility={data?.project_facility ?? data?.facility_amount ?? data?.facility ?? null}
                                                        iniDocMetadataById={iniDocMetadataById}
                                                        iniDocMetadataLoading={iniDocMetadataLoading}
                                                        iniDocMetadataError={iniDocMetadataError}
                                                        onSaveIniDocMetadata={saveIniDocumentMetadataOverride}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {isInterimView && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>
                                                    Interim Report {interimNumber || 1} Information
                                                </h2>
                                                {!interimReportForDisplay && <div style={{ color: '#666' }}>Loading interim report data…</div>}
                                                {interimReportForDisplay && currentInterimRequestedOnly && (
                                                    <div
                                                        style={{
                                                            marginBottom: 12,
                                                            padding: '10px 12px',
                                                            borderRadius: 10,
                                                            background: '#fff7ed',
                                                            border: '1px solid #fed7aa',
                                                            color: '#9a3412',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        Drawdown {currentInterimNumber} has been requested. Questionnaire responses are pending.
                                                    </div>
                                                )}
                                                {interimReportForDisplay && (
                                                    <InterimReportSections
                                                        interimReport={interimReportForDisplay}
                                                        initialReport={initialReportForPortal}
                                                        costAssessmentsByKey={costAssessmentsForPortal}
                                                        setupCostBaseline={setupCostBaseline}
                                                        portalMode={mode}
                                                        projectReference={reference}
                                                        projectFacility={data?.project_facility ?? data?.facility_amount ?? data?.facility ?? null}
                                                        currentInterimNumber={currentInterimNumber}
                                                        interimSubmissions={interimSubmissions}
                                                        authHeaders={authHeaders}
                                                        hideProjectFacilitySection={Number(currentInterimNumber) === 1}
                                                        allowInterimCostChanges={currentInterimHasSubmission}
                                                    />
                                                )}
                                            </div>
                                        )}

                                        {workspace === 'project_record' && activeProjectRecordSection === 'costs' ? (
                                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                                <Button
                                                    size="small"
                                                    variant={page === 'drawdown' ? 'outlined' : 'contained'}
                                                    onClick={goToCostTables}
                                                >
                                                    Cost plan
                                                </Button>
                                                <Button
                                                    size="small"
                                                    variant={page === 'drawdown' ? 'contained' : 'outlined'}
                                                    onClick={goToDrawdownTracker}
                                                >
                                                    Drawdown Tracker
                                                </Button>
                                            </Stack>
                                        ) : null}

                                        {(page === 'costs' || page === 'cost_tables') && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <ProjectCostDashboard
                                                    reference={reference}
                                                    interimNumber={currentInterimNumber}
                                                    drawdownsEnabled={Boolean(hasInitialIssuedPdf && initialConfirmedLive && isInProgressInterimPortal && isInProgressInterimUnlocked)}
                                                    liveDevelopmentCosts={projectSetupRecord?.development_costs || null}
                                                    aiBenchmark={acceptedCostBenchmark}
                                                />
                                            </div>
                                        )}

                                        {page === 'legacy_cost_tables' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                                                    <h2 style={{ margin: 0, fontSize: 18 }}>Cost Tables</h2>
                                                    {mode === 'surveyor' && canShowCostTables ? (
                                                        <Button variant="outlined" size="small" onClick={openCostEditor}>Edit</Button>
                                                    ) : null}
                                                </div>

                                                {costEditorError && !costEditorOpen ? (
                                                    <Alert severity="error" sx={{ mb: 1.5 }}>{costEditorError}</Alert>
                                                ) : null}

                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : !canShowCostTables ? (
                                                    <div style={{ color: '#666' }}>Cost Tables becomes available once the Initial Report has been uploaded.</div>
                                                ) : (!isInProgressInterimPortal && Number(currentInterimNumber || 1) !== 1) ? (
                                                    <div style={{ color: '#666' }}>Starter scope currently moves Project Facility tables for Interim Report 1.</div>
                                                ) : !interimReportForDisplay ? (
                                                    <div style={{ color: '#666' }}>Loading interim report data…</div>
                                                ) : (
                                                    <InterimReportSections
                                                        interimReport={interimReportForDisplay}
                                                        initialReport={initialReportForPortal}
                                                        costAssessmentsByKey={costAssessmentsForPortal}
                                                        setupCostBaseline={setupCostBaseline}
                                                        portalMode={mode}
                                                        projectReference={reference}
                                                        projectFacility={data?.project_facility ?? data?.facility_amount ?? data?.facility ?? null}
                                                        currentInterimNumber={currentInterimNumber}
                                                        interimSubmissions={interimSubmissions}
                                                        authHeaders={authHeaders}
                                                        showOnlyProjectFacilitySection
                                                        allowInterimCostChanges={currentInterimHasSubmission}
                                                    />
                                                )}

                                                <Dialog open={costEditorOpen} onClose={() => !costEditorSaving && setCostEditorOpen(false)} fullWidth maxWidth="md">
                                                    <DialogTitle>Edit Cost Tables</DialogTitle>
                                                    <DialogContent>
                                                        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                                                            Amend confirmed budget figures. Grey totals are calculated automatically.
                                                        </Typography>
                                                        {costEditorError ? <Alert severity="error" sx={{ mb: 1.5 }}>{costEditorError}</Alert> : null}
                                                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) minmax(240px, 1fr)' }, gap: 2, alignItems: 'center', mb: 2 }}>
                                                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 700 }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={costEditorDraft?.borrowerInputRelevant === true}
                                                                    onChange={(event) => setCostEditorDraft((previous) => ({ ...(previous || {}), borrowerInputRelevant: event.target.checked }))}
                                                                />
                                                                Borrower Input Relevant
                                                            </label>
                                                            <TextField
                                                                fullWidth
                                                                size="small"
                                                                type="number"
                                                                label="Borrower Input"
                                                                value={costEditorDraft?.borrowerInputAmount ?? ''}
                                                                onChange={(event) => setCostEditorDraft((previous) => ({
                                                                    ...(previous || {}),
                                                                    borrowerInputAmount: event.target.value,
                                                                    borrowerInputRelevant: toPortalMoney(event.target.value) > 0
                                                                        ? true
                                                                        : previous?.borrowerInputRelevant === true
                                                                }))}
                                                                InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                            />
                                                        </Box>
                                                        {(() => {
                                                            const sections = [
                                                                ['constructionBudgetRows', 'Construction Costs'],
                                                                ['contingencyBudgetRows', 'Contingency'],
                                                                ['professionalFeeBudgetRows', 'Professional Fees'],
                                                                ['additionalCostBudgetRows', 'Additional Costs']
                                                            ].map(([sectionKey, title]) => {
                                                                const rows = Array.isArray(costEditorDraft?.[sectionKey]) ? costEditorDraft[sectionKey] : [];
                                                                const isContingency = sectionKey === 'contingencyBudgetRows';
                                                                return {
                                                                    sectionKey,
                                                                    title,
                                                                    rows,
                                                                    isContingency,
                                                                    budgetTotal: isContingency
                                                                        ? toPortalMoney(costEditorDraft?.contingencyBudgetTotal)
                                                                        : rows.reduce((sum, row) => sum + toPortalMoney(row?.budget), 0)
                                                                };
                                                            });
                                                            const formatEditorMoney = (value) => toPortalMoney(value).toLocaleString('en-GB', {
                                                                style: 'currency',
                                                                currency: 'GBP',
                                                                maximumFractionDigits: 0
                                                            });
                                                            const editorInputSx = {
                                                                width: '100%',
                                                                '& .MuiInputBase-root': { fontSize: 14, fontWeight: 700 },
                                                                '& .MuiInputBase-input': { py: 0.75, textAlign: 'right' },
                                                                '& .MuiInputAdornment-root': { mr: 0.25 },
                                                                '& .MuiInput-root:before': { borderBottomColor: '#cbd5e1' }
                                                            };
                                                            const renderEditorInput = ({ label, value, onChange }) => (
                                                                <TextField
                                                                    type="number"
                                                                    variant="standard"
                                                                    value={value ?? ''}
                                                                    onChange={onChange}
                                                                    inputProps={{ 'aria-label': label }}
                                                                    InputProps={{ startAdornment: <InputAdornment position="start">£</InputAdornment> }}
                                                                    sx={editorInputSx}
                                                                />
                                                            );
                                                            const overallBudget = sections.reduce((sum, section) => sum + section.budgetTotal, 0);

                                                            return (
                                                                <TableContainer sx={{ border: '1px solid #d1d5db', overflowX: 'auto' }}>
                                                                    <Table size="small" sx={{ minWidth: 560, tableLayout: 'fixed' }}>
                                                                        <TableHead>
                                                                            <TableRow sx={{ bgcolor: '#e5e7eb' }}>
                                                                                <TableCell sx={{ width: '60%', py: 1.25, fontWeight: 900, fontSize: 14 }}>Description</TableCell>
                                                                                <TableCell align="right" sx={{ width: '40%', py: 1.25, fontWeight: 900, fontSize: 14, whiteSpace: 'nowrap' }}>Original Development Budget</TableCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <TableBody>
                                                                            {sections.map((section) => (
                                                                                <React.Fragment key={section.sectionKey}>
                                                                                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                                                        <TableCell sx={{ py: 1.2, fontWeight: 900, fontSize: 14 }}>{section.title}</TableCell>
                                                                                        <TableCell align="right" sx={{ py: section.isContingency ? 0.4 : 1.2, fontWeight: 900, fontSize: 14 }}>
                                                                                            {section.isContingency
                                                                                                ? renderEditorInput({
                                                                                                    label: 'Contingency original development budget',
                                                                                                    value: costEditorDraft?.contingencyBudgetTotal,
                                                                                                    onChange: (event) => setCostEditorDraft((previous) => ({ ...(previous || {}), contingencyBudgetTotal: event.target.value }))
                                                                                                })
                                                                                                : formatEditorMoney(section.budgetTotal)}
                                                                                        </TableCell>
                                                                                    </TableRow>
                                                                                    {section.rows.map((row, index) => (
                                                                                        <TableRow key={`${section.sectionKey}-${index}`}>
                                                                                            <TableCell sx={{ pl: 3, py: 0.8, fontWeight: 700 }}>{row?.item || `Item ${index + 1}`}</TableCell>
                                                                                            <TableCell align="right" sx={{ py: 0.4 }}>
                                                                                                {section.isContingency ? '—' : renderEditorInput({
                                                                                                    label: `${row?.item || `Item ${index + 1}`} original development budget`,
                                                                                                    value: row?.budget,
                                                                                                    onChange: (event) => updateCostEditorRow(section.sectionKey, index, 'budget', event.target.value)
                                                                                                })}
                                                                                            </TableCell>
                                                                                        </TableRow>
                                                                                    ))}
                                                                                </React.Fragment>
                                                                            ))}
                                                                            <TableRow sx={{ bgcolor: '#e5e7eb' }}>
                                                                                <TableCell sx={{ py: 1.25, fontWeight: 900, fontSize: 14 }}>Total Development Cost (excl VAT)</TableCell>
                                                                                <TableCell align="right" sx={{ py: 1.25, fontWeight: 900, fontSize: 14 }}>{formatEditorMoney(overallBudget)}</TableCell>
                                                                            </TableRow>
                                                                        </TableBody>
                                                                    </Table>
                                                                </TableContainer>
                                                            );
                                                        })()}
                                                    </DialogContent>
                                                    <DialogActions>
                                                        <Button onClick={() => setCostEditorOpen(false)} disabled={costEditorSaving}>Cancel</Button>
                                                        <Button variant="contained" onClick={() => void saveCostEditor()} disabled={costEditorSaving}>
                                                            {costEditorSaving ? 'Saving…' : 'Save Changes'}
                                                        </Button>
                                                    </DialogActions>
                                                </Dialog>
                                            </div>
                                        )}

                                        {page === 'conditions' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Planning & Statutory Conditions Tracker</h2>

                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        {planningError && (
                                                            <div style={{ marginTop: 10, color: '#b91c1c', fontSize: 13 }}>
                                                                <strong>Error:</strong> {planningError}
                                                            </div>
                                                        )}

                                                        <div style={{ marginTop: 12, overflowX: 'auto' }}>
                                                            {planningLoading && !planningItems.length ? (
                                                                <div style={{ color: '#666' }}>Loading…</div>
                                                            ) : null}

                                                            {!planningLoading && !planningItems.length ? (
                                                                <div style={{ color: '#666' }}>No tracked conditions yet.</div>
                                                            ) : null}

                                                            {!!planningItems.length && (() => {
                                                                const groupedItems = planningItems.reduce((acc, item) => {
                                                                    const key = String(item?.notice_id || 'ungrouped');
                                                                    if (!acc[key]) acc[key] = [];
                                                                    acc[key].push(item);
                                                                    return acc;
                                                                }, {});
                                                                const statusLabelMap = {
                                                                    not_submitted: 'Not submitted',
                                                                    part_submitted: 'Part submitted',
                                                                    submitted: 'Submitted',
                                                                    refused: 'Refused',
                                                                    discharged: 'Discharged',
                                                                    not_applicable: 'Not applicable'
                                                                };

                                                                return Object.values(groupedItems).map((group, groupIndex) => {
                                                                    const first = group[0] || {};
                                                                    const planningReference = String(first?.notice_planning_reference || '').trim();
                                                                    const noticeHeader = planningReference
                                                                        ? `Planning Reference: ${planningReference}`
                                                                        : (String(first?.notice_file_name || '').trim() || `Planning Notice ${groupIndex + 1}`);

                                                                    return (
                                                                        <div key={`planning-group-${String(first?.notice_id || groupIndex)}`} style={{ marginBottom: 18 }}>
                                                                            <div style={{ marginBottom: 8, padding: '10px 12px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe', fontWeight: 800, color: '#1d4ed8' }}>
                                                                                {noticeHeader}
                                                                            </div>
                                                                            <table style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 12, lineHeight: 1.25 }}>
                                                                                <thead>
                                                                                    <tr style={{ background: '#f3f4f6' }}>
                                                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', minWidth: 140 }}>Condition No.</th>
                                                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', minWidth: 520 }}>Abridged Description</th>
                                                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', minWidth: 220 }}>Type</th>
                                                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', minWidth: 200 }}>Status</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {group.map((it) => {
                                                                                        const id = it?.id;
                                                                                        const status = String(it?.status || 'not_submitted');
                                                                                        const typeRaw = String(it?.condition_type || '').trim();
                                                                                        const typeLabel = (typeRaw.toLowerCase() === 'ongoing/compliance') ? 'Compliance' : (typeRaw || '—');
                                                                                        const statusStyle = (() => {
                                                                                            if (status === 'discharged') return { background: '#dcfce7', borderColor: '#16a34a', color: '#166534' };
                                                                                            if (status === 'submitted' || status === 'part_submitted') return { background: '#fef3c7', borderColor: '#f59e0b', color: '#92400e' };
                                                                                            if (status === 'refused') return { background: '#fee2e2', borderColor: '#dc2626', color: '#991b1b' };
                                                                                            if (status === 'not_applicable') return { background: '#f3f4f6', borderColor: '#d1d5db', color: '#374151' };
                                                                                            return { background: '#fff', borderColor: '#d1d5db', color: '#111827' };
                                                                                        })();
                                                                                        return (
                                                                                            <tr key={String(id)}>
                                                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>{it?.condition_number || '—'}</td>
                                                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb' }}>{it?.abridged_description || '—'}</td>
                                                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{typeLabel}</td>
                                                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb' }}>
                                                                                                    <select
                                                                                                        value={status}
                                                                                                        onChange={(e) => {
                                                                                                            const next = e.target.value;
                                                                                                            updatePlanningConditionStatus({ id, status: next }).catch((err) => setPlanningError(err?.message || 'Update failed'));
                                                                                                        }}
                                                                                                        style={{
                                                                                                            padding: '6px 10px',
                                                                                                            borderRadius: 8,
                                                                                                            border: `1px solid ${statusStyle.borderColor}`,
                                                                                                            fontWeight: 800,
                                                                                                            fontSize: 12,
                                                                                                            background: statusStyle.background,
                                                                                                            color: statusStyle.color
                                                                                                        }}
                                                                                                    >
                                                                                                        {Object.keys(statusLabelMap).map((k) => (
                                                                                                            <option key={k} value={k}>{statusLabelMap[k]}</option>
                                                                                                        ))}
                                                                                                    </select>
                                                                                                </td>
                                                                                            </tr>
                                                                                        );
                                                                                    })}
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    );
                                                                });
                                                            })()}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {page === 'risk' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Report Risk Matrix</h2>

                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                                Questionnaire-confirmed risk values are shown here as read-only. Use the questionnaire review flow to update risk ratings and commentary.
                                                            </div>
                                                        </div>

                                                        {riskAiError && (
                                                            <div style={{ marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>
                                                                <strong>AI failed:</strong> {riskAiError}
                                                            </div>
                                                        )}

                                                        {riskLoading && (
                                                            <div style={{ color: '#666', marginBottom: 10 }}>Loading…</div>
                                                        )}

                                                        {riskError && (
                                                            <div style={{ marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>
                                                                <strong>Load failed:</strong> {riskError}
                                                            </div>
                                                        )}

                                                        <div style={{ border: '2px solid #e5e7eb', borderRadius: 12, background: '#f9fafb', padding: 24, minHeight: 320 }}>
                                                            <form style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: deviceType === 'desktop' ? 1100 : 700 }}>
                                                                {Object.keys(riskMatrix).length === 0 ? (
                                                                    <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>No risk criteria found for this project.</div>
                                                                ) : (
                                                                    Object.keys(riskMatrix).map((criteria) => {
                                                                        const onRiskChange = (e) => setRiskMatrix((prev) => ({
                                                                            ...prev,
                                                                            [criteria]: {
                                                                                ...(prev[criteria] || { rating: '', comment: '' }),
                                                                                rating: e.target.value
                                                                            }
                                                                        }));
                                                                        const onRiskCommentChange = (e) => setRiskMatrix((prev) => ({
                                                                            ...prev,
                                                                            [criteria]: {
                                                                                ...(prev[criteria] || { rating: '', comment: '' }),
                                                                                comment: e.target.value
                                                                            }
                                                                        }));
                                                                        const rating = normalizeRiskRating((riskMatrix[criteria]?.rating || '').trim());
                                                                        return (
                                                                            <div key={criteria} style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
                                                                                <label style={{ width: 180, fontWeight: 500, marginTop: 8 }}>{criteria}</label>
                                                                                <select
                                                                                    value={rating}
                                                                                    onChange={onRiskChange}
                                                                                    disabled={riskMatrixReadOnly}
                                                                                    style={{
                                                                                        width: 140,
                                                                                        padding: '0.5rem',
                                                                                        borderRadius: 4,
                                                                                        border: '1px solid #ccc',
                                                                                        fontWeight: 600,
                                                                                        color:
                                                                                            rating === 'High' ? '#fff' :
                                                                                                rating === 'Medium' ? '#111827' :
                                                                                                    rating === 'Low' ? '#fff' : '#111',
                                                                                        background:
                                                                                            rating === 'High' ? '#dc2626' :
                                                                                                rating === 'Medium' ? '#f59e0b' :
                                                                                                    rating === 'Low' ? '#22c55e' : '#fff',
                                                                                        transition: 'background 0.2s, color 0.2s',
                                                                                        opacity: riskMatrixReadOnly ? 0.9 : 1,
                                                                                        cursor: riskMatrixReadOnly ? 'not-allowed' : 'pointer'
                                                                                    }}
                                                                                >
                                                                                    <option value="">Select rating</option>
                                                                                    <option value="High">High (Red)</option>
                                                                                    <option value="Medium">Medium (Yellow)</option>
                                                                                    <option value="Low">Low (Green)</option>
                                                                                </select>
                                                                                <textarea
                                                                                    value={riskMatrix[criteria]?.comment || ''}
                                                                                    onChange={onRiskCommentChange}
                                                                                    disabled={riskMatrixReadOnly}
                                                                                    placeholder="Enter commentary for this risk rating..."
                                                                                    style={{ flex: 1, minHeight: 60, padding: '0.5rem', borderRadius: 4, border: '1px solid #ccc', resize: 'vertical', opacity: riskMatrixReadOnly ? 0.9 : 1, cursor: riskMatrixReadOnly ? 'not-allowed' : 'text' }}
                                                                                />
                                                                            </div>
                                                                        );
                                                                    })
                                                                )}
                                                            </form>
                                                            {Object.keys(riskMatrix).length > 0 && Object.values(riskMatrix).every((v) => !(v?.rating || '').trim()) && (
                                                                <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', marginTop: 24 }}>
                                                                    Please select a risk rating and add commentary for each criterion above.
                                                                </div>
                                                            )}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {page === 'drawdown' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Project Drawdown Tracker</h2>

                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 10 }}>
                                                            Updates are saved against this project (same data as Project Summary).
                                                        </div>

                                                        {drawdownPrefillLoading && (
                                                            <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>
                                                                Linking Interim drawdown values…
                                                            </div>
                                                        )}

                                                        {!drawdownPrefillLoading && drawdownPrefillError && (
                                                            <div style={{ color: '#b91c1c', fontSize: 12, marginBottom: 8 }}>
                                                                {drawdownPrefillError}
                                                            </div>
                                                        )}

                                                        <SCurveChart
                                                            {...(() => {
                                                                const row = initialReportForPortal?.tables?.ini_section_7_costs?.[0];
                                                                if (!row) return { baselineAmount: null, baselineSource: null };
                                                                const devGIA = initialReportForPortal?.tables?.ini_section_6_development?.[0]?.gross_internal_area || null;
                                                                const b = computeSCurveBaseline({ row, devGrossInternalArea: devGIA, valuesByKey: costAssessmentsForPortal });
                                                                return { baselineAmount: b?.amount ?? null, baselineSource: b?.source ?? null };
                                                            })()}
                                                            duration={Number(programmeTrackerState?.surveyor_duration_months || 0) || Number(data?.project_duration || 0) || null}
                                                            storageKeyPrefix={reference}
                                                            prefillActuals={drawdownPrefillActuals}
                                                        />
                                                        <DrawdownHistoryTable
                                                            items={drawdownHistory}
                                                            projectSetupRecord={projectSetupRecord}
                                                            loading={drawdownHistoryLoading}
                                                            error={drawdownHistoryError}
                                                        />
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {page === 'programme' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Programme Tracker</h2>
                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setProgrammeTrackerManualOverride((prev) => !prev);
                                                                    setProgrammeTrackerError('');
                                                                    setProgrammeTrackerWarning('');
                                                                }}
                                                                disabled={programmeTrackerLoading || programmeTrackerSaving}
                                                                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: programmeTrackerManualOverride ? '#1d4ed8' : '#f9fafb', color: programmeTrackerManualOverride ? '#fff' : '#111827', fontWeight: 700, cursor: (programmeTrackerLoading || programmeTrackerSaving) ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                {programmeTrackerManualOverride ? 'Manual Override On' : 'Enable Manual Override'}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => { runProgrammeTrackerTargetedOcrRead(); }}
                                                                disabled={programmeTrackerLoading || programmeTrackerSaving || programmeTrackerOcrBusy}
                                                                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #1d4ed8', background: '#dbeafe', color: '#1e3a8a', fontWeight: 700, cursor: (programmeTrackerLoading || programmeTrackerSaving || programmeTrackerOcrBusy) ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                {programmeTrackerOcrBusy ? 'Running Targeted OCR…' : 'Targeted OCR Read (Previous Issued Report)'}
                                                            </button>
                                                            {programmeTrackerManualOverride && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { saveProgrammeTrackerManualOverride(); }}
                                                                    disabled={programmeTrackerSaving || programmeTrackerLoading}
                                                                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #166534', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: (programmeTrackerSaving || programmeTrackerLoading) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {programmeTrackerSaving ? 'Saving...' : 'Save Override'}
                                                                </button>
                                                            )}
                                                            {programmeTrackerManualOverride && (
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        setProgrammeTrackerError('');
                                                                        setProgrammeTrackerWarning('');
                                                                        await syncProgrammeTrackerFromQuestionnaire({ silent: false, overwrite: true });
                                                                        await refreshProgrammeTracker({ silent: true });
                                                                        setProgrammeTrackerManualOverride(false);
                                                                    }}
                                                                    disabled={programmeTrackerSaving || programmeTrackerLoading}
                                                                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: (programmeTrackerSaving || programmeTrackerLoading) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    Reset From Questionnaire/Setup
                                                                </button>
                                                            )}
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                                {programmeTrackerManualOverride
                                                                    ? 'Manual override active. Edits are saved to Programme Tracker.'
                                                                    : 'Default source is deterministic questionnaire/setup data.'}
                                                            </div>
                                                        </div>

                                                        {programmeTrackerError && (
                                                            <div style={{ marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>
                                                                <strong>Error:</strong> {programmeTrackerError}
                                                            </div>
                                                        )}

                                                        {programmeTrackerWarning && !programmeTrackerError && (
                                                            <div style={{ marginBottom: 10, color: '#92400e', fontSize: 13 }}>
                                                                {programmeTrackerWarning}
                                                            </div>
                                                        )}

                                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 10 }}>
                                                            {programmeTrackerSource ? `Source: ${programmeTrackerSource}` : 'Source: not set'}
                                                            {programmeTrackerUpdatedAt ? ` | Updated: ${new Date(programmeTrackerUpdatedAt).toLocaleString()}` : ''}
                                                        </div>
                                                        <div style={{ marginTop: 8 }}>
                                                            <h3 style={{ margin: '0 0 8px 0', textDecoration: 'underline' }}>Programme Details</h3>
                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '38%' }}>Element</th>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '62%' }}>Assessment</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {[
                                                                            {
                                                                                label: 'Start on Site Date',
                                                                                key: 'works_start_date'
                                                                            },
                                                                            {
                                                                                label: 'Borrower Assessment',
                                                                                key: 'borrower_duration_months'
                                                                            },
                                                                            {
                                                                                label: 'Borrower Estimated Completion Date',
                                                                                key: 'borrower_completion_date'
                                                                            },
                                                                            {
                                                                                label: 'Surveyor Assessment',
                                                                                key: 'surveyor_duration_months'
                                                                            },
                                                                            {
                                                                                label: 'Surveyor Assessment Completion Date',
                                                                                key: 'surveyor_completion_date'
                                                                            }
                                                                        ].map((row) => (
                                                                            <tr key={row.key}>
                                                                                <td style={{ border: '1px solid #111827', padding: '8px 10px', fontWeight: 700 }}>{row.label}</td>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    {(() => {
                                                                                        const rawValue = String(programmeTrackerState?.[row.key] || '');
                                                                                        const shouldMarkCorrected = row.key === 'borrower_completion_date'
                                                                                            && programmeTrackerBorrowerCompletionCorrected
                                                                                            && !programmeTrackerManualOverride
                                                                                            && rawValue;
                                                                                        const displayValue = shouldMarkCorrected
                                                                                            ? `${rawValue}*`
                                                                                            : rawValue;
                                                                                        return (
                                                                                            <input
                                                                                                value={displayValue}
                                                                                                readOnly={!programmeTrackerManualOverride}
                                                                                                onChange={(e) => setProgrammeTrackerState((prev) => ({ ...prev, [row.key]: e.target.value }))}
                                                                                                style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                            />
                                                                                        );
                                                                                    })()}
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            {programmeTrackerBorrowerCompletionCorrected && (
                                                                <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                                                                    *: Corrected Figure
                                                                </div>
                                                            )}
                                                        </div>

                                                        <div style={{ marginTop: 14 }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                                                                <h3 style={{ margin: 0, textDecoration: 'underline' }}>Programme Risks Table</h3>
                                                                {programmeTrackerManualOverride && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => { addProgrammeTrackerRiskRow(); }}
                                                                        style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: 'pointer' }}
                                                                    >
                                                                        Add Row
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left' }}>Programme Risks</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {(Array.isArray(programmeTrackerState?.risks) && programmeTrackerState.risks.length
                                                                            ? programmeTrackerState.risks
                                                                            : ['']).map((risk, idx) => (
                                                                                <tr key={`programme-risk-${idx}`}>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        {programmeTrackerManualOverride ? (
                                                                                            <div style={{ display: 'flex', alignItems: 'stretch' }}>
                                                                                                <input
                                                                                                    value={String(risk || '')}
                                                                                                    onChange={(e) => { updateProgrammeTrackerRiskRow(idx, e.target.value); }}
                                                                                                    style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                                />
                                                                                                <button
                                                                                                    type="button"
                                                                                                    onClick={() => { removeProgrammeTrackerRiskRow(idx); }}
                                                                                                    style={{ border: 0, borderLeft: '1px solid #d1d5db', background: '#fff1f2', color: '#9f1239', fontWeight: 700, padding: '0 10px', cursor: 'pointer' }}
                                                                                                >
                                                                                                    Remove
                                                                                                </button>
                                                                                            </div>
                                                                                        ) : (
                                                                                            <div style={{ padding: '8px 10px' }}>{String(risk || '').trim() || 'No programme risks recorded.'}</div>
                                                                                        )}
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {page === 'design_team' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Design Team Tracker</h2>
                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => setDesignTeamManualOverride((previous) => !previous)}
                                                                disabled={designTeamLoading || designTeamSaving}
                                                                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: designTeamManualOverride ? '#334155' : '#f9fafb', color: designTeamManualOverride ? '#fff' : '#111827', fontWeight: 700 }}
                                                            >
                                                                {designTeamManualOverride ? 'Manual Override On' : 'Enable Manual Override'}
                                                            </button>
                                                            {designTeamManualOverride && (
                                                                <>
                                                                    <button type="button" onClick={saveDesignTeamTracker} disabled={designTeamSaving} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #166534', background: '#16a34a', color: '#fff', fontWeight: 700 }}>
                                                                        {designTeamSaving ? 'Saving...' : 'Save Override'}
                                                                    </button>
                                                                    <button type="button" onClick={() => setDesignTeamMembers((previous) => [...previous, { role: '', appointment: '', collateral_warranty_provided: false, collateral_warranty_status: '' }])} style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', fontWeight: 700 }}>
                                                                        Add Team Member
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                        {designTeamError && <div style={{ marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>{designTeamError}</div>}
                                                        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 10 }}>
                                                            {designTeamSource ? `Source: ${designTeamSource}` : 'Source: not set'}
                                                            {designTeamUpdatedAt ? ` | Updated: ${new Date(designTeamUpdatedAt).toLocaleString()}` : ''}
                                                        </div>
                                                        <div style={{ overflowX: 'auto' }}>
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 850 }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '22%' }}>Role</th>
                                                                        <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '31%' }}>Appointment</th>
                                                                        <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '15%' }}>Warranty Provided</th>
                                                                        <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left' }}>Warranty Status</th>
                                                                        {designTeamManualOverride && <th style={{ border: '1px solid #111827', width: 84 }}>Action</th>}
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {designTeamMembers.map((member, index) => (
                                                                        <tr key={`design-team-${index}`}>
                                                                            {['role', 'appointment'].map((field) => (
                                                                                <td key={field} style={{ border: '1px solid #111827', padding: designTeamManualOverride ? 0 : '8px 10px' }}>
                                                                                    {designTeamManualOverride ? <input value={member?.[field] || ''} onChange={(event) => updateDesignTeamMember(index, field, event.target.value)} style={{ width: '100%', border: 0, padding: '8px 10px' }} /> : (member?.[field] || '-')}
                                                                                </td>
                                                                            ))}
                                                                            <td style={{ border: '1px solid #111827', padding: '8px 10px', textAlign: 'center' }}>
                                                                                <input type="checkbox" checked={member?.collateral_warranty_provided === true} disabled={!designTeamManualOverride} onChange={(event) => updateDesignTeamMember(index, 'collateral_warranty_provided', event.target.checked)} />
                                                                            </td>
                                                                            <td style={{ border: '1px solid #111827', padding: designTeamManualOverride ? 0 : '8px 10px' }}>
                                                                                {designTeamManualOverride ? <input value={member?.collateral_warranty_status || ''} onChange={(event) => updateDesignTeamMember(index, 'collateral_warranty_status', event.target.value)} style={{ width: '100%', border: 0, padding: '8px 10px' }} /> : (member?.collateral_warranty_status || '-')}
                                                                            </td>
                                                                            {designTeamManualOverride && <td style={{ border: '1px solid #111827', textAlign: 'center' }}><button type="button" onClick={() => setDesignTeamMembers((previous) => previous.filter((_, memberIndex) => memberIndex !== index))}>Remove</button></td>}
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                        {page === 'insurance' && (
                                            <div style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                                                <h2 style={{ margin: '0 0 12px 0', fontSize: 18 }}>Insurance Tracker</h2>
                                                {mode !== 'surveyor' ? (
                                                    <div style={{ color: '#666' }}>This view is available in surveyor mode only.</div>
                                                ) : (
                                                    <>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setInsuranceManualOverride((prev) => !prev);
                                                                    setInsuranceError('');
                                                                }}
                                                                disabled={insuranceLoading || insuranceSaving}
                                                                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: insuranceManualOverride ? '#1d4ed8' : '#f9fafb', color: insuranceManualOverride ? '#fff' : '#111827', fontWeight: 700, cursor: (insuranceLoading || insuranceSaving) ? 'not-allowed' : 'pointer' }}
                                                            >
                                                                {insuranceManualOverride ? 'Manual Override On' : 'Enable Manual Override'}
                                                            </button>
                                                            {insuranceManualOverride && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => { saveInsuranceTrackerManualOverride(); }}
                                                                    disabled={insuranceSaving || insuranceLoading}
                                                                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #166534', background: '#16a34a', color: '#fff', fontWeight: 700, cursor: (insuranceSaving || insuranceLoading) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    {insuranceSaving ? 'Saving...' : 'Save Override'}
                                                                </button>
                                                            )}
                                                            {insuranceManualOverride && (
                                                                <button
                                                                    type="button"
                                                                    onClick={async () => {
                                                                        setInsuranceError('');
                                                                        await syncInsuranceTrackerFromQuestionnaire({ silent: false, overwrite: true });
                                                                        await refreshInsuranceTracker({ silent: true });
                                                                        setInsuranceManualOverride(false);
                                                                    }}
                                                                    disabled={insuranceSaving || insuranceLoading}
                                                                    style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: (insuranceSaving || insuranceLoading) ? 'not-allowed' : 'pointer' }}
                                                                >
                                                                    Reset From Questionnaire/OCR
                                                                </button>
                                                            )}
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                                {insuranceManualOverride
                                                                    ? 'Manual override active. Edits are saved to the Insurance Tracker.'
                                                                    : 'Default source is questionnaire and OCR extraction.'}
                                                            </div>
                                                            <div style={{ color: '#6b7280', fontSize: 12 }}>
                                                                Coverage Amount accepts TBC or numeric values (auto-formatted as £). Expiry Date accepts TBC or DD/MM/YYYY.
                                                            </div>
                                                        </div>

                                                        {insuranceError && (
                                                            <div style={{ marginBottom: 10, color: '#b91c1c', fontSize: 13 }}>
                                                                <strong>Error:</strong> {insuranceError}
                                                            </div>
                                                        )}

                                                        {!insuranceLoading && !insuranceBorrowerPolicies.length && !insuranceContractorPolicies.length && !insuranceSubcontractorPolicies.length && !insuranceConsultantPi.length ? (
                                                            <div style={{ marginBottom: 10, fontSize: 12, color: '#6b7280' }}>No insurance items recorded yet.</div>
                                                        ) : null}

                                                        <div
                                                            style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: deviceType === 'desktop' ? '1fr 1fr' : '1fr',
                                                                gap: 18,
                                                                alignItems: 'start'
                                                            }}
                                                        >
                                                            <div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                                    <h3 style={{ margin: 0, textDecoration: 'underline' }}>Developer and Project Insurances</h3>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={insuranceRelevance.borrower !== false}
                                                                                onChange={async (e) => {
                                                                                    const next = { ...insuranceRelevance, borrower: Boolean(e.target.checked) };
                                                                                    setInsuranceRelevance(next);
                                                                                    await saveInsuranceRelevanceOnly(next);
                                                                                }}
                                                                                disabled={insuranceLoading || insuranceSaving}
                                                                            />
                                                                            Relevant
                                                                        </label>
                                                                        {insuranceManualOverride && insuranceRelevance.borrower !== false && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { addInsuranceRow('borrower'); }}
                                                                                style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: 'pointer' }}
                                                                            >
                                                                                Add Policy
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {insuranceRelevance.borrower === false && (
                                                                    <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Marked as not relevant for this project.</div>
                                                                )}

                                                                <div style={{ overflowX: 'auto' }}>
                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '34%' }}>Coverage Type</th>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '44%' }}>Coverage Amount</th>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '22%' }}>Expiry Date</th>
                                                                                {insuranceManualOverride && insuranceRelevance.borrower !== false && <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'center', width: 90 }}>Action</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {insuranceBorrowerPolicies.map((row, idx) => (
                                                                                <tr key={String(row.id ?? row._cid ?? idx)}>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={row.policy || ''}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.borrower === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('borrower', idx, { policy: v });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                        />
                                                                                    </td>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={row.coverage || ''}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.borrower === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('borrower', idx, { coverage: v });
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const val = String(insuranceBorrowerPolicies?.[idx]?.coverage || '').trim();
                                                                                                const next = normalizeInsuranceCoverageValue(val);
                                                                                                if (!isInsuranceCoverageValueValid(val)) {
                                                                                                    setInsuranceError(`Developer and Project Insurances: row ${idx + 1} coverage must be TBC or numeric currency.`);
                                                                                                    return;
                                                                                                }
                                                                                                updateInsuranceRow('borrower', idx, { coverage: next });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                        />
                                                                                    </td>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={(insuranceManualOverride && insuranceRelevance.borrower !== false) ? (row.expiry_date || '') : formatInsuranceTrackerDate(row.expiry_date || '')}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.borrower === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('borrower', idx, { expiry_date: v, is_expired: isInsuranceTrackerDateExpired(v) });
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const val = String(insuranceBorrowerPolicies?.[idx]?.expiry_date || '').trim();
                                                                                                const next = normalizeInsuranceExpiryValue(val);
                                                                                                if (!isInsuranceExpiryValueValid(val)) {
                                                                                                    setInsuranceError(`Developer and Project Insurances: row ${idx + 1} expiry date must be TBC or DD/MM/YYYY.`);
                                                                                                    return;
                                                                                                }
                                                                                                updateInsuranceRow('borrower', idx, { expiry_date: next, is_expired: isInsuranceTrackerDateExpired(next) });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px', backgroundColor: row.is_expired ? '#fef08a' : '#ffffff' }}
                                                                                        />
                                                                                    </td>
                                                                                    {insuranceManualOverride && insuranceRelevance.borrower !== false && (
                                                                                        <td style={{ border: '1px solid #111827', padding: '6px 8px', textAlign: 'center' }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => { removeInsuranceRow('borrower', idx); }}
                                                                                                style={{ padding: '4px 7px', borderRadius: 7, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                                                                                            >
                                                                                                Remove
                                                                                            </button>
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>

                                                            <div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                                    <h3 style={{ margin: 0, textDecoration: 'underline' }}>Consultant PI Insurances</h3>
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={insuranceRelevance.consultant_pi !== false}
                                                                                onChange={async (e) => {
                                                                                    const next = { ...insuranceRelevance, consultant_pi: Boolean(e.target.checked) };
                                                                                    setInsuranceRelevance(next);
                                                                                    await saveInsuranceRelevanceOnly(next);
                                                                                }}
                                                                                disabled={insuranceLoading || insuranceSaving}
                                                                            />
                                                                            Relevant
                                                                        </label>
                                                                        {insuranceManualOverride && insuranceRelevance.consultant_pi !== false && (
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => { addInsuranceRow('consultant_pi'); }}
                                                                                style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: 'pointer' }}
                                                                            >
                                                                                Add Policy
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                </div>

                                                                {insuranceRelevance.consultant_pi === false && (
                                                                    <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Marked as not relevant for this project.</div>
                                                                )}

                                                                <div style={{ overflowX: 'auto' }}>
                                                                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '34%' }}>Consultant</th>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '44%' }}>Coverage Amount</th>
                                                                                <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '22%' }}>Expiry Date</th>
                                                                                {insuranceManualOverride && insuranceRelevance.consultant_pi !== false && <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'center', width: 90 }}>Action</th>}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {insuranceConsultantPi.map((row, idx) => (
                                                                                <tr key={String(row.id ?? row._cid ?? idx)}>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={row.consultant || ''}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.consultant_pi === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('consultant_pi', idx, { consultant: v });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                        />
                                                                                    </td>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={row.coverage || ''}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.consultant_pi === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('consultant_pi', idx, { coverage: v });
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const val = String(insuranceConsultantPi?.[idx]?.coverage || '').trim();
                                                                                                const next = normalizeInsuranceCoverageValue(val);
                                                                                                if (!isInsuranceCoverageValueValid(val)) {
                                                                                                    setInsuranceError(`Consultant PI Insurances: row ${idx + 1} coverage must be TBC or numeric currency.`);
                                                                                                    return;
                                                                                                }
                                                                                                updateInsuranceRow('consultant_pi', idx, { coverage: next });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                        />
                                                                                    </td>
                                                                                    <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                        <input
                                                                                            value={(insuranceManualOverride && insuranceRelevance.consultant_pi !== false) ? (row.expiry_date || '') : formatInsuranceTrackerDate(row.expiry_date || '')}
                                                                                            readOnly={!insuranceManualOverride || insuranceRelevance.consultant_pi === false}
                                                                                            onChange={(e) => {
                                                                                                const v = e.target.value;
                                                                                                updateInsuranceRow('consultant_pi', idx, { expiry_date: v, is_expired: isInsuranceTrackerDateExpired(v) });
                                                                                            }}
                                                                                            onBlur={() => {
                                                                                                const val = String(insuranceConsultantPi?.[idx]?.expiry_date || '').trim();
                                                                                                const next = normalizeInsuranceExpiryValue(val);
                                                                                                if (!isInsuranceExpiryValueValid(val)) {
                                                                                                    setInsuranceError(`Consultant PI Insurances: row ${idx + 1} expiry date must be TBC or DD/MM/YYYY.`);
                                                                                                    return;
                                                                                                }
                                                                                                updateInsuranceRow('consultant_pi', idx, { expiry_date: next, is_expired: isInsuranceTrackerDateExpired(next) });
                                                                                            }}
                                                                                            style={{ width: '100%', border: 0, padding: '8px 10px', backgroundColor: row.is_expired ? '#fef08a' : '#ffffff' }}
                                                                                        />
                                                                                    </td>
                                                                                    {insuranceManualOverride && insuranceRelevance.consultant_pi !== false && (
                                                                                        <td style={{ border: '1px solid #111827', padding: '6px 8px', textAlign: 'center' }}>
                                                                                            <button
                                                                                                type="button"
                                                                                                onClick={() => { removeInsuranceRow('consultant_pi', idx); }}
                                                                                                style={{ padding: '4px 7px', borderRadius: 7, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                                                                                            >
                                                                                                Remove
                                                                                            </button>
                                                                                        </td>
                                                                                    )}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: 18 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                                <h3 style={{ margin: 0, textDecoration: 'underline' }}>Contractor Insurances</h3>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={insuranceRelevance.contractor !== false}
                                                                            onChange={async (e) => {
                                                                                const next = { ...insuranceRelevance, contractor: Boolean(e.target.checked) };
                                                                                setInsuranceRelevance(next);
                                                                                await saveInsuranceRelevanceOnly(next);
                                                                            }}
                                                                            disabled={insuranceLoading || insuranceSaving}
                                                                        />
                                                                        Relevant
                                                                    </label>
                                                                    {insuranceManualOverride && insuranceRelevance.contractor !== false && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => { addInsuranceRow('contractor'); }}
                                                                            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: 'pointer' }}
                                                                        >
                                                                            Add Policy
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {insuranceRelevance.contractor === false && (
                                                                <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Marked as not relevant for this project.</div>
                                                            )}

                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '34%' }}>Coverage Type</th>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '44%' }}>Coverage Amount</th>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '22%' }}>Expiry Date</th>
                                                                            {insuranceManualOverride && insuranceRelevance.contractor !== false && <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'center', width: 90 }}>Action</th>}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {insuranceContractorPolicies.map((row, idx) => (
                                                                            <tr key={String(row.id ?? row._cid ?? idx)}>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={row.policy || ''}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.contractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('contractor', idx, { policy: v });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={row.coverage || ''}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.contractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('contractor', idx, { coverage: v });
                                                                                        }}
                                                                                        onBlur={() => {
                                                                                            const val = String(insuranceContractorPolicies?.[idx]?.coverage || '').trim();
                                                                                            const next = normalizeInsuranceCoverageValue(val);
                                                                                            if (!isInsuranceCoverageValueValid(val)) {
                                                                                                setInsuranceError(`Contractor Insurances: row ${idx + 1} coverage must be TBC or numeric currency.`);
                                                                                                return;
                                                                                            }
                                                                                            updateInsuranceRow('contractor', idx, { coverage: next });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={(insuranceManualOverride && insuranceRelevance.contractor !== false) ? (row.expiry_date || '') : formatInsuranceTrackerDate(row.expiry_date || '')}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.contractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('contractor', idx, { expiry_date: v, is_expired: isInsuranceTrackerDateExpired(v) });
                                                                                        }}
                                                                                        onBlur={() => {
                                                                                            const val = String(insuranceContractorPolicies?.[idx]?.expiry_date || '').trim();
                                                                                            const next = normalizeInsuranceExpiryValue(val);
                                                                                            if (!isInsuranceExpiryValueValid(val)) {
                                                                                                setInsuranceError(`Contractor Insurances: row ${idx + 1} expiry date must be TBC or DD/MM/YYYY.`);
                                                                                                return;
                                                                                            }
                                                                                            updateInsuranceRow('contractor', idx, { expiry_date: next, is_expired: isInsuranceTrackerDateExpired(next) });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px', backgroundColor: row.is_expired ? '#fef08a' : '#ffffff' }}
                                                                                    />
                                                                                </td>
                                                                                {insuranceManualOverride && insuranceRelevance.contractor !== false && (
                                                                                    <td style={{ border: '1px solid #111827', padding: '6px 8px', textAlign: 'center' }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => { removeInsuranceRow('contractor', idx); }}
                                                                                            style={{ padding: '4px 7px', borderRadius: 7, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                                                                                        >
                                                                                            Remove
                                                                                        </button>
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>

                                                        <div style={{ marginTop: 18 }}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                                                                <h3 style={{ margin: 0, textDecoration: 'underline' }}>Sub-contractor Insurances</h3>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#374151' }}>
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={insuranceRelevance.subcontractor !== false}
                                                                            onChange={async (e) => {
                                                                                const next = { ...insuranceRelevance, subcontractor: Boolean(e.target.checked) };
                                                                                setInsuranceRelevance(next);
                                                                                await saveInsuranceRelevanceOnly(next);
                                                                            }}
                                                                            disabled={insuranceLoading || insuranceSaving}
                                                                        />
                                                                        Relevant
                                                                    </label>
                                                                    {insuranceManualOverride && insuranceRelevance.subcontractor !== false && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => { addInsuranceRow('subcontractor'); }}
                                                                            style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', color: '#111827', fontWeight: 700, cursor: 'pointer' }}
                                                                        >
                                                                            Add Policy
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {insuranceRelevance.subcontractor === false && (
                                                                <div style={{ marginBottom: 8, fontSize: 12, color: '#6b7280' }}>Marked as not relevant for this project.</div>
                                                            )}

                                                            <div style={{ overflowX: 'auto' }}>
                                                                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                                    <thead>
                                                                        <tr>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '34%' }}>Sub-Contractor</th>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '44%' }}>Coverage Amount</th>
                                                                            <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'left', width: '22%' }}>Expiry Date</th>
                                                                            {insuranceManualOverride && insuranceRelevance.subcontractor !== false && <th style={{ padding: '8px 10px', border: '1px solid #111827', textAlign: 'center', width: 90 }}>Action</th>}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {insuranceSubcontractorPolicies.map((row, idx) => (
                                                                            <tr key={String(row.id ?? row._cid ?? idx)}>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={row.policy || ''}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.subcontractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('subcontractor', idx, { policy: v });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={row.coverage || ''}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.subcontractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('subcontractor', idx, { coverage: v });
                                                                                        }}
                                                                                        onBlur={() => {
                                                                                            const val = String(insuranceSubcontractorPolicies?.[idx]?.coverage || '').trim();
                                                                                            const next = normalizeInsuranceCoverageValue(val);
                                                                                            if (!isInsuranceCoverageValueValid(val)) {
                                                                                                setInsuranceError(`Sub-contractor Insurances: row ${idx + 1} coverage must be TBC or numeric currency.`);
                                                                                                return;
                                                                                            }
                                                                                            updateInsuranceRow('subcontractor', idx, { coverage: next });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px' }}
                                                                                    />
                                                                                </td>
                                                                                <td style={{ border: '1px solid #111827', padding: 0 }}>
                                                                                    <input
                                                                                        value={(insuranceManualOverride && insuranceRelevance.subcontractor !== false) ? (row.expiry_date || '') : formatInsuranceTrackerDate(row.expiry_date || '')}
                                                                                        readOnly={!insuranceManualOverride || insuranceRelevance.subcontractor === false}
                                                                                        onChange={(e) => {
                                                                                            const v = e.target.value;
                                                                                            updateInsuranceRow('subcontractor', idx, { expiry_date: v, is_expired: isInsuranceTrackerDateExpired(v) });
                                                                                        }}
                                                                                        onBlur={() => {
                                                                                            const val = String(insuranceSubcontractorPolicies?.[idx]?.expiry_date || '').trim();
                                                                                            const next = normalizeInsuranceExpiryValue(val);
                                                                                            if (!isInsuranceExpiryValueValid(val)) {
                                                                                                setInsuranceError(`Sub-contractor Insurances: row ${idx + 1} expiry date must be TBC or DD/MM/YYYY.`);
                                                                                                return;
                                                                                            }
                                                                                            updateInsuranceRow('subcontractor', idx, { expiry_date: next, is_expired: isInsuranceTrackerDateExpired(next) });
                                                                                        }}
                                                                                        style={{ width: '100%', border: 0, padding: '8px 10px', backgroundColor: row.is_expired ? '#fef08a' : '#ffffff' }}
                                                                                    />
                                                                                </td>
                                                                                {insuranceManualOverride && insuranceRelevance.subcontractor !== false && (
                                                                                    <td style={{ border: '1px solid #111827', padding: '6px 8px', textAlign: 'center' }}>
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => { removeInsuranceRow('subcontractor', idx); }}
                                                                                            style={{ padding: '4px 7px', borderRadius: 7, border: '1px solid #dc2626', background: '#fff', color: '#dc2626', fontWeight: 700, cursor: 'pointer' }}
                                                                                        >
                                                                                            Remove
                                                                                        </button>
                                                                                    </td>
                                                                                )}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}

                                    </>
                                )}
                            </AnalyticsPanelSection>

                            {actionError && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    Save failed: {actionError}
                                </Alert>
                            )}

                            {generateError && (
                                <Alert severity="error" sx={{ mt: 1.5 }}>
                                    Generate report failed: {generateError}
                                </Alert>
                            )}



                            {mode === 'developer' && (
                                <div style={{ marginTop: 12, fontSize: 13, color: '#666' }}>
                                    If you were sent here by email, keep this page open while completing the reporting process.
                                </div>
                            )}
                        </>
                    )}
                </div>
            </Container>

            {reportDataRequest && !questionnaireReviewRequest && reportDataRequest.context === generationContext && (
                <ReportDataConflictDialog
                    open
                    reference={reportDataRequest.reference}
                    reportInstance={reportDataRequest.reportInstance}
                    initialState={reportDataRequest.initialState}
                    authHeaders={authHeaders}
                    onCancel={cancelReportDataCheck}
                    onReady={() => continueReportDataGeneration(reportDataRequest)}
                />
            )}

            {questionnaireReviewRequest && questionnaireReviewRequest.context === generationContext && <QuestionnairePreflightDialog
                reference={questionnaireReviewRequest.reference}
                reportInstance={questionnaireReviewRequest.reportInstance}
                authHeaders={authHeaders}
                onCancel={cancelReportDataCheck}
                onDraft={() => continueReportDataGeneration(questionnaireReviewRequest, true)}
                onUpload={() => {
                    const request = questionnaireReviewRequest;
                    cancelReportDataCheck();
                    setDataIntroduction({ intakeType: 'data_pack', reportInstance: request.reportInstance });
                }}
                onEnter={() => {
                    const request = questionnaireReviewRequest;
                    cancelReportDataCheck();
                    setDataIntroduction({ intakeType: 'notes', reportInstance: request.reportInstance });
                }}
            />}

            <Dialog open={appendixModalOpen} onClose={() => setAppendixModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Appendices</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <FormControl fullWidth>
                            <InputLabel id="appendix-count-label">Please define required Appendices</InputLabel>
                            <Select
                                labelId="appendix-count-label"
                                label="Please define required Appendices"
                                value={appendixCount}
                                onChange={(e) => handleAppendixCountChange(e.target.value)}
                            >
                                {(appendixTarget === 'initial' ? [0, 1, 2, 3, 4, 5] : [1, 2, 3, 4, 5]).map((n) => (
                                    <MenuItem key={n} value={n}>{n}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {appendixSelections.map((item, idx) => (
                            <Stack key={item.label} spacing={1}>
                                <TextField
                                    label={item.label}
                                    value={item.label}
                                    InputProps={{ readOnly: true }}
                                />
                                <FormControl fullWidth>
                                    <InputLabel id={`appendix-doc-${idx}`}>Appendix document</InputLabel>
                                    <Select
                                        labelId={`appendix-doc-${idx}`}
                                        label="Appendix document"
                                        value={item.document || ''}
                                        onChange={(e) => handleAppendixDocChange(idx, e.target.value)}
                                    >
                                        <MenuItem value="">(Select)</MenuItem>
                                        {appendixTarget === 'initial'
                                            ? initialAppendixDocs.map((doc) => {
                                                const key = String(doc || '').trim().toLowerCase();
                                                const avail = initialAppendixAvailabilityByTitle.get(key);
                                                const label = `${doc}${avail === false ? ' (N/A)' : ''}`;
                                                return (
                                                    <MenuItem key={`${doc}-${idx}`} value={doc}>
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })
                                            : interimAppendixDocs.map((doc) => {
                                                const isSitePhotos = String(doc || '').trim().toLowerCase() === 'site photos';
                                                const label = `${doc}${isSitePhotos && interimSitePhotosAvailable === false ? ' (N/A)' : ''}`;
                                                return (
                                                    <MenuItem key={`${doc}-${idx}`} value={doc}>
                                                        {label}
                                                    </MenuItem>
                                                );
                                            })}
                                    </Select>
                                </FormControl>
                            </Stack>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAppendixModalOpen(false)} variant="text">Cancel</Button>
                    <Button
                        onClick={confirmAppendicesAndGenerate}
                        variant="contained"
                        disabled={generating}
                        startIcon={generating ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                        {generating ? 'Generating…' : 'Continue'}
                    </Button>
                </DialogActions>
                {appendixGenMessage ? (
                    <div style={{ marginTop: 10, fontSize: 13, color: appendixGenMessage === 'Appendices Generated' ? '#047857' : '#b91c1c' }}>
                        {appendixGenMessage}
                    </div>
                ) : null}
                {appendixPdfUrl ? (
                    <div style={{ marginTop: 8, fontSize: 13 }}>
                        <a href={appendixPdfUrl} target="_blank" rel="noreferrer" style={{ color: '#7c3aed', fontWeight: 800 }}>
                            View Appendices
                        </a>
                    </div>
                ) : null}
            </Dialog>
        </PortalShell>
    );
}

function InterimReportSections({ interimReport, initialReport, costAssessmentsByKey, setupCostBaseline, portalMode, projectReference, projectFacility, currentInterimNumber, interimSubmissions, authHeaders, showOnlyProjectFacilitySection = false, hideProjectFacilitySection = false, allowInterimCostChanges = false }) {
    const answers = (interimReport && interimReport.answers && typeof interimReport.answers === 'object') ? interimReport.answers : {};
    const submittedAt = interimReport?.submission?.submitted_at ? new Date(interimReport.submission.submitted_at) : null;
    const showOnlyProjectFacility = Boolean(showOnlyProjectFacilitySection);
    const hideProjectFacility = Boolean(hideProjectFacilitySection);
    const [drawdownOverrides, setDrawdownOverrides] = useState(() => {
        const raw = answers.drawdown_surveyor_overrides;
        return raw && typeof raw === 'object' ? raw : {};
    });
    const [drawdownEditor, setDrawdownEditor] = useState(null);
    const [drawdownEditorBusy, setDrawdownEditorBusy] = useState(false);
    const [drawdownEditorError, setDrawdownEditorError] = useState('');
    const [priorCumulativeCosts, setPriorCumulativeCosts] = useState(() => ({
        construction: 0,
        professional: 0,
        additional: 0,
        contingency: 0
    }));
    const [priorCumulativeByRow, setPriorCumulativeByRow] = useState(() => ({
        construction: {},
        professional: {},
        additional: {},
        contingency: {}
    }));
    const [priorDrawdownTotalsByInterim, setPriorDrawdownTotalsByInterim] = useState(() => ({}));

    function isFieldVisible(fieldId) {
        const val = (id) => String(answers?.[id] ?? '').trim().toLowerCase();
        switch (fieldId) {
            case 'cp_accidents_explain':
            case 'cp_accidents_hse_submitted':
                return val('cp_accidents') === 'yes';
            case 'cp_accidents_hse_when':
                return val('cp_accidents') === 'yes' && val('cp_accidents_hse_submitted') === 'no';
            case 'prog_total_delay':
                return val('prog_any_delays') === 'yes';
            case 'prog_units_completed':
                return val('prog_any_units_completed') === 'yes';
            case 'bc_issues_identified':
            case 'bc_site_report_issued':
                return val('bc_attended') === 'yes';
            case 'bc_issues_details':
                return val('bc_attended') === 'yes' && val('bc_issues_identified') === 'yes';
            case 'bc_when_received':
                return val('bc_attended') === 'yes' && val('bc_site_report_issued') === 'no';
            case 'pc_design_changed_details':
                return val('pc_design_changed') === 'yes';
            case 'pc_planning_changed_details':
                return val('pc_planning_changed') === 'yes';
            case 'pc_material_changes_details':
                return val('pc_material_changes_since_initial') === 'yes';
            default:
                return true;
        }
    }

    const initialTables = initialReport?.tables || {};
    // Section 7 budgets/breakdowns live in ini_section_7_costs (preferred). Keep fallback for legacy.
    const ini7 = (() => {
        const costs = Array.isArray(initialTables.ini_section_7_costs) && initialTables.ini_section_7_costs.length
            ? initialTables.ini_section_7_costs[0]
            : null;
        if (costs) return costs;
        const legacy = Array.isArray(initialTables.ini_section_7) && initialTables.ini_section_7.length
            ? initialTables.ini_section_7[0]
            : null;
        return legacy;
    })();

    function parseJsonLoose(v) {
        if (v == null) return null;
        if (typeof v === 'object') return v;
        if (typeof v !== 'string') return null;
        const s = v.trim();
        if (!s) return null;
        try { return JSON.parse(s); } catch { return null; }
    }

    function parseBreakdownRows(raw) {
        const v = parseJsonLoose(raw);
        const arr = Array.isArray(v) ? v : [];
        return arr
            .map((it) => ({
                heading: String(it?.heading ?? it?.item ?? '').trim(),
                amount: String(it?.amount ?? it?.value ?? '').trim()
            }))
            .filter((it) => (it.heading || it.amount) && normalizeKeyTextLite(it.heading) !== 'total');
    }

    function normalizeKeyTextLite(text) {
        return String(text || '')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    function parseMoneyLoose(v) {
        const s = String(v ?? '').trim();
        if (!s) return 0;
        const cleaned = s.replace(/[^0-9.-]/g, '');
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : 0;
    }

    function normalizeComparableValue(v) {
        const raw = String(v ?? '').trim();
        if (!raw) return '';
        const numeric = raw.replace(/[^0-9.-]/g, '');
        if (numeric && /^-?\d+(?:\.\d+)?$/.test(numeric)) {
            const n = Number(numeric);
            if (Number.isFinite(n)) return String(n);
        }
        return raw.toLowerCase();
    }

    function formatPoundsLite(v) {
        const n = Math.round(parseMoneyLoose(v));
        return `£${n.toLocaleString('en-GB')}`;
    }

    function formatCurrencyInput(v) {
        const numeric = String(v ?? '').replace(/[^0-9.-]/g, '');
        if (!numeric || numeric === '-' || numeric === '.' || numeric === '-.') return '';
        const n = Number(numeric);
        if (!Number.isFinite(n)) return String(v ?? '').trim();
        return n.toLocaleString('en-GB', { maximumFractionDigits: 2 });
    }

    useEffect(() => {
        const raw = answers.drawdown_surveyor_overrides;
        setDrawdownOverrides(raw && typeof raw === 'object' ? raw : {});
    }, [answers.drawdown_surveyor_overrides]);

    function makeCostItemKey(prefix, heading, index) {
        const h = String(heading || '').trim();
        if (h) return `${prefix}:${h}`;
        return `${prefix}:idx:${index + 1}`;
    }

    function getInitialCostValue(itemKey) {
        if (!costAssessmentsByKey?.get) return '';
        return costAssessmentsByKey.get(String(itemKey)) ?? '';
    }

    function getDrawdownOverride(groupKey, itemKey) {
        const groups = drawdownOverrides && typeof drawdownOverrides === 'object' ? drawdownOverrides : {};
        const group = groups[groupKey] && typeof groups[groupKey] === 'object' ? groups[groupKey] : null;
        if (!group) return null;
        const entry = group[itemKey];
        return entry && typeof entry === 'object' ? entry : null;
    }

    function getDrawdownOverrideFromAnswers(answersObj, groupKey, itemKey) {
        const overrides = answersObj?.drawdown_surveyor_overrides;
        const groups = overrides && typeof overrides === 'object' ? overrides : {};
        const group = groups[groupKey] && typeof groups[groupKey] === 'object' ? groups[groupKey] : null;
        if (!group) return null;
        const entry = group[itemKey];
        return entry && typeof entry === 'object' ? entry : null;
    }

    function getResolvedDrawdownValueFromAnswers(answersObj, groupKey, itemKey, borrowerValue) {
        const override = getDrawdownOverrideFromAnswers(answersObj, groupKey, itemKey);
        const surveyorValue = String(override?.surveyor_value || '').trim();
        if (surveyorValue) return surveyorValue;

        const borrowerRaw = String(borrowerValue ?? '').trim();
        if (borrowerRaw) return borrowerRaw;

        const setupHydrationEnabled = Boolean(setupCostBaseline) && !allowInterimCostChanges;
        if (!setupHydrationEnabled) return borrowerValue;
        if (groupKey === 'dd_professional_fees') {
            const setupMap = setupCostBaseline?.professionalCostToDateByKey;
            const setupValue = setupMap && typeof setupMap === 'object' ? setupMap[itemKey] : null;
            return setupValue == null ? borrowerValue : String(setupValue);
        }
        if (groupKey === 'dd_additional_costs') {
            const setupMap = setupCostBaseline?.additionalCostToDateByKey;
            const setupValue = setupMap && typeof setupMap === 'object' ? setupMap[itemKey] : null;
            return setupValue == null ? borrowerValue : String(setupValue);
        }
        return borrowerValue;
    }

    function getResolvedDrawdownValue(groupKey, itemKey, borrowerValue) {
        return getResolvedDrawdownValueFromAnswers({ drawdown_surveyor_overrides: drawdownOverrides }, groupKey, itemKey, borrowerValue);
    }

    function hasChangedDrawdownValue(groupKey, itemKey, borrowerValue) {
        const override = getDrawdownOverride(groupKey, itemKey);
        const surveyorValue = String(override?.surveyor_value || '').trim();
        if (!surveyorValue) return false;
        return normalizeComparableValue(surveyorValue) !== normalizeComparableValue(borrowerValue);
    }

    const canEditDrawdown = portalMode === 'surveyor' && typeof authHeaders === 'function' && String(projectReference || '').trim() !== '';

    function openDrawdownEditor({ groupKey, itemKey, label, borrowerValue }) {
        const override = getDrawdownOverride(groupKey, itemKey);
        setDrawdownEditorError('');
        setDrawdownEditor({
            groupKey,
            itemKey,
            label,
            borrowerValue: String(borrowerValue || '').trim(),
            surveyorValue: String(override?.surveyor_value || '').trim(),
            commentary: String(override?.commentary || '').trim()
        });
    }

    async function saveDrawdownEditor() {
        if (!drawdownEditor || !canEditDrawdown) return;
        setDrawdownEditorBusy(true);
        setDrawdownEditorError('');
        try {
            const qs = new URLSearchParams();
            if (portalMode) qs.set('mode', String(portalMode));
            const payload = await fetchJson(`${API_BASE_URL}/api/project/${encodeURIComponent(projectReference)}/interim-report/drawdown-override?${qs.toString()}`, {
                method: 'PATCH',
                headers: authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    interim_number: Number(currentInterimNumber || answers?.valuation_number || 1) || 1,
                    group_key: drawdownEditor.groupKey,
                    item_key: drawdownEditor.itemKey,
                    borrower_value: drawdownEditor.borrowerValue,
                    surveyor_value: drawdownEditor.surveyorValue,
                    commentary: drawdownEditor.commentary
                })
            });
            setDrawdownOverrides(payload?.drawdown_surveyor_overrides && typeof payload.drawdown_surveyor_overrides === 'object'
                ? payload.drawdown_surveyor_overrides
                : {});
            setDrawdownEditor(null);
        } catch (e) {
            setDrawdownEditorError(e?.message || 'Failed to save drawdown override');
        } finally {
            setDrawdownEditorBusy(false);
        }
    }

    function renderEditableDdCell({ groupKey, itemKey, label, borrowerValue }) {
        const resolvedValue = getResolvedDrawdownValue(groupKey, itemKey, borrowerValue);
        const changed = hasChangedDrawdownValue(groupKey, itemKey, borrowerValue);
        const override = getDrawdownOverride(groupKey, itemKey);
        const display = String(resolvedValue || '').trim() ? formatPoundsLite(resolvedValue) : '—';
        const borrowerAmount = parseMoneyLoose(borrowerValue);
        const surveyorAmount = parseMoneyLoose(resolvedValue);
        const delta = borrowerAmount - surveyorAmount;
        const hoverCommentary = String(override?.commentary || '').trim();
        const tooltipTitle = changed ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 220 }}>
                <div style={{ fontSize: 12, fontWeight: 900, letterSpacing: 0.3, textTransform: 'uppercase', color: '#f8fafc' }}>
                    Surveyor Adjustment
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 10px', alignItems: 'center' }}>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>Borrower</span>
                    <span style={{ color: '#f8fafc', fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{formatPoundsLite(borrowerValue)}</span>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>Surveyor</span>
                    <span style={{ color: '#f8fafc', fontSize: 12, fontWeight: 800, textAlign: 'right' }}>{formatPoundsLite(resolvedValue)}</span>
                    <span style={{ color: '#94a3b8', fontSize: 12 }}>{delta >= 0 ? 'Reduction' : 'Increase'}</span>
                    <span style={{ color: delta >= 0 ? '#fde68a' : '#bfdbfe', fontSize: 12, fontWeight: 900, textAlign: 'right' }}>{formatPoundsLite(Math.abs(delta))}</span>
                </div>
                {hoverCommentary ? (
                    <div style={{ marginTop: 2, paddingTop: 8, borderTop: '1px solid rgba(148, 163, 184, 0.28)' }}>
                        <div style={{ color: '#94a3b8', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.35, marginBottom: 4 }}>Commentary</div>
                        <div style={{ color: '#e2e8f0', fontSize: 12, lineHeight: 1.45 }}>{hoverCommentary}</div>
                    </div>
                ) : null}
            </div>
        ) : null;

        const cellContent = canEditDrawdown ? (
            <button
                type="button"
                onClick={() => openDrawdownEditor({ groupKey, itemKey, label, borrowerValue })}
                style={{
                    border: 'none',
                    background: 'transparent',
                    padding: 0,
                    margin: 0,
                    width: '100%',
                    textAlign: 'right',
                    font: 'inherit',
                    color: '#111827',
                    cursor: 'pointer'
                }}
            >
                {display}
            </button>
        ) : <span>{display}</span>;

        return (
            <td
                style={{
                    padding: '8px 10px',
                    border: '1px solid #e5e7eb',
                    textAlign: 'right',
                    fontWeight: 700,
                    background: changed ? 'rgba(250, 204, 21, 0.3)' : undefined
                }}
            >
                {tooltipTitle ? (
                    <Tooltip
                        title={tooltipTitle}
                        arrow
                        placement="top"
                        slotProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: '#0f172a',
                                    color: '#f8fafc',
                                    border: '1px solid rgba(148, 163, 184, 0.22)',
                                    borderRadius: '12px',
                                    boxShadow: '0 18px 40px rgba(15, 23, 42, 0.28)',
                                    px: 1.5,
                                    py: 1.25,
                                    maxWidth: 320
                                }
                            },
                            arrow: {
                                sx: {
                                    color: '#0f172a'
                                }
                            }
                        }}
                    >
                        <span style={{ display: 'block', width: '100%' }}>{cellContent}</span>
                    </Tooltip>
                ) : cellContent}
            </td>
        );
    }

    function getPriorSpentForRow(groupKey, itemKey) {
        if (groupKey === 'dd_construction') return parseMoneyLoose(priorCumulativeByRow.construction?.[itemKey]);
        if (groupKey === 'dd_professional_fees') return parseMoneyLoose(priorCumulativeByRow.professional?.[itemKey]);
        if (groupKey === 'dd_additional_costs') return parseMoneyLoose(priorCumulativeByRow.additional?.[itemKey]);
        if (groupKey === 'contingency_claims') return parseMoneyLoose(priorCumulativeByRow.contingency?.[itemKey]);
        return 0;
    }

    function renderBudgetProgressCells({ groupKey, itemKey, budgetValue, borrowerValue, strong = false }) {
        const budget = parseMoneyLoose(budgetValue);
        const current = parseMoneyLoose(getResolvedDrawdownValue(groupKey, itemKey, borrowerValue));
        const cumulativeSpent = getPriorSpentForRow(groupKey, itemKey) + current;
        const remaining = Math.max(0, budget - cumulativeSpent);
        const commonStyle = {
            padding: '8px 10px',
            border: '1px solid #e5e7eb',
            textAlign: 'right',
            fontWeight: strong ? 900 : 700,
            whiteSpace: 'nowrap'
        };

        return (
            <>
                <td style={commonStyle}>{budget > 0 ? formatPercentLite(cumulativeSpent, budget) : '—'}</td>
                <td style={commonStyle}>{budget > 0 ? formatPoundsLite(remaining) : '—'}</td>
            </>
        );
    }

    function computeFacilityBalance() {
        // Intentionally disabled: baseline must match explicit Initial Surveyor assessment
        // and not include any derived uplift from benchmark/rate recomputation.
        return 0;
    }

    const drawdownCosts = (allowInterimCostChanges && answers.drawdown_costs && typeof answers.drawdown_costs === 'object')
        ? answers.drawdown_costs
        : null;
    const ddConstruction = drawdownCosts?.dd_construction && typeof drawdownCosts.dd_construction === 'object' ? drawdownCosts.dd_construction : {};
    const ddProfessional = drawdownCosts?.dd_professional_fees && typeof drawdownCosts.dd_professional_fees === 'object' ? drawdownCosts.dd_professional_fees : {};
    const ddAdditional = drawdownCosts?.dd_additional_costs && typeof drawdownCosts.dd_additional_costs === 'object' ? drawdownCosts.dd_additional_costs : {};
    const contingencyClaims = Array.isArray(drawdownCosts?.contingency_claims) ? drawdownCosts.contingency_claims : [];

    const ddLabel = (() => {
        const n = Number(answers?.valuation_number || 0);
        return n ? `DD${n}` : 'DD';
    })();

    const facilityBalance = computeFacilityBalance();

    const COST_COL_BUDGET_W = 150;
    const COST_COL_DD_W = 150;
    const COST_SUMMARY_LABEL_W = 260;
    const COST_SUMMARY_VAL_W = 150;

    const contingencyDdTotal = contingencyClaims.reduce((acc, c, idx) => {
        const itemKey = String(c?.id || `idx:${idx + 1}`);
        return acc + parseMoneyLoose(getResolvedDrawdownValue('contingency_claims', itemKey, c?.value));
    }, 0);
    const constructionRows = parseBreakdownRows(ini7?.construction_cost_breakdown);
    const professionalRows = parseBreakdownRows(ini7?.professional_fees_breakdown);
    const safeProfessionalRows = professionalRows.length ? professionalRows : [{ heading: 'Design Team Fees', amount: '' }];
    const additionalRows = parseBreakdownRows(ini7?.other_fees_breakdown);
    const safeAdditionalRows = additionalRows.length ? additionalRows : [{ heading: 'Monitoring Surveyor', amount: '' }];

    const constructionBudgetFromSurveyor = parseMoneyLoose(getInitialCostValue('build_cost'));
    const constructionBudgetDerived = constructionRows.reduce((acc, r) => acc + parseMoneyLoose(r?.amount), 0);
    const constructionBudgetTotal = constructionBudgetFromSurveyor > 0 ? constructionBudgetFromSurveyor : constructionBudgetDerived;
    const constructionDdTotal = constructionRows.reduce((acc, r, idx) => {
        const key = makeCostItemKey('construction_cost_breakdown', r?.heading, idx);
        return acc + parseMoneyLoose(getResolvedDrawdownValue('dd_construction', key, ddConstruction[key]));
    }, 0);

    const professionalExtraRowIds = parseJsonArrayLoose(getInitialCostValue('professional_fees_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);
    const professionalChildKeys = professionalRows.map((r, idx) => makeCostItemKey('professional_fees_breakdown', r?.heading, idx));
    const professionalChildNums = professionalChildKeys
        .map((key) => parseNumberLoose(getInitialCostValue(key)))
        .filter((n) => n !== null);
    const derivedSurveyorProfessional = professionalChildKeys.length
        ? (professionalChildNums.length ? professionalChildNums.reduce((a, b) => a + b, 0) : null)
        : null;
    const professionalExtraNums = professionalExtraRowIds
        .map((id) => parseNumberLoose(getInitialCostValue(`professional_fee_extra:${id}:amount`)))
        .filter((n) => n !== null);
    const professionalExtraSum = professionalExtraNums.length ? professionalExtraNums.reduce((a, b) => a + b, 0) : null;
    const professionalBudgetTotal = safeProfessionalRows.reduce((acc, r, idx) => {
        const key = makeCostItemKey('professional_fees_breakdown', r?.heading, idx);
        return acc + parseMoneyLoose(getInitialCostValue(key));
    }, 0);
    const linkedProfessionalBudgetTotal = (derivedSurveyorProfessional === null && professionalExtraSum === null)
        ? professionalBudgetTotal
        : ((derivedSurveyorProfessional || 0) + (professionalExtraSum || 0));
    const professionalBudgetFromSurveyor = parseMoneyLoose(getInitialCostValue('professional_fees'));
    const effectiveProfessionalBudgetTotal = professionalBudgetFromSurveyor > 0 ? professionalBudgetFromSurveyor : linkedProfessionalBudgetTotal;
    const professionalDdTotal = safeProfessionalRows.reduce((acc, r, idx) => {
        const key = makeCostItemKey('professional_fees_breakdown', r?.heading, idx);
        return acc + parseMoneyLoose(getResolvedDrawdownValue('dd_professional_fees', key, ddProfessional[key]));
    }, 0);

    const additionalBudgetTotal = safeAdditionalRows.reduce((acc, r, idx) => {
        const label = r.heading || `Item ${idx + 1}`;
        const key = `other_cost:${normalizeKeyTextLite(label).replace(/\s/g, '_')}`;
        return acc + parseMoneyLoose(getInitialCostValue(key));
    }, 0);
    const additionalDdTotal = safeAdditionalRows.reduce((acc, r, idx) => {
        const label = r.heading || `Item ${idx + 1}`;
        const key = `other_cost:${normalizeKeyTextLite(label).replace(/\s/g, '_')}`;
        return acc + parseMoneyLoose(getResolvedDrawdownValue('dd_additional_costs', key, ddAdditional[key]));
    }, 0);

    const otherExtraRowIds = parseJsonArrayLoose(getInitialCostValue('other_costs_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);
    const linkedAdditionalRows = (() => {
        const seen = new Set();
        const items = [];
        additionalRows.forEach((r) => {
            const heading = String(r?.heading || '').trim();
            const key = normalizeKeyTextLite(heading);
            if (!heading || !key || seen.has(key)) return;
            seen.add(key);
            items.push(heading);
        });
        if (!seen.has(normalizeKeyTextLite('Monitoring Surveyor'))) items.push('Monitoring Surveyor');
        return items;
    })();
    const linkedAdditionalBaseNums = linkedAdditionalRows
        .map((label) => parseNumberLoose(getInitialCostValue(`other_cost:${normalizeKeyTextLite(label).replace(/\s/g, '_')}`)))
        .filter((n) => n !== null);
    const linkedAdditionalBaseSum = linkedAdditionalBaseNums.length ? linkedAdditionalBaseNums.reduce((a, b) => a + b, 0) : null;
    const otherExtraNums = otherExtraRowIds
        .map((id) => parseNumberLoose(getInitialCostValue(`other_cost_extra:${id}:amount`)))
        .filter((n) => n !== null);
    const otherExtraSum = otherExtraNums.length ? otherExtraNums.reduce((a, b) => a + b, 0) : null;
    const linkedAdditionalBudgetTotal = (linkedAdditionalBaseSum === null && otherExtraSum === null)
        ? additionalBudgetTotal
        : ((linkedAdditionalBaseSum || 0) + (otherExtraSum || 0));
    const additionalBudgetFromSurveyor = parseMoneyLoose(getInitialCostValue('other_development_costs'));
    const effectiveAdditionalBudgetTotal = additionalBudgetFromSurveyor > 0 ? additionalBudgetFromSurveyor : linkedAdditionalBudgetTotal;

    const linkedContingencyBudgetTotal = computeContingencyAmount(getInitialCostValue('contingency') || ini7?.contingency, constructionBudgetTotal) || 0;
    const linkedSurveyorTotalDevelopmentCost = constructionBudgetTotal + effectiveProfessionalBudgetTotal + effectiveAdditionalBudgetTotal + linkedContingencyBudgetTotal;

    const overallDdTotal = constructionDdTotal + professionalDdTotal + additionalDdTotal + contingencyDdTotal;
    const useSetupHydration = Boolean(setupCostBaseline) && !allowInterimCostChanges;
    const setupCostToDate = setupCostBaseline?.costToDate || {};
    const sectionToDate = {
        construction: useSetupHydration ? parseMoneyLoose(setupCostToDate.construction) : constructionDdTotal,
        contingency: useSetupHydration ? parseMoneyLoose(setupCostToDate.contingency) : contingencyDdTotal,
        professional: useSetupHydration ? parseMoneyLoose(setupCostToDate.professional) : professionalDdTotal,
        additional: useSetupHydration ? parseMoneyLoose(setupCostToDate.additional) : additionalDdTotal
    };
    const setupDrawdownHistory = useSetupHydration && Array.isArray(setupCostBaseline?.drawdownHistory)
        ? setupCostBaseline.drawdownHistory
        : [];
    const setupDrawdownTotal = useSetupHydration ? parseMoneyLoose(setupCostBaseline?.drawdownTotal) : 0;
    const [openSections, setOpenSections] = useState(() => ({ 'Project Details': true }));
    const [openCostRows, setOpenCostRows] = useState(() => ({}));
    const interimInformationSections = useMemo(() => [
        { key: 'project-details', label: 'Project Details', title: 'Project Details' },
        { key: 'construction-progress', label: 'Construction Progress', title: 'Construction Progress' },
        { key: 'programme', label: 'Programme', title: 'Programme' },
        { key: 'building-control', label: 'Building Control / Warranty', title: 'Building Control / Warranty' },
        { key: 'statutory', label: 'Statutory Elements', title: 'Statutory Elements' },
        { key: 'project-changes', label: 'Project Changes', title: 'Project Changes' },
        { key: 'costs', label: 'Costs / Drawdown', title: 'Costs / Drawdown' },
        ...(!hideProjectFacility ? [{ key: 'project-facility', label: 'Project Facility', title: 'Project Facility' }] : [])
    ], [hideProjectFacility]);
    const [activeInterimSectionKey, setActiveInterimSectionKey] = useState(interimInformationSections[0].key);
    const activeInterimSection = interimInformationSections.find((section) => section.key === activeInterimSectionKey)
        || interimInformationSections[0];

    useEffect(() => {
        if (interimInformationSections.some((section) => section.key === activeInterimSectionKey)) return;
        setActiveInterimSectionKey(interimInformationSections[0].key);
    }, [activeInterimSectionKey, interimInformationSections]);

    const handleBlockToggle = useCallback((title, nextOpen) => {
        setOpenSections((prev) => {
            if (prev?.[title] === nextOpen) return prev;
            return { ...prev, [title]: nextOpen };
        });
    }, []);

    const handleCostRowToggle = useCallback((title, nextOpen) => {
        setOpenCostRows((prev) => {
            if (prev?.[title] === nextOpen) return prev;
            return { ...prev, [title]: nextOpen };
        });
    }, []);

    function pretty(v) {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return v;
        try {
            return JSON.stringify(v, null, 2);
        } catch {
            return String(v);
        }
    }

    function lineValue(v) {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return formatLongOrdinalDate(v);
        if (typeof v === 'number' || typeof v === 'boolean') return String(v);
        if (Array.isArray(v)) {
            const names = v
                .map((x) => (x && typeof x === 'object' ? (x.name || x.file_name || x.filename || '') : String(x)))
                .map((s) => String(s || '').trim())
                .filter(Boolean);
            return names.length ? names.join(', ') : pretty(v);
        }
        return pretty(v);
    }

    function Block({ title, children, defaultOpen = false }) {
        if (!showOnlyProjectFacility) {
            if (activeInterimSection.title !== title) return null;
            return (
                <section
                    role="tabpanel"
                    id={`interim-report-panel-${activeInterimSection.key}`}
                    aria-labelledby={`interim-report-tab-${activeInterimSection.key}`}
                    className="info-section"
                    style={{ marginTop: 14 }}
                >
                    <div className="info-section-body">{children}</div>
                </section>
            );
        }
        const open = Object.prototype.hasOwnProperty.call(openSections, title)
            ? Boolean(openSections[title])
            : Boolean(defaultOpen);
        return (
            <details
                className="info-section"
                open={open}
                onToggle={(e) => handleBlockToggle(title, Boolean(e.currentTarget.open))}
                style={{ marginTop: 14 }}
            >
                <summary className="info-section-summary">
                    <span className="info-section-title">{title}</span>
                </summary>
                <div className="info-section-body">
                    {children}
                </div>
            </details>
        );
    }

    function CostPanel({ title, note, children, fullWidth = false }) {
        return (
            <section
                style={{
                    gridColumn: fullWidth ? '1 / -1' : undefined,
                    border: '1px solid #e5e7eb',
                    borderRadius: 14,
                    background: '#ffffff',
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
                }}
            >
                <div style={{ padding: '12px 14px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc' }}>
                    <div style={{ fontWeight: 900, color: '#0f172a' }}>{title}</div>
                    {note ? <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>{note}</div> : null}
                </div>
                <div style={{ padding: 14 }}>{children}</div>
            </section>
        );
    }

    function CostAccordionRow({ title, note, children, defaultOpen = false }) {
        const open = Object.prototype.hasOwnProperty.call(openCostRows, title)
            ? Boolean(openCostRows[title])
            : Boolean(defaultOpen);

        return (
            <details
                open={open}
                onToggle={(e) => handleCostRowToggle(title, Boolean(e.currentTarget.open))}
                style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 14,
                    background: '#ffffff',
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)'
                }}
            >
                <summary
                    style={{
                        listStyle: 'none',
                        cursor: 'pointer',
                        padding: '12px 14px',
                        background: '#f8fafc',
                        borderBottom: open ? '1px solid #e5e7eb' : 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12
                    }}
                >
                    <span style={{ minWidth: 0 }}>
                        <span style={{ display: 'block', fontWeight: 900, color: '#0f172a' }}>{title}</span>
                        {note ? <span style={{ display: 'block', marginTop: 4, fontSize: 12, color: '#64748b' }}>{note}</span> : null}
                    </span>
                    <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 800, color: '#64748b' }}>{open ? 'Hide' : 'Show'}</span>
                </summary>
                <div style={{ padding: 14 }}>{children}</div>
            </details>
        );
    }

    function CostStat({ label, value, accent }) {
        return (
            <div
                style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '10px 12px',
                    background: '#fff',
                    minWidth: 0
                }}
            >
                <div style={{ fontSize: 11, fontWeight: 900, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</div>
                <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900, color: accent || '#0f172a', whiteSpace: 'nowrap' }}>{value}</div>
            </div>
        );
    }

    function formatPercentLite(numerator, denominator) {
        const top = parseMoneyLoose(numerator);
        const bottom = parseMoneyLoose(denominator);
        if (!bottom) return '0%';
        return `${Math.round((top / bottom) * 100)}%`;
    }

    function computeContingencyAmount(raw, baseAmount) {
        const s = String(raw ?? '').trim();
        if (!s) return null;
        if (s.includes('%')) {
            const pct = parseMoneyLoose(s);
            if (!pct || !baseAmount) return null;
            return (Number(baseAmount) * pct) / 100;
        }
        const amount = parseMoneyLoose(s);
        return Number.isFinite(amount) ? amount : null;
    }

    const costSnapshotRows = [
        {
            label: 'Construction Costs',
            budget: constructionBudgetTotal,
            toDate: sectionToDate.construction,
            toComplete: Math.max(0, constructionBudgetTotal - sectionToDate.construction)
        },
        {
            label: 'Contingency',
            budget: linkedContingencyBudgetTotal,
            toDate: sectionToDate.contingency,
            toComplete: Math.max(0, linkedContingencyBudgetTotal - sectionToDate.contingency)
        },
        {
            label: 'Professional Fees',
            budget: effectiveProfessionalBudgetTotal,
            toDate: sectionToDate.professional,
            toComplete: Math.max(0, effectiveProfessionalBudgetTotal - sectionToDate.professional)
        },
        {
            label: 'Additional Costs',
            budget: effectiveAdditionalBudgetTotal,
            toDate: sectionToDate.additional,
            toComplete: Math.max(0, effectiveAdditionalBudgetTotal - sectionToDate.additional)
        }
    ];

    const totalDevelopmentBudget = costSnapshotRows.reduce((acc, row) => acc + parseMoneyLoose(row.budget), 0);
    const currentDevelopmentToDate = sectionToDate.construction + sectionToDate.professional + sectionToDate.additional + sectionToDate.contingency;
    const totalDevelopmentToDate = costSnapshotRows.reduce((acc, row) => acc + parseMoneyLoose(row.toDate), 0);
    const totalDevelopmentToComplete = costSnapshotRows.reduce((acc, row) => acc + parseMoneyLoose(row.toComplete), 0);

    useEffect(() => {
        const reference = String(projectReference || '').trim();
        const interimNo = Number(currentInterimNumber || answers?.valuation_number || 0);
        const baselineCosts = {
            construction: parseMoneyLoose(setupCostBaseline?.costToDate?.construction),
            professional: parseMoneyLoose(setupCostBaseline?.costToDate?.professional),
            additional: parseMoneyLoose(setupCostBaseline?.costToDate?.additional),
            contingency: parseMoneyLoose(setupCostBaseline?.costToDate?.contingency)
        };
        const baselineRows = {
            construction: setupCostBaseline?.constructionCostToDateByKey && typeof setupCostBaseline.constructionCostToDateByKey === 'object'
                ? { ...setupCostBaseline.constructionCostToDateByKey }
                : {},
            professional: setupCostBaseline?.professionalCostToDateByKey && typeof setupCostBaseline.professionalCostToDateByKey === 'object'
                ? { ...setupCostBaseline.professionalCostToDateByKey }
                : {},
            additional: setupCostBaseline?.additionalCostToDateByKey && typeof setupCostBaseline.additionalCostToDateByKey === 'object'
                ? { ...setupCostBaseline.additionalCostToDateByKey }
                : {},
            contingency: {}
        };
        const setupHistory = Array.isArray(setupCostBaseline?.drawdownHistory) ? setupCostBaseline.drawdownHistory : [];
        const baselineInterimTotals = {};
        setupHistory.forEach((row, idx) => {
            const label = String(row?.label || row?.item || '').trim();
            const m = label.match(/(\d+)/);
            const num = Number(m?.[1] || (idx + 1));
            const amt = parseMoneyLoose(row?.amount);
            if (!Number.isFinite(num) || num <= 0 || !Number.isFinite(amt) || amt <= 0) return;
            baselineInterimTotals[Math.trunc(num)] = amt;
        });

        const priorNums = getPriorInterimNumbers(interimSubmissions, interimNo, Object.keys(baselineInterimTotals));

        if (!reference || typeof authHeaders !== 'function') {
            setPriorCumulativeCosts(baselineCosts);
            setPriorCumulativeByRow(baselineRows);
            setPriorDrawdownTotalsByInterim(baselineInterimTotals);
            return;
        }

        if (!priorNums.length) {
            setPriorCumulativeCosts(baselineCosts);
            setPriorCumulativeByRow(baselineRows);
            setPriorDrawdownTotalsByInterim(baselineInterimTotals);
            return;
        }

        let cancelled = false;

        async function loadPriorCumulativeCosts() {
            const next = { ...baselineCosts };
            const nextRows = {
                construction: { ...baselineRows.construction },
                professional: { ...baselineRows.professional },
                additional: { ...baselineRows.additional },
                contingency: { ...baselineRows.contingency }
            };
            const nextInterimTotals = { ...baselineInterimTotals };

            await Promise.all(priorNums.map(async (n) => {
                try {
                    const url = `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/interim-report?mode=${encodeURIComponent(String(portalMode || 'surveyor'))}&interim=${encodeURIComponent(String(n))}`;
                    const ir = await fetchJson(url, { method: 'GET', headers: authHeaders() });
                    const priorAnswers = ir?.answers && typeof ir.answers === 'object' ? ir.answers : {};
                    const priorDrawdownCosts = priorAnswers.drawdown_costs && typeof priorAnswers.drawdown_costs === 'object' ? priorAnswers.drawdown_costs : {};
                    const priorConstruction = priorDrawdownCosts.dd_construction && typeof priorDrawdownCosts.dd_construction === 'object' ? priorDrawdownCosts.dd_construction : {};
                    const priorProfessional = priorDrawdownCosts.dd_professional_fees && typeof priorDrawdownCosts.dd_professional_fees === 'object' ? priorDrawdownCosts.dd_professional_fees : {};
                    const priorAdditional = priorDrawdownCosts.dd_additional_costs && typeof priorDrawdownCosts.dd_additional_costs === 'object' ? priorDrawdownCosts.dd_additional_costs : {};
                    const priorContingency = Array.isArray(priorDrawdownCosts.contingency_claims) ? priorDrawdownCosts.contingency_claims : [];

                    Object.entries(priorConstruction).forEach(([itemKey, value]) => {
                        const resolved = getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_construction', itemKey, value);
                        const amount = parseMoneyLoose(resolved);
                        next.construction += amount;
                        nextRows.construction[itemKey] = parseMoneyLoose(nextRows.construction[itemKey]) + amount;
                    });

                    Object.entries(priorProfessional).forEach(([itemKey, value]) => {
                        const resolved = getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_professional_fees', itemKey, value);
                        const amount = parseMoneyLoose(resolved);
                        next.professional += amount;
                        nextRows.professional[itemKey] = parseMoneyLoose(nextRows.professional[itemKey]) + amount;
                    });

                    Object.entries(priorAdditional).forEach(([itemKey, value]) => {
                        const resolved = getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_additional_costs', itemKey, value);
                        const amount = parseMoneyLoose(resolved);
                        next.additional += amount;
                        nextRows.additional[itemKey] = parseMoneyLoose(nextRows.additional[itemKey]) + amount;
                    });

                    priorContingency.forEach((claim, idx) => {
                        const itemKey = String(claim?.id || `idx:${idx + 1}`);
                        const resolved = getResolvedDrawdownValueFromAnswers(priorAnswers, 'contingency_claims', itemKey, claim?.value);
                        const amount = parseMoneyLoose(resolved);
                        next.contingency += amount;
                        nextRows.contingency[itemKey] = parseMoneyLoose(nextRows.contingency[itemKey]) + amount;
                    });

                    nextInterimTotals[n] =
                        Object.entries(priorConstruction).reduce((acc, [itemKey, value]) => acc + parseMoneyLoose(getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_construction', itemKey, value)), 0) +
                        Object.entries(priorProfessional).reduce((acc, [itemKey, value]) => acc + parseMoneyLoose(getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_professional_fees', itemKey, value)), 0) +
                        Object.entries(priorAdditional).reduce((acc, [itemKey, value]) => acc + parseMoneyLoose(getResolvedDrawdownValueFromAnswers(priorAnswers, 'dd_additional_costs', itemKey, value)), 0) +
                        priorContingency.reduce((acc, claim, idx) => acc + parseMoneyLoose(getResolvedDrawdownValueFromAnswers(priorAnswers, 'contingency_claims', String(claim?.id || `idx:${idx + 1}`), claim?.value)), 0);
                } catch {
                    // fail open and keep partial cumulative totals
                }
            }));

            if (!cancelled) {
                setPriorCumulativeCosts(next);
                setPriorCumulativeByRow(nextRows);
                setPriorDrawdownTotalsByInterim(nextInterimTotals);
            }
        }

        loadPriorCumulativeCosts();
        return () => { cancelled = true; };
    }, [answers?.valuation_number, authHeaders, currentInterimNumber, interimSubmissions, portalMode, projectReference, setupCostBaseline]);

    const costSnapshotRowsWithCumulative = costSnapshotRows.map((row) => {
        let prior = 0;
        if (row.label === 'Construction Costs') prior = priorCumulativeCosts.construction;
        else if (row.label === 'Contingency') prior = priorCumulativeCosts.contingency;
        else if (row.label === 'Professional Fees') prior = priorCumulativeCosts.professional;
        else if (row.label === 'Additional Costs') prior = priorCumulativeCosts.additional;

        const baselineForRow = (() => {
            if (!useSetupHydration) return 0;
            if (row.label === 'Construction Costs') return parseMoneyLoose(setupCostToDate.construction);
            if (row.label === 'Contingency') return parseMoneyLoose(setupCostToDate.contingency);
            if (row.label === 'Professional Fees') return parseMoneyLoose(setupCostToDate.professional);
            if (row.label === 'Additional Costs') return parseMoneyLoose(setupCostToDate.additional);
            return 0;
        })();

        // priorCumulativeCosts includes setup baseline + prior submitted interims.
        // For hydrated setup rows, avoid counting the baseline twice.
        const priorDelta = useSetupHydration ? Math.max(0, prior - baselineForRow) : prior;

        const cumulativeToDate = priorDelta + parseMoneyLoose(row.toDate);
        return {
            ...row,
            cumulativeToDate,
            cumulativeToComplete: Math.max(0, parseMoneyLoose(row.budget) - cumulativeToDate)
        };
    });

    const cumulativeDevelopmentToDate = costSnapshotRowsWithCumulative.reduce((acc, row) => acc + parseMoneyLoose(row.cumulativeToDate), 0);
    const cumulativeDevelopmentToComplete = costSnapshotRowsWithCumulative.reduce((acc, row) => acc + parseMoneyLoose(row.cumulativeToComplete), 0);

    const surveyorConstructionTotal = (() => {
        const devTotal = constructionRows.reduce((acc, r) => acc + parseMoneyLoose(r.amount), 0);
        const giaM2 = parseMoneyLoose(getInitialCostValue('proposed_gia'));
        const ft2 = giaM2 ? giaM2 * 10.7639 : 0;
        const rateFt2 = parseMoneyLoose(getInitialCostValue('construction_items_rate_ft2'));
        if (!ft2 || !rateFt2) return devTotal;

        const bespokeTotals = constructionRows.reduce((acc, r, idx) => {
            const itemKey = makeCostItemKey('construction_cost_breakdown', r?.heading, idx);
            const bespokeKey = `construction_bespoke:${itemKey}`;
            const isBespoke = String(getInitialCostValue(bespokeKey) || '').trim().toLowerCase() === 'yes';
            const amount = parseMoneyLoose(r?.amount);
            if (isBespoke) acc.bespoke += amount;
            return acc;
        }, { bespoke: 0 });

        const bespokeRate = ft2 ? (bespokeTotals.bespoke / ft2) : 0;
        return Math.round((rateFt2 + bespokeRate) * ft2);
    })();

    const surveyorContingencyTotal = linkedContingencyBudgetTotal;
    const surveyorTotalDevelopmentCost = linkedSurveyorTotalDevelopmentCost;
    const section7SurveyorSubtotal = Number.isFinite(Number(surveyorTotalDevelopmentCost)) && Number(surveyorTotalDevelopmentCost) > 0
        ? Number(surveyorTotalDevelopmentCost)
        : null;
    const facilityConfirmedRaw = getInitialCostValue('facility_confirmed');
    const facilityConfirmed = truthyFromString(facilityConfirmedRaw);
    const surveyorFacilityAmount = parseNumberLoose(getInitialCostValue('facility_amount'));
    const surveyorBorrowerEquity = parseNumberLoose(getInitialCostValue('less_borrower_input'));
    const dashboardFacilityAmount = parseNumberLoose(projectFacility);
    const sourceFacilityAmount = facilityConfirmed
        ? (surveyorFacilityAmount ?? section7SurveyorSubtotal)
        : (surveyorFacilityAmount ?? dashboardFacilityAmount ?? section7SurveyorSubtotal);
    const setupBorrowerInputAmount = parseNumberLoose(setupCostBaseline?.borrowerInputAmount);
    const borrowerEquityProvided = (() => {
        const setupBorrowerFloor = (setupBorrowerInputAmount !== null && setupBorrowerInputAmount > 0)
            ? setupBorrowerInputAmount
            : null;

        if (setupBorrowerFloor !== null) {
            if (surveyorBorrowerEquity !== null && surveyorBorrowerEquity > 0) {
                return Math.max(setupBorrowerFloor, surveyorBorrowerEquity);
            }
            const derivedFromFacility = sourceFacilityAmount
                ? Math.max(0, surveyorTotalDevelopmentCost - sourceFacilityAmount)
                : 0;
            return Math.max(setupBorrowerFloor, derivedFromFacility);
        }

        if (surveyorBorrowerEquity !== null && surveyorBorrowerEquity > 0) return surveyorBorrowerEquity;
        return sourceFacilityAmount ? Math.max(0, surveyorTotalDevelopmentCost - sourceFacilityAmount) : 0;
    })();
    const derivedFacilityAmount = Number.isFinite(Number(surveyorTotalDevelopmentCost))
        ? Math.max(0, parseMoneyLoose(surveyorTotalDevelopmentCost) - parseMoneyLoose(borrowerEquityProvided))
        : null;
    const projectFacilityAmount = Number.isFinite(Number(derivedFacilityAmount))
        ? derivedFacilityAmount
        : sourceFacilityAmount;
    const facilityHeadroom = projectFacilityAmount
        ? (projectFacilityAmount + borrowerEquityProvided) - surveyorTotalDevelopmentCost
        : null;
    const currentInterimNo = Number(currentInterimNumber || answers?.valuation_number || 0);
    const currentInterimHasSubmittedQuestionnaire = (() => {
        return Boolean(interimReport?.submission && (interimReport.submission.id != null || interimReport.submission.submitted_at));
    })();
    const drawdownRows = (() => {
        const byInterim = new Map();

        const addRow = ({ interimNo, amount, label }) => {
            const no = Number(interimNo);
            const amt = parseMoneyLoose(amount);
            if (!Number.isFinite(no) || no <= 0) return;
            if (!Number.isFinite(amt) || amt <= 0) return;
            byInterim.set(Math.trunc(no), {
                interimNo: Math.trunc(no),
                amount: amt,
                label: String(label || `Drawdown ${Math.trunc(no)}`).trim() || `Drawdown ${Math.trunc(no)}`
            });
        };

        setupDrawdownHistory.forEach((row, idx) => {
            const rawLabel = String(row?.label || row?.item || '').trim();
            const m = rawLabel.match(/(\d+)/);
            const no = Number(m?.[1] || (idx + 1));
            addRow({ interimNo: no, amount: row?.amount, label: rawLabel || `Drawdown ${no}` });
        });

        Object.entries(priorDrawdownTotalsByInterim).forEach(([interimNo, amount]) => {
            addRow({ interimNo, amount, label: `Drawdown ${interimNo}` });
        });

        if (currentInterimNo > 0 && overallDdTotal > 0 && currentInterimHasSubmittedQuestionnaire) {
            addRow({ interimNo: currentInterimNo, amount: overallDdTotal, label: `Drawdown ${currentInterimNo}` });
        }

        return Array.from(byInterim.values()).sort((a, b) => a.interimNo - b.interimNo);
    })();
    const cumulativeDrawdownByFacility = drawdownRows.reduce((acc, row) => acc + parseMoneyLoose(row.amount), 0);
    const remainingFacilityAmount = projectFacilityAmount ? Math.max(0, projectFacilityAmount - cumulativeDrawdownByFacility) : null;
    const totalExpenditureToDate = useSetupHydration
        ? (setupDrawdownTotal + parseMoneyLoose(borrowerEquityProvided))
        : cumulativeDevelopmentToDate;

    const facilityTableHeaderBg = '#f3f4f6';
    const facilityTableEmphasisBg = '#fafafa';

    function formatFacilityFigure(value, { signed = false } = {}) {
        const amount = Number(value);
        if (!Number.isFinite(amount)) return '—';
        if (signed && amount < 0) return `(£${Math.round(Math.abs(amount)).toLocaleString('en-GB')})`;
        return `£${Math.round(amount).toLocaleString('en-GB')}`;
    }

    const facilityHeadroomRows = [
        { label: 'Total Loan Facility', value: projectFacilityAmount, signed: false },
        { label: 'Borrower Equity Provided', value: borrowerEquityProvided, signed: false },
        { label: 'Less Anticipated Total Cost of Development', value: -surveyorTotalDevelopmentCost, signed: true, negative: true },
        { label: 'Facility Headroom', value: facilityHeadroom, signed: false, highlight: true },
        { label: 'Total Expenditure to Date', value: totalExpenditureToDate, signed: false },
        { label: 'Cost to Complete', value: cumulativeDevelopmentToComplete, signed: false },
        { label: 'Remaining Facility', value: remainingFacilityAmount, signed: false }
    ];

    const facilitySummaryRows = [
        { label: 'Development Facility', value: projectFacilityAmount, signed: false },
        ...drawdownRows.map((row) => ({ label: row?.label || `Drawdown No ${row.interimNo}`, value: -parseMoneyLoose(row.amount), signed: true, negative: true })),
        { label: 'Balance left in Facility', value: remainingFacilityAmount, signed: false, highlight: true },
        { label: 'Anticipated construction cost to complete', value: cumulativeDevelopmentToComplete, signed: false },
        { label: 'Facility Headroom', value: facilityHeadroom, signed: false, highlight: true }
    ];

    const budgetSummaryRows = [
        { description: 'Construction Costs', original: constructionBudgetTotal, adjustment: 0, forecast: constructionBudgetTotal },
        { description: 'Contingency', original: linkedContingencyBudgetTotal, adjustment: 0, forecast: linkedContingencyBudgetTotal },
        { description: 'Professional Fees', original: effectiveProfessionalBudgetTotal, adjustment: 0, forecast: effectiveProfessionalBudgetTotal },
        { description: 'Additional Costs', original: effectiveAdditionalBudgetTotal, adjustment: 0, forecast: effectiveAdditionalBudgetTotal }
    ];
    const budgetSummaryTotals = {
        original: budgetSummaryRows.reduce((acc, row) => acc + parseMoneyLoose(row.original), 0),
        adjustment: budgetSummaryRows.reduce((acc, row) => acc + parseMoneyLoose(row.adjustment), 0),
        forecast: budgetSummaryRows.reduce((acc, row) => acc + parseMoneyLoose(row.forecast), 0)
    };

    const budgetStatusRows = costSnapshotRowsWithCumulative.map((row) => ({
        description: row.label,
        forecast: row.budget,
        percentComplete: formatPercentLite(row.cumulativeToDate, row.budget),
        costToDate: row.cumulativeToDate,
        budgetRemaining: row.cumulativeToComplete
    }));

    const contingencyExpenditureToDate = priorCumulativeCosts.contingency + contingencyDdTotal;
    const contingencyRemaining = Math.max(0, linkedContingencyBudgetTotal - contingencyExpenditureToDate);

    const budgetAdjustmentRows = contingencyClaims.map((claim, idx) => {
        const itemKey = String(claim?.id || `idx:${idx + 1}`);
        const periodValue = parseMoneyLoose(getResolvedDrawdownValue('contingency_claims', itemKey, claim?.value));
        const cumulative = getPriorSpentForRow('contingency_claims', itemKey) + periodValue;
        return {
            element: String(claim?.description || `Variation ${idx + 1}`),
            finalAccountAdjustment: periodValue,
            movementPeriod: periodValue,
            totalExpenditureToDate: cumulative
        };
    });
    const budgetAdjustmentsTotals = {
        finalAccountAdjustment: budgetAdjustmentRows.reduce((acc, row) => acc + parseMoneyLoose(row.finalAccountAdjustment), 0),
        movementPeriod: budgetAdjustmentRows.reduce((acc, row) => acc + parseMoneyLoose(row.movementPeriod), 0),
        totalExpenditureToDate: budgetAdjustmentRows.reduce((acc, row) => acc + parseMoneyLoose(row.totalExpenditureToDate), 0)
    };

    const additionalCostsRowsForTable = safeAdditionalRows.map((row, idx) => {
        const label = row.heading || `Item ${idx + 1}`;
        const itemKey = `other_cost:${normalizeKeyTextLite(label).replace(/\s/g, '_')}`;
        const budget = parseMoneyLoose(getInitialCostValue(itemKey));
        const inPeriod = allowInterimCostChanges
            ? parseMoneyLoose(getResolvedDrawdownValue('dd_additional_costs', itemKey, ddAdditional[itemKey]))
            : 0;
        const setupCostToDate = useSetupHydration
            ? parseMoneyLoose(setupCostBaseline?.additionalCostToDateByKey?.[itemKey])
            : null;
        const hydratedCumulativeToDate = useSetupHydration
            ? parseMoneyLoose(priorCumulativeByRow.additional?.[itemKey])
            : null;
        const costToDate = useSetupHydration
            ? (hydratedCumulativeToDate || setupCostToDate)
            : (getPriorSpentForRow('dd_additional_costs', itemKey) + inPeriod);
        return {
            element: label,
            otherCosts: budget,
            costsInPeriod: inPeriod,
            budgetRemaining: Math.max(0, budget - costToDate),
            costToDate
        };
    });

    const professionalFeesRowsForTable = safeProfessionalRows.map((row, idx) => {
        const label = row.heading || `Item ${idx + 1}`;
        const itemKey = makeCostItemKey('professional_fees_breakdown', row?.heading, idx);
        const budget = parseMoneyLoose(getInitialCostValue(itemKey));
        const inPeriod = allowInterimCostChanges
            ? parseMoneyLoose(getResolvedDrawdownValue('dd_professional_fees', itemKey, ddProfessional[itemKey]))
            : 0;
        const setupCostToDate = useSetupHydration
            ? parseMoneyLoose(setupCostBaseline?.professionalCostToDateByKey?.[itemKey])
            : null;
        const hydratedCumulativeToDate = useSetupHydration
            ? parseMoneyLoose(priorCumulativeByRow.professional?.[itemKey])
            : null;
        const costToDate = useSetupHydration
            ? (hydratedCumulativeToDate || setupCostToDate)
            : (getPriorSpentForRow('dd_professional_fees', itemKey) + inPeriod);
        return {
            element: label,
            otherCosts: budget,
            costsInPeriod: inPeriod,
            budgetRemaining: Math.max(0, budget - costToDate),
            costToDate
        };
    });

    function FactGrid({ facts }) {
        return (
            <InitialReportInformationGrid
                ariaLabel="Interim report information"
                facts={facts.map((fact) => ({
                    ...fact,
                    value: lineValue(fact.value),
                    source: 'Questionnaire'
                }))}
            />
        );
    }

    return (
        <div>
            {submittedAt && (
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>
                    Submitted: <strong>{submittedAt.toLocaleString()}</strong>
                </div>
            )}

            {!showOnlyProjectFacility ? (
                <InitialReportSectionTabs
                    sections={interimInformationSections}
                    activeKey={activeInterimSection.key}
                    onChange={setActiveInterimSectionKey}
                    ariaLabel="Interim report information sets"
                    idPrefix="interim-report"
                />
            ) : null}

            {!showOnlyProjectFacility && (
                <>
                    <Block title="Project Details" defaultOpen>
                        <FactGrid facts={[
                            { key: 'valuation-date', label: 'Valuation Date', value: answers.valuation_date },
                            { key: 'valuation-number', label: 'Valuation No.', value: answers.valuation_number }
                        ]} />
                    </Block>

                    <Block title="Construction Progress">
                        <FactGrid facts={[
                            { key: 'site-visit-date', label: 'Site visit date', value: answers.site_visit_date },
                            { key: 'site-visit-party', label: 'Party met on site', value: answers.site_visit_party_met },
                            { key: 'monthly-progress', label: 'Monthly progress summary', value: answers.cp_month_summary },
                            { key: 'issues-encountered', label: 'Issues encountered', value: answers.cp_issues_encountered },
                            { key: 'site-photos', label: 'Site photos', value: answers.cp_site_photos },
                            { key: 'accidents', label: 'Accidents on site', value: answers.cp_accidents },
                            ...(isFieldVisible('cp_accidents_explain') ? [{ key: 'incident-details', label: 'Incident details', value: answers.cp_accidents_explain }] : []),
                            ...(isFieldVisible('cp_accidents_hse_submitted') ? [{ key: 'hse-submitted', label: 'HSE incident report submitted', value: answers.cp_accidents_hse_submitted }] : []),
                            ...(isFieldVisible('cp_accidents_hse_when') ? [{ key: 'hse-when', label: 'When will it be filed?', value: answers.cp_accidents_hse_when }] : [])
                        ]} />
                    </Block>

                    <Block title="Programme">
                        <FactGrid facts={[
                            { key: 'programme-delays', label: 'Any delays in the period?', value: answers.prog_any_delays },
                            ...(isFieldVisible('prog_total_delay') ? [{ key: 'programme-delay-total', label: 'Total programme delay', value: answers.prog_total_delay }] : []),
                            { key: 'units-completed', label: 'Any plots/units completed?', value: answers.prog_any_units_completed },
                            ...(isFieldVisible('prog_units_completed') ? [{ key: 'completed-units', label: 'Which plots/units?', value: answers.prog_units_completed }] : []),
                            { key: 'programme-suitability', label: 'Construction duration / programme suitability', value: answers.prog_duration_suitability_commentary }
                        ]} />
                    </Block>

                    <Block title="Building Control / Warranty">
                        <FactGrid facts={[
                            { key: 'bc-attended', label: 'BC Officer attended?', value: answers.bc_attended },
                            ...(isFieldVisible('bc_issues_identified') ? [{ key: 'bc-issues', label: 'Issues identified?', value: answers.bc_issues_identified }] : []),
                            ...(isFieldVisible('bc_issues_details') ? [{ key: 'bc-issue-details', label: 'Issues details', value: answers.bc_issues_details }] : []),
                            ...(isFieldVisible('bc_site_report_issued') ? [{ key: 'bc-report-issued', label: 'Site report issued?', value: answers.bc_site_report_issued }] : []),
                            ...(isFieldVisible('bc_when_received') ? [{ key: 'bc-report-when', label: 'When will it be received?', value: answers.bc_when_received }] : [])
                        ]} />
                    </Block>

                    <Block title="Statutory Elements">
                        <div style={{ color: '#64748b' }}>Content for this section will be added in the next phase when the interim questionnaire is amended.</div>
                    </Block>

                    <Block title="Project Changes">
                        <FactGrid facts={[
                            { key: 'design-changed', label: 'Has the design changed?', value: answers.pc_design_changed },
                            ...(isFieldVisible('pc_design_changed_details') ? [{ key: 'design-change-details', label: 'Design changes details', value: answers.pc_design_changed_details }] : []),
                            { key: 'planning-changed', label: 'Has planning status changed?', value: answers.pc_planning_changed },
                            ...(isFieldVisible('pc_planning_changed_details') ? [{ key: 'planning-change-details', label: 'Planning changes details', value: answers.pc_planning_changed_details }] : []),
                            { key: 'material-changes', label: 'Material changes since Initial Report?', value: answers.pc_material_changes_since_initial },
                            ...(isFieldVisible('pc_material_changes_details') ? [{ key: 'material-change-details', label: 'Material changes details', value: answers.pc_material_changes_details }] : [])
                        ]} />
                    </Block>

                    <Block title="Costs / Drawdown">
                        {!drawdownCosts ? (
                            <div style={{ color: '#64748b' }}>No drawdown costs recorded yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                <CostPanel title="Summary" note="Development cost position and current drawdown roll-up." fullWidth>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.75fr) minmax(240px, 0.7fr)', gap: 12, alignItems: 'start' }}>
                                        <div style={{ minWidth: 0 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11, lineHeight: 1.15 }}>
                                                <colgroup>
                                                    <col style={{ width: '26%' }} />
                                                    <col style={{ width: '17%' }} />
                                                    <col style={{ width: '12%' }} />
                                                    <col style={{ width: '17%' }} />
                                                    <col style={{ width: '28%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Description</th>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb' }}>Borrower Budget</th>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb' }}>% Complete</th>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb' }}>Cost to Date</th>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb' }}>Cost to Complete</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {costSnapshotRowsWithCumulative.map((row) => (
                                                        <tr key={row.label}>
                                                            <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', fontWeight: 800 }}>{row.label}</td>
                                                            <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPoundsLite(row.budget)}</td>
                                                            <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPercentLite(row.cumulativeToDate, row.budget)}</td>
                                                            <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPoundsLite(row.cumulativeToDate)}</td>
                                                            <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{formatPoundsLite(row.cumulativeToComplete)}</td>
                                                        </tr>
                                                    ))}
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total Development Cost</td>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(totalDevelopmentBudget)}</td>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPercentLite(cumulativeDevelopmentToDate, totalDevelopmentBudget)}</td>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(cumulativeDevelopmentToDate)}</td>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(cumulativeDevelopmentToComplete)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        <div style={{ minWidth: 0 }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', fontSize: 11, lineHeight: 1.15 }}>
                                                <colgroup>
                                                    <col style={{ width: '63%' }} />
                                                    <col style={{ width: '37%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'left' }} />
                                                        <th style={{ padding: '5px 6px', border: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>{ddLabel}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', fontWeight: 800 }}>Construction Cost</td>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap' }}>{formatPoundsLite(constructionDdTotal)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', fontWeight: 800 }}>Contingency</td>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap' }}>{formatPoundsLite(contingencyDdTotal)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', fontWeight: 800 }}>Professional Fees</td>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap' }}>{formatPoundsLite(professionalDdTotal)}</td>
                                                    </tr>
                                                    <tr>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', fontWeight: 800 }}>Additional Costs</td>
                                                        <td style={{ padding: '4px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800, whiteSpace: 'nowrap' }}>{formatPoundsLite(additionalDdTotal)}</td>
                                                    </tr>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                                        <td style={{ padding: '5px 6px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(overallDdTotal)}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </CostPanel>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                                    <CostAccordionRow title="Construction Costs" note="Baseline construction budget alongside the current drawdown claim.">
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Element</th>
                                                        <th style={{ width: COST_COL_BUDGET_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Budget</th>
                                                        <th style={{ width: COST_COL_DD_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>{ddLabel}</th>
                                                        <th style={{ width: 90, padding: '8px 10px', border: '1px solid #e5e7eb' }}>%</th>
                                                        <th style={{ width: 150, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Remaining Budget</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {constructionRows.map((r, idx) => {
                                                        const key = makeCostItemKey('construction_cost_breakdown', r?.heading, idx);
                                                        return (
                                                            <tr key={`${key}:${idx}`}>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 800 }}>{r.heading || `Item ${idx + 1}`}</td>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{r.amount ? formatPoundsLite(r.amount) : '—'}</td>
                                                                {renderEditableDdCell({ groupKey: 'dd_construction', itemKey: key, label: r.heading || `Item ${idx + 1}`, borrowerValue: ddConstruction[key] })}
                                                                {renderBudgetProgressCells({ groupKey: 'dd_construction', itemKey: key, budgetValue: r.amount, borrowerValue: ddConstruction[key] })}
                                                            </tr>
                                                        );
                                                    })}
                                                    {facilityBalance > 0 ? (
                                                        <tr>
                                                            <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Facility Balance</td>
                                                            <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(facilityBalance)}</td>
                                                            {renderEditableDdCell({ groupKey: 'dd_construction', itemKey: 'facility_balance', label: 'Facility Balance', borrowerValue: ddConstruction.facility_balance })}
                                                            {renderBudgetProgressCells({ groupKey: 'dd_construction', itemKey: 'facility_balance', budgetValue: facilityBalance, borrowerValue: ddConstruction.facility_balance, strong: true })}
                                                        </tr>
                                                    ) : null}

                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(constructionBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(constructionDdTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPercentLite(priorCumulativeCosts.construction + constructionDdTotal, constructionBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(Math.max(0, constructionBudgetTotal - (priorCumulativeCosts.construction + constructionDdTotal)))}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CostAccordionRow>

                                    <CostAccordionRow title="Professional Fees" note="Surveyor baseline against the live claim for this drawdown.">
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Role</th>
                                                        <th style={{ width: COST_COL_BUDGET_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Budget</th>
                                                        <th style={{ width: COST_COL_DD_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>{ddLabel}</th>
                                                        <th style={{ width: 90, padding: '8px 10px', border: '1px solid #e5e7eb' }}>%</th>
                                                        <th style={{ width: 150, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Remaining Budget</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeProfessionalRows.map((r, idx) => {
                                                        const key = makeCostItemKey('professional_fees_breakdown', r?.heading, idx);
                                                        const budget = getInitialCostValue(key);
                                                        return (
                                                            <tr key={`${key}:${idx}`}>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 800 }}>{r.heading || `Item ${idx + 1}`}</td>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{budget ? formatPoundsLite(budget) : '—'}</td>
                                                                {renderEditableDdCell({ groupKey: 'dd_professional_fees', itemKey: key, label: r.heading || `Item ${idx + 1}`, borrowerValue: ddProfessional[key] })}
                                                                {renderBudgetProgressCells({ groupKey: 'dd_professional_fees', itemKey: key, budgetValue: budget, borrowerValue: ddProfessional[key] })}
                                                            </tr>
                                                        );
                                                    })}

                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalDdTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPercentLite(priorCumulativeCosts.professional + professionalDdTotal, professionalBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(Math.max(0, professionalBudgetTotal - (priorCumulativeCosts.professional + professionalDdTotal)))}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CostAccordionRow>

                                    <CostAccordionRow title="Additional Costs" note="Other project costs tracked separately from main build and professional fees.">
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Other Costs</th>
                                                        <th style={{ width: COST_COL_BUDGET_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Budget</th>
                                                        <th style={{ width: COST_COL_DD_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>{ddLabel}</th>
                                                        <th style={{ width: 90, padding: '8px 10px', border: '1px solid #e5e7eb' }}>%</th>
                                                        <th style={{ width: 150, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Remaining Budget</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {safeAdditionalRows.map((r, idx) => {
                                                        const label = r.heading || `Item ${idx + 1}`;
                                                        const key = `other_cost:${normalizeKeyTextLite(label).replace(/\s/g, '_')}`;
                                                        const budget = getInitialCostValue(key);
                                                        return (
                                                            <tr key={`${key}:${idx}`}>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 800 }}>{label}</td>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{budget ? formatPoundsLite(budget) : '—'}</td>
                                                                {renderEditableDdCell({ groupKey: 'dd_additional_costs', itemKey: key, label, borrowerValue: ddAdditional[key] })}
                                                                {renderBudgetProgressCells({ groupKey: 'dd_additional_costs', itemKey: key, budgetValue: budget, borrowerValue: ddAdditional[key] })}
                                                            </tr>
                                                        );
                                                    })}

                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalDdTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPercentLite(priorCumulativeCosts.additional + additionalDdTotal, additionalBudgetTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{formatPoundsLite(Math.max(0, additionalBudgetTotal - (priorCumulativeCosts.additional + additionalDdTotal)))}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CostAccordionRow>

                                    <CostAccordionRow title="Contingency Claims" note="Separate claim items recorded against contingency rather than base budget lines.">
                                        <div style={{ overflowX: 'auto' }}>
                                            <table style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                                                <thead>
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Description</th>
                                                        <th style={{ width: COST_COL_BUDGET_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Budget</th>
                                                        <th style={{ width: COST_COL_DD_W, padding: '8px 10px', border: '1px solid #e5e7eb' }}>{ddLabel}</th>
                                                        <th style={{ width: 90, padding: '8px 10px', border: '1px solid #e5e7eb' }}>%</th>
                                                        <th style={{ width: 150, padding: '8px 10px', border: '1px solid #e5e7eb' }}>Remaining Budget</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {contingencyClaims.length ? contingencyClaims.map((c, idx) => {
                                                        const label = String(c?.description || 'Contingency');
                                                        const itemKey = String(c?.id || `idx:${idx + 1}`);
                                                        return (
                                                            <tr key={itemKey}>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 800 }}>{label}</td>
                                                                <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>—</td>
                                                                {renderEditableDdCell({ groupKey: 'contingency_claims', itemKey, label, borrowerValue: c?.value })}
                                                                {renderBudgetProgressCells({ groupKey: 'contingency_claims', itemKey, budgetValue: '', borrowerValue: c?.value })}
                                                            </tr>
                                                        );
                                                    }) : (
                                                        <tr>
                                                            <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', color: '#94a3b8' }} colSpan={5}>—</td>
                                                        </tr>
                                                    )}
                                                    <tr style={{ background: '#f3f4f6' }}>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{parseMoneyLoose(ini7?.contingency) ? formatPoundsLite(ini7?.contingency) : '—'}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(contingencyDdTotal)}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{parseMoneyLoose(ini7?.contingency) ? formatPercentLite(priorCumulativeCosts.contingency + contingencyDdTotal, parseMoneyLoose(ini7?.contingency)) : '—'}</td>
                                                        <td style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900, whiteSpace: 'nowrap' }}>{parseMoneyLoose(ini7?.contingency) ? formatPoundsLite(Math.max(0, parseMoneyLoose(ini7?.contingency) - (priorCumulativeCosts.contingency + contingencyDdTotal))) : '—'}</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </CostAccordionRow>

                                </div>
                            </div>
                        )}
                    </Block>

                </>
            )}

            {!hideProjectFacility && (
                showOnlyProjectFacility ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        <div style={{ marginTop: 8, fontSize: 16, fontWeight: 800, color: '#111827' }}>Project Facility</div>
                        <div style={{ marginTop: 4, fontSize: 16, fontWeight: 800, color: '#111827' }}>Budget Summary</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Description</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Original Development Budget</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Adjustment</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Forecast Development Budget</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgetSummaryRows.map((row) => (
                                        <tr key={row.description}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{row.description}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.original)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.adjustment)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.forecast)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total Development Cost (excl VAT)</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetSummaryTotals.original)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetSummaryTotals.adjustment)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetSummaryTotals.forecast)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 8, fontSize: 16, fontWeight: 800, color: '#111827' }}>Facility Headroom / Shortfall</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5, lineHeight: 1.2 }}>
                                <colgroup>
                                    <col style={{ width: '63%' }} />
                                    <col style={{ width: '37%' }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ background: facilityTableHeaderBg }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 800 }}>Element</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800 }}>Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {facilityHeadroomRows.map((row) => (
                                        <tr key={row.label} style={row.highlight ? { background: facilityTableEmphasisBg } : undefined}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: row.highlight ? 900 : 800, color: '#111827' }}>{row.label}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: row.highlight ? 900 : 800, whiteSpace: 'nowrap', color: row.negative ? '#b91c1c' : '#111827' }}>
                                                {formatFacilityFigure(row.value, { signed: row.signed })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Budget Status</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Description</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Borrower Assessment (Forecast)</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>% Complete</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Cost to Date</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Budget Remaining</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgetStatusRows.map((row) => (
                                        <tr key={row.description}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{row.description}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.forecast)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{row.percentComplete}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.costToDate)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.budgetRemaining)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total Development Cost</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(totalDevelopmentBudget)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPercentLite(cumulativeDevelopmentToDate, totalDevelopmentBudget)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(cumulativeDevelopmentToDate)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(cumulativeDevelopmentToComplete)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Contingency Expenditure</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 620, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Item</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>Total Contingency</td>
                                        <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(linkedContingencyBudgetTotal)}</td>
                                    </tr>
                                    <tr>
                                        <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>Contingency Expenditure</td>
                                        <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(contingencyExpenditureToDate)}</td>
                                    </tr>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total Contingency Remaining</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(contingencyRemaining)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Budget Adjustments</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Element</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Final Account Adjustments</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Movement in the period</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Total Expenditure to Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {budgetAdjustmentRows.length ? budgetAdjustmentRows.map((row, idx) => (
                                        <tr key={`${row.element}:${idx}`}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{row.element}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.finalAccountAdjustment)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.movementPeriod)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.totalExpenditureToDate)}</td>
                                        </tr>
                                    )) : (
                                        <tr>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', color: '#6b7280' }} colSpan={4}>No budget adjustment rows available for this period.</td>
                                        </tr>
                                    )}
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Variations Total</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetAdjustmentsTotals.finalAccountAdjustment)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetAdjustmentsTotals.movementPeriod)}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(budgetAdjustmentsTotals.totalExpenditureToDate)}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Professional Fees</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Element</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Other Costs</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Costs in the Period</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Budget Remaining</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Cost to Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionalFeesRowsForTable.map((row) => (
                                        <tr key={row.element}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{row.element}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.otherCosts)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.costsInPeriod)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.budgetRemaining)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.costToDate)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalFeesRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.otherCosts), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalFeesRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.costsInPeriod), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalFeesRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.budgetRemaining), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(professionalFeesRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.costToDate), 0))}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Additional Costs</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5 }}>
                                <thead>
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left' }}>Element</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Other Costs</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Costs in the Period</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Budget Remaining</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right' }}>Cost to Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {additionalCostsRowsForTable.map((row) => (
                                        <tr key={row.element}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: 700 }}>{row.element}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.otherCosts)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.costsInPeriod)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.budgetRemaining)}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 700 }}>{formatPoundsLite(row.costToDate)}</td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: '#f3f4f6' }}>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: 900 }}>Total</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalCostsRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.otherCosts), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalCostsRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.costsInPeriod), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalCostsRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.budgetRemaining), 0))}</td>
                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 900 }}>{formatPoundsLite(additionalCostsRowsForTable.reduce((acc, row) => acc + parseMoneyLoose(row.costToDate), 0))}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 800, color: '#111827' }}>Facility Summary</div>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', maxWidth: 980, margin: 0, borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5, lineHeight: 1.2 }}>
                                <colgroup>
                                    <col style={{ width: '63%' }} />
                                    <col style={{ width: '37%' }} />
                                </colgroup>
                                <thead>
                                    <tr style={{ background: facilityTableHeaderBg }}>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 800 }}>Element</th>
                                        <th style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800 }}>Surveyor Assessment</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {facilitySummaryRows.map((row) => (
                                        <tr key={row.label} style={row.highlight ? { background: facilityTableEmphasisBg } : undefined}>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', fontWeight: row.highlight ? 900 : 800, color: '#111827' }}>{row.label}</td>
                                            <td style={{ padding: '5px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: row.highlight ? 900 : 800, whiteSpace: 'nowrap', color: row.negative ? '#b91c1c' : '#111827' }}>
                                                {formatFacilityFigure(row.value, { signed: row.signed })}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <Block title="Project Facility">
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 360px', minWidth: 320 }}>
                                    <div style={{ marginBottom: 8, fontSize: 15, fontWeight: 500, color: '#111827' }}>Facility Headroom / Shortfall</div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5, lineHeight: 1.2 }}>
                                            <colgroup>
                                                <col style={{ width: '63%' }} />
                                                <col style={{ width: '37%' }} />
                                            </colgroup>
                                            <thead>
                                                <tr style={{ background: facilityTableHeaderBg }}>
                                                    <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 800 }}>Element</th>
                                                    <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800 }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {facilityHeadroomRows.map((row) => (
                                                    <tr key={row.label} style={row.highlight ? { background: facilityTableEmphasisBg } : undefined}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: row.highlight ? 900 : 800, color: '#111827' }}>{row.label}</td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: row.highlight ? 900 : 800, whiteSpace: 'nowrap', color: row.negative ? '#b91c1c' : '#111827' }}>
                                                            {formatFacilityFigure(row.value, { signed: row.signed })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div style={{ flex: '1 1 360px', minWidth: 320 }}>
                                    <div style={{ marginBottom: 8, fontSize: 15, fontWeight: 500, color: '#111827' }}>Facility Summary</div>
                                    <div style={{ overflowX: 'auto' }}>
                                        <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', tableLayout: 'fixed', fontSize: 11.5, lineHeight: 1.2 }}>
                                            <colgroup>
                                                <col style={{ width: '63%' }} />
                                                <col style={{ width: '37%' }} />
                                            </colgroup>
                                            <thead>
                                                <tr style={{ background: facilityTableHeaderBg }}>
                                                    <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'left', fontWeight: 800 }}>Element</th>
                                                    <th style={{ padding: '8px 10px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 800 }}>Surveyor Assessment</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {facilitySummaryRows.map((row) => (
                                                    <tr key={row.label} style={row.highlight ? { background: facilityTableEmphasisBg } : undefined}>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', fontWeight: row.highlight ? 900 : 800, color: '#111827' }}>{row.label}</td>
                                                        <td style={{ padding: '6px 8px', border: '1px solid #e5e7eb', textAlign: 'right', fontWeight: row.highlight ? 900 : 800, whiteSpace: 'nowrap', color: row.negative ? '#b91c1c' : '#111827' }}>
                                                            {formatFacilityFigure(row.value, { signed: row.signed })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Block>
                )
            )}

            <Dialog open={Boolean(drawdownEditor)} onClose={() => (drawdownEditorBusy ? null : setDrawdownEditor(null))} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Drawdown Figure</DialogTitle>
                <DialogContent>
                    {drawdownEditor ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, paddingTop: 6 }}>
                            <div>
                                <div style={{ fontWeight: 900, color: '#111827' }}>{drawdownEditor.label}</div>
                                <div style={{ marginTop: 4, fontSize: 13, color: '#64748b' }}>
                                    Borrower value: <strong>{String(drawdownEditor.borrowerValue || '').trim() ? formatPoundsLite(drawdownEditor.borrowerValue) : '—'}</strong>
                                </div>
                            </div>
                            <TextField
                                label="Surveyor value"
                                value={drawdownEditor.surveyorValue}
                                onChange={(e) => setDrawdownEditor((prev) => (prev ? {
                                    ...prev,
                                    surveyorValue: String(e.target.value || '').replace(/[^0-9,.-]/g, '')
                                } : prev))}
                                onBlur={() => setDrawdownEditor((prev) => (prev ? {
                                    ...prev,
                                    surveyorValue: formatCurrencyInput(prev.surveyorValue)
                                } : prev))}
                                fullWidth
                                size="small"
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">£</InputAdornment>
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px'
                                    }
                                }}
                            />
                            <TextField
                                label="Commentary"
                                value={drawdownEditor.commentary}
                                onChange={(e) => setDrawdownEditor((prev) => (prev ? { ...prev, commentary: e.target.value } : prev))}
                                fullWidth
                                multiline
                                minRows={4}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px'
                                    }
                                }}
                            />
                            {drawdownEditorError ? <Alert severity="error">{drawdownEditorError}</Alert> : null}
                        </div>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDrawdownEditor(null)} disabled={drawdownEditorBusy}>Cancel</Button>
                    <Button
                        onClick={() => setDrawdownEditor((prev) => (prev ? { ...prev, surveyorValue: '', commentary: '' } : prev))}
                        disabled={drawdownEditorBusy || !drawdownEditor}
                    >
                        Clear
                    </Button>
                    <Button onClick={saveDrawdownEditor} variant="contained" disabled={drawdownEditorBusy || !drawdownEditor}>
                        {drawdownEditorBusy ? 'Saving…' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

function InitialReportSections({ initialReport, reference, mode, code, notesByKey, onSaveRowNote, fieldReviewsByKey, onSaveFieldReview, costAssessmentsByKey, onSaveCostAssessment, onSaveIniField, editsByKey, projectFacility, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, onSaveIniDocMetadata }) {
    const baseTables = initialReport?.tables || {};
    const documentMetadataById = new Map();
    for (const row of (Array.isArray(baseTables.ini_document_ai_cache) ? baseTables.ini_document_ai_cache : [])) {
        const documentId = row?.document_id;
        if (documentId === null || documentId === undefined) continue;

        let summaryLines = row?.ai_summary_lines;
        if (typeof summaryLines === 'string') {
            try {
                const parsed = JSON.parse(summaryLines);
                summaryLines = Array.isArray(parsed) ? parsed : [];
            } catch {
                summaryLines = summaryLines.split('\n').map((line) => line.trim()).filter(Boolean);
            }
        }

        documentMetadataById.set(String(documentId), {
            document_id: documentId,
            ai: row,
            effective: {
                display_title: row?.ai_display_title || row?.source_document_title || '',
                authoring_company: row?.ai_authoring_company || '',
                report_date: row?.ai_report_date || '',
                purpose: row?.ai_purpose || '',
                methods: row?.ai_methods || '',
                conclusions: row?.ai_conclusions || '',
                recommendations: row?.ai_recommendations || '',
                summary_lines: Array.isArray(summaryLines) ? summaryLines : []
            }
        });
    }
    for (const [documentId, metadata] of (iniDocMetadataById?.entries ? iniDocMetadataById.entries() : [])) {
        const fallback = documentMetadataById.get(String(documentId)) || {};
        documentMetadataById.set(String(documentId), {
            ...fallback,
            ...metadata,
            ai: { ...(fallback.ai || {}), ...(metadata?.ai || {}) },
            effective: { ...(fallback.effective || {}), ...(metadata?.effective || {}) }
        });
    }
    const devGrossInternalArea = Array.isArray(baseTables.ini_section_6_development) && baseTables.ini_section_6_development.length
        ? baseTables.ini_section_6_development[0]?.gross_internal_area
        : null;

    const borrowerGiaM2 = Array.isArray(baseTables.ini_section_3_location) && baseTables.ini_section_3_location.length
        ? baseTables.ini_section_3_location[0]?.borrower_gia_m2
        : null;

    const allDocuments = Array.isArray(baseTables.ini_documents) ? baseTables.ini_documents : [];

    const DOC_TYPES = {
        CASE_STUDIES: 'Project Case Studies (Photos)'
    };

    function normalizeText(v) {
        return String(v || '').trim().toLowerCase();
    }

    function docMatches(doc, { relatedSection, docTypes }) {
        const rs = normalizeText(doc?.related_section);
        if (relatedSection && rs !== normalizeText(relatedSection)) return false;
        if (Array.isArray(docTypes) && docTypes.length) {
            const dt = normalizeText(doc?.document_type);
            return docTypes.some((t) => dt === normalizeText(t));
        }
        return true;
    }

    const docsExperienceCaseStudies = allDocuments.filter((d) => docMatches(d, { relatedSection: 'section_10', docTypes: [DOC_TYPES.CASE_STUDIES] }));
    const docsSurveys = allDocuments.filter((d) => docMatches(d, { relatedSection: 'section_5' }));

    const tables = { ...baseTables };
    if (docsSurveys.length) tables.ini_documents__surveys = docsSurveys;
    if (docsExperienceCaseStudies.length) tables.ini_documents__experience = docsExperienceCaseStudies;

    const sections = [
        {
            key: 'surveys',
            title: 'Surveys',
            tableNames: ['ini_surveys', 'ini_documents__surveys']
        },
        {
            key: 'experience',
            title: 'Borrower and/or Contractor Experience',
            tableNames: ['ini_section_10_experience', 'ini_section_10_conclusion', 'ini_documents__experience']
        }
    ];

    const hasAnyRows = Object.values(tables).some((rows) => Array.isArray(rows) && rows.length);
    const sectionTabs = sections.map((section) => ({
        key: section.key,
        label: section.title,
        count: section.tableNames.reduce((count, tableName) => count + (Array.isArray(tables[tableName]) ? tables[tableName].length : 0), 0)
    }));
    const [activeSectionKey, setActiveSectionKey] = useState(sections[0].key);
    const activeSection = sections.find((section) => section.key === activeSectionKey) || sections[0];

    if (!hasAnyRows) {
        return <div style={{ color: '#666' }}>No ini_* table rows found for this project reference yet.</div>;
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, width: '100%' }}>
            <InitialReportSectionTabs
                sections={sectionTabs}
                activeKey={activeSection.key}
                onChange={setActiveSectionKey}
                ariaLabel="Initial report information sets"
                idPrefix="initial-report"
            />
            <SectionBlock
                key={activeSection.key}
                sectionKey={activeSection.key}
                title={activeSection.title}
                tableNames={activeSection.tableNames}
                tables={tables}
                reference={reference}
                mode={mode}
                code={code}
                notesByKey={notesByKey}
                onSaveRowNote={onSaveRowNote}
                fieldReviewsByKey={fieldReviewsByKey}
                onSaveFieldReview={onSaveFieldReview}
                costAssessmentsByKey={costAssessmentsByKey}
                onSaveCostAssessment={onSaveCostAssessment}
                onSaveIniField={onSaveIniField}
                editsByKey={editsByKey}
                projectFacility={projectFacility}
                devGrossInternalArea={devGrossInternalArea}
                borrowerGiaM2={borrowerGiaM2}
                iniDocMetadataById={documentMetadataById}
                iniDocMetadataLoading={iniDocMetadataLoading}
                iniDocMetadataError={iniDocMetadataError}
                onSaveIniDocMetadata={onSaveIniDocMetadata}
            />
        </div>
    );
}

function SectionBlock({ sectionKey, title, tableNames, tables, reference, mode, code, notesByKey, onSaveRowNote, fieldReviewsByKey, onSaveFieldReview, costAssessmentsByKey, onSaveCostAssessment, onSaveIniField, editsByKey, projectFacility, devGrossInternalArea, borrowerGiaM2, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, onSaveIniDocMetadata }) {
    const present = tableNames
        .map((t) => ({ name: t, rows: tables[t] }))
        .filter(({ rows }) => Array.isArray(rows) && rows.length > 0);

    return (
        <section
            className="initial-report-tab-panel"
            id={`initial-report-panel-${sectionKey}`}
            role="tabpanel"
            aria-labelledby={`initial-report-tab-${sectionKey}`}
        >
            <div className="initial-report-tab-panel-body">
                {present.length === 0 && (
                    <div style={{ color: 'var(--muted)' }}>No information recorded for {title}.</div>
                )}

                {/* If a section only has one subsection/table, omit the table subheading. */}
                {present.length === 1 && (
                    <IniTableRowsOnly
                        rows={present[0].rows}
                        tableName={present[0].name}
                        reference={reference}
                        mode={mode}
                        code={code}
                        notesByKey={notesByKey}
                        onSaveRowNote={onSaveRowNote}
                        fieldReviewsByKey={fieldReviewsByKey}
                        onSaveFieldReview={onSaveFieldReview}
                        costAssessmentsByKey={costAssessmentsByKey}
                        onSaveCostAssessment={onSaveCostAssessment}
                        onSaveIniField={onSaveIniField}
                        editsByKey={editsByKey}
                        projectFacility={projectFacility}
                        devGrossInternalArea={devGrossInternalArea}
                        borrowerGiaM2={borrowerGiaM2}
                        iniDocMetadataById={iniDocMetadataById}
                        iniDocMetadataLoading={iniDocMetadataLoading}
                        iniDocMetadataError={iniDocMetadataError}
                        onSaveIniDocMetadata={onSaveIniDocMetadata}
                    />
                )}

                {/* Keep subheadings when there are 2+ subsections/tables. */}
                {present.length >= 2 && present.map(({ name, rows }) => (
                    <IniTableCard
                        key={name}
                        tableName={name}
                        rows={rows}
                        reference={reference}
                        mode={mode}
                        code={code}
                        notesByKey={notesByKey}
                        onSaveRowNote={onSaveRowNote}
                        fieldReviewsByKey={fieldReviewsByKey}
                        onSaveFieldReview={onSaveFieldReview}
                        costAssessmentsByKey={costAssessmentsByKey}
                        onSaveCostAssessment={onSaveCostAssessment}
                        onSaveIniField={onSaveIniField}
                        editsByKey={editsByKey}
                        projectFacility={projectFacility}
                        devGrossInternalArea={devGrossInternalArea}
                        borrowerGiaM2={borrowerGiaM2}
                        iniDocMetadataById={iniDocMetadataById}
                        iniDocMetadataLoading={iniDocMetadataLoading}
                        iniDocMetadataError={iniDocMetadataError}
                        onSaveIniDocMetadata={onSaveIniDocMetadata}
                    />
                ))}
            </div>
        </section>
    );
}

function IniTableRowsOnly({ rows, tableName, reference, mode, code, notesByKey, onSaveRowNote, fieldReviewsByKey, onSaveFieldReview, costAssessmentsByKey, onSaveCostAssessment, onSaveIniField, editsByKey, projectFacility, devGrossInternalArea, borrowerGiaM2, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, onSaveIniDocMetadata }) {
    const list = Array.isArray(rows) ? rows : [];
    const label = prettyTableName(tableName);
    const isUploadedDocuments = label === 'Uploaded Documents';

    if (list.length === 0) {
        return <div style={{ color: '#6b7280' }}>No records yet.</div>;
    }

    if (isUploadedDocuments) {
        return (
            <div className="initial-report-data-group">
                <div className="initial-report-data-group-heading">
                    <span className="info-subsection-title">{label}</span>
                    <span className="info-subsection-count">{list.length}</span>
                </div>
                <IniTableBody
                    tableName={tableName}
                    rows={list}
                    reference={reference}
                    mode={mode}
                    code={code}
                    notesByKey={notesByKey}
                    onSaveRowNote={onSaveRowNote}
                    fieldReviewsByKey={fieldReviewsByKey}
                    onSaveFieldReview={onSaveFieldReview}
                    costAssessmentsByKey={costAssessmentsByKey}
                    onSaveCostAssessment={onSaveCostAssessment}
                    projectFacility={projectFacility}
                    devGrossInternalArea={devGrossInternalArea}
                    borrowerGiaM2={borrowerGiaM2}
                    iniDocMetadataById={iniDocMetadataById}
                    iniDocMetadataLoading={iniDocMetadataLoading}
                    iniDocMetadataError={iniDocMetadataError}
                    onSaveIniDocMetadata={onSaveIniDocMetadata}
                />
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <IniTableBody
                tableName={tableName}
                rows={list}
                reference={reference}
                mode={mode}
                code={code}
                notesByKey={notesByKey}
                onSaveRowNote={onSaveRowNote}
                fieldReviewsByKey={fieldReviewsByKey}
                onSaveFieldReview={onSaveFieldReview}
                costAssessmentsByKey={costAssessmentsByKey}
                onSaveCostAssessment={onSaveCostAssessment}
                onSaveIniField={onSaveIniField}
                editsByKey={editsByKey}
                projectFacility={projectFacility}
                devGrossInternalArea={devGrossInternalArea}
                borrowerGiaM2={borrowerGiaM2}
                iniDocMetadataById={iniDocMetadataById}
                iniDocMetadataLoading={iniDocMetadataLoading}
                iniDocMetadataError={iniDocMetadataError}
                onSaveIniDocMetadata={onSaveIniDocMetadata}
            />
        </div>
    );
}

function IniTableCard({ tableName, rows, reference, mode, code, notesByKey, onSaveRowNote, fieldReviewsByKey, onSaveFieldReview, costAssessmentsByKey, onSaveCostAssessment, onSaveIniField, editsByKey, projectFacility, devGrossInternalArea, borrowerGiaM2, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, onSaveIniDocMetadata }) {
    const list = Array.isArray(rows) ? rows : [];
    const label = prettyTableName(tableName);
    const isUploadedDocuments = label === 'Uploaded Documents';

    if (isUploadedDocuments) {
        return (
            <div className="initial-report-data-group">
                <div className="initial-report-data-group-heading">
                    <span className="info-subsection-title">{label}</span>
                    <span className="info-subsection-count">{list.length}</span>
                </div>
                <IniTableBody
                    tableName={tableName}
                    rows={list}
                    reference={reference}
                    mode={mode}
                    code={code}
                    notesByKey={notesByKey}
                    onSaveRowNote={onSaveRowNote}
                    fieldReviewsByKey={fieldReviewsByKey}
                    onSaveFieldReview={onSaveFieldReview}
                    costAssessmentsByKey={costAssessmentsByKey}
                    onSaveCostAssessment={onSaveCostAssessment}
                    onSaveIniField={onSaveIniField}
                    editsByKey={editsByKey}
                    projectFacility={projectFacility}
                    devGrossInternalArea={devGrossInternalArea}
                    borrowerGiaM2={borrowerGiaM2}
                    iniDocMetadataById={iniDocMetadataById}
                    iniDocMetadataLoading={iniDocMetadataLoading}
                    iniDocMetadataError={iniDocMetadataError}
                    onSaveIniDocMetadata={onSaveIniDocMetadata}
                />
            </div>
        );
    }

    return (
        <div className="initial-report-data-group">
            <div className="initial-report-data-group-heading">
                <span className="info-subsection-title">{label}</span>
                <span className="info-subsection-count">{list.length}</span>
            </div>
            <IniTableBody
                tableName={tableName}
                rows={list}
                reference={reference}
                mode={mode}
                code={code}
                notesByKey={notesByKey}
                onSaveRowNote={onSaveRowNote}
                fieldReviewsByKey={fieldReviewsByKey}
                onSaveFieldReview={onSaveFieldReview}
                costAssessmentsByKey={costAssessmentsByKey}
                onSaveCostAssessment={onSaveCostAssessment}
                onSaveIniField={onSaveIniField}
                editsByKey={editsByKey}
                projectFacility={projectFacility}
                devGrossInternalArea={devGrossInternalArea}
                borrowerGiaM2={borrowerGiaM2}
                iniDocMetadataById={iniDocMetadataById}
                iniDocMetadataLoading={iniDocMetadataLoading}
                iniDocMetadataError={iniDocMetadataError}
                onSaveIniDocMetadata={onSaveIniDocMetadata}
            />
        </div>
    );
}

function getRowIdForNotes(row, index, totalRows) {
    if (row?.id !== undefined && row?.id !== null && String(row.id) !== '') return String(row.id);
    if (totalRows === 1) return 'singleton';

    // Stable synthetic id for tables that don't expose a primary key.
    // Prefer content-based ids so notes stay attached even if row ordering changes.
    const entries = Object.entries(row || {})
        .filter(([k, v]) => v !== undefined && typeof v !== 'function')
        .filter(([k]) => k !== 'updated_at' && k !== 'updatedAt');
    entries.sort(([a], [b]) => a.localeCompare(b));
    const signature = entries.map(([k, v]) => `${k}=${String(v)}`).join('|');

    let h = 2166136261;
    for (let i = 0; i < signature.length; i++) {
        h ^= signature.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    // Keep it compact and URL/key friendly.
    const hash = (h >>> 0).toString(36);
    return `hash:${hash}`;
}

function parseBreakdownValue(v) {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    if (typeof v === 'object') return [];

    const s = String(v || '').trim();
    if (!s) return [];
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function parseNumberLoose(v) {
    if (v === null || v === undefined) return null;
    const s = String(v).trim();
    if (!s) return null;
    // allow TBC / - / etc
    if (!/[0-9]/.test(s)) return null;
    const cleaned = s.replace(/[^0-9.-]/g, '');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

function formatPounds(v) {
    if (v === null || v === undefined) return '—';
    const n = typeof v === 'number' ? v : parseNumberLoose(v);
    if (n === null) {
        const s = String(v || '').trim();
        return s ? s : '—';
    }
    return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

function makeCostItemKey(prefix, heading, index) {
    const h = String(heading || '').trim();
    if (h) return `${prefix}:${h}`;
    return `${prefix}:idx:${index + 1}`;
}

function normalizeKeyText(v) {
    return String(v || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[\u2013\u2014]/g, '-')
        .replace(/[^a-z0-9 -]/g, '');
}

function truthyFromString(v) {
    const s = String(v ?? '').trim().toLowerCase();
    return s === 'true' || s === '1' || s === 'yes' || s === 'y' || s === 'on';
}

function findBreakdownAmount(breakdown, label) {
    const target = normalizeKeyText(label);
    const arr = Array.isArray(breakdown) ? breakdown : [];
    const found = arr.find((x) => normalizeKeyText(x?.heading) === target);
    return found?.amount;
}

function parsePercentOrAmount(raw, baseAmount) {
    const s = String(raw ?? '').trim();
    if (!s) return { amount: null, percent: null };

    const base = parseNumberLoose(baseAmount);
    if (s.includes('%')) {
        const pct = parseNumberLoose(s);
        if (pct === null) return { amount: null, percent: null };
        const amt = base !== null ? (base * pct) / 100 : null;
        return { amount: amt, percent: pct };
    }

    const amt = parseNumberLoose(s);
    if (amt === null) return { amount: null, percent: null };
    const pct = base !== null && base !== 0 ? (amt / base) * 100 : null;
    return { amount: amt, percent: pct };
}

function formatPercent(v) {
    const n = typeof v === 'number' ? v : parseNumberLoose(v);
    if (n === null) return '—';
    return `${n.toFixed(1)}%`;
}

function round2(n) {
    if (!Number.isFinite(n)) return n;
    return Math.round(n * 100) / 100;
}

function AssessmentInput({ value, canEdit, onCommit }) {
    const [draft, setDraft] = useState(String(value ?? ''));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(String(value ?? ''));
    }, [value]);

    async function commit() {
        if (!canEdit) return;
        if (String(draft ?? '') === String(value ?? '')) return;
        if (!onCommit) return;
        setSaving(true);
        try {
            try {
                await onCommit(draft);
            } catch (e) {
                // Swallow to avoid hard-crashing the UI; parent can show an error banner.
                console.error('AssessmentInput commit failed:', e);
            }
        } finally {
            setSaving(false);
        }
    }

    if (!canEdit) {
        return <div style={{ fontWeight: 700 }}>{draft ? formatPounds(draft) : '—'}</div>;
    }

    return (
        <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            disabled={saving}
            placeholder="TBC"
            style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontWeight: 700,
                fontSize: 13
            }}
        />
    );
}

function parseJsonArrayLoose(raw) {
    const s = String(raw ?? '').trim();
    if (!s) return [];
    try {
        const parsed = JSON.parse(s);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

function TextInput({ value, canEdit, onCommit, placeholder }) {
    const [draft, setDraft] = useState(String(value ?? ''));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(String(value ?? ''));
    }, [value]);

    async function commit() {
        if (!canEdit) return;
        if (String(draft ?? '') === String(value ?? '')) return;
        if (!onCommit) return;
        setSaving(true);
        try {
            try {
                await onCommit(draft);
            } catch (e) {
                console.error('TextInput commit failed:', e);
            }
        } finally {
            setSaving(false);
        }
    }

    if (!canEdit) {
        return <div style={{ fontWeight: 800 }}>{draft || '—'}</div>;
    }

    return (
        <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            disabled={saving}
            placeholder={placeholder || 'Enter text'}
            style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontWeight: 800,
                fontSize: 13
            }}
        />
    );
}

function TextAreaInput({ value, canEdit, onCommit, placeholder, minHeight }) {
    const [draft, setDraft] = useState(String(value ?? ''));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(String(value ?? ''));
    }, [value]);

    async function commit() {
        if (!canEdit) return;
        if (String(draft ?? '') === String(value ?? '')) return;
        if (!onCommit) return;
        setSaving(true);
        try {
            try {
                await onCommit(draft);
            } catch (e) {
                console.error('TextAreaInput commit failed:', e);
            }
        } finally {
            setSaving(false);
        }
    }

    if (!canEdit) {
        return (
            <div style={{ fontWeight: 700, whiteSpace: 'pre-wrap', color: draft ? '#111827' : '#6b7280' }}>
                {draft || '—'}
            </div>
        );
    }

    return (
        <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            disabled={saving}
            placeholder={placeholder || 'Enter commentary'}
            style={{
                width: '100%',
                minHeight: minHeight || 110,
                padding: '8px 10px',
                borderRadius: 8,
                border: '1px solid #d1d5db',
                fontWeight: 700,
                fontSize: 13,
                resize: 'vertical'
            }}
        />
    );
}

function CurrencyInput({ value, canEdit, onCommit, placeholder, disabledStyle }) {
    const [draft, setDraft] = useState(String(value ?? ''));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setDraft(String(value ?? ''));
    }, [value]);

    async function commit() {
        if (!canEdit) return;
        if (String(draft ?? '') === String(value ?? '')) return;
        if (!onCommit) return;
        setSaving(true);
        try {
            try {
                await onCommit(draft);
            } catch (e) {
                console.error('CurrencyInput commit failed:', e);
            }
        } finally {
            setSaving(false);
        }
    }

    const isDisabled = !canEdit;
    return (
        <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            disabled={saving || isDisabled}
            placeholder={placeholder || 'TBC'}
            style={{
                width: '100%',
                padding: '6px 8px',
                borderRadius: 6,
                border: '1px solid #d1d5db',
                fontWeight: 800,
                fontSize: 13,
                background: isDisabled ? '#f3f4f6' : '#fff',
                color: isDisabled ? '#6b7280' : '#111827',
                ...(isDisabled && disabledStyle ? disabledStyle : null)
            }}
        />
    );
}

function Section7CostsAssessmentTable({ row, projectFacility, devGrossInternalArea, borrowerGiaM2, canEdit, valuesByKey, onSave }) {
    const constructionBreakdown = parseBreakdownValue(row?.construction_cost_breakdown);
    const professionalBreakdown = parseBreakdownValue(row?.professional_fees_breakdown);
    const otherBreakdown = parseBreakdownValue(row?.other_fees_breakdown);

    const headerRowBg = '#f3f4f6';
    const subtotalRowBg = '#fafafa';

    const projectReference = String(row?.project_reference || row?.projectReference || '').trim();
    const INI_UPLOADS_BASE_URL = process.env.REACT_APP_INI_UPLOADS_BASE_URL || getDefaultInitialqBaseUrl();
    const INI_API_BASE_URL = process.env.REACT_APP_INI_API_BASE_URL || INI_UPLOADS_BASE_URL;
    const BCIS_RELATED_SECTION = 'bcis_benchmarking';
    const BCIS_DOCUMENT_TYPE = 'BCIS Benchmarking';

    const [bcisDocs, setBcisDocs] = useState([]);
    const [bcisLoading, setBcisLoading] = useState(false);
    const [bcisError, setBcisError] = useState('');
    const [bcisFile, setBcisFile] = useState(null);
    const [csvStructure, setCsvStructure] = useState('qa_detailed');
    const bcisInputRef = useRef(null);
    const [bcisDragOver, setBcisDragOver] = useState(false);

    function iniBaseUrl() {
        return String(INI_API_BASE_URL || '').replace(/\/+$/, '');
    }

    function toIniUploadsUrl(filePath) {
        if (!filePath) return '';
        if (/^https?:\/\//i.test(String(filePath))) return String(filePath);
        const base = String(INI_UPLOADS_BASE_URL || '').replace(/\/+$/, '');
        const rel = String(filePath).replace(/^\/+/, '');
        return `${base}/${rel}`;
    }

    async function refreshBcisDocs() {
        if (!projectReference) return;
        setBcisLoading(true);
        setBcisError('');
        try {
            const base = iniBaseUrl();
            const url = `${base}/api/documents/${encodeURIComponent(projectReference)}?related_section=${encodeURIComponent(BCIS_RELATED_SECTION)}&document_type=${encodeURIComponent(BCIS_DOCUMENT_TYPE)}`;
            const res = await fetch(url);
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) {
                throw new Error(data?.message || `Failed to load BCIS docs (${res.status})`);
            }
            setBcisDocs(Array.isArray(data?.documents) ? data.documents : []);
        } catch (e) {
            setBcisError(e?.message || String(e));
            setBcisDocs([]);
        } finally {
            setBcisLoading(false);
        }
    }

    useEffect(() => {
        refreshBcisDocs();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectReference]);

    async function uploadBcis() {
        if (!canEdit) return;
        if (!projectReference) {
            setBcisError('Missing project reference; cannot upload.');
            return;
        }
        if (!bcisFile) {
            setBcisError('Please choose a file to upload.');
            return;
        }

        setBcisLoading(true);
        setBcisError('');
        try {
            const base = iniBaseUrl();
            const form = new FormData();
            form.append('file', bcisFile);
            form.append('project_reference', projectReference);
            form.append('related_section', BCIS_RELATED_SECTION);
            form.append('document_type', BCIS_DOCUMENT_TYPE);
            form.append('uploaded_by', 'Surveyor');

            const res = await fetch(`${base}/api/documents/upload`, {
                method: 'POST',
                body: form
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.ok) {
                throw new Error(data?.message || `Upload failed (${res.status})`);
            }
            setBcisFile(null);
            await refreshBcisDocs();
        } catch (e) {
            setBcisError(e?.message || String(e));
        } finally {
            setBcisLoading(false);
        }
    }

    function onBcisPickClick() {
        if (!canEdit || bcisLoading) return;
        try {
            bcisInputRef.current?.click?.();
        } catch {
            // ignore
        }
    }

    function onBcisDragOver(e) {
        e.preventDefault();
        if (!canEdit || bcisLoading) return;
        setBcisDragOver(true);
    }

    function onBcisDragLeave(e) {
        e.preventDefault();
        setBcisDragOver(false);
    }

    function onBcisDrop(e) {
        e.preventDefault();
        setBcisDragOver(false);
        if (!canEdit || bcisLoading) return;
        const f = e?.dataTransfer?.files && e.dataTransfer.files[0] ? e.dataTransfer.files[0] : null;
        if (f) setBcisFile(f);
    }

    const otherLines = (() => {
        const seen = new Set();
        const items = [];
        (Array.isArray(otherBreakdown) ? otherBreakdown : []).forEach((it) => {
            const heading = String(it?.heading || '').trim();
            if (!heading) return;
            const k = normalizeKeyText(heading);
            if (!k || seen.has(k)) return;
            seen.add(k);
            items.push(heading);
        });
        if (!seen.has(normalizeKeyText('Monitoring Surveyor'))) items.push('Monitoring Surveyor');
        return items;
    })();

    const cell = { padding: '8px 10px', border: '1px solid #e5e7eb' };
    const cellRight = { ...cell, textAlign: 'right', fontWeight: 700 };
    const cellLabel = { ...cell, fontWeight: 800 };

    // Main headline table: slightly taller rows for readability and to better align with the RHS cards.
    const mainCell = { ...cell, padding: '12px 10px' };
    const mainCellRight = { ...mainCell, textAlign: 'right', fontWeight: 700 };
    const mainCellLabel = { ...mainCell, fontWeight: 800 };

    function getSurveyorValue(itemKey) {
        if (!valuesByKey?.get) return '';
        return valuesByKey.get(String(itemKey)) ?? '';
    }

    function differenceText(borrowerValue, surveyorValue) {
        const b = parseNumberLoose(borrowerValue);
        const s = parseNumberLoose(surveyorValue);
        if (b === null || s === null) return '—';
        return formatPounds(s - b);
    }

    function formatMainPounds(value) {
        const raw = String(value ?? '').trim();
        if (raw.toUpperCase() === 'TBC') return formatPounds(0);
        const num = parseNumberLoose(value);
        return num === null ? '—' : formatPounds(num);
    }

    const facilityConfirmedRaw = getSurveyorValue('facility_confirmed');
    const facilityConfirmed = truthyFromString(facilityConfirmedRaw);
    const facilityAmountStored = getSurveyorValue('facility_amount');
    const facilityAmountDisplay = facilityConfirmed ? facilityAmountStored : 'TBC';
    const facilityAmountNum = parseNumberLoose(facilityAmountDisplay);

    const constructionBreakdownNums = constructionBreakdown
        .map((it) => parseNumberLoose(it?.amount))
        .filter((n) => n !== null);
    const constructionBreakdownSum = constructionBreakdownNums.length
        ? constructionBreakdownNums.reduce((a, b) => a + b, 0)
        : null;
    const borrowerConstructionTotal = constructionBreakdownSum ?? parseNumberLoose(row?.build_cost);

    const borrowerProfessionalTotal = parseNumberLoose(row?.professional_fees) ?? (
        professionalBreakdown
            .map((it) => parseNumberLoose(it?.amount))
            .filter((n) => n !== null)
            .reduce((a, b) => a + b, 0)
    );

    const borrowerOtherTotal = otherLines
        .map((label) => {
            if (normalizeKeyText(label) === normalizeKeyText('Monitoring Surveyor')) return 0;
            return parseNumberLoose(findBreakdownAmount(otherBreakdown, label));
        })
        .filter((n) => n !== null)
        .reduce((a, b) => a + b, 0);

    const borrowerCont = parsePercentOrAmount(row?.contingency, borrowerConstructionTotal);

    const professionalExtraRowIds = parseJsonArrayLoose(getSurveyorValue('professional_fees_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);
    const otherExtraRowIds = parseJsonArrayLoose(getSurveyorValue('other_costs_extra_rows'))
        .map((x) => String(x))
        .filter(Boolean);

    const giaDevM2 = parseNumberLoose(borrowerGiaM2) ?? parseNumberLoose(devGrossInternalArea);
    const giaSurveyorRaw = getSurveyorValue('proposed_gia');
    const giaSurveyorM2 = parseNumberLoose(giaSurveyorRaw);
    const effectiveGiaM2 = giaSurveyorM2;

    // Use Surveyor GIA throughout (m² -> ft²).
    const effectiveFt2 = effectiveGiaM2 === null ? null : effectiveGiaM2 * 10.7639;

    function isBespokeKey(itemKey) {
        return `construction_bespoke:${String(itemKey)}`;
    }

    const bespokeTotals = constructionBreakdown.reduce(
        (acc, it, idx) => {
            const itemKey = makeCostItemKey('construction_cost_breakdown', it?.heading, idx);
            const isBespoke = truthyFromString(getSurveyorValue(isBespokeKey(itemKey)));
            const amount = parseNumberLoose(it?.amount) ?? 0;
            if (isBespoke) acc.bespoke += amount;
            else acc.construction += amount;
            return acc;
        },
        { bespoke: 0, construction: 0 }
    );

    const developerConstructionRateFt2 = effectiveFt2 ? round2(bespokeTotals.construction / effectiveFt2) : null;
    const surveyorConstructionRateRaw = getSurveyorValue('construction_items_rate_ft2');
    const surveyorConstructionRateFt2 = (() => {
        const v = parseNumberLoose(surveyorConstructionRateRaw);
        return v === null ? null : round2(v);
    })();

    const bespokeRateFt2 = effectiveFt2 ? round2(bespokeTotals.bespoke / effectiveFt2) : null;
    let surveyorConstructionTotal = (effectiveFt2 && surveyorConstructionRateFt2 !== null)
        ? (round2(surveyorConstructionRateFt2 + (bespokeRateFt2 || 0)) * effectiveFt2)
        : null;

    // Guardrail: if Surveyor £/ft² matches Developer £/ft² (to 2dp),
    // force the Surveyor construction cost to match the Developer construction total.
    // This avoids tiny rounding drift in the derived total.
    const ratesMatch = (
        borrowerConstructionTotal !== null &&
        developerConstructionRateFt2 !== null &&
        surveyorConstructionRateFt2 !== null &&
        round2(developerConstructionRateFt2) === round2(surveyorConstructionRateFt2)
    );
    if (ratesMatch) surveyorConstructionTotal = borrowerConstructionTotal;

    const professionalChildKeys = professionalBreakdown.map((it, idx) =>
        makeCostItemKey('professional_fees_breakdown', it?.heading, idx)
    );
    const professionalChildNums = professionalChildKeys
        .map((k) => parseNumberLoose(getSurveyorValue(k)))
        .filter((n) => n !== null);
    const derivedSurveyorProfessional = professionalChildKeys.length
        ? (professionalChildNums.length ? professionalChildNums.reduce((a, b) => a + b, 0) : null)
        : null;
    const professionalExtraNums = professionalExtraRowIds
        .map((id) => parseNumberLoose(getSurveyorValue(`professional_fee_extra:${id}:amount`)))
        .filter((n) => n !== null);
    const professionalExtraSum = professionalExtraNums.length ? professionalExtraNums.reduce((a, b) => a + b, 0) : null;

    const surveyorProfessionalBase = professionalChildKeys.length
        ? derivedSurveyorProfessional
        : parseNumberLoose(getSurveyorValue('professional_fees'));
    const surveyorProfessionalTotal = (surveyorProfessionalBase === null && professionalExtraSum === null)
        ? null
        : ((surveyorProfessionalBase || 0) + (professionalExtraSum || 0));

    function otherLineKey(label) {
        return `other_cost:${normalizeKeyText(label).replace(/\s/g, '_')}`;
    }
    const otherSurveyorNums = otherLines
        .map((label) => parseNumberLoose(getSurveyorValue(otherLineKey(label))))
        .filter((n) => n !== null);
    const otherExtraNums = otherExtraRowIds
        .map((id) => parseNumberLoose(getSurveyorValue(`other_cost_extra:${id}:amount`)))
        .filter((n) => n !== null);
    const otherBaseSum = otherSurveyorNums.length ? otherSurveyorNums.reduce((a, b) => a + b, 0) : null;
    const otherExtraSum = otherExtraNums.length ? otherExtraNums.reduce((a, b) => a + b, 0) : null;
    const surveyorOtherTotal = (otherBaseSum === null && otherExtraSum === null)
        ? null
        : ((otherBaseSum || 0) + (otherExtraSum || 0));

    const surveyorCont = parsePercentOrAmount(getSurveyorValue('contingency'), surveyorConstructionTotal);

    const borrowerSubtotal = [borrowerConstructionTotal, borrowerCont.amount, borrowerProfessionalTotal, borrowerOtherTotal]
        .map((n) => (typeof n === 'number' ? n : parseNumberLoose(n)))
        .filter((n) => n !== null)
        .reduce((a, b) => a + b, 0);

    const surveyorSubtotalParts = [surveyorConstructionTotal, surveyorCont.amount, surveyorProfessionalTotal, surveyorOtherTotal]
        .map((n) => (typeof n === 'number' ? n : parseNumberLoose(n)))
        .filter((n) => n !== null);
    const surveyorSubtotal = surveyorSubtotalParts.length ? surveyorSubtotalParts.reduce((a, b) => a + b, 0) : null;

    const borrowerInputSurveyorRaw = getSurveyorValue('less_borrower_input');
    const borrowerInputSurveyorNum = parseNumberLoose(borrowerInputSurveyorRaw);
    const facilityAmountForCalc = facilityAmountNum ?? 0;
    const borrowerInputForCalc = borrowerInputSurveyorNum ?? 0;
    const headroomSurveyor = surveyorSubtotal === null
        ? null
        : surveyorSubtotal - facilityAmountForCalc - borrowerInputForCalc;

    function csvEscape(value) {
        return `"${String(value ?? '').replace(/"/g, '""')}"`;
    }

    function downloadCsv(filename, columns, rowsForCsv) {
        const header = columns.map((column) => csvEscape(column.label)).join(',');
        const body = rowsForCsv.map((rowForCsv) => columns.map((column) => csvEscape(rowForCsv?.[column.key] ?? '')).join(',')).join('\n');
        const csv = `${header}\n${body}`;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    function buildSummaryCsvRows() {
        return [
            {
                section: 'Summary',
                label: 'Construction Costs',
                developer_value: borrowerConstructionTotal === null ? '' : borrowerConstructionTotal,
                surveyor_value: surveyorConstructionTotal === null ? '' : surveyorConstructionTotal,
                difference: (surveyorConstructionTotal === null || borrowerConstructionTotal === null) ? '' : surveyorConstructionTotal - borrowerConstructionTotal
            },
            {
                section: 'Summary',
                label: 'Contingency',
                developer_value: borrowerCont.amount === null ? '' : borrowerCont.amount,
                surveyor_value: surveyorCont.amount === null ? '' : surveyorCont.amount,
                difference: (surveyorCont.amount === null || borrowerCont.amount === null) ? '' : surveyorCont.amount - borrowerCont.amount
            },
            {
                section: 'Summary',
                label: 'Professional Fees',
                developer_value: borrowerProfessionalTotal === null ? '' : borrowerProfessionalTotal,
                surveyor_value: surveyorProfessionalTotal === null ? '' : surveyorProfessionalTotal,
                difference: (surveyorProfessionalTotal === null || borrowerProfessionalTotal === null) ? '' : surveyorProfessionalTotal - borrowerProfessionalTotal
            },
            {
                section: 'Summary',
                label: 'Other Costs',
                developer_value: borrowerOtherTotal === null ? '' : borrowerOtherTotal,
                surveyor_value: surveyorOtherTotal === null ? '' : surveyorOtherTotal,
                difference: (surveyorOtherTotal === null || borrowerOtherTotal === null) ? '' : surveyorOtherTotal - borrowerOtherTotal
            },
            {
                section: 'Summary',
                label: 'Sub-total Development Cost',
                developer_value: borrowerSubtotal,
                surveyor_value: surveyorSubtotal === null ? '' : surveyorSubtotal,
                difference: surveyorSubtotal === null ? '' : surveyorSubtotal - borrowerSubtotal
            },
            {
                section: 'Funding',
                label: 'Less Development Facility',
                developer_value: facilityAmountDisplay,
                surveyor_value: facilityAmountDisplay,
                difference: 0
            },
            {
                section: 'Funding',
                label: 'Less Borrower input',
                developer_value: 0,
                surveyor_value: borrowerInputSurveyorRaw,
                difference: ''
            },
            {
                section: 'Funding',
                label: 'Headroom / (Shortfall)',
                developer_value: 0,
                surveyor_value: headroomSurveyor === null ? '' : headroomSurveyor,
                difference: ''
            }
        ];
    }

    function buildDetailedCsvRows() {
        const rowsForCsv = [];

        constructionBreakdown.forEach((it, idx) => {
            const itemKey = makeCostItemKey('construction_cost_breakdown', it?.heading, idx);
            const developerAmount = parseNumberLoose(it?.amount);
            const isBespoke = truthyFromString(getSurveyorValue(isBespokeKey(itemKey)));
            const amountPerFt2 = effectiveFt2 && developerAmount !== null ? round2(developerAmount / effectiveFt2) : null;
            rowsForCsv.push({
                section: 'Construction Costs',
                item_key: itemKey,
                label: String(it?.heading || `Item ${idx + 1}`),
                developer_value: developerAmount === null ? '' : developerAmount,
                surveyor_value_raw: '',
                surveyor_numeric: '',
                difference: '',
                meta_1: isBespoke ? 'Bespoke' : 'Standard',
                meta_2: amountPerFt2 === null ? '' : amountPerFt2
            });
        });

        rowsForCsv.push({
            section: 'Contingency',
            item_key: 'contingency',
            label: 'Contingency',
            developer_value: borrowerCont.amount === null ? '' : borrowerCont.amount,
            surveyor_value_raw: getSurveyorValue('contingency'),
            surveyor_numeric: surveyorCont.amount === null ? '' : surveyorCont.amount,
            difference: (surveyorCont.amount === null || borrowerCont.amount === null) ? '' : surveyorCont.amount - borrowerCont.amount,
            meta_1: borrowerCont.percent === null ? '' : borrowerCont.percent,
            meta_2: surveyorCont.percent === null ? '' : surveyorCont.percent
        });

        if (!professionalBreakdown.length) {
            rowsForCsv.push({
                section: 'Professional Fees',
                item_key: 'professional_fees',
                label: 'Professional Fees (base)',
                developer_value: borrowerProfessionalTotal === null ? '' : borrowerProfessionalTotal,
                surveyor_value_raw: getSurveyorValue('professional_fees'),
                surveyor_numeric: parseNumberLoose(getSurveyorValue('professional_fees')) ?? '',
                difference: differenceText(borrowerProfessionalTotal, getSurveyorValue('professional_fees')),
                meta_1: 'Base',
                meta_2: ''
            });
        }

        professionalBreakdown.forEach((it, idx) => {
            const itemKey = makeCostItemKey('professional_fees_breakdown', it?.heading, idx);
            const developerAmount = parseNumberLoose(it?.amount);
            const surveyorRaw = getSurveyorValue(itemKey);
            const surveyorAmount = parseNumberLoose(surveyorRaw);
            rowsForCsv.push({
                section: 'Professional Fees',
                item_key: itemKey,
                label: String(it?.heading || `Item ${idx + 1}`),
                developer_value: developerAmount === null ? '' : developerAmount,
                surveyor_value_raw: surveyorRaw,
                surveyor_numeric: surveyorAmount === null ? '' : surveyorAmount,
                difference: (developerAmount === null || surveyorAmount === null) ? '' : surveyorAmount - developerAmount,
                meta_1: 'Breakdown',
                meta_2: ''
            });
        });

        professionalExtraRowIds.forEach((id) => {
            const labelKey = `professional_fee_extra:${id}:label`;
            const amountKey = `professional_fee_extra:${id}:amount`;
            const surveyorRaw = getSurveyorValue(amountKey);
            const surveyorAmount = parseNumberLoose(surveyorRaw);
            rowsForCsv.push({
                section: 'Professional Fees',
                item_key: amountKey,
                label: getSurveyorValue(labelKey) || 'Extra row',
                developer_value: 0,
                surveyor_value_raw: surveyorRaw,
                surveyor_numeric: surveyorAmount === null ? '' : surveyorAmount,
                difference: surveyorAmount === null ? '' : surveyorAmount,
                meta_1: 'Extra row',
                meta_2: id
            });
        });

        otherLines.forEach((label) => {
            const itemKey = otherLineKey(label);
            const developerAmount = normalizeKeyText(label) === normalizeKeyText('Monitoring Surveyor')
                ? 0
                : parseNumberLoose(findBreakdownAmount(otherBreakdown, label));
            const surveyorRaw = getSurveyorValue(itemKey);
            const surveyorAmount = parseNumberLoose(surveyorRaw);
            rowsForCsv.push({
                section: 'Other Costs',
                item_key: itemKey,
                label,
                developer_value: developerAmount === null ? '' : developerAmount,
                surveyor_value_raw: surveyorRaw,
                surveyor_numeric: surveyorAmount === null ? '' : surveyorAmount,
                difference: (developerAmount === null || surveyorAmount === null) ? '' : surveyorAmount - developerAmount,
                meta_1: 'Base row',
                meta_2: ''
            });
        });

        otherExtraRowIds.forEach((id) => {
            const labelKey = `other_cost_extra:${id}:label`;
            const amountKey = `other_cost_extra:${id}:amount`;
            const surveyorRaw = getSurveyorValue(amountKey);
            const surveyorAmount = parseNumberLoose(surveyorRaw);
            rowsForCsv.push({
                section: 'Other Costs',
                item_key: amountKey,
                label: getSurveyorValue(labelKey) || 'Extra row',
                developer_value: 0,
                surveyor_value_raw: surveyorRaw,
                surveyor_numeric: surveyorAmount === null ? '' : surveyorAmount,
                difference: surveyorAmount === null ? '' : surveyorAmount,
                meta_1: 'Extra row',
                meta_2: id
            });
        });

        return rowsForCsv;
    }

    function exportCostsCsv() {
        const structures = {
            qa_detailed: {
                filename: `${projectReference || 'project'}_costs_detailed.csv`,
                columns: [
                    { key: 'section', label: 'Section' },
                    { key: 'item_key', label: 'Item Key' },
                    { key: 'label', label: 'Label' },
                    { key: 'developer_value', label: 'Developer Value' },
                    { key: 'surveyor_value_raw', label: 'Surveyor Value Raw' },
                    { key: 'surveyor_numeric', label: 'Surveyor Numeric' },
                    { key: 'difference', label: 'Difference' },
                    { key: 'meta_1', label: 'Meta 1' },
                    { key: 'meta_2', label: 'Meta 2' }
                ],
                rows: buildDetailedCsvRows()
            },
            summary_snapshot: {
                filename: `${projectReference || 'project'}_costs_summary.csv`,
                columns: [
                    { key: 'section', label: 'Section' },
                    { key: 'label', label: 'Label' },
                    { key: 'developer_value', label: 'Developer Value' },
                    { key: 'surveyor_value', label: 'Surveyor Value' },
                    { key: 'difference', label: 'Difference' }
                ],
                rows: buildSummaryCsvRows()
            }
        };

        const selected = structures[csvStructure] || structures.qa_detailed;
        downloadCsv(selected.filename, selected.columns, selected.rows);
    }

    function renderCard({ title, children }) {
        return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '10px 12px', background: headerRowBg, fontWeight: 900 }}>{title}</div>
                <div style={{ padding: 12 }}>{children}</div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 10,
                    background: '#fff'
                }}
            >
                <div>
                    <div style={{ fontWeight: 900, fontSize: 14 }}>Costs CSV Export</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>Export the current cost data in a configurable CSV structure for QA and manual adjustment workflows.</div>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 800, color: '#374151' }}>
                        CSV structure
                        <select
                            value={csvStructure}
                            onChange={(e) => setCsvStructure(String(e.target.value || 'qa_detailed'))}
                            style={{ border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 10px', minWidth: 190, background: '#fff' }}
                        >
                            <option value="qa_detailed">QA detailed line items</option>
                            <option value="summary_snapshot">Summary snapshot</option>
                        </select>
                    </label>

                    <button
                        type="button"
                        onClick={exportCostsCsv}
                        style={{
                            border: '1px solid #d1d5db',
                            background: '#111827',
                            color: '#fff',
                            borderRadius: 8,
                            padding: '9px 12px',
                            fontWeight: 900,
                            cursor: 'pointer'
                        }}
                    >
                        Export to CSV
                    </button>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 640px', minWidth: 320, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                        <thead>
                            <tr style={{ background: headerRowBg }}>
                                <th style={{ ...mainCell, textAlign: 'left' }}>Description</th>
                                <th style={mainCell}>Developer Assessment</th>
                                <th style={mainCell}>Surveyor Assessment</th>
                                <th style={mainCell}>Difference</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Construction Costs</td>
                                <td style={mainCellRight}>{formatPounds(borrowerConstructionTotal)}</td>
                                <td style={mainCellRight}>{surveyorConstructionTotal === null ? '—' : formatPounds(surveyorConstructionTotal)}</td>
                                <td style={mainCellRight}>{surveyorConstructionTotal === null ? '—' : formatPounds(surveyorConstructionTotal - borrowerConstructionTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Contingency</td>
                                <td style={mainCellRight}>{borrowerCont.amount === null ? '—' : formatPounds(borrowerCont.amount)}</td>
                                <td style={mainCellRight}>
                                    {surveyorCont.amount === null ? '—' : formatPounds(surveyorCont.amount)}
                                </td>
                                <td style={mainCellRight}>{surveyorCont.amount === null ? '—' : formatPounds(surveyorCont.amount - (borrowerCont.amount || 0))}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Professional Fees</td>
                                <td style={mainCellRight}>{formatPounds(borrowerProfessionalTotal)}</td>
                                <td style={mainCellRight}>{surveyorProfessionalTotal === null ? '—' : formatPounds(surveyorProfessionalTotal)}</td>
                                <td style={mainCellRight}>{surveyorProfessionalTotal === null ? '—' : formatPounds(surveyorProfessionalTotal - borrowerProfessionalTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Other Costs</td>
                                <td style={mainCellRight}>{formatPounds(borrowerOtherTotal)}</td>
                                <td style={mainCellRight}>{surveyorOtherTotal === null ? '—' : formatPounds(surveyorOtherTotal)}</td>
                                <td style={mainCellRight}>{surveyorOtherTotal === null ? '—' : formatPounds(surveyorOtherTotal - borrowerOtherTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={{ ...mainCellLabel, fontWeight: 900 }}>Sub-total Development Cost</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{formatPounds(borrowerSubtotal)}</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{surveyorSubtotal === null ? '—' : formatPounds(surveyorSubtotal)}</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{surveyorSubtotal === null ? '—' : formatPounds(surveyorSubtotal - borrowerSubtotal)}</td>
                            </tr>

                            <tr>
                                <td style={mainCellLabel}>Less Development Facility</td>
                                <td style={mainCellRight}>{formatMainPounds(facilityAmountDisplay)}</td>
                                <td style={mainCellRight}>{formatMainPounds(facilityAmountDisplay)}</td>
                                <td style={mainCellRight}>{formatPounds(0)}</td>
                            </tr>

                            <tr>
                                <td style={mainCellLabel}>Less Borrower input</td>
                                <td style={mainCellRight}>{formatPounds(0)}</td>
                                <td style={mainCell}>
                                    <AssessmentInput
                                        value={borrowerInputSurveyorRaw}
                                        canEdit={canEdit}
                                        onCommit={(next) => onSave?.({ itemKey: 'less_borrower_input', surveyorValue: next })}
                                    />
                                </td>
                                <td style={mainCellRight}>—</td>
                            </tr>

                            <tr style={{ background: headerRowBg }}>
                                <td style={{ ...mainCellLabel, fontWeight: 900 }}>Headroom / (Shortfall)</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{formatPounds(0)}</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{headroomSurveyor === null ? '—' : formatPounds(headroomSurveyor)}</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>—</td>
                            </tr>
                        </tbody>
                    </table>

                    {String(row?.cost_commentary || '').trim() && (
                        <div style={{ marginTop: 10, color: '#374151', fontSize: 13 }}>
                            <strong>Cost Commentary (Developer):</strong> {String(row.cost_commentary)}
                        </div>
                    )}
                </div>

                <div style={{ flex: '0 0 280px', width: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {renderCard({
                        title: 'Proposed Development GIA',
                        children: (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 13, color: '#374151' }}>
                                    <div style={{ fontWeight: 800, marginBottom: 4 }}>Developer GIA (m²)</div>
                                    <div style={{ fontWeight: 900 }}>{giaDevM2 === null ? '—' : giaDevM2.toLocaleString('en-GB')}</div>
                                </div>
                                <div>
                                    <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 13 }}>Surveyor Assessment of GIA (m²)</div>
                                    <AssessmentInput
                                        value={giaSurveyorRaw}
                                        canEdit={canEdit}
                                        onCommit={(next) => onSave?.({ itemKey: 'proposed_gia', surveyorValue: next })}
                                    />
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    Surveyor GIA used throughout: {effectiveGiaM2 === null ? '—' : `${effectiveGiaM2.toLocaleString('en-GB')} m²`} ({effectiveGiaM2 === null ? '—' : `${Math.round(effectiveGiaM2 * 10.7639).toLocaleString('en-GB')} ft²`}).
                                </div>
                            </div>
                        )
                    })}

                    {renderCard({
                        title: 'Facility',
                        children: (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontWeight: 900, fontSize: 13 }}>Is the Facility Confirmed?</div>
                                <div style={{ display: 'flex', gap: 10 }}>
                                    <button
                                        type="button"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            onSave?.({ itemKey: 'facility_confirmed', surveyorValue: 'true' });
                                            if (String(facilityAmountStored || '').trim().toUpperCase() === 'TBC') {
                                                onSave?.({ itemKey: 'facility_amount', surveyorValue: '' });
                                            }
                                        }}
                                        style={{
                                            flex: 1,
                                            border: '1px solid #d1d5db',
                                            background: facilityConfirmed ? '#111827' : '#fff',
                                            color: facilityConfirmed ? '#fff' : '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 900,
                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            onSave?.({ itemKey: 'facility_confirmed', surveyorValue: 'false' });
                                            onSave?.({ itemKey: 'facility_amount', surveyorValue: 'TBC' });
                                        }}
                                        style={{
                                            flex: 1,
                                            border: '1px solid #d1d5db',
                                            background: !facilityConfirmed ? '#111827' : '#fff',
                                            color: !facilityConfirmed ? '#fff' : '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 900,
                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        No
                                    </button>
                                </div>

                                <div>
                                    <div style={{ fontWeight: 800, marginBottom: 6, fontSize: 13 }}>Facility Amount</div>
                                    <CurrencyInput
                                        value={facilityAmountDisplay}
                                        canEdit={canEdit && facilityConfirmed}
                                        placeholder="Enter amount"
                                        onCommit={(next) => onSave?.({ itemKey: 'facility_amount', surveyorValue: next })}
                                    />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 520px', minWidth: 320, display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {renderCard({
                        title: 'Construction Costs',
                        children: (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: headerRowBg }}>
                                            <th style={{ ...cell, textAlign: 'left' }}>Element</th>
                                            <th style={cell}>Developer</th>
                                            <th style={cell}>%</th>
                                            <th style={cell}>Bespoke Items £/ft²</th>
                                            <th style={cell}>Construction Items £/ft²</th>
                                            <th style={cell}>Bespoke?</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {constructionBreakdown.map((it, idx) => {
                                            const key = makeCostItemKey('construction_cost_breakdown', it?.heading, idx);
                                            const bAmt = parseNumberLoose(it?.amount);
                                            const bPct = borrowerConstructionTotal ? (bAmt !== null ? (bAmt / borrowerConstructionTotal) * 100 : null) : null;
                                            const bespoke = truthyFromString(getSurveyorValue(isBespokeKey(key)));
                                            const amtPerFt2 = effectiveFt2 && bAmt !== null ? round2(bAmt / effectiveFt2) : null;
                                            return (
                                                <tr key={`${key}:${idx}`}>
                                                    <td style={cellLabel}>{String(it?.heading || `Item ${idx + 1}`)}</td>
                                                    <td style={cellRight}>{formatPounds(bAmt)}</td>
                                                    <td style={cellRight}>{bPct === null ? '—' : formatPercent(bPct)}</td>
                                                    <td style={cellRight}>{bespoke ? (amtPerFt2 === null ? '—' : `£${amtPerFt2.toFixed(2)}`) : '—'}</td>
                                                    <td style={cellRight}>{!bespoke ? (amtPerFt2 === null ? '—' : `£${amtPerFt2.toFixed(2)}`) : '—'}</td>
                                                    <td style={{ ...cell, textAlign: 'center' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={bespoke}
                                                            disabled={!canEdit}
                                                            onChange={(e) => onSave?.({ itemKey: isBespokeKey(key), surveyorValue: e.target.checked ? 'true' : 'false' })}
                                                        />
                                                    </td>
                                                </tr>
                                            );
                                        })}

                                        <tr style={{ background: subtotalRowBg }}>
                                            <td style={{ ...cellLabel, fontWeight: 900 }}>Total</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerConstructionTotal)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{constructionBreakdown.length ? '100.0%' : '—'}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{effectiveFt2 ? `£${round2(bespokeTotals.bespoke / effectiveFt2).toFixed(2)}` : '—'}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{effectiveFt2 ? `£${round2(bespokeTotals.construction / effectiveFt2).toFixed(2)}` : '—'}</td>
                                            <td style={{ ...cell, textAlign: 'center', fontWeight: 900 }}>—</td>
                                        </tr>
                                    </tbody>
                                </table>

                                <div style={{ height: 12 }} />

                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: headerRowBg }}>
                                            <th style={{ ...cell, textAlign: 'left' }}>Benchmarking</th>
                                            <th style={cell}>Developer £/ft²</th>
                                            <th style={cell}>Surveyor £/ft²</th>
                                            <th style={cell}>Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={cellLabel}>Construction Items (excluding bespoke)</td>
                                            <td style={cellRight}>{developerConstructionRateFt2 === null ? '—' : `£${developerConstructionRateFt2.toFixed(2)}`}</td>
                                            <td style={cell}>
                                                <AssessmentInput
                                                    value={surveyorConstructionRateRaw}
                                                    canEdit={canEdit}
                                                    onCommit={(next) => onSave?.({ itemKey: 'construction_items_rate_ft2', surveyorValue: next })}
                                                />
                                            </td>
                                            <td style={cellRight}>
                                                {(developerConstructionRateFt2 === null || surveyorConstructionRateFt2 === null)
                                                    ? '—'
                                                    : `£${(surveyorConstructionRateFt2 - developerConstructionRateFt2).toFixed(2)}`}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )
                    })}

                    {renderCard({
                        title: 'Contingency',
                        children: (
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: headerRowBg }}>
                                            <th style={{ ...cell, textAlign: 'left' }}>Element</th>
                                            <th style={cell}>Developer</th>
                                            <th style={cell}>% of Contract Sum</th>
                                            <th style={cell}>Surveyor Assessment</th>
                                            <th style={cell}>% of Contract Sum</th>
                                            <th style={cell}>Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td style={cellLabel}>Contingency</td>
                                            <td style={cellRight}>{borrowerCont.amount === null ? '—' : formatPounds(borrowerCont.amount)}</td>
                                            <td style={cellRight}>{borrowerCont.percent === null ? '—' : formatPercent(borrowerCont.percent)}</td>
                                            <td style={cell}>
                                                <AssessmentInput
                                                    value={getSurveyorValue('contingency')}
                                                    canEdit={canEdit}
                                                    onCommit={(next) => onSave?.({ itemKey: 'contingency', surveyorValue: next })}
                                                />
                                            </td>
                                            <td style={cellRight}>{surveyorCont.percent === null ? '—' : formatPercent(surveyorCont.percent)}</td>
                                            <td style={cellRight}>{(borrowerCont.amount === null || surveyorCont.amount === null) ? '—' : formatPounds(surveyorCont.amount - borrowerCont.amount)}</td>
                                        </tr>
                                        <tr style={{ background: subtotalRowBg }}>
                                            <td style={{ ...cellLabel, fontWeight: 900 }}>Total</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{borrowerCont.amount === null ? '—' : formatPounds(borrowerCont.amount)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{borrowerCont.percent === null ? '—' : formatPercent(borrowerCont.percent)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorCont.amount === null ? '—' : formatPounds(surveyorCont.amount)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorCont.percent === null ? '—' : formatPercent(surveyorCont.percent)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{(borrowerCont.amount === null || surveyorCont.amount === null) ? '—' : formatPounds(surveyorCont.amount - borrowerCont.amount)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                                <div style={{ marginTop: 8, fontSize: 12, color: '#a16207', fontStyle: 'italic', fontWeight: 700 }}>
                                    Industry standard contingency: 10%
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {renderCard({
                        title: 'Professional Fees',
                        children: (
                            <div style={{ overflowX: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                    <button
                                        type="button"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            const id = `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
                                            const next = [...professionalExtraRowIds, id];
                                            onSave?.({ itemKey: 'professional_fees_extra_rows', surveyorValue: JSON.stringify(next) });
                                        }}
                                        style={{
                                            border: '1px solid #d1d5db',
                                            background: canEdit ? '#fff' : '#f3f4f6',
                                            color: '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 800,
                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        Add row
                                    </button>
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: headerRowBg }}>
                                            <th style={{ ...cell, textAlign: 'left' }}>Role</th>
                                            <th style={cell}>Developer</th>
                                            <th style={cell}>Surveyor Assessment</th>
                                            <th style={cell}>Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!professionalBreakdown.length && (
                                            <tr>
                                                <td style={cellLabel}>Professional Fees (base)</td>
                                                <td style={cellRight}>{formatPounds(borrowerProfessionalTotal)}</td>
                                                <td style={cell}>
                                                    <AssessmentInput
                                                        value={getSurveyorValue('professional_fees')}
                                                        canEdit={canEdit}
                                                        onCommit={(next) => onSave?.({ itemKey: 'professional_fees', surveyorValue: next })}
                                                    />
                                                </td>
                                                <td style={cellRight}>{differenceText(borrowerProfessionalTotal, getSurveyorValue('professional_fees'))}</td>
                                            </tr>
                                        )}
                                        {professionalBreakdown.map((it, idx) => {
                                            const key = makeCostItemKey('professional_fees_breakdown', it?.heading, idx);
                                            const bAmt = parseNumberLoose(it?.amount);
                                            const sRaw = getSurveyorValue(key);
                                            const sAmt = parseNumberLoose(sRaw);
                                            return (
                                                <tr key={`${key}:${idx}`}>
                                                    <td style={cellLabel}>{String(it?.heading || `Item ${idx + 1}`)}</td>
                                                    <td style={cellRight}>{formatPounds(bAmt)}</td>
                                                    <td style={cell}>
                                                        <AssessmentInput
                                                            value={sRaw}
                                                            canEdit={canEdit}
                                                            onCommit={(next) => onSave?.({ itemKey: key, surveyorValue: next })}
                                                        />
                                                    </td>
                                                    <td style={cellRight}>{(bAmt === null || sAmt === null) ? '—' : formatPounds(sAmt - bAmt)}</td>
                                                </tr>
                                            );
                                        })}

                                        {professionalExtraRowIds.map((id) => {
                                            const labelKey = `professional_fee_extra:${id}:label`;
                                            const amountKey = `professional_fee_extra:${id}:amount`;
                                            const labelRaw = getSurveyorValue(labelKey);
                                            const amountRaw = getSurveyorValue(amountKey);
                                            const amt = parseNumberLoose(amountRaw);
                                            return (
                                                <tr key={`pf_extra_${id}`}>
                                                    <td style={cell}>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            <div style={{ flex: 1, minWidth: 120 }}>
                                                                <TextInput
                                                                    value={labelRaw}
                                                                    canEdit={canEdit}
                                                                    placeholder="Role"
                                                                    onCommit={(next) => onSave?.({ itemKey: labelKey, surveyorValue: next })}
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                disabled={!canEdit}
                                                                onClick={() => {
                                                                    if (!window.confirm('Delete this row?')) return;
                                                                    const next = professionalExtraRowIds.filter((x) => x !== id);
                                                                    onSave?.({ itemKey: 'professional_fees_extra_rows', surveyorValue: JSON.stringify(next) });
                                                                    onSave?.({ itemKey: labelKey, surveyorValue: '' });
                                                                    onSave?.({ itemKey: amountKey, surveyorValue: '' });
                                                                }}
                                                                title="Delete row"
                                                                style={{
                                                                    border: '1px solid #d1d5db',
                                                                    background: canEdit ? '#fff' : '#f3f4f6',
                                                                    color: '#991b1b',
                                                                    borderRadius: 8,
                                                                    padding: '6px 10px',
                                                                    fontWeight: 900,
                                                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td style={cellRight}>{formatPounds(0)}</td>
                                                    <td style={cell}>
                                                        <AssessmentInput
                                                            value={amountRaw}
                                                            canEdit={canEdit}
                                                            onCommit={(next) => onSave?.({ itemKey: amountKey, surveyorValue: next })}
                                                        />
                                                    </td>
                                                    <td style={cellRight}>{amt === null ? '—' : formatPounds(amt)}</td>
                                                </tr>
                                            );
                                        })}
                                        <tr style={{ background: subtotalRowBg }}>
                                            <td style={{ ...cellLabel, fontWeight: 900 }}>Total Fees</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerProfessionalTotal)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorProfessionalTotal === null ? '—' : formatPounds(surveyorProfessionalTotal)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorProfessionalTotal === null ? '—' : formatPounds(surveyorProfessionalTotal - borrowerProfessionalTotal)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )
                    })}

                    {renderCard({
                        title: 'Additional Costs (Other Costs)',
                        children: (
                            <div style={{ overflowX: 'auto' }}>
                                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                                    <button
                                        type="button"
                                        disabled={!canEdit}
                                        onClick={() => {
                                            const id = `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
                                            const next = [...otherExtraRowIds, id];
                                            onSave?.({ itemKey: 'other_costs_extra_rows', surveyorValue: JSON.stringify(next) });
                                        }}
                                        style={{
                                            border: '1px solid #d1d5db',
                                            background: canEdit ? '#fff' : '#f3f4f6',
                                            color: '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 800,
                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        Add row
                                    </button>
                                </div>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: headerRowBg }}>
                                            <th style={{ ...cell, textAlign: 'left' }}>Other Costs</th>
                                            <th style={cell}>Developer</th>
                                            <th style={cell}>Surveyor Assessment</th>
                                            <th style={cell}>Difference</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {otherLines.map((label, idx) => {
                                            const borrowerAmt = normalizeKeyText(label) === normalizeKeyText('Monitoring Surveyor')
                                                ? 0
                                                : parseNumberLoose(findBreakdownAmount(otherBreakdown, label));
                                            const key = otherLineKey(label);
                                            const sRaw = getSurveyorValue(key);
                                            const sAmt = parseNumberLoose(sRaw);
                                            return (
                                                <tr key={`${key}:${idx}`}>
                                                    <td style={cellLabel}>{label}</td>
                                                    <td style={cellRight}>{formatPounds(borrowerAmt)}</td>
                                                    <td style={cell}>
                                                        <AssessmentInput
                                                            value={sRaw}
                                                            canEdit={canEdit}
                                                            onCommit={(next) => onSave?.({ itemKey: key, surveyorValue: next })}
                                                        />
                                                    </td>
                                                    <td style={cellRight}>{(borrowerAmt === null || sAmt === null) ? '—' : formatPounds(sAmt - borrowerAmt)}</td>
                                                </tr>
                                            );
                                        })}

                                        {otherExtraRowIds.map((id) => {
                                            const labelKey = `other_cost_extra:${id}:label`;
                                            const amountKey = `other_cost_extra:${id}:amount`;
                                            const labelRaw = getSurveyorValue(labelKey);
                                            const amountRaw = getSurveyorValue(amountKey);
                                            const amt = parseNumberLoose(amountRaw);
                                            return (
                                                <tr key={`oc_extra_${id}`}>
                                                    <td style={cell}>
                                                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                            <div style={{ flex: 1, minWidth: 120 }}>
                                                                <TextInput
                                                                    value={labelRaw}
                                                                    canEdit={canEdit}
                                                                    placeholder="Other cost"
                                                                    onCommit={(next) => onSave?.({ itemKey: labelKey, surveyorValue: next })}
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                disabled={!canEdit}
                                                                onClick={() => {
                                                                    if (!window.confirm('Delete this row?')) return;
                                                                    const next = otherExtraRowIds.filter((x) => x !== id);
                                                                    onSave?.({ itemKey: 'other_costs_extra_rows', surveyorValue: JSON.stringify(next) });
                                                                    onSave?.({ itemKey: labelKey, surveyorValue: '' });
                                                                    onSave?.({ itemKey: amountKey, surveyorValue: '' });
                                                                }}
                                                                title="Delete row"
                                                                style={{
                                                                    border: '1px solid #d1d5db',
                                                                    background: canEdit ? '#fff' : '#f3f4f6',
                                                                    color: '#991b1b',
                                                                    borderRadius: 8,
                                                                    padding: '6px 10px',
                                                                    fontWeight: 900,
                                                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        </div>
                                                    </td>
                                                    <td style={cellRight}>{formatPounds(0)}</td>
                                                    <td style={cell}>
                                                        <AssessmentInput
                                                            value={amountRaw}
                                                            canEdit={canEdit}
                                                            onCommit={(next) => onSave?.({ itemKey: amountKey, surveyorValue: next })}
                                                        />
                                                    </td>
                                                    <td style={cellRight}>{amt === null ? '—' : formatPounds(amt)}</td>
                                                </tr>
                                            );
                                        })}

                                        <tr style={{ background: subtotalRowBg }}>
                                            <td style={{ ...cellLabel, fontWeight: 900 }}>Other Costs Total</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerOtherTotal)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorOtherTotal === null ? '—' : formatPounds(surveyorOtherTotal)}</td>
                                            <td style={{ ...cellRight, fontWeight: 900 }}>{surveyorOtherTotal === null ? '—' : formatPounds(surveyorOtherTotal - borrowerOtherTotal)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        )
                    })}
                </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {renderCard({
                    title: 'Construction Costs Commentary',
                    children: (
                        <TextAreaInput
                            value={getSurveyorValue('commentary_construction_costs')}
                            canEdit={canEdit}
                            placeholder="Surveyor commentary on Construction Costs"
                            onCommit={(next) => onSave?.({ itemKey: 'commentary_construction_costs', surveyorValue: next })}
                        />
                    )
                })}

                {renderCard({
                    title: 'Contingency Commentary',
                    children: (
                        <TextAreaInput
                            value={getSurveyorValue('commentary_contingency')}
                            canEdit={canEdit}
                            placeholder="Surveyor commentary on Contingency"
                            onCommit={(next) => onSave?.({ itemKey: 'commentary_contingency', surveyorValue: next })}
                        />
                    )
                })}

                {renderCard({
                    title: 'Professional Fees Commentary',
                    children: (
                        <TextAreaInput
                            value={getSurveyorValue('commentary_professional_fees')}
                            canEdit={canEdit}
                            placeholder="Surveyor commentary on Professional Fees"
                            onCommit={(next) => onSave?.({ itemKey: 'commentary_professional_fees', surveyorValue: next })}
                        />
                    )
                })}

                {renderCard({
                    title: 'Additional (Other) Costs Commentary',
                    children: (
                        <TextAreaInput
                            value={getSurveyorValue('commentary_additional_other_costs')}
                            canEdit={canEdit}
                            placeholder="Surveyor commentary on Additional (Other) Costs"
                            onCommit={(next) => onSave?.({ itemKey: 'commentary_additional_other_costs', surveyorValue: next })}
                        />
                    )
                })}

                {renderCard({
                    title: 'BCIS Benchmarking',
                    children: (
                        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                            <div style={{ flex: '1 1 360px', minWidth: 260 }}>
                                <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>Commentary</div>
                                <TextAreaInput
                                    value={getSurveyorValue('commentary_bcis_benchmarking')}
                                    canEdit={canEdit}
                                    placeholder="Surveyor BCIS benchmarking commentary"
                                    minHeight={140}
                                    onCommit={(next) => onSave?.({ itemKey: 'commentary_bcis_benchmarking', surveyorValue: next })}
                                />
                            </div>

                            <div style={{ flex: '1 1 360px', minWidth: 260 }}>
                                <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 8 }}>Upload</div>
                                <input
                                    ref={bcisInputRef}
                                    type="file"
                                    style={{ display: 'none' }}
                                    disabled={!canEdit || bcisLoading}
                                    onChange={(e) => setBcisFile(e.target.files && e.target.files[0] ? e.target.files[0] : null)}
                                />

                                <div
                                    role="button"
                                    tabIndex={0}
                                    onClick={onBcisPickClick}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') onBcisPickClick();
                                    }}
                                    onDragOver={onBcisDragOver}
                                    onDragLeave={onBcisDragLeave}
                                    onDrop={onBcisDrop}
                                    style={{
                                        border: '1px dashed #d1d5db',
                                        background: bcisDragOver ? '#f3f4f6' : '#fff',
                                        borderRadius: 10,
                                        padding: '10px 12px',
                                        cursor: canEdit && !bcisLoading ? 'pointer' : 'not-allowed',
                                        userSelect: 'none'
                                    }}
                                >
                                    <div style={{ fontWeight: 900, fontSize: 13 }}>Drag & drop a BCIS file here</div>
                                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                                        or click to choose a file
                                    </div>
                                    {bcisFile ? (
                                        <div style={{ marginTop: 8, fontSize: 12, fontWeight: 800 }}>
                                            Selected: {bcisFile.name}
                                        </div>
                                    ) : null}
                                </div>

                                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
                                    <button
                                        type="button"
                                        disabled={!canEdit || bcisLoading || !bcisFile}
                                        onClick={uploadBcis}
                                        style={{
                                            border: '1px solid #d1d5db',
                                            background: canEdit ? '#fff' : '#f3f4f6',
                                            color: '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 800,
                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        {bcisLoading ? 'Uploading…' : 'Upload'}
                                    </button>
                                    <button
                                        type="button"
                                        disabled={bcisLoading || !projectReference}
                                        onClick={refreshBcisDocs}
                                        style={{
                                            border: '1px solid #d1d5db',
                                            background: '#fff',
                                            color: '#111827',
                                            borderRadius: 8,
                                            padding: '6px 10px',
                                            fontWeight: 800,
                                            cursor: projectReference ? 'pointer' : 'not-allowed'
                                        }}
                                    >
                                        Refresh
                                    </button>
                                </div>
                                <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                                    Uploads are stored against the INI Questionnaire project (max 25MB).
                                </div>
                                {bcisError ? (
                                    <div style={{ marginTop: 8, color: '#b91c1c', fontSize: 12, fontWeight: 800 }}>{bcisError}</div>
                                ) : null}

                                <div style={{ marginTop: 10 }}>
                                    {bcisLoading && !bcisDocs.length ? (
                                        <div style={{ color: '#6b7280' }}>Loading…</div>
                                    ) : null}
                                    {!bcisLoading && !bcisDocs.length ? (
                                        <div style={{ color: '#6b7280' }}>No BCIS uploads yet.</div>
                                    ) : null}
                                    {!!bcisDocs.length && (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            {bcisDocs.map((d) => {
                                                const key = d?.id ?? `${d?.file_path || ''}_${d?.upload_date || ''}`;
                                                const name = d?.document_name || d?.file_name || d?.name || 'Document';
                                                const when = d?.upload_date ? new Date(d.upload_date).toLocaleString() : '';
                                                const href = toIniUploadsUrl(d?.file_path);
                                                return (
                                                    <div key={key} style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                                                        <div style={{ fontWeight: 800, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}{when ? <span style={{ color: '#6b7280', fontWeight: 700 }}> - {when}</span> : null}</div>
                                                        {href ? (
                                                            <a href={href} target="_blank" rel="noreferrer" style={{ fontWeight: 900, fontSize: 13 }}>
                                                                Open
                                                            </a>
                                                        ) : (
                                                            <span style={{ color: '#9ca3af' }}>No file</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    );
}

function Section7CostsDeveloperTable({ row, devGrossInternalArea, borrowerGiaM2, canEdit, onSaveIniField }) {
    const constructionBreakdown = parseBreakdownValue(row?.construction_cost_breakdown);
    const professionalBreakdown = parseBreakdownValue(row?.professional_fees_breakdown);
    const otherBreakdown = parseBreakdownValue(row?.other_fees_breakdown);

    const headerRowBg = '#f3f4f6';
    const subtotalRowBg = '#fafafa';

    const cell = { padding: '8px 10px', border: '1px solid #e5e7eb' };
    const cellRight = { ...cell, textAlign: 'right', fontWeight: 700 };
    const cellLabel = { ...cell, fontWeight: 800 };

    const mainCell = { ...cell, padding: '12px 10px' };
    const mainCellRight = { ...mainCell, textAlign: 'right', fontWeight: 700 };
    const mainCellLabel = { ...mainCell, fontWeight: 800 };

    const giaDevM2 = parseNumberLoose(borrowerGiaM2) ?? parseNumberLoose(devGrossInternalArea);
    const effectiveFt2 = giaDevM2 === null ? null : giaDevM2 * 10.7639;

    const constructionBreakdownNums = constructionBreakdown
        .map((it) => parseNumberLoose(it?.amount))
        .filter((n) => n !== null);
    const constructionBreakdownSum = constructionBreakdownNums.length
        ? constructionBreakdownNums.reduce((a, b) => a + b, 0)
        : null;

    const borrowerConstructionTotal = constructionBreakdownSum ?? parseNumberLoose(row?.build_cost);
    const borrowerProfessionalTotal = parseNumberLoose(row?.professional_fees) ?? (
        professionalBreakdown
            .map((it) => parseNumberLoose(it?.amount))
            .filter((n) => n !== null)
            .reduce((a, b) => a + b, 0)
    );
    const borrowerOtherTotal = parseNumberLoose(row?.other_development_costs) ?? (
        otherBreakdown
            .map((it) => parseNumberLoose(it?.amount))
            .filter((n) => n !== null)
            .reduce((a, b) => a + b, 0)
    );

    const borrowerCont = parsePercentOrAmount(row?.contingency, borrowerConstructionTotal);

    const borrowerSubtotal = [borrowerConstructionTotal, borrowerCont.amount, borrowerProfessionalTotal, borrowerOtherTotal]
        .map((n) => (typeof n === 'number' ? n : parseNumberLoose(n)))
        .filter((n) => n !== null)
        .reduce((a, b) => a + b, 0);

    function patchField(fieldName, nextValue) {
        if (!canEdit || !onSaveIniField) return;
        return onSaveIniField({
            tableName: 'ini_section_7_costs',
            rowId: null,
            patch: { [fieldName]: nextValue }
        });
    }

    function updateBreakdown(fieldName, updater) {
        if (!canEdit || !onSaveIniField) return;
        const current = parseBreakdownValue(row?.[fieldName]);
        const next = updater(Array.isArray(current) ? current : []);
        return patchField(fieldName, JSON.stringify(next));
    }

    function renderCard({ title, children }) {
        return (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
                <div style={{ padding: '10px 12px', background: headerRowBg, fontWeight: 900 }}>{title}</div>
                <div style={{ padding: 12 }}>{children}</div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 640px', minWidth: 320, overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
                        <thead>
                            <tr style={{ background: headerRowBg }}>
                                <th style={{ ...mainCell, textAlign: 'left' }}>Description</th>
                                <th style={mainCell}>Developer Assessment</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Construction Costs</td>
                                <td style={mainCellRight}>{formatPounds(borrowerConstructionTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Contingency</td>
                                <td style={mainCellRight}>{borrowerCont.amount === null ? '—' : formatPounds(borrowerCont.amount)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Professional Fees</td>
                                <td style={mainCellRight}>{formatPounds(borrowerProfessionalTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={mainCellLabel}>Other Costs</td>
                                <td style={mainCellRight}>{formatPounds(borrowerOtherTotal)}</td>
                            </tr>

                            <tr style={{ background: subtotalRowBg }}>
                                <td style={{ ...mainCellLabel, fontWeight: 900 }}>Sub-total Development Cost</td>
                                <td style={{ ...mainCellRight, fontWeight: 900 }}>{formatPounds(borrowerSubtotal)}</td>
                            </tr>

                            <tr>
                                <td style={mainCellLabel}>Total Development Cost</td>
                                <td style={mainCell}>
                                    <EditableValue
                                        value={row?.total_development_cost}
                                        canEdit={canEdit}
                                        onSave={(next) => patchField('total_development_cost', next)}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 360px', minWidth: 260 }}>
                            <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Contingency (input)</div>
                            <EditableValue
                                value={row?.contingency}
                                canEdit={canEdit}
                                onSave={(next) => patchField('contingency', next)}
                            />
                            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                                {borrowerCont.percent === null ? '—' : `${formatPercent(borrowerCont.percent)} of contract sum`}
                            </div>
                        </div>

                        <div style={{ flex: '1 1 360px', minWidth: 260 }}>
                            <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Cost Commentary</div>
                            <EditableValue
                                value={row?.cost_commentary}
                                canEdit={canEdit}
                                onSave={(next) => patchField('cost_commentary', next)}
                            />
                        </div>
                    </div>

                    <div style={{ marginTop: 12 }}>
                        <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Costs Provided Explanation</div>
                        <EditableValue
                            value={row?.costs_provided_explanation}
                            canEdit={canEdit}
                            onSave={(next) => patchField('costs_provided_explanation', next)}
                        />
                    </div>
                </div>

                <div style={{ flex: '0 0 280px', width: 280, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {renderCard({
                        title: 'Proposed Development GIA',
                        children: (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                <div style={{ fontSize: 13, color: '#374151' }}>
                                    <div style={{ fontWeight: 800, marginBottom: 4 }}>Developer GIA (m²)</div>
                                    <div style={{ fontWeight: 900 }}>{giaDevM2 === null ? '—' : giaDevM2.toLocaleString('en-GB')}</div>
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280' }}>
                                    {effectiveFt2 === null ? '—' : `${Math.round(effectiveFt2).toLocaleString('en-GB')} ft²`}
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            {renderCard({
                title: 'Construction Costs',
                children: (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateBreakdown('construction_cost_breakdown', (arr) => [...arr, { heading: '', amount: '' }])}
                                style={{
                                    border: '1px solid #d1d5db',
                                    background: canEdit ? '#fff' : '#f3f4f6',
                                    color: '#111827',
                                    borderRadius: 8,
                                    padding: '6px 10px',
                                    fontWeight: 800,
                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Add row
                            </button>
                        </div>

                        {!constructionBreakdown.length ? (
                            <div style={{ color: '#6b7280' }}>
                                No breakdown provided. You can add rows above, or set a total build cost below.
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Build Cost (total)</div>
                                    <EditableValue
                                        value={row?.build_cost}
                                        canEdit={canEdit}
                                        onSave={(next) => patchField('build_cost', next)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: headerRowBg }}>
                                        <th style={{ ...cell, textAlign: 'left' }}>Element</th>
                                        <th style={cell}>Developer</th>
                                        <th style={cell}>%</th>
                                        <th style={cell}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {constructionBreakdown.map((it, idx) => {
                                        const bAmt = parseNumberLoose(it?.amount);
                                        const bPct = borrowerConstructionTotal ? (bAmt !== null ? (bAmt / borrowerConstructionTotal) * 100 : null) : null;
                                        return (
                                            <tr key={`c_${idx}`}>
                                                <td style={cell}>
                                                    <EditableValue
                                                        value={it?.heading}
                                                        canEdit={canEdit}
                                                        onSave={(next) => updateBreakdown('construction_cost_breakdown', (arr) =>
                                                            arr.map((x, i) => (i === idx ? { ...x, heading: String(next ?? '') } : x))
                                                        )}
                                                    />
                                                </td>
                                                <td style={cell}>
                                                    <EditableValue
                                                        value={it?.amount}
                                                        canEdit={canEdit}
                                                        onSave={(next) => updateBreakdown('construction_cost_breakdown', (arr) =>
                                                            arr.map((x, i) => (i === idx ? { ...x, amount: String(next ?? '') } : x))
                                                        )}
                                                    />
                                                </td>
                                                <td style={cellRight}>{bPct === null ? '—' : formatPercent(bPct)}</td>
                                                <td style={{ ...cell, textAlign: 'center' }}>
                                                    <button
                                                        type="button"
                                                        disabled={!canEdit}
                                                        onClick={() => {
                                                            if (!window.confirm('Delete this row?')) return;
                                                            updateBreakdown('construction_cost_breakdown', (arr) => arr.filter((_, i) => i !== idx));
                                                        }}
                                                        style={{
                                                            border: '1px solid #d1d5db',
                                                            background: canEdit ? '#fff' : '#f3f4f6',
                                                            color: '#991b1b',
                                                            borderRadius: 8,
                                                            padding: '6px 10px',
                                                            fontWeight: 900,
                                                            cursor: canEdit ? 'pointer' : 'not-allowed'
                                                        }}
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}

                                    <tr style={{ background: subtotalRowBg }}>
                                        <td style={{ ...cellLabel, fontWeight: 900 }}>Total</td>
                                        <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerConstructionTotal)}</td>
                                        <td style={{ ...cellRight, fontWeight: 900 }}>{constructionBreakdown.length ? '100.0%' : '—'}</td>
                                        <td style={{ ...cell, textAlign: 'center', fontWeight: 900 }}>—</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                )
            })}

            {renderCard({
                title: 'Professional Fees',
                children: (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateBreakdown('professional_fees_breakdown', (arr) => [...arr, { heading: '', amount: '' }])}
                                style={{
                                    border: '1px solid #d1d5db',
                                    background: canEdit ? '#fff' : '#f3f4f6',
                                    color: '#111827',
                                    borderRadius: 8,
                                    padding: '6px 10px',
                                    fontWeight: 800,
                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Add row
                            </button>
                        </div>

                        {!professionalBreakdown.length ? (
                            <div style={{ color: '#6b7280' }}>
                                No breakdown provided.
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Professional Fees (total)</div>
                                    <EditableValue
                                        value={row?.professional_fees}
                                        canEdit={canEdit}
                                        onSave={(next) => patchField('professional_fees', next)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: headerRowBg }}>
                                        <th style={{ ...cell, textAlign: 'left' }}>Role</th>
                                        <th style={cell}>Developer</th>
                                        <th style={cell}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {professionalBreakdown.map((it, idx) => (
                                        <tr key={`p_${idx}`}>
                                            <td style={cell}>
                                                <EditableValue
                                                    value={it?.heading}
                                                    canEdit={canEdit}
                                                    onSave={(next) => updateBreakdown('professional_fees_breakdown', (arr) =>
                                                        arr.map((x, i) => (i === idx ? { ...x, heading: String(next ?? '') } : x))
                                                    )}
                                                />
                                            </td>
                                            <td style={cell}>
                                                <EditableValue
                                                    value={it?.amount}
                                                    canEdit={canEdit}
                                                    onSave={(next) => updateBreakdown('professional_fees_breakdown', (arr) =>
                                                        arr.map((x, i) => (i === idx ? { ...x, amount: String(next ?? '') } : x))
                                                    )}
                                                />
                                            </td>
                                            <td style={{ ...cell, textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    disabled={!canEdit}
                                                    onClick={() => {
                                                        if (!window.confirm('Delete this row?')) return;
                                                        updateBreakdown('professional_fees_breakdown', (arr) => arr.filter((_, i) => i !== idx));
                                                    }}
                                                    style={{
                                                        border: '1px solid #d1d5db',
                                                        background: canEdit ? '#fff' : '#f3f4f6',
                                                        color: '#991b1b',
                                                        borderRadius: 8,
                                                        padding: '6px 10px',
                                                        fontWeight: 900,
                                                        cursor: canEdit ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: subtotalRowBg }}>
                                        <td style={{ ...cellLabel, fontWeight: 900 }}>Total Fees</td>
                                        <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerProfessionalTotal)}</td>
                                        <td style={{ ...cell, textAlign: 'center', fontWeight: 900 }}>—</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                )
            })}

            {renderCard({
                title: 'Additional Costs (Other Costs)',
                children: (
                    <div style={{ overflowX: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                            <button
                                type="button"
                                disabled={!canEdit}
                                onClick={() => updateBreakdown('other_fees_breakdown', (arr) => [...arr, { heading: '', amount: '' }])}
                                style={{
                                    border: '1px solid #d1d5db',
                                    background: canEdit ? '#fff' : '#f3f4f6',
                                    color: '#111827',
                                    borderRadius: 8,
                                    padding: '6px 10px',
                                    fontWeight: 800,
                                    cursor: canEdit ? 'pointer' : 'not-allowed'
                                }}
                            >
                                Add row
                            </button>
                        </div>

                        {!otherBreakdown.length ? (
                            <div style={{ color: '#6b7280' }}>
                                No breakdown provided.
                                <div style={{ marginTop: 10 }}>
                                    <div style={{ fontWeight: 900, fontSize: 13, marginBottom: 6 }}>Other Development Costs (total)</div>
                                    <EditableValue
                                        value={row?.other_development_costs}
                                        canEdit={canEdit}
                                        onSave={(next) => patchField('other_development_costs', next)}
                                    />
                                </div>
                            </div>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ background: headerRowBg }}>
                                        <th style={{ ...cell, textAlign: 'left' }}>Other Costs</th>
                                        <th style={cell}>Developer</th>
                                        <th style={cell}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {otherBreakdown.map((it, idx) => (
                                        <tr key={`o_${idx}`}>
                                            <td style={cell}>
                                                <EditableValue
                                                    value={it?.heading}
                                                    canEdit={canEdit}
                                                    onSave={(next) => updateBreakdown('other_fees_breakdown', (arr) =>
                                                        arr.map((x, i) => (i === idx ? { ...x, heading: String(next ?? '') } : x))
                                                    )}
                                                />
                                            </td>
                                            <td style={cell}>
                                                <EditableValue
                                                    value={it?.amount}
                                                    canEdit={canEdit}
                                                    onSave={(next) => updateBreakdown('other_fees_breakdown', (arr) =>
                                                        arr.map((x, i) => (i === idx ? { ...x, amount: String(next ?? '') } : x))
                                                    )}
                                                />
                                            </td>
                                            <td style={{ ...cell, textAlign: 'center' }}>
                                                <button
                                                    type="button"
                                                    disabled={!canEdit}
                                                    onClick={() => {
                                                        if (!window.confirm('Delete this row?')) return;
                                                        updateBreakdown('other_fees_breakdown', (arr) => arr.filter((_, i) => i !== idx));
                                                    }}
                                                    style={{
                                                        border: '1px solid #d1d5db',
                                                        background: canEdit ? '#fff' : '#f3f4f6',
                                                        color: '#991b1b',
                                                        borderRadius: 8,
                                                        padding: '6px 10px',
                                                        fontWeight: 900,
                                                        cursor: canEdit ? 'pointer' : 'not-allowed'
                                                    }}
                                                >
                                                    Delete
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr style={{ background: subtotalRowBg }}>
                                        <td style={{ ...cellLabel, fontWeight: 900 }}>Total Other Costs</td>
                                        <td style={{ ...cellRight, fontWeight: 900 }}>{formatPounds(borrowerOtherTotal)}</td>
                                        <td style={{ ...cell, textAlign: 'center', fontWeight: 900 }}>—</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
                    </div>
                )
            })}
        </div>
    );
}

function IniTableBody({ tableName, rows, reference, mode, code, notesByKey, onSaveRowNote, costAssessmentsByKey, onSaveCostAssessment, onSaveIniField, editsByKey, projectFacility, devGrossInternalArea, borrowerGiaM2, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, onSaveIniDocMetadata }) {
    // Section 7 Costs: surveyor view shows surveyor-only cost assessments; developer view shows developer-submitted costs.
    if (tableName === 'ini_section_7_costs' && mode === 'surveyor') {
        const row = Array.isArray(rows) && rows.length ? rows[0] : {};
        return (
            <Section7CostsAssessmentTable
                row={row}
                projectFacility={projectFacility}
                devGrossInternalArea={devGrossInternalArea}
                borrowerGiaM2={borrowerGiaM2}
                canEdit={mode === 'surveyor'}
                valuesByKey={costAssessmentsByKey}
                onSave={onSaveCostAssessment}
            />
        );
    }

    if (tableName === 'ini_section_7_costs' && mode === 'developer') {
        const row = Array.isArray(rows) && rows.length ? rows[0] : {};
        return (
            <Section7CostsDeveloperTable
                row={row}
                devGrossInternalArea={devGrossInternalArea}
                borrowerGiaM2={borrowerGiaM2}
                canEdit={mode === 'developer'}
                onSaveIniField={onSaveIniField}
            />
        );
    }

    if (tableName === 'ini_section_3_uploads') {
        return (
            <Section3UploadsList
                uploads={rows}
                reference={reference}
                mode={mode}
                code={code}
                tableName={tableName}
                notesByKey={notesByKey}
                canEditNotes={mode === 'surveyor'}
                onSaveRowNote={onSaveRowNote}
            />
        );
    }

    if (tableName === 'ini_documents' || String(tableName || '').startsWith('ini_documents__')) {
        const noteTableName = 'ini_documents';
        const showNotes = tableName === 'ini_documents__experience' || tableName === 'ini_documents__professional_team';
        return (
            <DocumentsList
                documents={rows}
                tableName={tableName}
                noteTableName={noteTableName}
                showNotes={showNotes}
                notesByKey={notesByKey}
                canEditNotes={mode === 'surveyor'}
                onSaveRowNote={onSaveRowNote}
                iniDocMetadataById={iniDocMetadataById}
                iniDocMetadataLoading={iniDocMetadataLoading}
                iniDocMetadataError={iniDocMetadataError}
                canEditMetadata={mode === 'surveyor'}
                onSaveMetadata={onSaveIniDocMetadata}
            />
        );
    }

    if (prettyTableName(tableName) === 'Uploaded Documents') {
        return (
            <GenericUploadsList
                uploads={rows}
                tableName={tableName}
                notesByKey={notesByKey}
                canEditNotes={mode === 'surveyor'}
                onSaveRowNote={onSaveRowNote}
            />
        );
    }

    if (mode === 'surveyor') return <InitialReportFactContainers tableName={tableName} rows={rows} />;

    return (
        <>
            {rows.map((row, idx) => (
                (() => {
                    const rowId = getRowIdForNotes(row, idx, rows.length);
                    return (
                        <IniRow
                            key={row.id ?? `${tableName}_${idx}`}
                            row={row}
                            index={idx}
                            tableName={tableName}
                            rowId={rowId}
                            noteText={notesByKey?.get ? notesByKey.get(`${tableName}:${rowId}`) : ''}
                            canEditNotes={mode === 'surveyor'}
                            onSaveRowNote={onSaveRowNote}
                            canEditFields={mode === 'developer'}
                            onSaveIniField={onSaveIniField}
                            editsByKey={editsByKey}
                        />
                    );
                })()
            ))}
        </>
    );
}

function InitialReportFactContainers({ tableName, rows }) {
    const list = Array.isArray(rows) ? rows : [];

    const hiddenKeys = new Set([
        'id',
        'project_reference',
        'created_at',
        'updated_at',
        'createdAt',
        'updatedAt'
    ]);

    const showRowPrefix = list.length > 1;
    const facts = [];
    list.forEach((row, rowIndex) => {
        const rowId = getRowIdForNotes(row, rowIndex, list.length);

        const primary = [];
        for (const [k, v] of Object.entries(row || {})) {
            if (hiddenKeys.has(k)) continue;
            const normalizedKey = String(k).trim().toLowerCase();
            if (normalizedKey === 'surveyor_opinion' || normalizedKey === 'surveyor_comments' || normalizedKey === 'rag' || normalizedKey.endsWith('_rag')) continue;
            primary.push([k, v]);
        }

        const ordered = filterInfoRowFields({ tableName, row, fields: [...primary] });
        for (const [k, v] of ordered) {
            facts.push({
                key: `${String(rowId)}:${String(k)}`,
                label: prettyFieldName(k),
                value: formatValue(v, { truncate: false }),
                source: 'Questionnaire',
                recordLabel: showRowPrefix ? `Record ${rowIndex + 1}` : ''
            });
        }
    });

    return <InitialReportInformationGrid facts={facts} />;
}

function GenericUploadsList({ uploads, tableName, notesByKey, canEditNotes, onSaveRowNote }) {
    const [preview, setPreview] = useState(null);
    const items = Array.isArray(uploads) ? uploads : [];

    const INI_UPLOADS_BASE_URL = process.env.REACT_APP_INI_UPLOADS_BASE_URL || getDefaultInitialqBaseUrl();

    function buildUrl(row) {
        const raw = row?.file_path || row?.filePath || '';
        const p = String(raw || '').trim();
        if (!p) return '';
        if (/^https?:\/\//i.test(p)) return p;
        const base = String(INI_UPLOADS_BASE_URL || '').replace(/\/+$/, '');
        const rel = p.replace(/^\/+/, '');
        return `${base}/${rel}`;
    }

    function canInline(contentType, fileName) {
        const ct = String(contentType || '').toLowerCase();
        if (ct.includes('pdf')) return true;
        if (ct.startsWith('image/')) return true;
        const name = String(fileName || '').toLowerCase();
        return name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.webp');
    }

    if (items.length === 0) {
        return <div style={{ color: '#6b7280' }}>No uploads yet.</div>;
    }

    // Newest first when possible
    items.sort((a, b) => {
        const at = a?.created_at || a?.upload_date || a?.createdAt || a?.uploadDate;
        const bt = b?.created_at || b?.upload_date || b?.createdAt || b?.uploadDate;
        const atMs = at ? new Date(at).getTime() : 0;
        const btMs = bt ? new Date(bt).getTime() : 0;
        return btMs - atMs;
    });

    return (
        <>
            <div className="uploads-list">
                <div className="upload-group">
                    <div className="upload-group-body">
                        {items.map((u, idx) => {
                            const rowId = getRowIdForNotes(u, idx, items.length);
                            const key = u?.id ?? `${tableName || 'uploads'}_${rowId}`;
                            const fileName = u?.file_name || u?.document_name || u?.name || `Upload ${idx + 1}`;
                            const displayName = abridgeFileName(fileName);
                            const url = buildUrl(u);
                            const createdAt = (u?.created_at || u?.upload_date) ? new Date(u.created_at || u.upload_date).toLocaleString() : '';
                            const note = notesByKey?.get ? notesByKey.get(`${tableName}:${rowId}`) : '';

                            return (
                                <div key={key} className="upload-item" style={{ alignItems: 'stretch' }}>
                                    <div className="upload-item-main">
                                        <Chip size="small" variant="outlined" color="info" label="Data Upload" sx={{ mb: 0.75, height: 24, fontSize: 11, fontWeight: 750 }} />
                                        <div className="upload-item-line">
                                            {url ? (
                                                <button
                                                    type="button"
                                                    onClick={() => setPreview({ url, fileName, contentType: u?.content_type || u?.contentType })}
                                                    className="upload-item-name"
                                                    title={fileName}
                                                >
                                                    {displayName}
                                                </button>
                                            ) : (
                                                <div className="doc-item-name" title={fileName}>{displayName}</div>
                                            )}

                                            {createdAt ? <span className="upload-item-date">- {createdAt}</span> : null}
                                        </div>

                                        <RowNoteEditor
                                            tableName={tableName}
                                            rowId={rowId}
                                            noteText={note}
                                            canEditNotes={canEditNotes}
                                            onSaveRowNote={onSaveRowNote}
                                            showNotes={false}
                                        />
                                    </div>

                                    {url ? (
                                        <a href={url} target="_blank" rel="noreferrer" className="upload-item-open">
                                            Open
                                        </a>
                                    ) : (
                                        <span className="upload-item-open" style={{ color: '#9ca3af' }}>
                                            Missing file
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {preview && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999
                    }}
                    onClick={() => setPreview(null)}
                >
                    <div
                        style={{ width: 'min(1000px, 95vw)', height: 'min(80vh, 800px)', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '10px 12px', background: '#111827', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                            <div style={{ fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={preview.fileName}>
                                {preview.fileName}
                            </div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <a href={preview.url} target="_blank" rel="noreferrer" style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>
                                    Open in new tab
                                </a>
                                <button type="button" onClick={() => setPreview(null)} style={{ padding: '6px 10px', fontWeight: 800, cursor: 'pointer' }}>
                                    Close
                                </button>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 'calc(100% - 44px)', background: '#f9fafb' }}>
                            {canInline(preview.contentType, preview.fileName) ? (
                                <iframe title="Upload preview" src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
                            ) : (
                                <div style={{ padding: 16, color: '#374151' }}>
                                    Preview not available for this file type. Use “Open in new tab” to download/view.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function looksLikeSurveyReportDocument(doc) {
    const related = String(doc?.related_section || '').trim().toLowerCase();
    const type = String(doc?.document_type || '').trim().toLowerCase();
    const title = String(doc?.document_title || doc?.document_name || '').trim().toLowerCase();
    const hay = `${type} ${title}`;
    if (related.includes('section_5')) return true;
    return /\b(survey|report)\b/i.test(hay);
}

function hasUsefulDocumentMetadata(meta) {
    const effective = meta?.effective || {};
    return [
        effective.display_title,
        effective.authoring_company,
        effective.report_date,
        effective.purpose,
        effective.methods,
        effective.conclusions,
        effective.recommendations,
        ...(Array.isArray(effective.summary_lines) ? effective.summary_lines : [])
    ].some((value) => String(value || '').trim());
}

function MetaField({ label, value, onChange, onBlur, disabled, multiline = false, placeholder, saving }) {
    const commonStyle = {
        width: '100%',
        padding: '6px 8px',
        borderRadius: 8,
        border: '1px solid var(--border)',
        fontWeight: 700,
        fontSize: 13,
        background: disabled ? 'color-mix(in srgb, var(--panel) 92%, var(--text) 8%)' : 'var(--input-bg)',
        color: 'var(--text)',
        resize: multiline ? 'vertical' : undefined
    };

    return (
        <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 10, alignItems: 'start' }}>
            <div style={{ fontWeight: 900, fontSize: 12, color: '#374151', paddingTop: 7 }}>
                {label}{saving ? ' (saving…)' : ''}
            </div>
            {multiline ? (
                <textarea
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={disabled}
                    placeholder={placeholder || ''}
                    rows={2}
                    style={commonStyle}
                />
            ) : (
                <input
                    type="text"
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={disabled}
                    placeholder={placeholder || ''}
                    style={commonStyle}
                />
            )}
        </div>
    );
}

function DocumentMetadataPanel({ meta, canEdit, onSave }) {
    const effective = meta?.effective || {};
    const docId = meta?.document_id;

    const [draft, setDraft] = useState(() => ({
        display_title: String(effective.display_title || ''),
        authoring_company: String(effective.authoring_company || ''),
        report_date: String(effective.report_date || ''),
        purpose: String(effective.purpose || ''),
        methods: String(effective.methods || ''),
        conclusions: String(effective.conclusions || ''),
        recommendations: String(effective.recommendations || ''),
        summary_lines: Array.isArray(effective.summary_lines) ? effective.summary_lines.map((x) => String(x ?? '')) : ['', '', '', '']
    }));

    const [savingField, setSavingField] = useState('');
    const [err, setErr] = useState('');

    useEffect(() => {
        setDraft({
            display_title: String(effective.display_title || ''),
            authoring_company: String(effective.authoring_company || ''),
            report_date: String(effective.report_date || ''),
            purpose: String(effective.purpose || ''),
            methods: String(effective.methods || ''),
            conclusions: String(effective.conclusions || ''),
            recommendations: String(effective.recommendations || ''),
            summary_lines: Array.isArray(effective.summary_lines) ? effective.summary_lines.map((x) => String(x ?? '')) : ['', '', '', '']
        });
        setErr('');
        setSavingField('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId, effective?.display_title, effective?.authoring_company, effective?.report_date, effective?.purpose, effective?.methods, effective?.conclusions, effective?.recommendations, Array.isArray(effective?.summary_lines) ? effective.summary_lines.join('\n') : '']);

    async function commit(fieldName) {
        if (!canEdit) return;
        if (!onSave) return;
        if (!docId) return;

        setErr('');
        setSavingField(fieldName);
        try {
            if (fieldName === 'summary_lines') {
                await onSave(docId, { summary_lines: Array.isArray(draft.summary_lines) ? draft.summary_lines : [] });
            } else {
                await onSave(docId, { [fieldName]: draft[fieldName] });
            }
        } catch (e) {
            setErr(e?.message || 'Failed to save metadata');
        } finally {
            setSavingField('');
        }
    }

    const aiProcessedAt = meta?.ai?.ai_processed_at || null;
    const lastError = String(meta?.ai?.last_error || '').trim();
    const showStatus = !aiProcessedAt || !!lastError;

    return (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 8, border: '1px solid #e5e7eb', background: 'var(--panel)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 900, fontSize: 13 }}>Extracted document information</div>
                    <Chip size="small" variant="outlined" color="secondary" label="AI Data Collection" sx={{ height: 24, fontSize: 11, fontWeight: 750 }} />
                </div>
                {showStatus ? (
                    <div style={{ fontSize: 12, fontWeight: 800, color: lastError ? '#ef4444' : 'var(--muted)' }}>
                        {lastError ? `AI error: ${lastError.slice(0, 140)}` : 'AI extraction pending…'}
                    </div>
                ) : (
                    <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--muted)' }}>
                        AI extracted
                    </div>
                )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <MetaField
                    label="Title"
                    value={draft.display_title}
                    onChange={(v) => setDraft((p) => ({ ...p, display_title: v }))}
                    onBlur={() => commit('display_title')}
                    disabled={!canEdit}
                    saving={savingField === 'display_title'}
                />
                <MetaField
                    label="Authoring Company"
                    value={draft.authoring_company}
                    onChange={(v) => setDraft((p) => ({ ...p, authoring_company: v }))}
                    onBlur={() => commit('authoring_company')}
                    disabled={!canEdit}
                    saving={savingField === 'authoring_company'}
                />
                <MetaField
                    label="Report Date"
                    value={draft.report_date}
                    onChange={(v) => setDraft((p) => ({ ...p, report_date: v }))}
                    onBlur={() => commit('report_date')}
                    disabled={!canEdit}
                    placeholder="e.g. Jan 2026"
                    saving={savingField === 'report_date'}
                />
                <MetaField
                    label="Purpose"
                    value={draft.purpose}
                    onChange={(v) => setDraft((p) => ({ ...p, purpose: v }))}
                    onBlur={() => commit('purpose')}
                    disabled={!canEdit}
                    multiline
                    saving={savingField === 'purpose'}
                />
                <MetaField
                    label="Methods"
                    value={draft.methods}
                    onChange={(v) => setDraft((p) => ({ ...p, methods: v }))}
                    onBlur={() => commit('methods')}
                    disabled={!canEdit}
                    multiline
                    saving={savingField === 'methods'}
                />
                <MetaField
                    label="Conclusions"
                    value={draft.conclusions}
                    onChange={(v) => setDraft((p) => ({ ...p, conclusions: v }))}
                    onBlur={() => commit('conclusions')}
                    disabled={!canEdit}
                    multiline
                    saving={savingField === 'conclusions'}
                />
                <MetaField
                    label="Recommendations"
                    value={draft.recommendations}
                    onChange={(v) => setDraft((p) => ({ ...p, recommendations: v }))}
                    onBlur={() => commit('recommendations')}
                    disabled={!canEdit}
                    multiline
                    saving={savingField === 'recommendations'}
                />

                <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 10, alignItems: 'start' }}>
                    <div style={{ fontWeight: 900, fontSize: 12, color: '#374151', paddingTop: 7 }}>
                        Summary Lines{savingField === 'summary_lines' ? ' (saving…)' : ''}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {[0, 1, 2, 3].map((i) => (
                            <input
                                key={i}
                                type="text"
                                value={draft.summary_lines?.[i] ?? ''}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    setDraft((p) => {
                                        const next = Array.isArray(p.summary_lines) ? [...p.summary_lines] : ['', '', '', ''];
                                        next[i] = v;
                                        return { ...p, summary_lines: next };
                                    });
                                }}
                                onBlur={() => commit('summary_lines')}
                                disabled={!canEdit}
                                style={{
                                    width: '100%',
                                    padding: '6px 8px',
                                    borderRadius: 8,
                                    border: '1px solid #d1d5db',
                                    fontWeight: 700,
                                    fontSize: 13,
                                    background: canEdit ? '#fff' : '#f3f4f6',
                                    color: '#111827'
                                }}
                            />
                        ))}
                    </div>
                </div>

                {err ? (
                    <div style={{ color: '#991b1b', fontWeight: 800, fontSize: 12 }}>{err}</div>
                ) : null}
            </div>
        </div>
    );
}

function DocumentsList({ documents, tableName, noteTableName, showNotes = false, notesByKey, canEditNotes, onSaveRowNote, iniDocMetadataById, iniDocMetadataLoading, iniDocMetadataError, canEditMetadata = false, onSaveMetadata }) {
    const [preview, setPreview] = useState(null);
    const items = Array.isArray(documents) ? documents : [];
    const effectiveNoteTableName = String(noteTableName || tableName || '');

    const INI_UPLOADS_BASE_URL = process.env.REACT_APP_INI_UPLOADS_BASE_URL || getDefaultInitialqBaseUrl();

    function buildUrl(doc) {
        const raw = doc?.file_path || doc?.filePath || '';
        const p = String(raw || '').trim();
        if (!p) return '';
        if (/^https?:\/\//i.test(p)) return p;
        const base = String(INI_UPLOADS_BASE_URL || '').replace(/\/+$/, '');
        const rel = p.replace(/^\/+/, '');
        return `${base}/${rel}`;
    }

    function canInline(contentType, fileName) {
        const ct = String(contentType || '').toLowerCase();
        if (ct.includes('pdf')) return true;
        if (ct.startsWith('image/')) return true;
        const name = String(fileName || '').toLowerCase();
        return name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.webp');
    }

    function normalizeTypeLabel(value) {
        if (!value) return 'Documents';
        return String(value)
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (m) => m.toUpperCase());
    }

    if (items.length === 0) {
        return <div style={{ color: '#6b7280' }}>No documents yet.</div>;
    }

    const groups = new Map();
    for (const d of items) {
        const label = normalizeTypeLabel(d?.document_type);
        const arr = groups.get(label) || [];
        arr.push(d);
        groups.set(label, arr);
    }

    const orderedLabels = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));

    return (
        <>
            {iniDocMetadataLoading ? (
                <div style={{ marginBottom: 10, color: '#6b7280', fontWeight: 700 }}>Loading document metadata…</div>
            ) : null}
            {iniDocMetadataError ? (
                <div style={{ marginBottom: 10, color: '#991b1b', fontWeight: 800 }}>{iniDocMetadataError}</div>
            ) : null}
            <div className="uploads-list">
                {orderedLabels.map((label) => {
                    const groupItems = groups.get(label) || [];

                    groupItems.sort((a, b) => {
                        const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
                        const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
                        return bt - at;
                    });

                    return (
                        <div key={label} className="upload-group">
                            <div className="upload-group-header">
                                <div className="upload-group-title">{label}</div>
                                <div className="upload-group-count">{groupItems.length}</div>
                            </div>
                            <div className="upload-group-body">
                                {groupItems.map((d, idx) => {
                                    const rawRowId = getRowIdForNotes(d, idx, groupItems.length);
                                    const rowId = rawRowId.startsWith('idx:') || rawRowId === 'singleton' ? `${label}|${rawRowId}` : rawRowId;
                                    const key = d?.id ?? `${label}_${rowId}`;
                                    const displayName = abridgeFileName(d?.document_name || 'Document');
                                    const url = buildUrl(d);
                                    const fileName = d?.document_name || 'Document';
                                    const note = notesByKey?.get
                                        ? (notesByKey.get(`${effectiveNoteTableName}:${rowId}`) || notesByKey.get(`${tableName}:${rowId}`) || '')
                                        : '';
                                    const meta = iniDocMetadataById?.get ? iniDocMetadataById.get(String(d?.id)) : null;
                                    const showMeta = looksLikeSurveyReportDocument(d) || hasUsefulDocumentMetadata(meta);
                                    const accessMode = String(meta?.file_access_mode || '').trim().toLowerCase();
                                    const accessWarning = accessMode === 'missing'
                                        ? 'The database row exists, but the file is not currently reachable by the backend for transcription.'
                                        : accessMode === 'remote_candidate'
                                            ? 'The file is not local to this backend; transcription will try the INI uploads service if that URL returns the real file.'
                                            : '';

                                    return (
                                        <div key={key} className="upload-item" style={{ alignItems: 'stretch' }}>
                                            <div className="upload-item-main">
                                                <Chip size="small" variant="outlined" color="info" label="Data Upload" sx={{ mb: 0.75, height: 24, fontSize: 11, fontWeight: 750 }} />
                                                <div className="upload-item-line">
                                                    {url ? (
                                                        <button
                                                            type="button"
                                                            onClick={() => setPreview({ url, fileName, contentType: d?.content_type || d?.contentType })}
                                                            className="upload-item-name"
                                                            title={fileName}
                                                        >
                                                            {displayName}
                                                        </button>
                                                    ) : (
                                                        <div className="doc-item-name" title={fileName}>{displayName}</div>
                                                    )}
                                                </div>

                                                {accessWarning ? (
                                                    <div style={{ marginTop: 6, color: accessMode === 'missing' ? '#991b1b' : '#92400e', fontSize: 12, fontWeight: 700 }}>
                                                        {accessWarning}
                                                    </div>
                                                ) : null}

                                                <RowNoteEditor
                                                    tableName={effectiveNoteTableName}
                                                    rowId={rowId}
                                                    noteText={note}
                                                    canEditNotes={canEditNotes}
                                                    onSaveRowNote={onSaveRowNote}
                                                    showNotes={showNotes}
                                                />

                                                {showMeta && meta ? (
                                                    <DocumentMetadataPanel
                                                        meta={meta}
                                                        canEdit={canEditMetadata}
                                                        onSave={onSaveMetadata}
                                                    />
                                                ) : null}
                                            </div>

                                            {url ? (
                                                <a href={url} target="_blank" rel="noreferrer" className="upload-item-open">
                                                    Open
                                                </a>
                                            ) : (
                                                <span className="upload-item-open" style={{ color: '#9ca3af' }}>
                                                    Missing file
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {preview && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999
                    }}
                    onClick={() => setPreview(null)}
                >
                    <div
                        style={{ width: 'min(1000px, 95vw)', height: 'min(80vh, 800px)', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '10px 12px', background: '#111827', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                            <div style={{ fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={preview.fileName}>
                                {preview.fileName}
                            </div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <a href={preview.url} target="_blank" rel="noreferrer" style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>
                                    Open in new tab
                                </a>
                                <button type="button" onClick={() => setPreview(null)} style={{ padding: '6px 10px', fontWeight: 800, cursor: 'pointer' }}>
                                    Close
                                </button>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 'calc(100% - 44px)', background: '#f9fafb' }}>
                            {canInline(preview.contentType, preview.fileName) ? (
                                <iframe title="Document preview" src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
                            ) : (
                                <div style={{ padding: 16, color: '#374151' }}>
                                    Preview not available for this file type. Use “Open in new tab” to download/view.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Section3UploadsList({ uploads, reference, mode, code, tableName, notesByKey, canEditNotes, onSaveRowNote }) {
    const [preview, setPreview] = useState(null);

    const items = Array.isArray(uploads) ? uploads : [];

    function normalizeCategoryLabel(value) {
        if (!value) return 'Uploaded Documents';
        return String(value)
            .replace(/[-_]/g, ' ')
            .replace(/\b\w/g, (m) => m.toUpperCase());
    }

    function buildUrl(id) {
        const qs = new URLSearchParams();
        qs.set('mode', mode);
        if (mode === 'developer') qs.set('code', code);
        return `${API_BASE_URL}/api/project/${encodeURIComponent(reference)}/uploads/section3/${encodeURIComponent(id)}?${qs.toString()}`;
    }

    function canInline(contentType, fileName) {
        const ct = String(contentType || '').toLowerCase();
        if (ct.includes('pdf')) return true;
        if (ct.startsWith('image/')) return true;
        const name = String(fileName || '').toLowerCase();
        return name.endsWith('.pdf') || name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.gif') || name.endsWith('.webp');
    }

    return (
        <>
            <div className="uploads-list">
                {(() => {
                    const groups = new Map();
                    for (const u of items) {
                        const label = normalizeCategoryLabel(u?.upload_category);
                        const arr = groups.get(label) || [];
                        arr.push(u);
                        groups.set(label, arr);
                    }

                    const orderedLabels = Array.from(groups.keys()).sort((a, b) => a.localeCompare(b));

                    return orderedLabels.map((label) => {
                        const groupItems = groups.get(label) || [];
                        // Sort by created_at (desc) when present
                        groupItems.sort((a, b) => {
                            const at = a?.created_at ? new Date(a.created_at).getTime() : 0;
                            const bt = b?.created_at ? new Date(b.created_at).getTime() : 0;
                            return bt - at;
                        });

                        return (
                            <div key={label} className="upload-group">
                                <div className="upload-group-header">
                                    <div className="upload-group-title">{label}</div>
                                    <div className="upload-group-count">{groupItems.length}</div>
                                </div>
                                <div className="upload-group-body">
                                    {groupItems.map((u) => {
                                        const url = buildUrl(u.id);
                                        const displayName = abridgeFileName(u.file_name || `Upload ${u.id}`);
                                        const createdAt = u.created_at ? new Date(u.created_at).toLocaleString() : '';
                                        const rowId = String(u.id);
                                        const note = notesByKey?.get ? notesByKey.get(`${tableName}:${rowId}`) : '';
                                        return (
                                            <div key={u.id} className="upload-item" style={{ alignItems: 'stretch' }}>
                                                <div className="upload-item-main">
                                                    <Chip size="small" variant="outlined" color="info" label="Data Upload" sx={{ mb: 0.75, height: 24, fontSize: 11, fontWeight: 750 }} />
                                                    <div className="upload-item-line">
                                                        <button
                                                            type="button"
                                                            onClick={() => setPreview({ url, fileName: u.file_name || `upload_${u.id}`, contentType: u.content_type })}
                                                            className="upload-item-name"
                                                            title={u.file_name || ''}
                                                        >
                                                            {displayName}
                                                        </button>
                                                        {createdAt ? <span className="upload-item-date">- {createdAt}</span> : null}
                                                    </div>

                                                    <RowNoteEditor
                                                        tableName={tableName}
                                                        rowId={rowId}
                                                        noteText={note}
                                                        canEditNotes={canEditNotes}
                                                        onSaveRowNote={onSaveRowNote}
                                                        showNotes={false}
                                                    />
                                                </div>

                                                <a href={url} target="_blank" rel="noreferrer" className="upload-item-open">
                                                    Open
                                                </a>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    });
                })()}

                {items.length === 0 && <div style={{ color: '#6b7280' }}>No uploads yet.</div>}
            </div>

            {preview && (
                <div
                    role="dialog"
                    aria-modal="true"
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999
                    }}
                    onClick={() => setPreview(null)}
                >
                    <div
                        style={{ width: 'min(1000px, 95vw)', height: 'min(80vh, 800px)', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{ padding: '10px 12px', background: '#111827', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                            <div style={{ fontWeight: 800, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={preview.fileName}>
                                {preview.fileName}
                            </div>
                            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                <a href={preview.url} target="_blank" rel="noreferrer" style={{ color: '#fff', fontWeight: 800, fontSize: 13 }}>
                                    Open in new tab
                                </a>
                                <button type="button" onClick={() => setPreview(null)} style={{ padding: '6px 10px', fontWeight: 800, cursor: 'pointer' }}>
                                    Close
                                </button>
                            </div>
                        </div>

                        <div style={{ width: '100%', height: 'calc(100% - 44px)', background: '#f9fafb' }}>
                            {canInline(preview.contentType, preview.fileName) ? (
                                <iframe title="Upload preview" src={preview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
                            ) : (
                                <div style={{ padding: 16, color: '#374151' }}>
                                    Preview not available for this file type. Use “Open in new tab” to download/view.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function abridgeFileName(name) {
    const s = String(name || '');
    if (s.length <= 60) return s;
    const dot = s.lastIndexOf('.');
    const ext = dot > 0 && dot >= s.length - 8 ? s.slice(dot) : '';
    const base = ext ? s.slice(0, dot) : s;
    const head = base.slice(0, 30);
    const tail = base.slice(-18);
    return `${head}…${tail}${ext}`;
}

function RowNoteEditor({ tableName, rowId, noteText, canEditNotes, onSaveRowNote, showNotes = true }) {
    const stableTableName = String(tableName || '');
    const stableRowId = rowId !== undefined && rowId !== null ? String(rowId) : '';
    const [draft, setDraft] = useState(String(noteText || ''));
    const [saving, setSaving] = useState(false);
    const [savedAt, setSavedAt] = useState(null);

    useEffect(() => {
        setDraft(String(noteText || ''));
    }, [noteText]);

    async function commit() {
        if (!onSaveRowNote) return;
        if (!canEditNotes) return;
        if (!stableTableName || !stableRowId) return;
        if (String(draft || '') === String(noteText || '')) return;
        setSaving(true);
        try {
            await onSaveRowNote({ tableName: stableTableName, rowId: stableRowId, noteText: draft });
            setSavedAt(Date.now());
        } finally {
            setSaving(false);
        }
    }

    if (!showNotes) return null;
    if (!stableTableName || !stableRowId) return null;
    if (!canEditNotes && !String(noteText || '').trim()) return null;

    return (
        <div className="row-note">
            <div className="row-note-header">
                <div className="row-note-title">Surveyor Note</div>
                <div className="row-note-meta">
                    <span style={{ marginRight: 10, color: '#6b7280' }}>{stableTableName} • {stableRowId}</span>
                    {saving ? 'Saving…' : (savedAt ? `Saved ${new Date(savedAt).toLocaleTimeString()}` : '')}
                </div>
            </div>

            {canEditNotes ? (
                <textarea
                    className="row-note-textarea"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    placeholder="Add comments on the developer’s answers…"
                    rows={3}
                    disabled={saving}
                />
            ) : (
                <div className="row-note-readonly">{draft ? draft : '—'}</div>
            )}
        </div>
    );
}

function formatEditHistoryTitle(edits) {
    const list = Array.isArray(edits) ? edits : [];
    if (!list.length) return '';
    const versions = [];

    const first = list[0];
    versions.push(first?.old_value);
    for (const e of list) versions.push(e?.new_value);

    const unique = [];
    for (const v of versions) {
        const s = v === null || v === undefined || String(v) === '' ? '—' : String(v);
        if (unique.length === 0 || unique[unique.length - 1] !== s) unique.push(s);
    }

    return unique
        .map((v, i) => `Version ${i + 1}: ${v}`)
        .join('\n');
}

function EditableValue({ value, canEdit, onSave, editsTitle }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(String(value ?? ''));
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editing) setDraft(String(value ?? ''));
    }, [value, editing]);

    const isLong = (() => {
        const s = String(draft ?? '');
        return s.length > 120;
    })();

    async function commit() {
        if (!canEdit || !onSave) return;
        if (String(draft ?? '') === String(value ?? '')) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(draft);
            setEditing(false);
        } finally {
            setSaving(false);
        }
    }

    if (!canEdit) {
        return (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{formatValue(value)}</div>
                {editsTitle ? (
                    <span
                        title={editsTitle}
                        style={{
                            display: 'inline-block',
                            padding: '1px 6px',
                            borderRadius: 999,
                            background: '#f3f4f6',
                            border: '1px solid #e5e7eb',
                            color: '#374151',
                            fontSize: 12,
                            fontWeight: 800,
                            cursor: 'help'
                        }}
                    >
                        Edits
                    </span>
                ) : null}
            </div>
        );
    }

    if (!editing) {
        return (
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{formatValue(value)}</div>
                <button
                    type="button"
                    onClick={() => setEditing(true)}
                    style={{
                        padding: '4px 8px',
                        fontSize: 12,
                        fontWeight: 800,
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        background: '#fff',
                        cursor: 'pointer'
                    }}
                >
                    Edit
                </button>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {isLong ? (
                <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={saving}
                    rows={4}
                    style={{
                        width: '100%',
                        minHeight: 90,
                        padding: '8px 10px',
                        borderRadius: 8,
                        border: '1px solid #d1d5db',
                        fontWeight: 700,
                        fontSize: 13,
                        resize: 'vertical'
                    }}
                />
            ) : (
                <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    disabled={saving}
                    style={{
                        width: '100%',
                        padding: '6px 8px',
                        borderRadius: 6,
                        border: '1px solid #d1d5db',
                        fontWeight: 800,
                        fontSize: 13
                    }}
                />
            )}

            <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={commit} disabled={saving} style={{ padding: '6px 10px', fontWeight: 800 }}>
                    {saving ? 'Saving…' : 'Save'}
                </button>
                <button
                    type="button"
                    onClick={() => { setDraft(String(value ?? '')); setEditing(false); }}
                    disabled={saving}
                    style={{ padding: '6px 10px', fontWeight: 800, background: '#f3f4f6' }}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
}

function IniRow({ row, index, tableName, rowId, noteText, canEditNotes, onSaveRowNote, canEditFields, onSaveIniField, editsByKey }) {
    const entries = Object.entries(row || {});
    const primary = [];

    const hiddenKeys = new Set([
        'id',
        'project_reference',
        'created_at',
        'updated_at',
        'createdAt',
        'updatedAt'
    ]);

    for (const [k, v] of entries) {
        if (hiddenKeys.has(k)) {
            continue;
        } else {
            primary.push([k, v]);
        }
    }

    const ordered = filterInfoRowFields({ tableName, row, fields: [...primary] });

    const canEdit = Boolean(canEditFields && typeof onSaveIniField === 'function');
    const editRowId = row?.id !== null && row?.id !== undefined ? String(row.id) : 'single';

    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
            <div className="info-portal-row-grid">
                {ordered.map(([k, v]) => (
                    <div key={k} style={{ borderBottom: '1px dashed #e5e7eb', paddingBottom: 8 }}>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{prettyFieldName(k)}</div>
                        <div style={{ marginTop: 6 }}>
                            {(() => {
                                const editsKey = `${tableName}:${editRowId}:${String(k)}`;
                                const edits = editsByKey?.get ? editsByKey.get(editsKey) : null;
                                const editsTitle = formatEditHistoryTitle(edits);
                                return (
                                    <EditableValue
                                        value={v}
                                        canEdit={canEdit}
                                        editsTitle={!canEdit ? editsTitle : ''}
                                        onSave={(next) => onSaveIniField?.({
                                            tableName,
                                            rowId: row?.id ?? null,
                                            patch: { [k]: next }
                                        })}
                                    />
                                );
                            })()}
                        </div>
                    </div>
                ))}
            </div>

            <RowNoteEditor
                tableName={tableName}
                rowId={rowId}
                noteText={noteText}
                canEditNotes={canEditNotes}
                onSaveRowNote={onSaveRowNote}
            />
        </div>
    );
}

function prettyTableName(name) {
    const n = String(name || '');

    const overrides = {
        ini_section_3_location: 'Location & Site Information',
        ini_section_3_uploads: 'Uploaded Documents',
        ini_section_4_planning_conditions: 'Uploaded Documents',
        ini_documents__surveys: 'Documents',
        ini_documents__cost: 'Project Cost Documentation',
        ini_documents__programme: 'Project Programme',
        ini_documents__experience: 'Borrower/Contractor Insurances',
        ini_documents__professional_team: 'Consultant PI Insurances',
        ini_documents__other: 'Other Documents'
    };

    if (overrides[n]) return overrides[n];

    return n
        .replace(/^ini_/, '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function prettyFieldName(name) {
    const n = String(name || '');

    const overrides = {
        application_date: 'Application Submission Date',
        decision_date: 'Planning Decision Date',
        further_decision_date: 'Further Planning Decision Date',
        further_notice_effect: 'Further Notice / Amendment Effect',
        listed_building_decision_date: 'Listed Building Decision Date',

        total_conditions_count: 'Total Conditions Count',
        key_conditions_summary: 'Key Conditions Summary',

        cil_payment_status: 'CIL Payment Status',
        cil_amount: 'CIL Amount',
        cil_paid_state: 'CIL Paid State',

        health_safety_overview: 'Health & Safety Overview',
        asbestos_status: 'Asbestos Status',
        construction_phase_plan_status: 'Construction Phase Plan Status',
        site_constraints_safety: 'Site Constraints / Safety Notes',

        warranty_status: 'Building Warranty Status',
        site_occupation_status: 'Site Occupation Status',
        site_occupation_details: 'Occupiers / Segregation Plan',
        delivery_access_constraints: 'Delivery and Access Constraints',
        delivery_access_constraints_details: 'Delivery and Access Constraints (Details)',
        sensitive_neighbours: 'Sensitive Neighbours Nearby',
        site_hazards_constraints: 'Site Hazards / Constraints'
    };
    if (overrides[n]) return overrides[n];

    return n
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

function formatValue(v, { truncate = true } = {}) {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return String(v);

    if (Array.isArray(v)) {
        // Special-case insurance table rows (from ini_section_9_procurement)
        const rows = v
            .filter((r) => r && typeof r === 'object' && !Array.isArray(r))
            .map((r) => ({
                coverage_type: String(r.coverage_type || '').trim(),
                coverage_amount: String(r.coverage_amount || '').trim(),
                expiry_date: String(r.expiry_date || '').trim()
            }))
            .filter((r) => r.coverage_type || r.coverage_amount || r.expiry_date);

        if (rows.length) {
            return rows
                .map((r) => {
                    const bits = [r.coverage_type || 'Coverage'];
                    if (r.coverage_amount) bits.push(r.coverage_amount);
                    if (r.expiry_date) bits.push(`Expiry: ${formatLongOrdinalDate(r.expiry_date)}`);
                    return `• ${bits.join(' — ')}`;
                })
                .join('\n');
        }

        // Special-case collateral warranty rows (from ini_section_11_professional_team)
        const cwRows = v
            .filter((r) => r && typeof r === 'object' && !Array.isArray(r))
            .map((r) => ({
                consultant: String(r.consultant || '').trim(),
                status: String(r.status || '').trim()
            }))
            .filter((r) => r.consultant || r.status);

        if (cwRows.length) {
            return cwRows
                .map((r) => {
                    const who = r.consultant || 'Consultant';
                    const st = r.status || '—';
                    return `• ${who} — ${st}`;
                })
                .join('\n');
        }

        try {
            return JSON.stringify(v);
        } catch {
            return String(v);
        }
    }

    if (typeof v === 'string') {
        const formattedDate = formatLongOrdinalDate(v);
        if (formattedDate !== v) return formattedDate;
        if (!truncate || v.length <= 140) return v;
        return `${v.slice(0, 140)}…`;
    }
    try {
        return JSON.stringify(v);
    } catch {
        return String(v);
    }
}

function formatLongOrdinalDate(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';

    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*$)/);
    if (!m) return s;

    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return s;

    const date = new Date(year, month - 1, day);
    if (!Number.isFinite(date.getTime())) return s;

    const monthName = date.toLocaleString('en-GB', { month: 'long' });
    const mod10 = day % 10;
    const mod100 = day % 100;
    const suffix = (mod10 === 1 && mod100 !== 11)
        ? 'st'
        : (mod10 === 2 && mod100 !== 12)
            ? 'nd'
            : (mod10 === 3 && mod100 !== 13)
                ? 'rd'
                : 'th';

    return `${day}${suffix} ${monthName} ${year}`;
}

function formatInsuranceTrackerDate(raw) {
    const s = String(raw || '').trim();
    if (!s) return '';

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*$)/);
    if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;

    return s;
}

function parseInsuranceTrackerDate(raw) {
    const s = String(raw || '').trim();
    if (!s) return null;

    const asDate = (year, monthZeroBased, day) => {
        if (!Number.isFinite(year) || !Number.isFinite(monthZeroBased) || !Number.isFinite(day)) return null;
        const date = new Date(Date.UTC(year, monthZeroBased, day));
        return Number.isFinite(date.getTime()) ? date : null;
    };

    const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|[T\s].*$)/);
    if (iso) return asDate(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));

    const uk = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
    if (uk) return asDate(Number(uk[3]), Number(uk[2]) - 1, Number(uk[1]));

    const named = s.match(/^(\d{1,2})(?:st|nd|rd|th)?\s+([A-Za-z]+)\s+(\d{4})$/i);
    if (named) {
        const months = {
            january: 0,
            february: 1,
            march: 2,
            april: 3,
            may: 4,
            june: 5,
            july: 6,
            august: 7,
            september: 8,
            october: 9,
            november: 10,
            december: 11
        };
        const month = months[String(named[2] || '').toLowerCase()];
        if (month != null) return asDate(Number(named[3]), month, Number(named[1]));
    }

    return null;
}

function isInsuranceTrackerDateExpired(raw) {
    const date = parseInsuranceTrackerDate(raw);
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    const today = new Date();
    const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    return date.getTime() < todayUtc.getTime();
}

function Field({ label, value }) {
    return (
        <Box
            sx={{
                p: 1.4,
                borderRadius: 2.5,
                backgroundColor: alpha('#ffffff', 0.72),
                border: `1px solid ${alpha('#0f172a', 0.05)}`,
                minHeight: 82
            }}
        >
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '0.98rem', fontWeight: 700, mt: 0.7, color: 'text.primary', lineHeight: 1.55 }}>
                {value || '—'}
            </Typography>
        </Box>
    );
}
